import { createInProcessTransport } from './chunk-JCTUAEMM.js';
import { collectTraitRefsFromValue, collectTraitRefsFromEffects } from './chunk-SLXJYKVO.js';
import { parseCron, cronMinuteKey, cronMatches, isValidCronExpression } from './chunk-TJQWHYNZ.js';
import { createContextFromBindings, interpolateValue, deferEntityBindings, applyRowAccess, checkMutationAccess, accessDeniedMessage } from './chunk-LPUBGPML.js';
import { __export } from './chunk-MLKGABMK.js';
import { getStdEffectOperators, getOperatorRunsOn, effectSiteFor } from '@almadar/std/registry';
import { createLogger, setNamespaceLevel } from '@almadar/logger';
import { isInlineTrait, LIFECYCLE_EVENTS, DispatchMemory, dispatchVisitKey, normalizeCallSiteConfigToValues, isEntityCall, storesRowsInBrowser, computeTraitDispatchMode, payloadTypeContainer, isRuntimeEntity, applyListenPayloadMapping, isPageReference, themeDataKey, omitFrameFields, JsonValueSchema, isPersistBatchOperation, findExternalInput, orbitalInlineEntities, parseOrbitalSchema, readableEntitiesOf, externalInputsOf, isStaticallyFalse, getNestedValue, instancesForLocale, OrbitalSchemaSchema, isEntityReference, parseEntityRef, parseImportedTraitRef, isPageReferenceString, isPageReferenceObject, parsePageRef, parseOrbitalRef, overrideDeclaredKnobs, asTraitId, asEntityId, asPageId, asOrbitalId, deriveId, isReferenceConfigType, configRefEventKnob, resolveConfigRefEventName, isCallSiteConfigDeclaration, asEventId, ledgerRename, isConfigRedeclaration, idPrefix, eventListPropsOf, splitEventAddress, joinEventAddress, isEventPayloadValue } from '@almadar/core';
export { LIFECYCLE_EVENTS, normalizeCallSiteConfigToValues } from '@almadar/core';
import { evaluateGuard, evaluateListenPayloadExpr, SExpressionEvaluator, evaluate, executeEffects } from '@almadar/evaluator';
import { sortRows } from '@almadar/db/rows';
import { RESERVED_FIELD_NAMES, entityAccessPolicies, identityEntitiesOf, roleVocabularyOf } from '@almadar/core/mock';
import { getPatternFieldsContract } from '@almadar/core/patterns';
import { InMemoryPersistence, MockPersistenceAdapter, entitySchemaOf } from '@almadar/db/mock';
import { IndexedDbPersistence } from '@almadar/db/browser';

function isEffectTuple(value) {
  return Array.isArray(value) && typeof value[0] === "string";
}
var EFFECT_ALIASES = { render: "render-ui" };
function withAliases(ops) {
  const aliases = Object.keys(EFFECT_ALIASES).filter((alias) => ops.includes(EFFECT_ALIASES[alias]));
  return [...ops, ...aliases].sort();
}
function effectsNotOn(site) {
  return withAliases(getStdEffectOperators().filter((op) => getOperatorRunsOn(op) !== site));
}
var HANDLER_MANIFEST = {
  client: effectsNotOn("server"),
  server: effectsNotOn("client"),
  test: withAliases(getStdEffectOperators()),
  ssr: withAliases(["render-ui", "fetch", "emit", "set", "log", "ref", "deref"])
};
var log = createLogger("almadar:runtime:eventbus");
var EventBus = class {
  listeners = /* @__PURE__ */ new Map();
  /** Maximum recursion depth before circuit breaker activates (RCG-05) */
  maxDepth;
  /** Current emission depth for circular loop detection */
  depth = 0;
  constructor(options = {}) {
    void options.debug;
    this.maxDepth = options.maxDepth ?? 10;
  }
  /**
   * Emit an event to all registered listeners.
   *
   * Includes circuit breaker (RCG-05): if emit is called recursively
   * beyond `maxDepth`, the event is dropped and an error is logged.
   * This prevents infinite loops from circular emit/listen chains.
   */
  emit(type, payload, source, routingKey) {
    if (this.depth >= this.maxDepth) {
      log.error("circular event loop dropped", { type, depth: this.depth, maxDepth: this.maxDepth });
      return;
    }
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      source
    };
    const deliveryKey = routingKey ?? type;
    const primary = this.listeners.get(deliveryKey);
    const secondary = routingKey !== void 0 && routingKey !== type ? this.listeners.get(type) : void 0;
    const listenerCount = (primary?.size ?? 0) + (secondary?.size ?? 0);
    if (listenerCount > 0) {
      log.debug("emit", { type, listenerCount, depth: this.depth });
    } else {
      log.debug("emit no listeners", { type });
    }
    this.depth++;
    try {
      const delivered = /* @__PURE__ */ new Set();
      const deliverTo = (set) => {
        if (!set) return;
        for (const listener of Array.from(set)) {
          if (delivered.has(listener)) continue;
          delivered.add(listener);
          try {
            listener(event);
          } catch (error) {
            log.error("listener threw", { type, error: error instanceof Error ? error : String(error) });
          }
        }
      };
      deliverTo(primary);
      deliverTo(secondary);
      if (type !== "*") {
        const wildcardListeners = this.listeners.get("*");
        if (wildcardListeners) {
          for (const listener of Array.from(wildcardListeners)) {
            try {
              listener(event);
            } catch (error) {
              log.error("wildcard listener threw", { error: error instanceof Error ? error : String(error) });
            }
          }
        }
      }
    } finally {
      this.depth--;
    }
  }
  /**
   * Subscribe to an event type
   */
  on(type, listener) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, /* @__PURE__ */ new Set());
    }
    const listeners = this.listeners.get(type);
    listeners.add(listener);
    log.debug("subscribe", { type, total: listeners.size });
    return () => {
      listeners.delete(listener);
      log.debug("unsubscribe", { type, remaining: listeners.size });
      if (listeners.size === 0) {
        this.listeners.delete(type);
      }
    };
  }
  /**
   * Subscribe to ALL events (wildcard listener)
   * Useful for event tracking, logging, debugging
   */
  onAny(listener) {
    return this.on("*", listener);
  }
  /**
   * Check if there are listeners for an event type
   */
  hasListeners(type) {
    const listeners = this.listeners.get(type);
    return listeners !== void 0 && listeners.size > 0;
  }
  /**
   * Get all registered event types
   */
  getRegisteredEvents() {
    return Array.from(this.listeners.keys());
  }
  /**
   * Clear all listeners
   */
  clear() {
    log.debug("clear", { eventTypeCount: this.listeners.size });
    this.listeners.clear();
  }
  /**
   * Get listener count for an event type (for testing)
   */
  getListenerCount(type) {
    return this.listeners.get(type)?.size ?? 0;
  }
};

// src/time/TickScheduler.ts
var EVERY_PASS = 0;
var CRON_CHECK_INTERVAL_MS = 1e3;
var TickScheduler = class {
  ticks = /* @__PURE__ */ new Map();
  nextId = 1;
  running = false;
  paused = false;
  frameHandle = null;
  hasRaf;
  constructor() {
    this.hasRaf = typeof requestAnimationFrame === "function" && typeof cancelAnimationFrame === "function";
  }
  /**
   * Register a tick. `intervalMs <= 0` means "every pass" (a frame-interval
   * tick, matching LOLO's `interval: "frame"`). Returns a handle to stop
   * just this tick.
   */
  add(intervalMs, onDue) {
    const id = this.nextId++;
    this.ticks.set(id, {
      kind: "interval",
      intervalMs: intervalMs > 0 ? intervalMs : EVERY_PASS,
      accumulatedMs: 0,
      lastTimestamp: null,
      onDue
    });
    this.ensureRunning();
    return {
      stop: () => {
        this.ticks.delete(id);
        this.maybeStop();
      }
    };
  }
  /**
   * Register a cron-scheduled tick (`ticks { ... every "0 9 * * *" }`).
   * Fires at most once per matching calendar minute — the same dedup shape
   * `os/watch-cron` uses. Throws if `expression` isn't a valid 5-field cron
   * string; callers on a path the compiler has already validated (e.g.
   * generated code) won't hit this, but a raw runtime value might.
   */
  addCron(expression, onDue) {
    const fields = parseCron(expression);
    const id = this.nextId++;
    this.ticks.set(id, {
      kind: "cron",
      fields,
      accumulatedMs: CRON_CHECK_INTERVAL_MS,
      // check on the first pass, not after a full second
      lastTimestamp: null,
      lastFiredMinuteKey: -1,
      onDue
    });
    this.ensureRunning();
    return {
      stop: () => {
        this.ticks.delete(id);
        this.maybeStop();
      }
    };
  }
  /** Stop every tick and the shared loop. */
  stopAll() {
    this.ticks.clear();
    this.maybeStop();
  }
  /** True while the scheduler is paused (no tick callback fires and no backlog accumulates). */
  get isPaused() {
    return this.paused;
  }
  /**
   * Halt the shared loop without dropping registered ticks. No `onDue`
   * fires again until `resume()`. A no-op if already paused.
   */
  pause() {
    if (this.paused) return;
    this.paused = true;
    this.stopLoop();
  }
  /**
   * Restart the shared loop after `pause()`. Every registered tick's phase
   * is reset (`lastTimestamp = null`) so the paused duration is never
   * counted as elapsed time — the same "first pass just captures the
   * timestamp" behavior a freshly `add()`-ed tick gets, which is what keeps
   * resume from bursting a backlog of missed beats. A no-op if not paused.
   */
  resume() {
    if (!this.paused) return;
    this.paused = false;
    for (const tick of this.ticks.values()) {
      tick.lastTimestamp = null;
    }
    if (this.ticks.size > 0) {
      this.ensureRunning();
    }
  }
  ensureRunning() {
    if (this.running || this.paused) return;
    this.running = true;
    if (this.hasRaf) {
      const loop = (timestamp) => {
        if (!this.running) return;
        this.advance(timestamp);
        this.frameHandle = requestAnimationFrame(loop);
      };
      this.frameHandle = requestAnimationFrame(loop);
    } else {
      this.frameHandle = setInterval(() => this.advance(Date.now()), 16);
    }
  }
  maybeStop() {
    if (this.ticks.size > 0 || !this.running) return;
    this.stopLoop();
  }
  /** Cancel the active rAF/interval loop, if any, and clear the running flag. */
  stopLoop() {
    this.running = false;
    if (this.frameHandle !== null) {
      if (this.hasRaf) {
        cancelAnimationFrame(this.frameHandle);
      } else {
        clearInterval(this.frameHandle);
      }
      this.frameHandle = null;
    }
  }
  advance(timestamp) {
    for (const tick of this.ticks.values()) {
      if (tick.kind === "cron") {
        this.advanceCron(tick, timestamp);
        continue;
      }
      if (tick.intervalMs <= EVERY_PASS) {
        tick.onDue();
        continue;
      }
      if (tick.lastTimestamp === null) {
        tick.lastTimestamp = timestamp;
        continue;
      }
      tick.accumulatedMs += timestamp - tick.lastTimestamp;
      tick.lastTimestamp = timestamp;
      if (tick.accumulatedMs >= tick.intervalMs) {
        tick.onDue();
        tick.accumulatedMs = tick.accumulatedMs % tick.intervalMs;
      }
    }
  }
  /** Cron ticks check calendar-match at most once a second, deduped per matching minute. */
  advanceCron(tick, timestamp) {
    if (tick.lastTimestamp === null) {
      tick.lastTimestamp = timestamp;
      return;
    }
    tick.accumulatedMs += timestamp - tick.lastTimestamp;
    tick.lastTimestamp = timestamp;
    if (tick.accumulatedMs < CRON_CHECK_INTERVAL_MS) return;
    tick.accumulatedMs = 0;
    const now = /* @__PURE__ */ new Date();
    const minuteKey = cronMinuteKey(now);
    if (minuteKey !== tick.lastFiredMinuteKey && cronMatches(tick.fields, now)) {
      tick.lastFiredMinuteKey = minuteKey;
      tick.onDue();
    }
  }
};
function createTickScheduler() {
  return new TickScheduler();
}

// src/time/duration.ts
function parseDurationString(interval) {
  const match = interval.match(/^(\d+)(ms|s|m|h)?$/);
  if (!match) {
    throw new Error(
      `Invalid duration '${interval}': expected a shape like '5ms'/'5s'/'1m'/'1h'`
    );
  }
  const value = parseInt(match[1], 10);
  const unit = match[2] || "ms";
  switch (unit) {
    case "ms":
      return value;
    case "s":
      return value * 1e3;
    case "m":
      return value * 60 * 1e3;
    case "h":
      return value * 60 * 60 * 1e3;
    default:
      return value;
  }
}
function isValidDurationString(interval) {
  return /^(\d+)(ms|s|m|h)?$/.test(interval);
}
var smLog = createLogger("almadar:runtime:sm");
var UNMOUNT_EVENT = "$UNMOUNT";
function findInitialState(trait) {
  if (!trait.states || trait.states.length === 0) {
    smLog.warn("trait-has-no-states", { trait: trait.name });
    return "unknown";
  }
  const markedInitial = trait.states.find((s) => s.isInitial)?.name;
  const firstState = trait.states[0]?.name;
  return markedInitial || firstState || "unknown";
}
function createInitialTraitState(trait) {
  return {
    traitName: trait.name,
    currentState: findInitialState(trait),
    previousState: null,
    lastEvent: null,
    context: {}
  };
}
function findMatchingTransitions(trait, currentState, eventKey, eventId) {
  if (!trait.transitions || trait.transitions.length === 0) {
    return [];
  }
  return trait.transitions.filter((t) => {
    const fromMatches = Array.isArray(t.from) ? t.from.includes(currentState) : t.from === currentState;
    if (!fromMatches) return false;
    if (eventId && t.eventId) {
      return t.eventId === eventId;
    }
    return t.event === eventKey;
  });
}
function findTransition(trait, currentState, eventKey, eventId) {
  return findMatchingTransitions(trait, currentState, eventKey, eventId)[0];
}
function normalizeEventKey(eventKey) {
  if (!eventKey) return "";
  return eventKey.startsWith("UI:") ? eventKey.slice(3) : eventKey;
}
function processEvent(options) {
  const {
    traitState,
    trait,
    eventKey,
    eventId,
    payload,
    entityData,
    config,
    user,
    now,
    dispatch,
    guardMode = "permissive",
    strictBindings = false,
    contextExtensions
  } = options;
  const normalizedEvent = normalizeEventKey(eventKey);
  const candidates = findMatchingTransitions(trait, traitState.currentState, normalizedEvent, eventId);
  if (candidates.length === 0) {
    smLog.debug("noTransition", { trait: trait.name, event: normalizedEvent, currentState: traitState.currentState });
    return {
      executed: false,
      newState: traitState.currentState,
      previousState: traitState.currentState,
      effects: []
    };
  }
  let lastFailedGuardTransition;
  for (const transition of candidates) {
    smLog.debug("processEvent", { trait: trait.name, event: normalizedEvent, currentState: traitState.currentState, to: transition.to });
    if (transition.guard === void 0 || transition.guard === null) {
      return {
        executed: true,
        newState: transition.to,
        previousState: traitState.currentState,
        effects: transition.effects || [],
        transition: {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent
        }
      };
    }
    const ctx = createContextFromBindings({
      entity: entityData,
      payload,
      state: traitState.currentState,
      // Surface call-site config so `@config.X` resolves inside
      // guard expressions (matches the validator's allowed sigil
      // list as of v3.12.0). createContextFromBindings already
      // propagates `bindings.config` to ctx.config when present —
      // we just need to pass it through here.
      config,
      user,
      ...now !== void 0 ? { now } : {},
      fromState: traitState.currentState,
      toState: transition.to,
      ...dispatch !== void 0 ? dispatch : {}
    }, strictBindings, contextExtensions);
    try {
      const guardPasses = evaluateGuard(
        transition.guard,
        ctx
      );
      smLog.debug("guard:evaluate", { trait: trait.name, event: normalizedEvent, guardResult: guardPasses });
      if (guardPasses) {
        return {
          executed: true,
          newState: transition.to,
          previousState: traitState.currentState,
          effects: transition.effects || [],
          transition: {
            from: traitState.currentState,
            to: transition.to,
            event: normalizedEvent
          },
          guardResult: true
        };
      }
      lastFailedGuardTransition = transition;
    } catch (error) {
      if (guardMode === "strict") {
        smLog.error("guard-error-blocks-transition", {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent,
          error: error instanceof Error ? error : String(error)
        });
        return {
          executed: false,
          newState: traitState.currentState,
          previousState: traitState.currentState,
          effects: [],
          transition: {
            from: traitState.currentState,
            to: transition.to,
            event: normalizedEvent
          },
          guardResult: false
        };
      }
      smLog.error("guard-evaluation-error", {
        error: error instanceof Error ? error : String(error)
      });
      return {
        executed: true,
        newState: transition.to,
        previousState: traitState.currentState,
        effects: transition.effects || [],
        transition: {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent
        },
        guardResult: true
      };
    }
  }
  return {
    executed: false,
    newState: traitState.currentState,
    previousState: traitState.currentState,
    effects: [],
    transition: lastFailedGuardTransition ? {
      from: traitState.currentState,
      to: lastFailedGuardTransition.to,
      event: normalizedEvent
    } : void 0,
    guardResult: false
  };
}
var SINGLETON_SCOPE = "__singleton__";
function scopeOf(entityData) {
  const id = entityData && entityData["id"];
  return id ?? SINGLETON_SCOPE;
}
function compositeKey(traitName, scope) {
  return `${traitName}::${scope}`;
}
function selectDispatchCandidates(traitNames, options) {
  const candidates = [];
  for (const traitName of traitNames) {
    if (options.targetTrait !== void 0) {
      if (traitName === options.targetTrait) candidates.push(traitName);
      continue;
    }
    if (options.excludeTrait !== void 0 && traitName === options.excludeTrait) continue;
    if (options.activeTraits && !options.activeTraits.has(traitName)) continue;
    if (options.canHandle && !options.canHandle(traitName)) continue;
    candidates.push(traitName);
  }
  return candidates;
}
var StateMachineManager = class {
  traits = /* @__PURE__ */ new Map();
  /**
   * V4 identity index: trait id → trait name. Populated for traits that
   * carry an `id` (ledger-backed schemas). Lets callers resolve a trait's
   * state by its stable id, which survives a mid-session rename — the
   * name-keyed maps are re-pointed on rename via {@link renameTrait}, but
   * an id holder never has to observe the rename at all. Empty for legacy
   * id-free schemas, where every lookup stays name-keyed (unchanged).
   */
  traitIdToName = /* @__PURE__ */ new Map();
  /**
   * Per-trait call-site config, surfaced to guard expressions so
   * `@config.X` resolves at runtime. Populated by the orbital's
   * registration step (see `OrbitalServerRuntime.registerOrbital`'s
   * `configByTrait` projection). Empty for atom-scope traits whose
   * call-site config wasn't supplied — guards that read `@config.X`
   * in that case will see `undefined` and short-circuit accordingly.
   */
  traitConfigs = /* @__PURE__ */ new Map();
  /**
   * State map keyed by `${traitName}::${entityId | __singleton__}`.
   *
   * Each entity instance of an orbital gets its own copy of every
   * trait's state machine. This is what enables N parallel subagents
   * (one OrbitalProcess row per dispatched orbital) to all run their
   * own Build/Validate/Fix cycle without colliding on shared trait
   * state — the original cause of GAP-AGB-2.
   *
   * Initial state is created lazily on first event for each scope, so
   * adding a trait does not pre-allocate state for every potential
   * entity (and there's no need to know entity ids up front).
   */
  states = /* @__PURE__ */ new Map();
  config;
  observer;
  // Actor-model queues, also keyed by `${traitName}::${entityId}` so
  // each entity gets its own actor loop and concurrent subagents do
  // not serialize through a shared queue.
  queues = /* @__PURE__ */ new Map();
  processing = /* @__PURE__ */ new Set();
  constructor(traits = [], config = {}, observer) {
    this.config = config;
    this.observer = observer;
    for (const trait of traits) {
      this.addTrait(trait);
    }
  }
  /**
   * Set the transition observer for runtime verification.
   * Wire this to `verificationRegistry.recordTransition()` to enable
   * automatic verification tracking.
   */
  setObserver(observer) {
    this.observer = observer;
  }
  /**
   * Add a trait to the manager.
   */
  addTrait(trait) {
    this.traits.set(trait.name, trait);
    if (trait.id !== void 0) {
      this.traitIdToName.set(trait.id, trait.name);
    }
  }
  /**
   * Resolve a trait by its name, in the SAME `TraitDefinition` shape
   * `sendEvent`/`processEvent` use internally (flat `states`/`transitions`,
   * not `Trait` from `@almadar/core`'s nested `stateMachine.*` — the two
   * are NOT structurally interchangeable, confirmed by a `tsup` DTS build
   * failure when a caller tried passing a raw `Trait` to `runTraitCascade`
   * directly). Needed by callers that must re-invoke `processEvent` for a
   * trait outside `sendEvent`'s own loop (same-trait cascade continuation
   * — see `TraitCascade.ts`).
   */
  getTraitDefinition(traitName) {
    return this.traits.get(traitName);
  }
  /**
   * Resolve a trait by its V4 id. Returns undefined when no trait carries
   * that id (legacy schema, or unknown id). Exact-match only — no name
   * similarity.
   */
  getTraitById(traitId) {
    const name = this.traitIdToName.get(traitId);
    return name === void 0 ? void 0 : this.traits.get(name);
  }
  /**
   * Get a trait's current state by its V4 id (id-first lookup). Falls back
   * to `undefined` when the id is unknown. The id survives a rename, so a
   * holder of the id reads the right state without ever seeing the new name.
   */
  getStateById(traitId, entityId) {
    const name = this.traitIdToName.get(traitId);
    return name === void 0 ? void 0 : this.getState(name, entityId);
  }
  /**
   * Apply a mid-session trait rename: re-point the name-keyed maps from
   * `oldName` to `newName`, preserving live state, queues, and the id
   * index. A no-op when the trait isn't registered under `oldName`. This
   * is the interpreter-side of a ledger `curName` edit for the trait's own
   * name-keyed storage; id-keyed references need no update.
   */
  renameTrait(oldName, newName) {
    const trait = this.traits.get(oldName);
    if (!trait || oldName === newName) return;
    const renamed = { ...trait, name: newName };
    this.traits.delete(oldName);
    this.traits.set(newName, renamed);
    if (renamed.id !== void 0) {
      this.traitIdToName.set(renamed.id, newName);
    }
    const remap = (m) => {
      const prefix2 = `${oldName}::`;
      for (const key of [...m.keys()]) {
        if (key.startsWith(prefix2)) {
          const scope = key.slice(prefix2.length);
          const v = m.get(key);
          m.delete(key);
          m.set(`${newName}::${scope}`, v);
        }
      }
    };
    remap(this.states);
    remap(this.queues);
    const prefix = `${oldName}::`;
    for (const key of [...this.processing]) {
      if (key.startsWith(prefix)) {
        this.processing.delete(key);
        this.processing.add(`${newName}::${key.slice(prefix.length)}`);
      }
    }
    const cfg = this.traitConfigs.get(oldName);
    if (cfg !== void 0) {
      this.traitConfigs.set(newName, cfg);
      this.traitConfigs.delete(oldName);
    }
  }
  /**
   * Bind the call-site config for a trait so guard `@config.X`
   * resolves at runtime. Typically called by the orbital
   * registration step right after `addTrait`. Idempotent; passing
   * `undefined` clears the binding.
   */
  setTraitConfig(traitName, config) {
    if (config === void 0) {
      this.traitConfigs.delete(traitName);
    } else {
      this.traitConfigs.set(traitName, config);
    }
  }
  /**
   * Remove a trait from the manager.
   */
  removeTrait(traitName) {
    const removed = this.traits.get(traitName);
    if (removed?.id !== void 0) {
      this.traitIdToName.delete(removed.id);
    }
    this.traits.delete(traitName);
    const prefix = `${traitName}::`;
    for (const key of [...this.states.keys()]) {
      if (key.startsWith(prefix)) this.states.delete(key);
    }
    for (const key of [...this.queues.keys()]) {
      if (key.startsWith(prefix)) this.queues.delete(key);
    }
    for (const key of [...this.processing]) {
      if (key.startsWith(prefix)) this.processing.delete(key);
    }
  }
  /**
   * Get-or-init the state row for a (trait, entityId) pair.
   * Returns undefined if the trait isn't registered.
   */
  getOrInitState(traitName, scope) {
    const trait = this.traits.get(traitName);
    if (!trait) return void 0;
    const key = compositeKey(traitName, scope);
    let state = this.states.get(key);
    if (!state) {
      state = createInitialTraitState(trait);
      this.states.set(key, state);
    }
    return state;
  }
  /**
   * Get current state for a trait.
   *
   * For single-entity orbitals, omit `entityId` — returns the singleton
   * scope. For multi-entity orbitals (e.g. agent OrbitalSubagent), pass
   * the entity row id to look up that specific instance's state.
   */
  getState(traitName, entityId) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key);
    if (existing) return existing;
    return this.getOrInitState(traitName, scope);
  }
  /**
   * Get a flat traitName→state map. For multi-entity orbitals this
   * returns one entry per trait collapsed onto the singleton scope (or
   * the first observed scope if singleton is empty). Use
   * `getAllStatesByScope()` to get the full per-entity view.
   */
  getAllStates() {
    const out = /* @__PURE__ */ new Map();
    for (const traitName of this.traits.keys()) {
      const singleton = this.states.get(compositeKey(traitName, SINGLETON_SCOPE));
      if (singleton) {
        out.set(traitName, singleton);
        continue;
      }
      for (const [key, state] of this.states) {
        if (key.startsWith(`${traitName}::`)) {
          out.set(traitName, state);
          break;
        }
      }
    }
    return out;
  }
  /**
   * Get every per-entity state row, grouped by trait. Useful for
   * inspecting parallel subagent state.
   */
  getAllStatesByScope() {
    const grouped = /* @__PURE__ */ new Map();
    for (const [key, state] of this.states) {
      const sep = key.indexOf("::");
      if (sep < 0) continue;
      const traitName = key.slice(0, sep);
      const scope = key.slice(sep + 2);
      let bucket = grouped.get(traitName);
      if (!bucket) {
        bucket = /* @__PURE__ */ new Map();
        grouped.set(traitName, bucket);
      }
      bucket.set(scope, state);
    }
    return grouped;
  }
  /**
   * Check if a trait can handle an event from its current state.
   */
  canHandleEvent(traitName, eventKey, entityId, eventId) {
    const trait = this.traits.get(traitName);
    const state = this.getOrInitState(traitName, entityId ?? SINGLETON_SCOPE);
    if (!trait || !state) return false;
    return !!findTransition(trait, state.currentState, normalizeEventKey(eventKey), eventId);
  }
  /**
   * The lifecycle event an embedded child can be repainted with: the one it
   * handles from its current state, provided every matching arm stays in that
   * state and only renders. A repaint never moves a trait (a loaded list's
   * `browsing -> loading` INIT) nor reloads it (a fetch + spinner self-loop).
   */
  repaintLifecycleEvent(traitName, entityId) {
    const trait = this.traits.get(traitName);
    const state = this.getOrInitState(traitName, entityId ?? SINGLETON_SCOPE);
    if (!trait || !state) return void 0;
    for (const event of LIFECYCLE_EVENTS) {
      const arms = findMatchingTransitions(trait, state.currentState, event);
      if (arms.length === 0) continue;
      const repaints = arms.every((t) => t.to === state.currentState && (t.effects ?? []).every((effect) => Array.isArray(effect) && effect[0] === "render-ui"));
      return repaints ? event : void 0;
    }
    return void 0;
  }
  /**
   * Send an event to all traits.
   *
   * `entityByTrait` lets callers supply per-trait entity rows for guard
   * evaluation when traits accumulate scalar state via `(set @entity.X)`
   * effects across transitions. The runtime UI hook builds this map from
   * its `traitFieldStatesRef` before each dispatch so guards reading
   * `@entity.X` see prior step writes — required for [runtime] entities
   * that have no persistence row to reload.
   *
   * `eventId` is the V4 dual-carry id sibling of `eventKey` (see
   * {@link ProcessEventOptions.eventId}) — additive and optional, threaded
   * through to `processEvent` for id-primary transition matching.
   *
   * @returns Array of transition results (one per trait that had a matching transition)
   */
  sendEvent(eventKey, payload, entityData, entityByTrait, eventId, targetTrait, user, allowedTraits, excludeTrait) {
    const results = [];
    const scope = scopeOf(entityData);
    const dispatchNow = Date.now();
    const candidates = new Set(
      selectDispatchCandidates(this.traits.keys(), { targetTrait, activeTraits: allowedTraits, excludeTrait })
    );
    for (const [traitName, trait] of this.traits) {
      if (!candidates.has(traitName)) continue;
      const traitState = this.getOrInitState(traitName, scope);
      if (!traitState) continue;
      const key = compositeKey(traitName, scope);
      const perTraitEntity = entityByTrait?.[traitName] ?? entityData;
      const result = processEvent({
        traitState,
        trait,
        eventKey,
        eventId,
        payload,
        entityData: perTraitEntity,
        config: this.traitConfigs.get(traitName),
        user,
        now: dispatchNow,
        guardMode: this.config.guardMode,
        strictBindings: this.config.strictBindings,
        contextExtensions: this.config.contextExtensions
      });
      if (result.executed) {
        this.states.set(key, {
          ...traitState,
          currentState: result.newState,
          previousState: result.previousState,
          lastEvent: normalizeEventKey(eventKey),
          context: { ...traitState.context, ...payload }
        });
        results.push({ traitName, result });
        if (this.observer && result.transition) {
          this.observer.onTransition({
            traitName,
            from: result.transition.from,
            to: result.transition.to,
            event: result.transition.event,
            guardResult: result.guardResult,
            // Effects will be traced when executed — placeholder here
            effects: []
          });
        }
      }
    }
    return results;
  }
  /**
   * Commit a same-trait cascade's FINAL state (see `runTraitCascade` in
   * `TraitCascade.ts`). `sendEvent` above already committed the state for
   * the single hop it computed; when a caller then drives that trait's own
   * emitted event through additional hops via `runTraitCascade` (bypassing
   * `sendEvent` for those extra hops, since they're same-trait continuations
   * rather than a new external dispatch), the manager's tracked state is
   * stale until this is called — otherwise `getAllStates()`/`canHandleEvent()`
   * would report the FIRST hop's state even though the trait actually
   * settled further along. A no-op if the trait has no tracked state yet
   * for this scope (nothing to update).
   */
  setCascadeFinalState(traitName, entityId, finalState, lastEvent) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key);
    if (!existing) return;
    this.states.set(key, {
      ...existing,
      previousState: existing.currentState,
      currentState: finalState,
      lastEvent: normalizeEventKey(lastEvent)
    });
  }
  /**
   * Seed a trait's current state directly (upsert) — the per-request
   * lifecycle hook: a stateless deployment replays the client's own
   * round-tripped states into a fresh manager at request start, so
   * `canHandleEvent`/`getState` see the client's truth without the
   * server holding anything after the response. No-op for an
   * unregistered trait (same contract as `getOrInitState`).
   */
  seedState(traitName, currentState, entityId) {
    const trait = this.traits.get(traitName);
    if (!trait) return;
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key);
    this.states.set(key, {
      ...createInitialTraitState(trait),
      ...existing,
      currentState,
      previousState: existing?.currentState ?? null
    });
  }
  /** Undo an optimistic commit: seed `toState` and report the hop back to the observer. */
  rollbackState(traitName, toState, entityId, cause) {
    const from = this.getState(traitName, entityId)?.currentState;
    this.seedState(traitName, toState, entityId);
    if (from !== void 0 && from !== toState) {
      this.observer?.onRollback?.({ traitName, from, to: toState, cause });
    }
  }
  /**
   * Commit a trait's final state (upsert) and notify the observer per
   * executed hop — the commit half of a caller-driven cascade
   * (`runTraitCascade`), replacing `sendEvent`'s built-in commit +
   * observer notification for callers that drive the cascade themselves.
   * `executedSteps` carries the cascade's per-hop from/to/event so the
   * verification registry sees the same trace shape `sendEvent` emits.
   */
  commitState(traitName, currentState, entityId, lastEvent, executedSteps) {
    const trait = this.traits.get(traitName);
    if (!trait) return;
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key) ?? createInitialTraitState(trait);
    this.states.set(key, {
      ...existing,
      previousState: existing.currentState,
      currentState,
      lastEvent: normalizeEventKey(lastEvent)
    });
    if (this.observer && executedSteps) {
      for (const step of executedSteps) {
        this.observer.onTransition({
          traitName,
          from: step.from,
          to: step.to,
          event: step.event,
          ...step.guardResult !== void 0 ? { guardResult: step.guardResult } : {},
          effects: []
        });
      }
    }
  }
  // ========================================================================
  // Actor-Model Queue API (opt-in, does not affect sendEvent)
  // ========================================================================
  /**
   * Enqueue an event into every trait's per-trait queue.
   *
   * Events are not processed immediately. Call `drainQueue()` for each
   * trait to process them sequentially (actor-model guarantee: one event
   * at a time per trait, effects fully awaited before the next event).
   */
  enqueueEvent(eventKey, payload, entityData, entityByTrait, eventId, user) {
    const scope = scopeOf(entityData);
    const now = Date.now();
    for (const [traitName] of this.traits) {
      const key = compositeKey(traitName, scope);
      const queue = this.queues.get(key) ?? [];
      queue.push({ eventKey, eventId, payload, entityData, entityByTrait, user, now });
      this.queues.set(key, queue);
    }
  }
  /**
   * Drain a single (trait, entity) pair's event queue, processing
   * events sequentially. Pass `entityId` to drain a specific entity
   * instance; omit it to drain the singleton scope.
   */
  async drainQueue(traitName, executeEffects2, entityId) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    if (this.processing.has(key)) return;
    this.processing.add(key);
    const queue = this.queues.get(key) ?? [];
    while (queue.length > 0) {
      const entry = queue.shift();
      const trait = this.traits.get(traitName);
      const traitState = this.getOrInitState(traitName, scope);
      if (!trait || !traitState) continue;
      const perTraitEntity = entry.entityByTrait?.[traitName] ?? entry.entityData;
      const result = processEvent({
        traitState,
        trait,
        eventKey: entry.eventKey,
        eventId: entry.eventId,
        payload: entry.payload,
        entityData: perTraitEntity,
        config: this.traitConfigs.get(traitName),
        user: entry.user,
        now: entry.now,
        guardMode: this.config.guardMode,
        strictBindings: this.config.strictBindings,
        contextExtensions: this.config.contextExtensions
      });
      if (result.executed) {
        this.states.set(key, {
          ...traitState,
          currentState: result.newState,
          previousState: result.previousState,
          lastEvent: normalizeEventKey(entry.eventKey),
          context: { ...traitState.context, ...entry.payload }
        });
        if (this.observer && result.transition) {
          this.observer.onTransition({
            traitName,
            from: result.transition.from,
            to: result.transition.to,
            event: result.transition.event,
            guardResult: result.guardResult,
            effects: []
          });
        }
        await executeEffects2(traitName, result, entry.payload);
      }
    }
    this.processing.delete(key);
  }
  /**
   * Check whether a trait's queue is currently being drained.
   */
  isProcessing(traitName, entityId) {
    return this.processing.has(compositeKey(traitName, entityId ?? SINGLETON_SCOPE));
  }
  /**
   * Get the number of pending events in a trait's queue.
   */
  getQueueLength(traitName, entityId) {
    return this.queues.get(compositeKey(traitName, entityId ?? SINGLETON_SCOPE))?.length ?? 0;
  }
  /**
   * Reset a trait to its initial state.
   *
   * If `entityId` is provided, resets only that entity's scope.
   * Otherwise resets every entity scope known for the trait.
   */
  resetTrait(traitName, entityId) {
    const trait = this.traits.get(traitName);
    if (!trait) return;
    if (entityId) {
      this.states.set(compositeKey(traitName, entityId), createInitialTraitState(trait));
      return;
    }
    const prefix = `${traitName}::`;
    for (const key of [...this.states.keys()]) {
      if (key.startsWith(prefix)) {
        this.states.set(key, createInitialTraitState(trait));
      }
    }
  }
  /**
   * Reset all traits to initial states (every entity scope).
   */
  resetAll() {
    for (const [traitName, trait] of this.traits) {
      const prefix = `${traitName}::`;
      for (const key of [...this.states.keys()]) {
        if (key.startsWith(prefix)) {
          this.states.set(key, createInitialTraitState(trait));
        }
      }
    }
  }
};

// src/traits/TraitCascade.ts
var cascadeLog = createLogger("almadar:runtime:trait-cascade");
async function runTraitCascade(options) {
  const {
    trait,
    fromState,
    eventKey,
    payload,
    getEntityData,
    config,
    user,
    now,
    guardMode,
    strictBindings,
    contextExtensions,
    runEffects,
    maxSteps,
    logContext
  } = options;
  const memory = options.memory ?? new DispatchMemory();
  const effectResults = [];
  const emitted = [];
  const hops = [];
  const visited = /* @__PURE__ */ new Set();
  const queue = [{
    event: eventKey,
    payload,
    delivery: options.delivery ?? { event: eventKey, ...payload !== void 0 ? { payload } : {}, source: {} }
  }];
  let currentState = fromState;
  let steps = 0;
  let executed = false;
  let cappedAt;
  while (queue.length > 0 && steps < maxSteps) {
    const item = queue.shift();
    const stepFromState = (steps > 0 ? options.readState?.() : void 0) ?? currentState;
    const entityData = await getEntityData();
    const stepKey = dispatchVisitKey(trait.name, item.event, stepFromState, item.payload, entityData ?? null);
    if (visited.has(stepKey)) continue;
    visited.add(stepKey);
    const log4 = memory.log(trait.name);
    const view = memory.view(trait.name, item.delivery);
    const result = processEvent({
      traitState: { traitName: trait.name, currentState: stepFromState, previousState: null, lastEvent: null, context: {} },
      trait,
      eventKey: item.event,
      payload: item.payload,
      entityData,
      config,
      user,
      ...now !== void 0 ? { now } : {},
      guardMode,
      strictBindings,
      contextExtensions,
      dispatch: view
    });
    memory.recordDelivery(trait.name, item.delivery);
    if (!result.executed) continue;
    steps += 1;
    if (steps === 1) executed = true;
    currentState = result.newState;
    memory.recordTransition(trait.name, stepFromState, result.newState);
    hops.push({ from: stepFromState, to: result.newState, event: item.event });
    if (result.effects.length > 0) {
      const stepOutcome = await runEffects(result.effects, {
        fromState: stepFromState,
        toState: result.newState,
        event: item.event,
        payload: item.payload,
        dispatch: { ...view, fromState: stepFromState, toState: result.newState },
        index: steps - 1,
        delivery: item.delivery,
        log: log4
      });
      effectResults.push(...stepOutcome.effectResults);
      emitted.push(...stepOutcome.emitted);
      for (const e of stepOutcome.emitted) {
        if (options.selfCascade === false) break;
        if (findMatchingTransitions(trait, result.newState, e.event).length === 0) continue;
        const delivery = {
          event: e.event,
          ...e.payload !== void 0 ? { payload: e.payload } : {},
          source: { ...e.source ?? {} }
        };
        e.source = { ...e.source, dispatched: true };
        queue.push({ event: e.event, payload: e.payload, delivery });
      }
    }
  }
  if (steps >= maxSteps) {
    cappedAt = maxSteps;
    cascadeLog.warn("cascade:cap-hit", { trait: trait.name, maxSteps, ...logContext });
  }
  return { executed, finalState: currentState, effectResults, emitted, steps, hops, cappedAt };
}

// src/events/identity/routing.ts
function eventRouteKey(eventName, eventId) {
  return eventId ? `@evt:${eventId}` : eventName;
}
function buildSourceMatcher(src, listenerOrbital) {
  if (src.kind === "any") return () => true;
  if (src.kind === "trait") {
    if (src.traitId !== void 0) {
      const wantedTraitId = src.traitId;
      return (source) => !!source && source.traitId === wantedTraitId;
    }
    const wantedTrait2 = src.trait;
    return (source) => !!source && source.orbital === listenerOrbital && source.trait === wantedTrait2;
  }
  if (src.orbitalId !== void 0 && src.traitId !== void 0) {
    const wantedOrbitalId = src.orbitalId;
    const wantedTraitId = src.traitId;
    return (source) => !!source && source.orbitalId === wantedOrbitalId && source.traitId === wantedTraitId;
  }
  const wantedOrbital = src.orbital;
  const wantedTrait = src.trait;
  return (source) => !!source && source.orbital === wantedOrbital && source.trait === wantedTrait;
}
function parseListenSource(listener, listenerOrbital) {
  const explicit = listener.source;
  if (explicit && typeof explicit === "object") {
    return {
      bareEvent: listener.event,
      matcher: buildSourceMatcher(explicit, listenerOrbital)
    };
  }
  const key = listener.event;
  const parts = key.split(".");
  if (parts.length === 1) {
    return { bareEvent: key, matcher: () => true };
  }
  if (parts.length === 2) {
    const [sourceOrStar, eventName] = parts;
    if (sourceOrStar === "*") {
      return { bareEvent: eventName, matcher: () => true };
    }
    return {
      bareEvent: eventName,
      matcher: buildSourceMatcher(
        { kind: "trait", trait: sourceOrStar },
        listenerOrbital
      )
    };
  }
  if (parts.length >= 3) {
    const eventName = parts[parts.length - 1];
    const trait = parts[parts.length - 2];
    const orbital = parts.slice(0, parts.length - 2).join(".");
    return {
      bareEvent: eventName,
      matcher: buildSourceMatcher({ kind: "orbital", orbital, trait }, listenerOrbital)
    };
  }
  return { bareEvent: key, matcher: () => true };
}
function collectDeclaredConfigDefaults(trait) {
  if (!trait) return void 0;
  const schema = trait.config;
  if (!schema || typeof schema !== "object") return void 0;
  const defaults = {};
  let hasAny = false;
  for (const [key, field] of Object.entries(schema)) {
    if (field && typeof field === "object" && !Array.isArray(field) && "default" in field) {
      const def = field.default;
      if (def !== void 0) {
        defaults[key] = def;
        hasAny = true;
      }
    }
  }
  return hasAny ? defaults : void 0;
}
function collectDeclaredEntityDefaults(entity) {
  if (!entity) return void 0;
  const defaults = {};
  let hasAny = false;
  for (const field of entity.fields) {
    if (field.name !== void 0 && "default" in field && field.default !== void 0) {
      defaults[field.name] = field.default;
      hasAny = true;
    }
  }
  return hasAny ? defaults : void 0;
}
function buildConfigBinding(input) {
  const declaredDefaults = collectDeclaredConfigDefaults(input.traitDef);
  const callSiteOverride = input.callSiteOverride ? Object.fromEntries(
    Object.entries(input.callSiteOverride).filter(
      ([, v]) => !(typeof v === "string" && v.startsWith("@config."))
    )
  ) : void 0;
  if (!declaredDefaults && !input.resolvedDefaults && !callSiteOverride) return void 0;
  const merged = {
    ...declaredDefaults ?? {},
    ...input.resolvedDefaults ?? {},
    ...callSiteOverride ?? {}
  };
  const USER_FORWARD = /^@user(?:\.[\w]+)+$/;
  const resolved = {};
  for (const [key, value] of Object.entries(merged)) {
    if (typeof value === "string" && USER_FORWARD.test(value)) {
      const cur = getNestedValue(input.user, value.slice("@user.".length));
      resolved[key] = typeof cur === "string" || typeof cur === "number" || typeof cur === "boolean" ? cur : "";
    } else {
      resolved[key] = value;
    }
  }
  return resolved;
}
function buildEntityBinding(input) {
  const defaults = input.entity !== void 0 ? collectDeclaredEntityDefaults(input.entity) : void 0;
  if (!defaults && !input.frame) return input.persisted;
  return { ...defaults ?? {}, ...input.persisted ?? {}, ...input.frame ?? {} };
}
function parseOrbitalTraits(orbital) {
  const configByTrait = /* @__PURE__ */ new Map();
  const unwrapped = (orbital.traits || []).map((t) => {
    if (t && typeof t === "object" && "ref" in t && "_resolved" in t) {
      const wrapper = t;
      const inner = wrapper._resolved;
      const normalizedConfig = normalizeCallSiteConfigToValues(wrapper.config);
      if (normalizedConfig && inner?.name) {
        configByTrait.set(inner.name, normalizedConfig);
      }
      return inner;
    }
    return t;
  });
  const inlineTraits = unwrapped.filter(isInlineTrait);
  const traits = inlineTraits.map((t) => {
    const sm = t.stateMachine;
    return {
      ...t.id !== void 0 ? { id: t.id } : {},
      name: t.name,
      states: sm?.states || [],
      transitions: sm?.transitions || [],
      listens: t.listens
    };
  });
  const entityRef = orbital.entity;
  let entity;
  if (typeof entityRef === "string") {
    entity = { name: entityRef, fields: [] };
  } else if (isEntityCall(entityRef)) {
    const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
    entity = {
      name: fallbackName,
      fields: entityRef.fields ?? [],
      ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
      ...entityRef.collection ? { collection: entityRef.collection } : {}
    };
  } else {
    entity = entityRef;
  }
  return { traits, inlineTraits, configByTrait, entity };
}
function findEntityAmongOrbitals(entities, name) {
  for (const entity of entities) {
    if (entity?.name === name) return entity;
  }
  return void 0;
}

// src/traits/trait-index.ts
var traitIndexLog = createLogger("almadar:runtime:trait-index");
var parseCache = /* @__PURE__ */ new WeakMap();
function parseOrbitalCached(orbital) {
  let cached = parseCache.get(orbital);
  if (cached === void 0) {
    cached = { traits: parseOrbitalTraits(orbital), entities: orbitalInlineEntities(orbital) };
    parseCache.set(orbital, cached);
  }
  return cached;
}
function containsConfigForward(value) {
  if (typeof value === "string") return value.startsWith("@config.");
  if (Array.isArray(value)) return value.some(containsConfigForward);
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(containsConfigForward);
  }
  return false;
}
function callsEffect(expr, resolve, matches) {
  const walk = (e) => callsEffect(e, resolve, matches);
  if (Array.isArray(expr)) {
    const head = expr[0];
    if ((head === "if" || head === "when") && expr.length >= 3) {
      const thenLive = !isStaticallyFalse(expr[1], resolve);
      if (head === "if") {
        return thenLive && walk(expr[2]) || expr[3] !== void 0 && walk(expr[3]);
      }
      return thenLive && expr.slice(2).some(walk);
    }
    if (head === "let" && expr.length >= 2) {
      const bindings = expr[1];
      const boundLive = Array.isArray(bindings) ? bindings.some((pair) => Array.isArray(pair) ? pair.slice(1).some(walk) : walk(pair)) : walk(bindings);
      return boundLive || expr.slice(2).some(walk);
    }
    if ((head === "fn" || head === "lambda") && expr.length >= 3) {
      return expr.slice(2).some(walk);
    }
    if (typeof head === "string" && matches(head, expr.slice(1))) return true;
    return expr.some(walk);
  }
  if (expr !== null && typeof expr === "object") return Object.values(expr).some(walk);
  return false;
}
function runsServerEffect(expr, resolve, storesRowsInBrowser5 = () => false) {
  return callsEffect(expr, resolve, (op, args) => effectSiteFor(op, args, storesRowsInBrowser5) === "server");
}
function runsBrowserDataEffect(expr, storesRowsInBrowser5, resolve) {
  return callsEffect(expr, resolve, (op, args) => getOperatorRunsOn(op) === "server" && effectSiteFor(op, args, storesRowsInBrowser5) === "client");
}
function configResolver(config) {
  return (binding) => {
    if (!binding.startsWith("@config.")) return void 0;
    const value = config?.[binding.slice("@config.".length)];
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value === null ? value : void 0;
  };
}
function touchesServer(traitDef, irTrait, config, storesRowsInBrowser5 = () => false, event) {
  const resolve = configResolver(config);
  return traitDef.transitions.some((t) => (event === void 0 || t.event === event) && !(t.guard !== void 0 && isStaticallyFalse(t.guard, resolve)) && (t.effects ?? []).some((e) => runsServerEffect(e, resolve, storesRowsInBrowser5))) || event === void 0 && (irTrait.ticks ?? []).some((tick) => tick.runsInBackground !== true && tick.effects.some((e) => runsServerEffect(e, resolve, storesRowsInBrowser5)));
}
function browserLegEvents(traitDef, storesRowsInBrowser5, config) {
  const resolve = configResolver(config);
  return new Set(traitDef.transitions.filter((t) => !(t.guard !== void 0 && isStaticallyFalse(t.guard, resolve))).filter((t) => (t.effects ?? []).some((e) => runsBrowserDataEffect(e, storesRowsInBrowser5, resolve))).map((t) => t.event));
}
function buildTraitIndex(orbitals, configOverridesByTrait, options = {}) {
  const parsed = orbitals.map((orbital) => ({ orbital, parsed: parseOrbitalCached(orbital).traits }));
  const allEntities = orbitals.map((orbital) => parseOrbitalCached(orbital).entities).flat();
  const orbitalsView = parsed.map(({ orbital, parsed: p }) => ({ schema: orbital, entity: p.entity }));
  const byName = /* @__PURE__ */ new Map();
  const browserStored = new Set(allEntities.filter(storesRowsInBrowser).map((e) => e.name));
  const storesRowsInBrowser5 = (entityType) => browserStored.has(entityType);
  for (const { orbital, parsed: p } of parsed) {
    const { traits, inlineTraits, configByTrait, entity } = p;
    for (let i = 0; i < traits.length; i++) {
      const traitDef = traits[i];
      const irTrait = inlineTraits[i];
      const linkedEntityName = irTrait?.linkedEntity;
      let traitEntity = entity;
      if (linkedEntityName && linkedEntityName !== traitEntity.name) {
        const resolved = findEntityAmongOrbitals(allEntities, linkedEntityName);
        if (resolved) {
          traitEntity = resolved;
        } else {
          traitIndexLog.warn("linkedEntity:unresolved", {
            orbital: orbital.name,
            trait: traitDef.name,
            linkedEntityName
          });
          traitEntity = { name: linkedEntityName, fields: [] };
        }
      }
      const declaredDefaults = collectDeclaredConfigDefaults(irTrait);
      const resolvedOverride = configOverridesByTrait?.[traitDef.name];
      const rawCallSiteOverride = configByTrait.get(traitDef.name);
      const callSiteOverride = rawCallSiteOverride ? Object.fromEntries(
        Object.entries(rawCallSiteOverride).filter(([, v]) => !containsConfigForward(v))
      ) : void 0;
      const config = declaredDefaults || resolvedOverride || callSiteOverride ? { ...declaredDefaults, ...resolvedOverride, ...callSiteOverride } : void 0;
      const isShared = traitEntity.shared === true;
      byName.set(traitDef.name, {
        traitDef,
        irTrait,
        entity: traitEntity,
        ...config !== void 0 ? { config } : {},
        orbitalName: orbital.name,
        ...orbital.id !== void 0 ? { orbitalId: orbital.id } : {},
        frameKey: isShared ? `$shared::${traitEntity.name}` : traitDef.name,
        isSharedEntity: isShared,
        dispatchMode: options.hostRunsProgram === true ? "persistedAwaited" : computeTraitDispatchMode(irTrait, traitEntity, touchesServer(traitDef, irTrait, config, storesRowsInBrowser5))
      });
    }
  }
  return { byName, allEntities, orbitals: orbitalsView };
}
function buildTraitIndexForOrbital(host, otherOrbitals = []) {
  const orbitals = [host, ...otherOrbitals];
  const full = buildTraitIndex(orbitals);
  const hostNames = new Set(parseOrbitalCached(host).traits.traits.map((t) => t.name));
  const byName = /* @__PURE__ */ new Map();
  for (const [name, entry] of full.byName) {
    if (hostNames.has(name)) byName.set(name, entry);
  }
  return { byName, allEntities: full.allEntities, orbitals: full.orbitals };
}

// src/events/emit-stamp.ts
function stampEmitSource(source, identity) {
  const stamp = source ?? {
    orbital: identity.orbitalName,
    trait: identity.traitName
  };
  if (stamp.orbitalId === void 0 && identity.orbitalId !== void 0) {
    stamp.orbitalId = identity.orbitalId;
  }
  if (stamp.traitId === void 0 && identity.traitId !== void 0) {
    stamp.traitId = identity.traitId;
  }
  if (stamp.eventId === void 0 && identity.emitContractEventId !== void 0) {
    stamp.eventId = identity.emitContractEventId;
  }
  if (identity.originClientId !== void 0 && stamp.originClientId === void 0) {
    stamp.originClientId = identity.originClientId;
  }
  return stamp;
}
function validateEventPayload(eventKey, payload, schema) {
  if (!schema || schema.length === 0) return [];
  const failures = [];
  for (const field of schema) {
    const value = payload?.[field.name];
    if (value === void 0 || value === null) {
      if (field.required) {
        failures.push({ event: eventKey, field: field.name, reason: value === void 0 ? "missing" : "null", expectedType: field.type });
      }
      continue;
    }
    const container = payloadTypeContainer(field.type);
    const wrongContainer = container.kind === "array" && !Array.isArray(value) || container.kind === "object" && (typeof value !== "object" || Array.isArray(value));
    if (wrongContainer) {
      failures.push({ event: eventKey, field: field.name, reason: "wrong-type", expectedType: field.type });
    }
  }
  return failures;
}
function formatPayloadValidationError(failures) {
  if (failures.length === 0) return "";
  const parts = failures.map(
    (f) => `${f.field} (${f.reason}${f.expectedType ? `, expected ${f.expectedType}` : ""})`
  );
  return `Payload validation failed for event '${failures[0].event}': ${parts.join("; ")}`;
}
function validatePayloadShapes(traits, emits) {
  const mismatches = [];
  const emitIndex = /* @__PURE__ */ new Map();
  for (const [traitName, declarations] of emits) {
    for (const decl of declarations) {
      const fields = decl.payloadSchema?.map((p) => p.name) ?? [];
      emitIndex.set(decl.event, { traitName, fields });
    }
  }
  for (const trait of traits) {
    if (!trait.listens) continue;
    for (const listener of trait.listens) {
      const emitter = emitIndex.get(listener.event);
      if (!emitter) continue;
      if (!listener.payloadMapping) continue;
      const payloadRefs = extractPayloadReferences(listener.payloadMapping);
      for (const ref of payloadRefs) {
        if (!emitter.fields.includes(ref)) {
          mismatches.push({
            listenerTrait: trait.name,
            emitterTrait: emitter.traitName,
            event: listener.event,
            referencedField: ref,
            availableFields: emitter.fields
          });
        }
      }
    }
  }
  return mismatches;
}
function extractPayloadReferences(mapping) {
  const refs = [];
  function collect(value) {
    if (typeof value === "string") {
      const match = value.match(/^@payload\.(\w+)$/);
      if (match) {
        refs.push(match[1]);
      }
    } else if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(collect);
      } else {
        Object.values(value).forEach(collect);
      }
    }
  }
  Object.values(mapping).forEach(collect);
  return [...new Set(refs)];
}
function buildEmitsFromTraits(traits, explicitEmits) {
  const result = new Map(explicitEmits ?? []);
  for (const trait of traits) {
    if (result.has(trait.name)) continue;
    const emitDecls = [];
    for (const transition of trait.transitions) {
      if (!transition.effects) continue;
      for (const effect of transition.effects) {
        if (!Array.isArray(effect)) continue;
        if (effect[0] === "emit" && typeof effect[1] === "string") {
          const event = effect[1];
          const payloadObj = effect[2];
          const payloadSchema = payloadObj ? Object.keys(payloadObj).map((name) => ({ name })) : void 0;
          if (!emitDecls.some((d) => d.event === event)) {
            emitDecls.push({ event, payloadSchema });
          }
        }
      }
    }
    if (emitDecls.length > 0) {
      result.set(trait.name, emitDecls);
    }
  }
  return result;
}

// src/evaluation/mount-lifecycle.ts
function isLifecycleEvent(event) {
  return LIFECYCLE_EVENTS.includes(event);
}
var MountLifecycle = class {
  awaiting = /* @__PURE__ */ new Set();
  held = /* @__PURE__ */ new Map();
  /** Traits entering the mount whose lifecycle event has not run yet. */
  mounting(traits) {
    for (const trait of traits) this.awaiting.add(trait);
  }
  /** Traits leaving the mount: nothing is held for a trait no longer there. */
  unmounted(traits) {
    for (const trait of traits) {
      this.awaiting.delete(trait);
      this.held.delete(trait);
    }
  }
  /** The traits still awaiting their lifecycle event — what a client tells its server leg. */
  awaitingTraits() {
    return [...this.awaiting];
  }
  /**
   * A server host's mount follows the client's: `awaiting` is what the client
   * still awaits when it posts. A trait the client has since initialized is
   * released here — its own INIT may never reach this host (a dispatch posts
   * only when it has server work) — except the request's own lifecycle
   * targets, which stay held until their steps run on this host.
   */
  sync(awaiting, lifecycleTargets) {
    const next = new Set(awaiting);
    for (const target of lifecycleTargets) if (this.awaiting.has(target)) next.add(target);
    const released = [];
    for (const trait of this.awaiting) {
      if (next.has(trait)) continue;
      released.push(...this.held.get(trait) ?? []);
      this.held.delete(trait);
    }
    this.awaiting.clear();
    for (const trait of next) this.awaiting.add(trait);
    return released;
  }
  isAwaiting(trait) {
    return this.awaiting.has(trait);
  }
  hold(trait, delivery) {
    const list = this.held.get(trait);
    if (list === void 0) this.held.set(trait, [delivery]);
    else list.push(delivery);
  }
  /** The trait's lifecycle step ran: it is initialized, its held deliveries go out in arrival order. */
  initialized(trait) {
    this.awaiting.delete(trait);
    const list = this.held.get(trait) ?? [];
    this.held.delete(trait);
    return list;
  }
};

// src/evaluation/evaluateOrbitalEvent.ts
var evaluateLog = createLogger("almadar:runtime:evaluate");
var CROSS_TRAIT_CASCADE_CAP = 2e3;
async function readTraitFrame(persistence, frames, entry, rowId) {
  const persisted = rowId ? await persistence.getById(entry.entity.name, rowId) ?? {} : {};
  return {
    ...collectDeclaredEntityDefaults(entry.entity) ?? {},
    ...persisted,
    ...frames.get(entry.frameKey) ?? {}
  };
}
async function evaluateOrbitalEvent(deps, request) {
  const { traitIndex, manager, persistence, frames } = deps;
  const now = deps.now ?? Date.now();
  const event = request.event;
  normalizeEventKey(event);
  const rawPayload = request.payload;
  const activeTraitsList = rawPayload?.["_activeTraits"];
  const activeTraits = activeTraitsList && activeTraitsList.length > 0 ? new Set(activeTraitsList) : void 0;
  const targetTrait = request.targetTrait ?? rawPayload?.["_targetTrait"];
  const awaitingSidecar = rawPayload?.["_awaitingInit"];
  const awaitingInit = Array.isArray(awaitingSidecar) ? awaitingSidecar.filter((t) => typeof t === "string") : void 0;
  const cleanPayload = rawPayload ? { ...rawPayload } : void 0;
  if (cleanPayload) {
    delete cleanPayload["_activeTraits"];
    delete cleanPayload["_targetTrait"];
    delete cleanPayload["_awaitingInit"];
  }
  const delegatedServerLeg = (request.targetTrait !== void 0 || request.mount !== void 0) && (request.traits !== void 0 || request.entityByTrait !== void 0);
  const entityId = request.entityId ?? deps.entityId;
  const defaultAddress = (entry) => deps.runtimeRowSentinel === true && entry !== void 0 && isRuntimeEntity(entry.entity) ? "runtime" : void 0;
  const carriedAddress = (trait) => entityId ?? defaultAddress(traitIndex.byName.get(trait));
  const traitEntityIds = /* @__PURE__ */ new Map();
  if (request.entityByTrait) {
    for (const [traitName, row] of Object.entries(request.entityByTrait)) {
      const rowId = row["id"];
      const entry = traitIndex.byName.get(traitName);
      if (typeof rowId === "string" && rowId !== "") {
        traitEntityIds.set(traitName, rowId);
        if (entry && isRuntimeEntity(entry.entity)) {
          const existing2 = frames.get(entry.frameKey);
          frames.set(entry.frameKey, existing2 === void 0 ? { ...row } : { ...existing2, ...row });
        }
        continue;
      }
      if (!entry) continue;
      const existing = frames.get(entry.frameKey);
      frames.set(entry.frameKey, existing === void 0 ? { ...row } : { ...existing, ...row });
    }
  }
  let targets;
  const seedEvents = /* @__PURE__ */ new Map();
  const mountRejections = [];
  const targetEntry = targetTrait !== void 0 ? traitIndex.byName.get(targetTrait) : void 0;
  const sidecarOutOfScope = request.targetTrait === void 0 && activeTraits !== void 0 && targetTrait !== void 0 && !activeTraits.has(targetTrait);
  if (request.mount !== void 0) {
    targets = [];
    for (const seed of request.mount) {
      const entry = traitIndex.byName.get(seed.trait);
      if (entry === void 0) {
        mountRejections.push({ code: "no-dispatchable-traits", trait: seed.trait, event: seed.event });
        continue;
      }
      const declared = request.traits?.find((t) => t.trait === seed.trait);
      const from = declared?.from ?? manager.getState(seed.trait, entityId)?.currentState ?? findInitialState(entry.traitDef);
      targets.push({ trait: seed.trait, from });
      seedEvents.set(seed.trait, seed.event);
    }
    if (request.traits !== void 0) {
      const seeded = new Set(request.mount.map((m) => m.trait));
      const carried = new Set(selectDispatchCandidates(request.traits.map(({ trait }) => trait), { activeTraits }));
      for (const { trait, from: carriedFrom } of request.traits) {
        if (carried.has(trait) || seeded.has(trait)) manager.seedState(trait, carriedFrom, carriedAddress(trait));
      }
    }
  } else if (targetTrait !== void 0) {
    if (sidecarOutOfScope || targetEntry === void 0) {
      targets = [];
    } else {
      const declared = request.traits?.find((t) => t.trait === targetTrait);
      const from = declared?.from ?? manager.getState(targetTrait, entityId)?.currentState ?? findInitialState(targetEntry.traitDef);
      const stateIsFact = request.targetTrait !== void 0 || declared !== void 0 || (targetEntry.traitDef.states?.length ?? 0) === 1;
      targets = stateIsFact ? [{ trait: targetTrait, from }] : [];
      if (request.traits !== void 0) {
        const carried = new Set(selectDispatchCandidates(request.traits.map(({ trait }) => trait), { activeTraits }));
        for (const { trait, from: carriedFrom } of request.traits) {
          if (trait !== targetTrait && carried.has(trait)) manager.seedState(trait, carriedFrom, carriedAddress(trait));
        }
      }
    }
  } else if (request.traits !== void 0) {
    if (request.traits.length === 0) {
      return {
        success: true,
        transitioned: false,
        states: {},
        emittedEvents: [],
        rejections: [{ code: "no-dispatchable-traits", event }]
      };
    }
    const dispatchable = new Set(
      selectDispatchCandidates(request.traits.map(({ trait }) => trait), { activeTraits })
    );
    targets = request.traits.filter(({ trait }) => dispatchable.has(trait));
    for (const { trait, from } of targets) {
      manager.seedState(trait, from, carriedAddress(trait));
    }
  } else {
    const activeSet = selectDispatchCandidates(traitIndex.byName.keys(), { activeTraits });
    for (const trait of activeSet) {
      if (traitIndex.byName.has(trait)) manager.getState(trait, entityId);
    }
    const dispatchable = selectDispatchCandidates(activeSet, {
      activeTraits,
      canHandle: (trait) => manager.canHandleEvent(trait, event, entityId, request.eventId)
    });
    targets = dispatchable.map((trait) => ({
      trait,
      from: manager.getState(trait, entityId)?.currentState ?? findInitialState(traitIndex.byName.get(trait).traitDef)
    }));
  }
  if (targets.length === 0) {
    const discoveryEcho = request.traits === void 0 && targetTrait === void 0;
    const heldStates = {};
    if (discoveryEcho) {
      for (const [name, state] of manager.getAllStates()) {
        heldStates[name] = state.currentState;
      }
    }
    return {
      success: true,
      transitioned: false,
      states: heldStates,
      emittedEvents: [],
      rejections: request.mount !== void 0 ? mountRejections : [{ code: "no-dispatchable-traits", event }]
    };
  }
  const relayMask = deps.relayMask ?? (deps.clientRelays === true ? new Set(traitIndex.byName.keys()) : delegatedServerLeg ? void 0 : request.traits !== void 0 ? new Set(request.traits.map((t) => t.trait)) : targetTrait !== void 0 ? new Set(targets.map((t) => relayMaskKey(t.trait, normalizeEventKey(event)))) : new Set(targets.map((t) => t.trait)));
  evaluateLog.info("dispatch:targets", {
    event,
    mode: request.traits !== void 0 ? "declared" : targetTrait !== void 0 ? "targetTrait" : "discovery",
    targets: targets.map((t) => `${t.trait}@${t.from}`),
    ...relayMask !== void 0 ? { relayMask: [...relayMask] } : {},
    ...deps.logContext ?? {}
  });
  const validationFailures = [];
  for (const { trait } of targets) {
    const entry = traitIndex.byName.get(trait);
    const targetEvent = seedEvents.get(trait) ?? event;
    const eventSchema = entry?.irTrait.stateMachine?.events?.find((e) => e.key === targetEvent);
    if (eventSchema?.payloadSchema && eventSchema.payloadSchema.length > 0) {
      validationFailures.push(
        ...validateEventPayload(targetEvent, cleanPayload, eventSchema.payloadSchema)
      );
    }
  }
  if (validationFailures.length > 0) {
    return {
      success: false,
      transitioned: false,
      states: {},
      emittedEvents: [],
      error: formatPayloadValidationError(validationFailures)
    };
  }
  const states = {};
  const emittedEvents = [];
  const effectResults = [];
  const clientEffects = [];
  const clientEffectsByTrait = [];
  const fetchedData = {};
  const entityByTrait = {};
  let transitioned = false;
  let guardFailed;
  const rejections = [...mountRejections];
  const truncatedTraits = [];
  const seedDelivery = deps.seedDelivery ?? request.delivery ?? { event, ...cleanPayload !== void 0 ? { payload: cleanPayload } : {}, source: {} };
  const queue = targets.map(({ trait, from }) => ({
    trait,
    from,
    event: seedEvents.get(trait) ?? event,
    payload: cleanPayload,
    entityId: traitEntityIds.get(trait) ?? entityId,
    onPage: true,
    delivery: seedDelivery
  }));
  if (deps.mount !== void 0 && awaitingInit !== void 0) {
    const lifecycleTargets = request.mount !== void 0 ? request.mount.map((m) => m.trait) : isLifecycleEvent(event) && targetTrait !== void 0 ? [targetTrait] : [];
    queue.unshift(...deps.mount.sync(awaitingInit, lifecycleTargets));
  }
  const memory = new DispatchMemory();
  if (request.dispatchLog !== void 0 && request.targetTrait !== void 0) {
    memory.seed(request.targetTrait, request.dispatchLog);
  }
  const scopeByTrait = /* @__PURE__ */ new Map();
  const rowByTrait = /* @__PURE__ */ new Map();
  const stepEntityId = (traitName, entry, forwarded) => forwarded ?? (scopeByTrait.has(traitName) ? scopeByTrait.get(traitName) : void 0) ?? defaultAddress(entry);
  const requesterTraits = new Set(targets.map((t) => t.trait));
  const visited = /* @__PURE__ */ new Set();
  let steps = 0;
  while (queue.length > 0 && steps < CROSS_TRAIT_CASCADE_CAP) {
    const item = queue.shift();
    if (!item) break;
    if (item.fromAtDelivery === true) {
      const listenerEntry = traitIndex.byName.get(item.trait);
      if (listenerEntry !== void 0) {
        item.from = manager.getState(item.trait, stepEntityId(item.trait, listenerEntry, item.entityId))?.currentState ?? findInitialState(listenerEntry.traitDef);
      }
    }
    if (deps.seedVisited?.has(alreadyDeliveredKey(item.trait, item.event)) === true) {
      evaluateLog.info("dispatch:already-delivered", {
        trait: item.trait,
        event: item.event,
        from: item.from,
        ...deps.logContext ?? {}
      });
      continue;
    }
    if (item.fromAtDelivery === true && !isLifecycleEvent(item.event) && deps.mount?.isAwaiting(item.trait) === true) {
      evaluateLog.info("fanout:held-until-init", { trait: item.trait, event: item.event, ...deps.logContext ?? {} });
      deps.mount.hold(item.trait, item);
      continue;
    }
    const entry = traitIndex.byName.get(item.trait);
    if (!entry) {
      evaluateLog.warn("dispatch:unknown-trait", { trait: item.trait, event: item.event });
      continue;
    }
    const currentEntityId = stepEntityId(item.trait, entry, item.entityId);
    let rowId = rowByTrait.get(item.trait) ?? currentEntityId;
    const readFrame = () => readTraitFrame(persistence, frames, entry, rowId);
    const visitKey = dispatchVisitKey(item.trait, item.event, item.from, item.payload, await readFrame());
    if (visited.has(visitKey)) continue;
    if (item.listenGuard !== void 0 && !listenGuardPasses(item.listenGuard, {
      payload: item.delivery.payload,
      state: item.from,
      fromState: item.from,
      ...memory.view(item.trait, item.delivery)
    })) {
      evaluateLog.info("fanout:listen-guard-blocked", { trait: item.trait, event: item.event, ...deps.logContext ?? {} });
      continue;
    }
    visited.add(visitKey);
    if (item.fromAtDelivery === true) steps += 1;
    let cascade;
    try {
      cascade = await runTraitCascade({
        trait: entry.traitDef,
        delivery: item.delivery,
        memory,
        fromState: item.from,
        readState: () => manager.getState(item.trait, currentEntityId)?.currentState,
        // The item's own first step plus what is left of the dispatch's budget for its follow-ups.
        maxSteps: CROSS_TRAIT_CASCADE_CAP - steps + 1,
        eventKey: item.event,
        payload: item.payload,
        config: entry.config,
        user: deps.user,
        now,
        ...deps.guardMode !== void 0 ? { guardMode: deps.guardMode } : {},
        ...deps.strictBindings !== void 0 ? { strictBindings: deps.strictBindings } : {},
        ...deps.contextExtensions !== void 0 ? { contextExtensions: deps.contextExtensions } : {},
        getEntityData: readFrame,
        ...deps.clientRelays === true ? { selfCascade: false } : {},
        runEffects: async (stepEffects, step) => {
          manager.commitState(item.trait, step.toState, currentEntityId, step.event);
          const emittedStart = emittedEvents.length;
          const effectStart = effectResults.length;
          const itemClientEffects = [];
          const itemClientEffectsByTrait = [];
          const persistedEntity = rowId ? await persistence.getById(entry.entity.name, rowId) ?? {} : {};
          await deps.runEffects(item.trait, {
            effects: stepEffects,
            payload: step.payload,
            entityData: persistedEntity,
            entityId: rowId,
            emittedEvents,
            fetchedData,
            clientEffects: itemClientEffects,
            effectResults,
            ...deps.user !== void 0 ? { user: deps.user } : {},
            ...deps.locale !== void 0 ? { locale: deps.locale } : {},
            now,
            clientEffectsByTrait: itemClientEffectsByTrait,
            firing: { event: step.event, fromState: step.fromState },
            received: { delivery: step.delivery, log: step.log },
            ...deps.originClientId !== void 0 ? { originClientId: deps.originClientId } : {},
            dispatch: step.dispatch,
            followOn: item.fromAtDelivery === true || step.index > 0
          });
          if (item.onPage || activeTraits?.has(item.trait) === true) {
            clientEffects.push(...itemClientEffects);
            clientEffectsByTrait.push(...itemClientEffectsByTrait);
          }
          if (rowId === void 0 && !entry.isSharedEntity) {
            const created = effectResults.slice(effectStart).find(
              (r) => r.effect === "persist" && r.action === "create" && r.entityType === entry.entity.name && r.success
            );
            const createdId = created?.data?.id;
            if (typeof createdId === "string") {
              rowId = createdId;
              rowByTrait.set(item.trait, createdId);
            }
          }
          return {
            effectResults: [{
              results: effectResults.slice(effectStart),
              from: step.fromState,
              to: step.toState,
              event: step.event
            }],
            emitted: emittedEvents.slice(emittedStart)
          };
        },
        ...deps.logContext !== void 0 ? { logContext: deps.logContext } : {}
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      rejections.push({ code: "effect-failed", trait: item.trait, from: item.from, event: item.event, error });
      evaluateLog.error("dispatch:effect-failed", { trait: item.trait, from: item.from, event: item.event, error, ...deps.logContext ?? {} });
      scopeByTrait.set(item.trait, currentEntityId);
      const reached = manager.getState(item.trait, currentEntityId)?.currentState;
      if (reached !== void 0) states[item.trait] = reached;
      entityByTrait[item.trait] = await readFrame();
      continue;
    }
    steps += Math.max(cascade.steps - 1, 0);
    if (cascade.cappedAt !== void 0 && !truncatedTraits.includes(item.trait)) truncatedTraits.push(item.trait);
    scopeByTrait.set(item.trait, currentEntityId);
    manager.commitState(
      item.trait,
      cascade.finalState,
      currentEntityId,
      item.event,
      cascade.executed ? cascade.hops : void 0
    );
    states[item.trait] = cascade.finalState;
    transitioned = transitioned || cascade.executed;
    evaluateLog.info("dispatch:step", {
      trait: item.trait,
      event: item.event,
      from: item.from,
      to: cascade.finalState,
      executed: cascade.executed,
      emitted: cascade.emitted.length,
      onPage: item.onPage,
      ...deps.logContext ?? {}
    });
    if (!cascade.executed) {
      const candidate = findTransition(entry.traitDef, item.from, normalizeEventKey(item.event));
      if (candidate !== void 0) {
        if (guardFailed === void 0) guardFailed = `${item.trait}.${item.event}`;
        rejections.push({
          code: "guard-rejected",
          trait: item.trait,
          from: item.from,
          event: item.event,
          transition: `${item.from}--${normalizeEventKey(item.event)}-->${candidate.to}`,
          ...candidate.guard !== void 0 ? { guard: candidate.guard } : {}
        });
      } else {
        rejections.push({
          code: "no-matching-transition",
          trait: item.trait,
          from: item.from,
          event: item.event,
          statesDeclaringEvent: statesDeclaringEvent(entry.traitDef, normalizeEventKey(item.event))
        });
      }
      const rejection = rejections[rejections.length - 1];
      evaluateLog.info("dispatch:rejected", {
        code: rejection.code,
        trait: item.trait,
        from: item.from,
        event: item.event,
        ...rejection.transition !== void 0 ? { transition: rejection.transition } : {},
        ...candidate?.guard !== void 0 ? { guard: JSON.stringify(candidate.guard) } : {},
        ...deps.logContext ?? {}
      });
    }
    entityByTrait[item.trait] = await readFrame();
    if (isLifecycleEvent(item.event) && deps.mount?.isAwaiting(item.trait) === true) {
      for (const held of deps.mount.initialized(item.trait)) queue.push(held);
    }
    for (const emitted of cascade.emitted) {
      const delivery = deliveryOf(emitted);
      for (const target of collectListenerTargets(traitIndex, emitted.source, emitted.event, emitted.payload, relayMask, { deferGuards: true })) {
        emitted.source = { ...emitted.source, dispatched: true };
        evaluateLog.info("fanout:enqueue", {
          listener: target.listenerTrait,
          triggers: target.triggers,
          source: emitted.source.trait ?? emitted.source.orbital,
          event: emitted.event,
          ...deps.logContext ?? {}
        });
        queue.push({
          trait: target.listenerTrait,
          from: findInitialState(target.entry.traitDef),
          fromAtDelivery: true,
          event: target.triggers,
          payload: target.payload,
          ...target.entityId !== void 0 ? { entityId: target.entityId } : {},
          // Withheld only when the requester's page is KNOWN and excludes it
          // — the client filters renders by its own mounted set anyway.
          onPage: activeTraits === void 0 || activeTraits.has(target.listenerTrait) || requesterTraits.has(target.listenerTrait),
          delivery,
          ...target.listener.guard !== void 0 ? { listenGuard: target.listener.guard } : {}
        });
      }
    }
  }
  if (queue.length > 0) {
    for (const remaining of queue) {
      if (!truncatedTraits.includes(remaining.trait)) truncatedTraits.push(remaining.trait);
    }
    evaluateLog.warn("cascade-truncated", {
      event,
      steps,
      dropped: truncatedTraits,
      ...deps.logContext ?? {}
    });
  }
  for (const [traitName, row] of Object.entries(entityByTrait)) {
    const frame = frames.get(traitIndex.byName.get(traitName)?.frameKey ?? traitName);
    if (frame === void 0) continue;
    const changed = Object.keys(frame).filter((k) => !Object.is(row[k], frame[k]));
    if (changed.length > 0) evaluateLog.debug("row:final", { trait: traitName, changed: changed.map((k) => `${k}:${JSON.stringify(row[k])}->${JSON.stringify(frame[k])}`) });
    entityByTrait[traitName] = { ...row, ...frame };
  }
  for (const [frameKey, row] of frames) {
    if (Object.keys(row).length === 0) continue;
    for (const [traitName, entry] of traitIndex.byName) {
      if (entry.frameKey !== frameKey) continue;
      if (entityByTrait[traitName] !== void 0) continue;
      entityByTrait[traitName] = { ...row };
    }
  }
  const allStates = {};
  for (const [name, state] of manager.getAllStates()) {
    allStates[name] = state.currentState;
  }
  Object.assign(allStates, states);
  const reportedRejections = request.mount !== void 0 ? rejections.filter((r) => r.code === "effect-failed" || r.trait !== void 0 && request.mount?.some((m) => m.trait === r.trait && m.event === r.event)) : transitioned ? rejections.filter((r) => r.code === "effect-failed") : rejections;
  return {
    success: true,
    transitioned,
    states: allStates,
    emittedEvents,
    ...Object.keys(entityByTrait).length > 0 ? { entityByTrait } : {},
    ...clientEffects.length > 0 ? { clientEffects } : {},
    ...clientEffectsByTrait.length > 0 ? { clientEffectsByTrait } : {},
    ...effectResults.length > 0 ? { effectResults } : {},
    ...guardFailed !== void 0 ? { guardFailed } : {},
    ...reportedRejections.length > 0 ? { rejections: reportedRejections } : {},
    ...truncatedTraits.length > 0 ? { cascadeTruncated: truncatedTraits } : {}
  };
}
function deliveryOf(emitted) {
  return {
    event: emitted.event,
    ...emitted.payload !== void 0 ? { payload: emitted.payload } : {},
    source: { ...emitted.source ?? {} }
  };
}
function freshDeliveryGuardBindings(delivery, stateOf) {
  return (listenerTrait) => {
    const state = stateOf(listenerTrait);
    return {
      payload: delivery.payload,
      state,
      fromState: state,
      ...new DispatchMemory().view(listenerTrait, delivery)
    };
  };
}
function listenGuardPasses(guard, bindings) {
  try {
    return evaluateGuard(guard, createContextFromBindings(bindings));
  } catch {
    return false;
  }
}
function alreadyDeliveredKey(trait, event) {
  return `${trait}\0${event}`;
}
function relayMaskKey(trait, event) {
  return `${trait}\0${event}`;
}
function collectListenerTargets(traitIndex, sourceStamp, event, payload, skip, opts = {}) {
  const targets = [];
  for (const [listenerName, listenerEntry] of traitIndex.byName) {
    const traitMasked = skip?.has(listenerName) === true;
    const listeners = listenerEntry.traitDef.listens ?? [];
    for (const listener of listeners) {
      const { bareEvent, matcher } = parseListenSource(listener, listenerEntry.orbitalName);
      if (bareEvent !== event || !matcher(sourceStamp)) continue;
      const masked = traitMasked || skip?.has(relayMaskKey(listenerName, normalizeEventKey(listener.triggers ?? bareEvent))) === true;
      if (masked) {
        evaluateLog.info("fanout:masked", {
          listener: listenerName,
          event,
          source: sourceStamp?.trait ?? sourceStamp?.orbital
        });
        continue;
      }
      if (listener.guard && opts.deferGuards !== true) {
        const bindings = opts.guardBindings?.(listenerName) ?? { payload };
        if (!listenGuardPasses(listener.guard, bindings)) continue;
      }
      const mappedPayload = applyListenPayloadMapping(
        listener.payloadMapping,
        payload,
        evaluateListenPayloadExpr
      );
      const pickId = (field) => mappedPayload?.[field] ?? payload?.[field];
      targets.push({
        listenerTrait: listenerName,
        entry: listenerEntry,
        listener,
        triggers: listener.triggers,
        ...listener.triggersId !== void 0 ? { triggersId: listener.triggersId } : {},
        payload: mappedPayload,
        entityId: pickId("entityId") ?? pickId("orbitalName")
      });
    }
  }
  return targets;
}
function statesDeclaringEvent(traitDef, eventKey) {
  const declaring = /* @__PURE__ */ new Set();
  for (const t of traitDef.transitions) {
    if (t.event !== eventKey) continue;
    if (Array.isArray(t.from)) {
      for (const f of t.from) declaring.add(f);
    } else {
      declaring.add(t.from);
    }
  }
  return Array.from(declaring);
}
var DEFAULT_THEME_KEY = "minimalist-light";
function inlineNavItems(pages) {
  const items = [];
  for (const page of pages) {
    if (isPageReference(page)) continue;
    const p = page;
    if (typeof p.label !== "string" || p.path.includes(":")) continue;
    const item = { href: p.path, label: p.label };
    if (typeof p.icon === "string") item.icon = p.icon;
    if (p.roles !== void 0) item.roles = [...p.roles];
    items.push(item);
  }
  return items;
}
function appNavItems(orbitals) {
  const items = [];
  const seen = /* @__PURE__ */ new Set();
  for (const orbital of orbitals) {
    for (const item of inlineNavItems(orbital.pages ?? [])) {
      if (seen.has(item.href)) continue;
      seen.add(item.href);
      items.push(item);
    }
  }
  return items;
}
function sigilThemeKey(orbitalTheme, appThemeKey) {
  return themeDataKey(orbitalTheme) || appThemeKey || DEFAULT_THEME_KEY;
}
function orderFetchedRows(rows, orderBy) {
  if (typeof orderBy !== "string" || orderBy === "") return rows;
  const [field, direction] = orderBy.split(":");
  return sortRows(rows, field, direction === "desc" ? "desc" : "asc");
}

// src/effects/in-flight-calls.ts
var InFlightCalls = class {
  calls = /* @__PURE__ */ new Map();
  /** Whether a call started with `key` is still running. */
  inFlight(key) {
    return (this.calls.get(key)?.size ?? 0) > 0;
  }
  /**
   * Run `work` registered under `key`, handing it the abort signal; `work` must settle promptly once the
   * signal aborts (see `untilAborted`). The call is cancelled when the signal was aborted by the time
   * `work` settles, whatever it settled with.
   */
  async run(key, work) {
    const controller = new AbortController();
    const running = this.calls.get(key) ?? /* @__PURE__ */ new Set();
    running.add(controller);
    this.calls.set(key, running);
    try {
      const value = await work(controller.signal);
      return controller.signal.aborted ? { cancelled: true } : { cancelled: false, value };
    } catch (err) {
      if (controller.signal.aborted) return { cancelled: true };
      throw err;
    } finally {
      running.delete(controller);
      if (running.size === 0 && this.calls.get(key) === running) this.calls.delete(key);
    }
  }
  /** Abort every call running under `key`; false (a no-op) when none is. */
  cancel(key) {
    const running = this.calls.get(key);
    if (running === void 0 || running.size === 0) return false;
    this.calls.delete(key);
    for (const controller of running) controller.abort();
    return true;
  }
};
function untilAborted(signal, promise) {
  if (signal.aborted) {
    promise.catch(() => void 0);
    return Promise.reject(new Error("The call was cancelled"));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new Error("The call was cancelled"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      }
    );
  });
}
var effectLog = createLogger("almadar:runtime:effects");
setNamespaceLevel("almadar:runtime:effects", "WARN");
var persistLog = createLogger("almadar:runtime:persist");
function failureMessage(err) {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "message" in err && typeof err.message === "string") return err.message;
  return String(err);
}
function issuePayload(issue) {
  const payload = { code: issue.code, message: issue.message };
  if (issue.path !== void 0) payload.path = issue.path;
  if (issue.line !== void 0) payload.line = issue.line;
  if (issue.column !== void 0) payload.column = issue.column;
  return payload;
}
function parseEffect(effect) {
  if (!Array.isArray(effect) || effect.length === 0) {
    return null;
  }
  const [operator, ...args] = effect;
  if (typeof operator !== "string") {
    return null;
  }
  return { operator, args };
}
function resolveArgs(args, bindings, strictBindings, contextExtensions) {
  const ctx = createContextFromBindings(bindings, strictBindings, contextExtensions);
  return args.map((arg) => interpolateValue(arg, ctx));
}
function interpolateFilterTraitRefs(value, ctx) {
  if (typeof value === "string") {
    if (value.startsWith("@config.")) {
      const knob = interpolateValue(value, ctx);
      return typeof knob === "string" && (knob === "@entity" || knob.startsWith("@entity.")) ? interpolateFilterTraitRefs(knob, ctx) : knob;
    }
    return value === "@entity" || value.startsWith("@entity.") ? interpolateValue(value, ctx) : value;
  }
  if (Array.isArray(value)) {
    if (value.length >= 2 && value[0] === "object/get" && value[1] === "@entity") {
      return value.map(
        (v, i) => i >= 2 && typeof v === "string" && v.startsWith("@config.") ? interpolateFilterTraitRefs(v, ctx) : v
      );
    }
    return value.map((v) => interpolateFilterTraitRefs(v, ctx));
  }
  return value;
}
function clientResolvesRenderBindings(entity) {
  return entity === void 0 || isRuntimeEntity(entity) || entity.shared === true || storesRowsInBrowser(entity);
}
var EffectExecutor = class _EffectExecutor {
  handlers;
  bindings;
  context;
  debug;
  onPersistRefused;
  strictBindings;
  contextExtensions;
  evaluator;
  deferRenderBindings;
  resolveIntrinsicFields;
  resolveEntityFields;
  environment;
  delegate;
  inFlightCalls;
  outsideEventQueue;
  deferCallOutcome;
  constructor(options) {
    this.handlers = options.handlers;
    this.bindings = options.bindings;
    this.context = options.context;
    this.debug = options.debug ?? false;
    this.onPersistRefused = options.onPersistRefused;
    this.strictBindings = options.strictBindings ?? false;
    this.contextExtensions = options.contextExtensions;
    this.evaluator = options.evaluator;
    this.deferRenderBindings = options.deferRenderBindings ?? false;
    this.resolveIntrinsicFields = options.resolveIntrinsicFields;
    this.resolveEntityFields = options.resolveEntityFields;
    this.environment = options.environment ?? "server";
    this.delegate = options.delegate;
    this.inFlightCalls = options.inFlightCalls;
    this.outsideEventQueue = options.outsideEventQueue ?? ((work) => work());
    this.deferCallOutcome = options.deferCallOutcome;
  }
  /**
   * On a client host, hand a registry `runsOn: 'server'` effect to the
   * `delegate` instead of executing it (twin of `executor.rs`
   * `delegate_to_server`). Returns `true` when the caller must not also
   * execute it: delegated, or dropped as server-only with no delegate.
   */
  delegateIfClient(operator, args) {
    if (this.environment !== "client" || getOperatorRunsOn(operator) !== "server") return false;
    this.delegate?.delegate([operator, ...args], "server");
    return true;
  }
  /**
   * `@intrinsic` fields are NEVER a persisted column — strips them from
   * `data` before a `persist create`/`persist update` write, regardless of
   * what expression produced `data` (bare `@entity`, an explicit literal
   * that names one, an `object/merge` result, …). Deterministic on the
   * entity's declared schema, not on how the value was constructed. A
   * no-op when no schema was supplied (`resolveIntrinsicFields` absent,
   * e.g. a browser/offline-preview handler set) or the entity declares no
   * intrinsic fields.
   */
  stripFrameFields(entityType, data) {
    if (data === void 0 || !this.resolveIntrinsicFields) return data;
    const intrinsicFields = this.resolveIntrinsicFields(entityType);
    return intrinsicFields.length > 0 ? omitFrameFields(data, intrinsicFields) : data;
  }
  /**
   * Names of `data`'s missing REQUIRED columns for a `persist create` —
   * shared with the Rust kernel's definition: an entity field with
   * `required: true`, excluding the framework-stamped columns
   * (`RESERVED_FIELD_NAMES` — id/audit timestamps the store mints, the
   * same set `@almadar/core`'s mock synthesis and `MockPersistenceAdapter`
   * already exempt), any `@intrinsic` field, any field declaring a
   * `default` (the store fills it in), and any field carrying `mergedFrom`
   * (a rebind-merged field's `required` is the imported atom's own write
   * contract, never this host writer's). "Missing" is key-absent or
   * `undefined`/`null`/`''`. A no-op (`[]`) when no schema was supplied
   * (`resolveEntityFields` absent).
   */
  missingRequiredFields(entityType, data) {
    if (!this.resolveEntityFields) return [];
    const row = data ?? {};
    const missing = [];
    for (const field of this.resolveEntityFields(entityType)) {
      if (field.required !== true || !field.name) continue;
      if (RESERVED_FIELD_NAMES.has(field.name)) continue;
      if (field.intrinsic === true) continue;
      if (field.default !== void 0) continue;
      if (field.mergedFrom !== void 0) continue;
      const value = row[field.name];
      if (value === void 0 || value === null || value === "") {
        missing.push(field.name);
      }
    }
    return missing;
  }
  _evaluator;
  getEvaluator() {
    if (this.evaluator) return this.evaluator;
    if (!this._evaluator) {
      this._evaluator = new SExpressionEvaluator();
    }
    return this._evaluator;
  }
  // ==========================================================================
  // Handler Manifest Validation (RCG-03)
  // ==========================================================================
  /**
   * Validate that all effect types used in a schema have handlers registered.
   * Call this at runtime startup to catch missing handler setup immediately.
   *
   * @param usedEffectTypes - Effect operator names used in the loaded schemas
   * @param environment - Execution environment for context-aware error messages
   * @returns Array of missing handler errors (empty if all handlers are available)
   *
   * @example
   * ```ts
   * const missing = EffectExecutor.validateHandlers(
   *   ['persist', 'render-ui', 'fetch'],
   *   executor.getRegisteredHandlers(),
   *   'client'
   * );
   * if (missing.length > 0) {
   *   console.error('Missing handlers:', missing);
   * }
   * ```
   */
  static validateHandlers(usedEffectTypes, registeredHandlers, environment) {
    const errors = [];
    const expectedHandlers = environment ? HANDLER_MANIFEST[environment] : void 0;
    for (const effectType of usedEffectTypes) {
      if (!registeredHandlers.includes(effectType)) {
        let message = `Effect "${effectType}" is used in schema but no handler is registered.`;
        if (expectedHandlers && !expectedHandlers.includes(effectType)) {
          message += ` Effect "${effectType}" is not expected in "${environment}" environment.`;
        }
        errors.push(message);
      }
    }
    return errors;
  }
  /**
   * Get list of effect operators that have handlers registered.
   */
  getRegisteredHandlers() {
    const registered = [];
    const handlerMap = {
      "emit": this.handlers.emit,
      "persist": this.handlers.persist,
      "set": this.handlers.set,
      "call-service": this.handlers.callService,
      "fetch": this.handlers.fetch,
      "fetch-stream": this.handlers.fetchStream,
      "send-server": this.handlers.sendServer,
      "spawn": this.handlers.spawn,
      "despawn": this.handlers.despawn,
      "render-ui": this.handlers.renderUI,
      "render": this.handlers.renderUI,
      "navigate": this.handlers.navigate,
      "navigate-back": this.handlers.navigateBack,
      "log": this.handlers.log,
      "ref": this.handlers.ref,
      "deref": this.handlers.deref,
      "swap": this.handlers.swap,
      "watch": this.handlers.watch,
      "atomic": this.handlers.atomic,
      "behavior/compose": this.handlers.composeBehaviors,
      "behavior/wire": this.handlers.applyEventWiring,
      "behavior/detect-layout": this.handlers.detectLayoutStrategy,
      "behavior/pipe": this.handlers.pipeBehaviors
    };
    for (const [name, handler] of Object.entries(handlerMap)) {
      if (handler) {
        registered.push(name);
      }
    }
    registered.push("do", "when", "let", "if");
    return registered;
  }
  /**
   * Resolve one effect's raw args before dispatch — shared by `execute()`
   * (and `executeAll()`, which loops it) and `executeWithResults()`
   * (RCG-04). Both must apply the SAME special-casing: compound operators
   * carry nested effects, not values to resolve; `set` with an
   * `@entity.<field>` path literal uses that first arg as a binding PATH;
   * fetch-like ops preserve their `filter` SExpression instead of
   * collapsing it against the outer trait's entity; render-ui on the
   * deferring path keeps `@entity`-dependent leaves as render-time
   * markers. `executeWithResults()` used to carry its own independent,
   * incomplete copy of this list (missing all three special cases) —
   * verified live: it silently corrupted `(set @entity.<field> value)`
   * (arg[0] resolved away to the field's CURRENT value before
   * `dispatch('set', ...)` could parse the path out of it) while still
   * reporting `status: 'executed'`. One shared method converges them so a
   * future special-case added here can't be missed on the other path.
   */
  async resolveEffectArgs(operator, args) {
    const isCompound = operator === "do" || operator === "when" || operator === "let" || operator === "if";
    const isSetPathForm = operator === "set" && args.length >= 2 && typeof args[0] === "string" && args[0].startsWith("@entity.");
    const isFetchLike = (operator === "fetch" || operator === "ref" || operator === "deref" || operator === "os/watch-collection" || operator === "os/watch") && args.length >= 2 && args[1] !== null && typeof args[1] === "object" && !Array.isArray(args[1]);
    const isDeferringRenderUI = (operator === "render-ui" || operator === "render") && this.deferRenderBindings;
    if (isCompound) {
      return args;
    }
    if (isDeferringRenderUI) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      return args.map(
        (arg, index) => index === 0 ? interpolateValue(arg, ctx) : deferEntityBindings(arg, ctx)
      );
    }
    if (isSetPathForm) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      const resolvedRest = [];
      for (const a of args.slice(1)) {
        if (Array.isArray(a) && a.length > 0 && typeof a[0] === "string") {
          let result = this.getEvaluator().evaluate(a, ctx);
          if (result instanceof Promise) {
            result = await result;
          }
          resolvedRest.push(result);
        } else {
          resolvedRest.push(interpolateValue(a, ctx));
        }
      }
      return [args[0], ...resolvedRest];
    }
    if (isFetchLike) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      const opts = args[1];
      const resolvedOpts = {
        ...opts.id !== void 0 && { id: interpolateValue(opts.id, ctx) },
        ...opts.filter !== void 0 && { filter: interpolateFilterTraitRefs(opts.filter, ctx) },
        ...opts.limit !== void 0 && { limit: interpolateValue(opts.limit, ctx) },
        ...opts.offset !== void 0 && { offset: interpolateValue(opts.offset, ctx) },
        ...opts.orderBy !== void 0 && { orderBy: interpolateValue(opts.orderBy, ctx) },
        ...opts.include !== void 0 && { include: interpolateValue(opts.include, ctx) },
        ...opts.emit !== void 0 && { emit: interpolateValue(opts.emit, ctx) }
      };
      return [
        interpolateValue(args[0], ctx),
        resolvedOpts,
        ...args.slice(2).map((a) => interpolateValue(a, ctx))
      ];
    }
    return resolveArgs(args, this.bindings, this.strictBindings, this.contextExtensions);
  }
  /**
   * Execute a single effect.
   */
  async execute(effect) {
    const parsed = parseEffect(effect);
    if (!parsed) {
      if (this.debug) {
        effectLog.warn("invalid-effect-format", () => ({ effectJson: JSON.stringify(effect ?? null) }));
      }
      return;
    }
    const { operator, args } = parsed;
    const resolvedArgs = await this.resolveEffectArgs(operator, args);
    effectLog.debug("execute", { operator, argCount: resolvedArgs.length, context: this.context.traitName });
    if (this.debug) {
      effectLog.debug("executing", () => ({ operator, argsJson: JSON.stringify(resolvedArgs) }));
    }
    try {
      await this.dispatch(operator, resolvedArgs);
      effectLog.debug("execute:result", { operator, success: true });
    } catch (error) {
      effectLog.error("execute:error", {
        operator,
        error: error instanceof Error ? error : String(error)
      });
      throw error;
    }
  }
  /**
   * Execute multiple effects in sequence.
   */
  async executeAll(effects) {
    for (const effect of effects) {
      await this.execute(effect);
    }
  }
  /**
   * Execute multiple effects in parallel.
   */
  async executeParallel(effects) {
    await Promise.all(effects.map((effect) => this.execute(effect)));
  }
  // ==========================================================================
  // Effect Execution with Results (RCG-04)
  // ==========================================================================
  /**
   * Execute effects and return detailed results for each.
   * Enables compensating transitions by reporting which effects failed.
   *
   * Unlike `executeAll`, this method does NOT throw on effect errors.
   * Instead, it captures errors in the returned `EffectResult[]` array.
   */
  async executeWithResults(effects) {
    const results = [];
    for (const effect of effects) {
      const parsed = parseEffect(effect);
      if (!parsed) {
        results.push({
          type: "unknown",
          args: [],
          status: "skipped",
          error: "Invalid effect format"
        });
        continue;
      }
      const start = Date.now();
      const { operator, args: rawArgs } = parsed;
      const resolvedArgs = await this.resolveEffectArgs(operator, rawArgs);
      try {
        const outcome = await this.dispatch(operator, resolvedArgs);
        if (outcome?.failed) {
          results.push({
            type: operator,
            args: resolvedArgs,
            status: "failed",
            error: outcome.error,
            durationMs: Date.now() - start,
            ...outcome.refusedBeforeHandler === true ? { refusedBeforeHandler: true } : {}
          });
        } else {
          results.push({
            type: operator,
            args: resolvedArgs,
            status: "executed",
            durationMs: Date.now() - start
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        results.push({
          type: operator,
          args: resolvedArgs,
          status: "failed",
          error: errorMessage,
          durationMs: Date.now() - start
        });
      }
    }
    return results;
  }
  // ==========================================================================
  // `emit:` config extraction — close-the-circuit on async/reactive ops.
  //
  // `fetch`, `persist`, `call-service`, `set`, `ref`, `os/watch-*` may carry
  // an `emit:` key in their options object. After the effect's work finishes,
  // the runtime fires the author-configured bus event so downstream state
  // machines can branch on success/failure without stitching async/sequence.
  //
  // See `docs/Almadar_Std_Gaps.md` §3.1 and the emit-config plan for the
  // semantics. The compiled-path shell does the same work in generated JS.
  // ==========================================================================
  extractEmitConfig(rawOpt) {
    if (!rawOpt || typeof rawOpt !== "object" || Array.isArray(rawOpt)) {
      return void 0;
    }
    const obj = rawOpt;
    const emitBlock = obj.emit;
    if (!emitBlock || typeof emitBlock !== "object" || Array.isArray(emitBlock)) {
      return void 0;
    }
    const block = emitBlock;
    const asStr = (v) => typeof v === "string" ? v : void 0;
    return {
      success: asStr(block.success),
      failure: asStr(block.failure),
      cancelled: asStr(block.cancelled),
      on_change: asStr(block.on_change) ?? asStr(block.onChange),
      on_message: asStr(block.on_message) ?? asStr(block.onMessage)
    };
  }
  requireInFlightCalls(use) {
    if (this.inFlightCalls === void 0) {
      throw new Error(`${use}: no running app registered an in-flight call registry on this executor`);
    }
    return this.inFlightCalls;
  }
  /** The call's declared `key`, already evaluated with the effect's args; undefined when the call declares none. */
  resolveCallKey(options, use) {
    if (!options || typeof options !== "object" || Array.isArray(options) || !("key" in options)) return void 0;
    const key = options.key;
    if (typeof key !== "string" || key === "") {
      throw new Error(`${use}: key resolved to ${String(key)}`);
    }
    return key;
  }
  /** Build the source metadata stamp for an emit fired from this trait. */
  sourceStamp(event) {
    const stamp = {
      orbital: this.context.orbitalName,
      trait: this.context.traitName,
      transition: this.context.transition
    };
    if (this.context.orbitalId !== void 0) stamp.orbitalId = this.context.orbitalId;
    if (this.context.traitId !== void 0) stamp.traitId = this.context.traitId;
    if (event !== void 0 && this.context.emits !== void 0) {
      const contract = this.context.emits.find((e) => e.event === event);
      if (contract?.eventId !== void 0) stamp.eventId = contract.eventId;
    }
    return stamp;
  }
  emitSuccess(emit, key, payload, fromPersistSuccess) {
    const eventName = emit?.[key];
    if (eventName) {
      this.handlers.emit(eventName, payload, this.sourceStamp(eventName), fromPersistSuccess);
    }
  }
  /** A follow-on step's call: started, not awaited; its outcome event goes to `deliver`. */
  startDeferredCall(service, action, params, options, emitCfg, deliver) {
    const route = emitCfg?.on_message !== void 0 ? { event: emitCfg.on_message, source: this.sourceStamp(emitCfg.on_message) } : void 0;
    const callKey = this.resolveCallKey(options, "call-service key");
    const outcome = callKey === void 0 ? this.handlers.callService(service, action, params, { outsideRequest: true }, route).then((value) => ({ cancelled: false, value })) : this.requireInFlightCalls("call-service key").run(callKey, (signal) => untilAborted(signal, this.handlers.callService(service, action, params, { signal, outsideRequest: true }, route)));
    void outcome.then(
      (settled) => {
        if (settled.cancelled) {
          if (emitCfg?.cancelled && callKey !== void 0) deliver(emitCfg.cancelled, { key: callKey });
        } else if (emitCfg?.success) {
          deliver(emitCfg.success, settled.value);
        }
      },
      (err) => {
        if (emitCfg?.failure) deliver(emitCfg.failure, { error: failureMessage(err) });
        else effectLog.error("call-service:deferred-failed", { service, action, error: failureMessage(err) });
      }
    );
  }
  emitCancelled(emit, key) {
    if (!emit?.cancelled) return;
    this.handlers.emit(emit.cancelled, { key }, this.sourceStamp(emit.cancelled));
  }
  emitFailure(emit, err, extra) {
    if (!emit?.failure) return;
    this.handlers.emit(emit.failure, { ...extra, error: failureMessage(err) }, this.sourceStamp(emit.failure));
  }
  /**
   * Narrow the generic emit config into the `os/watch-*` handler surface
   * (only `on_message` + `failure` are meaningful for streaming ops).
   * Returns `undefined` when neither field is set so handlers can test
   * with a single `if (emit)` guard.
   */
  osEmit(emit) {
    if (!emit || !emit.on_message && !emit.failure) return void 0;
    return {
      on_message: emit.on_message,
      failure: emit.failure
    };
  }
  /**
   * Run a substrate (async) effect and dispatch the author's `emit.success`
   * / `emit.failure` bus event when configured. Mirrors the compiled-path
   * generic substrate dispatch (server.rs): uniform `{ result }` payload on
   * success, `{ error }` on failure.
   *
   * When no `emit` config is present (fire-and-forget call) the op still runs
   * but no event is dispatched and errors propagate (legacy behavior).
   */
  async runSubstrate(invoke, emitCfg) {
    if (!emitCfg) {
      await invoke();
      return;
    }
    try {
      const result = await invoke();
      this.emitSuccess(emitCfg, "success", { result: result ?? null });
    } catch (err) {
      this.emitFailure(emitCfg, err);
    }
  }
  /**
   * One language-trio effect: hand its JSON args to the program host
   * (`runProgramEffect` parses them) and dispatch `{ result }` on success,
   * `{ error }` (+ `errors` for eval's validator issues) on failure. A
   * missing host or non-JSON args fail explicitly; without an emit config a
   * failure throws.
   */
  async runProgramEffectArm(op, positional, emitCfg) {
    const fail = (message, extra) => {
      if (!emitCfg) throw new Error(message);
      this.emitFailure(emitCfg, new Error(message), extra);
    };
    const handler = this.handlers.programEffect;
    if (handler === void 0) return fail(`no program host configured for ${op}`);
    const json = [];
    for (const arg of positional) {
      const parsed = JsonValueSchema.safeParse(arg);
      if (!parsed.success) return fail(`${op}: arguments must be JSON data`);
      json.push(parsed.data);
    }
    const outcome = await handler(op, json);
    if (!outcome.ok) return fail(outcome.error, outcome.errors ? { errors: outcome.errors.map(issuePayload) } : void 0);
    if (emitCfg) this.emitSuccess(emitCfg, "success", { result: outcome.result });
  }
  /**
   * Separate a trailing `{ emit: {...} }` options object from positional
   * substrate-op args. Returns `[positionalArgs, emitConfig]`.
   */
  splitSubstrateEmit(args) {
    const emitCfg = args.length > 0 ? this.extractEmitConfig(args[args.length - 1]) : void 0;
    return emitCfg ? [args.slice(0, -1), emitCfg] : [args, void 0];
  }
  // ==========================================================================
  // Effect Dispatch
  // ==========================================================================
  /**
   * Dispatches one effect. Returns `void` for the ordinary case (the
   * caller only cares whether it threw). A `persist` that resolves
   * without throwing — the store DENIED the write, signalled by return
   * value rather than an exception, per the "close the circuit" doctrine
   * — reports that outcome here so `executeWithResults` can record it as
   * `status: 'failed'` instead of the default `'executed'`.
   */
  async dispatch(operator, args) {
    if (this.delegateIfClient(operator, args)) {
      effectLog.debug("effect:delegated-to-server-leg", { operator, target: String(args[0]), traitName: this.context.traitName, transition: this.context.transition });
      return;
    }
    switch (operator) {
      // === Universal Effects ===
      case "emit": {
        const event = args[0];
        const payload = args[1];
        this.handlers.emit(event, payload, this.sourceStamp(event));
        break;
      }
      case "set": {
        const entity = this.bindings.entity;
        let entityId;
        let field;
        let value;
        let emitCfg;
        if (typeof args[0] === "string" && args[0].startsWith("@entity.")) {
          const path = args[0];
          field = path.slice("@entity.".length);
          value = args[1];
          emitCfg = this.extractEmitConfig(args[2]);
          entityId = typeof entity?.["id"] === "string" ? entity["id"] : void 0;
          if (!entityId) {
            const payload = this.bindings.payload;
            const payloadId = payload && typeof payload === "object" && "id" in payload ? payload.id : void 0;
            if (typeof payloadId === "string") {
              entityId = payloadId;
              if (!entity) {
                this.bindings.entity = { id: payloadId };
              } else {
                entity["id"] = payloadId;
              }
              effectLog.debug("set:auto-seed-entity-id", { path, id: payloadId });
            } else {
              effectLog.debug("set:in-memory-mirror-only", { path });
              if (!entity) {
                this.bindings.entity = {};
              }
              this.bindings.entity[field] = value;
              await this.handlers.set(entityId ?? "", field, value);
              this.emitSuccess(emitCfg, "success", value);
              break;
            }
          }
        } else {
          entityId = args[0];
          field = args[1];
          value = args[2];
          emitCfg = this.extractEmitConfig(args[3]);
        }
        await this.handlers.set(entityId, field, value);
        if (entity && (entity["id"] === entityId || !entity["id"])) {
          entity[field] = value;
        }
        this.emitSuccess(emitCfg, "success", value);
        break;
      }
      case "persist": {
        const action = args[0];
        const last = args[args.length - 1];
        const emitCfg = last && typeof last === "object" && !Array.isArray(last) && "emit" in last ? this.extractEmitConfig(last) : void 0;
        persistLog.debug("persist:dispatch", {
          action,
          argCount: args.length,
          argTypes: args.map((a) => Array.isArray(a) ? "array" : a === null ? "null" : typeof a).join(","),
          traitName: this.context.traitName,
          transition: this.context.transition
        });
        persistLog.debug("persist:emit-config", {
          action,
          hasEmitCfg: emitCfg !== void 0,
          success: emitCfg?.success,
          failure: emitCfg?.failure
        });
        if (this.handlers.persistDelegated === true) {
          persistLog.debug("persist:delegated", {
            action,
            entityType: action === "batch" ? "batch" : args[1],
            traitName: this.context.traitName,
            transition: this.context.transition
          });
          return;
        }
        try {
          if (action === "batch") {
            const requiredMissing = [];
            const rawOps = Array.isArray(args[1]) ? args[1] : [];
            const operations = rawOps.map((op) => {
              if (!isPersistBatchOperation(op)) return op;
              if (op[0] === "create") {
                const stripped = this.stripFrameFields(op[1], op[2]);
                const missing = this.missingRequiredFields(op[1], stripped);
                if (missing.length > 0) requiredMissing.push({ entityType: op[1], missing });
                return ["create", op[1], stripped];
              }
              if (op[0] === "update") {
                return ["update", op[1], op[2], this.stripFrameFields(op[1], op[3])];
              }
              return op;
            });
            if (requiredMissing.length > 0) {
              persistLog.error("persist:required-missing", { action, entityType: "batch", requiredMissing });
              const missingError = requiredMissing.map(({ entityType: t, missing }) => `persist create ${t}: required field(s) ${missing.join(", ")} missing`).join("; ");
              this.emitFailure(emitCfg, new Error(missingError));
              this.onPersistRefused?.({ args, error: missingError });
              return { failed: true, error: missingError, refusedBeforeHandler: true };
            }
            const batchSummary = await this.handlers.persist("batch", "", {
              operations
            });
            persistLog.debug("persist:success", {
              action,
              entityType: "batch",
              opCount: operations.length,
              willEmit: emitCfg?.success
            });
            this.emitSuccess(
              emitCfg,
              "success",
              batchSummary ?? { totalCount: operations.length },
              true
            );
            persistLog.debug("persist:emit-fired", { action, eventName: emitCfg?.success });
          } else {
            const entityType = args[1];
            const data = action === "create" || action === "update" ? this.stripFrameFields(entityType, args[2]) : args[2];
            if (action === "create") {
              const missing = this.missingRequiredFields(entityType, data);
              if (missing.length > 0) {
                persistLog.error("persist:required-missing", { entityType, missing });
                const missingError = `persist create ${entityType}: required field(s) ${missing.join(", ")} missing`;
                this.emitFailure(emitCfg, new Error(missingError), { entityType });
                this.onPersistRefused?.({ args, error: missingError });
                return { failed: true, error: missingError, refusedBeforeHandler: true };
              }
            }
            const persisted = await this.handlers.persist(action, entityType, data);
            if (persisted === void 0) {
              const attemptedId = typeof data === "string" ? data : data && typeof data === "object" ? data.id : void 0;
              persistLog.error("persist:denied", { action, entityType, attemptedId });
              const deniedError = `persist ${action} ${entityType} was denied or failed`;
              this.emitFailure(
                emitCfg,
                new Error(deniedError),
                { entityType, id: attemptedId }
              );
              return { failed: true, error: deniedError };
            } else {
              const dataId = typeof persisted === "string" ? persisted : persisted.id;
              persistLog.debug("persist:success", {
                action,
                entityType,
                dataId,
                willEmit: emitCfg?.success
              });
              if (action === "create" && this.bindings.entity && typeof dataId === "string") {
                const boundId = this.bindings.entity.id;
                const submittedId = data && typeof data === "object" ? data.id : void 0;
                if (boundId === void 0 || boundId === submittedId) {
                  this.bindings.entity.id = dataId;
                  persistLog.debug("persist:create-bound-id", {
                    entityType,
                    id: dataId
                  });
                }
              }
              this.emitSuccess(emitCfg, "success", persisted, true);
              persistLog.debug("persist:emit-fired", { action, eventName: emitCfg?.success });
            }
          }
        } catch (err) {
          persistLog.error("persist:error", {
            action,
            entityType: action === "batch" ? "batch" : args[1],
            error: err instanceof Error ? err.message : String(err)
          });
          this.emitFailure(emitCfg, err);
          throw err;
        }
        break;
      }
      case "call-service": {
        const service = args[0];
        const action = args[1];
        const params = args[2];
        const emitCfg = this.extractEmitConfig(args[3]);
        if (this.handlers.callServiceDelegated === true) {
          effectLog.debug("call-service:delegated", { service, action, traitName: this.context.traitName, transition: this.context.transition });
          break;
        }
        if (this.deferCallOutcome !== void 0) {
          this.startDeferredCall(service, action, params, args[3], emitCfg, this.deferCallOutcome);
          break;
        }
        try {
          const route = emitCfg?.on_message !== void 0 ? { event: emitCfg.on_message, source: this.sourceStamp(emitCfg.on_message) } : void 0;
          const callKey = this.resolveCallKey(args[3], "call-service key");
          if (callKey === void 0) {
            const result = await this.handlers.callService(service, action, params, void 0, route);
            this.emitSuccess(emitCfg, "success", result);
            break;
          }
          const registry = this.requireInFlightCalls("call-service key");
          const outcome = await registry.run(callKey, (signal) => this.outsideEventQueue(() => untilAborted(signal, this.handlers.callService(service, action, params, { signal }, route))));
          if (outcome.cancelled) {
            this.emitCancelled(emitCfg, callKey);
          } else {
            this.emitSuccess(emitCfg, "success", outcome.value);
          }
        } catch (err) {
          if (!emitCfg?.failure) throw err;
          this.emitFailure(emitCfg, err);
          return { failed: true, error: failureMessage(err) };
        }
        break;
      }
      case "cancel-call": {
        const callKey = args[0];
        if (typeof callKey !== "string" || callKey === "") {
          throw new Error(`cancel-call: key resolved to ${String(callKey)}`);
        }
        this.requireInFlightCalls("cancel-call").cancel(callKey);
        break;
      }
      case "fetch": {
        if (this.handlers.fetch) {
          const entityType = args[0];
          const rawOpt = args[1];
          const options = typeof rawOpt === "string" ? { id: rawOpt } : rawOpt;
          const emitCfg = this.extractEmitConfig(rawOpt);
          if (options !== void 0 && "id" in options && (options.id === void 0 || options.id === null || options.id === "")) {
            const error = new Error(`${entityType} fetch: id resolved to ${String(options.id)}`);
            effectLog.warn("fetch:unresolved-id", { entityType, traitName: this.context.traitName, transition: this.context.transition });
            this.emitFailure(emitCfg, error);
            break;
          }
          try {
            const result = await this.handlers.fetch(entityType, options);
            if (result === null) {
              if (options?.id !== void 0) {
                this.emitFailure(emitCfg, new Error(`${entityType} ${options.id} not found`));
              } else {
                this.emitSuccess(emitCfg, "success", { data: [], totalCount: 0 });
              }
            } else {
              const payload = { data: result.rows, totalCount: result.total };
              this.emitSuccess(emitCfg, "success", payload);
            }
          } catch (err) {
            this.emitFailure(emitCfg, err);
            throw err;
          }
        } else {
          this.logUnsupported("fetch");
        }
        break;
      }
      case "fetch-stream": {
        if (this.handlers.fetchStream) {
          const streamEntityType = args[0];
          const rawStreamOpt = args[1];
          const streamOptions = typeof rawStreamOpt === "object" && rawStreamOpt !== null ? rawStreamOpt : void 0;
          const streamEmitCfg = this.extractEmitConfig(rawStreamOpt);
          try {
            const result = await this.handlers.fetchStream(
              streamEntityType,
              streamOptions,
              (chunk) => {
                this.emitSuccess(streamEmitCfg, "on_message", { chunk });
              }
            );
            this.emitSuccess(streamEmitCfg, "success", { data: result });
          } catch (err) {
            this.emitFailure(streamEmitCfg, err);
            throw err;
          }
        } else {
          this.logUnsupported("fetch-stream");
        }
        break;
      }
      // === Resource Operators ===
      case "ref": {
        const refEntityType = args[0];
        const rawRefOpt = args[1];
        const refOptions = typeof rawRefOpt === "string" ? { id: rawRefOpt } : rawRefOpt;
        const refEmitCfg = this.extractEmitConfig(rawRefOpt);
        try {
          let result = null;
          if (this.handlers.ref) {
            result = await this.handlers.ref(refEntityType, refOptions);
          } else if (this.handlers.fetch) {
            result = await this.handlers.fetch(refEntityType, refOptions);
          } else {
            this.logUnsupported("ref");
          }
          const refPayload = result ? { data: result.rows, totalCount: result.total } : { data: null, totalCount: 0 };
          this.emitSuccess(refEmitCfg, "on_change", refPayload);
        } catch (err) {
          this.emitFailure(refEmitCfg, err);
          throw err;
        }
        break;
      }
      case "deref": {
        const derefEntityType = args[0];
        const rawDerefOpt = args[1];
        const derefOptions = typeof rawDerefOpt === "string" ? { id: rawDerefOpt } : rawDerefOpt;
        if (this.handlers.deref) {
          await this.handlers.deref(derefEntityType, derefOptions);
        } else if (this.handlers.fetch) {
          await this.handlers.fetch(derefEntityType, derefOptions);
        } else {
          this.logUnsupported("deref");
        }
        break;
      }
      case "swap": {
        if (this.handlers.swap) {
          const swapEntityType = args[0];
          const swapEntityId = args[1];
          const swapTransform = args[2];
          await this.handlers.swap(swapEntityType, swapEntityId, swapTransform);
        } else {
          this.logUnsupported("swap");
        }
        break;
      }
      case "watch": {
        if (this.handlers.watch) {
          const watchEntityType = args[0];
          const watchOptions = args[1];
          this.handlers.watch(watchEntityType, watchOptions);
        } else {
          if (this.debug) {
            effectLog.debug("watch:noop-server", { entityType: typeof args[0] === "string" ? args[0] : void 0 });
          }
        }
        break;
      }
      case "atomic": {
        if (this.handlers.atomic) {
          const atomicEffects = args;
          await this.handlers.atomic(atomicEffects);
        } else {
          const atomicEffects = args;
          for (const inner of atomicEffects) {
            await this.execute(inner);
          }
        }
        break;
      }
      case "send-server": {
        if (this.handlers.sendServer) {
          const sendEvent = args[0];
          const sendPayload = args[1];
          this.handlers.sendServer(sendEvent, sendPayload);
        } else {
          this.logUnsupported("send-server");
        }
        break;
      }
      case "spawn": {
        if (this.handlers.spawn) {
          const entityType = args[0];
          const props = args[1];
          this.handlers.spawn(entityType, props);
        } else {
          this.logUnsupported("spawn");
        }
        break;
      }
      case "despawn": {
        if (this.handlers.despawn) {
          const entityId = args[0];
          this.handlers.despawn(entityId);
        } else {
          this.logUnsupported("despawn");
        }
        break;
      }
      case "log": {
        if (this.handlers.log) {
          const message = args[0];
          const level = args[1];
          const data = args[2];
          this.handlers.log(message, level, data);
        } else {
          effectLog.debug("log:fallback", () => ({
            message: typeof args[0] === "string" ? args[0] : JSON.stringify(args[0] ?? null),
            extraJson: JSON.stringify(args.slice(1))
          }));
        }
        break;
      }
      // === Client-Only Effects ===
      case "render-ui":
      case "render": {
        if (this.handlers.renderUI) {
          const slot = args[0];
          const patternRaw = args[1];
          const pattern = patternRaw === null || patternRaw === void 0 ? null : patternRaw;
          const props = args[2];
          const priority = args[3];
          await this.handlers.renderUI(slot, pattern, props, priority);
        } else {
          this.logUnsupported("render-ui");
        }
        break;
      }
      case "navigate": {
        if (this.handlers.navigate) {
          const path = args[0];
          if (typeof path !== "string" || path === "") {
            effectLog.warn("navigate-path-unresolved", { pathType: typeof path });
            break;
          }
          const params = args[1];
          const options = args[2];
          const crumb = options && typeof options.crumb === "string" && options.crumb !== "" ? options.crumb : void 0;
          this.handlers.navigate(path, params, crumb);
        } else {
          this.logUnsupported("navigate");
        }
        break;
      }
      case "navigate-back": {
        if (this.handlers.navigateBack) {
          this.handlers.navigateBack();
        } else {
          this.logUnsupported("navigate-back");
        }
        break;
      }
      case "notify": {
        this.logUnsupported("notify");
        break;
      }
      // === Compound Effects ===
      case "do": {
        const nestedEffects = args;
        for (const nested of nestedEffects) {
          await this.execute(nested);
        }
        break;
      }
      case "when": {
        const ctx = createContextFromBindings(this.bindings, false, this.contextExtensions);
        const condition = interpolateValue(args[0], ctx);
        const thenEffect = args[1];
        const elseEffect = args[2];
        if (condition) {
          await this.execute(thenEffect);
        } else if (elseEffect) {
          await this.execute(elseEffect);
        }
        break;
      }
      case "if": {
        const ctx = createContextFromBindings(this.bindings, false, this.contextExtensions);
        const cond = interpolateValue(args[0], ctx);
        const branch = cond ? args[1] : args[2];
        if (Array.isArray(branch)) {
          await this.execute(branch);
        }
        break;
      }
      case "let": {
        const rawBindings = args[0];
        const body = args[1];
        const locals = new Map(this.bindings.locals);
        const pairs = Array.isArray(rawBindings) ? rawBindings.flatMap(
          (b) => Array.isArray(b) && typeof b[0] === "string" ? [[b[0], b[1]]] : []
        ) : Object.entries(rawBindings);
        for (const [name, valueExpr] of pairs) {
          const ctx = createContextFromBindings(
            { ...this.bindings, locals },
            false,
            this.contextExtensions
          );
          locals.set(name, interpolateValue(valueExpr, ctx));
        }
        const bodyExecutor = new _EffectExecutor({
          handlers: this.handlers,
          bindings: { ...this.bindings, locals },
          context: this.context,
          debug: this.debug,
          strictBindings: this.strictBindings,
          contextExtensions: this.contextExtensions,
          deferRenderBindings: this.deferRenderBindings,
          inFlightCalls: this.inFlightCalls,
          outsideEventQueue: this.outsideEventQueue
        });
        await bodyExecutor.execute(body);
        break;
      }
      // OS trigger operators (server-side only)
      //
      // Each may carry `emit:` inside a trailing options object. The
      // executor extracts it and passes it to the handler so the
      // hardcoded fallback name (e.g. OS_CRON_FIRE) is replaced by the
      // author-configured event.
      case "os/watch-files": {
        if (this.handlers.osWatchFiles) {
          const glob = args[0];
          const rawOptions = args[1];
          const emitCfg = this.extractEmitConfig(rawOptions);
          const options = rawOptions ? { recursive: rawOptions.recursive, debounce: rawOptions.debounce } : {};
          this.handlers.osWatchFiles(glob, options, this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-files");
        }
        break;
      }
      case "os/watch-process": {
        if (this.handlers.osWatchProcess) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchProcess(
            args[0],
            args[1],
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-process");
        }
        break;
      }
      case "os/watch-port": {
        if (this.handlers.osWatchPort) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchPort(
            args[0],
            args[1] ?? "tcp",
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-port");
        }
        break;
      }
      case "os/watch-http": {
        if (this.handlers.osWatchHttp) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchHttp(
            args[0],
            args[1],
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-http");
        }
        break;
      }
      case "os/watch-cron": {
        if (this.handlers.osWatchCron) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchCron(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-cron");
        }
        break;
      }
      case "os/watch-signal": {
        if (this.handlers.osWatchSignal) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchSignal(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-signal");
        }
        break;
      }
      case "os/watch-env": {
        if (this.handlers.osWatchEnv) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchEnv(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-env");
        }
        break;
      }
      case "os/debounce": {
        if (this.handlers.osDebounce) {
          this.handlers.osDebounce(args[0], args[1]);
        } else {
          this.logUnsupported("os/debounce");
        }
        break;
      }
      // === Composition operators (compile-time, optional) ===
      case "behavior/compose": {
        if (this.handlers.composeBehaviors) {
          const config = args[0];
          await this.handlers.composeBehaviors(config);
        } else {
          this.logUnsupported("behavior/compose");
        }
        break;
      }
      case "behavior/wire": {
        if (this.handlers.applyEventWiring) {
          const wireOrbitals = args[0];
          const wireEntries = args[1];
          await this.handlers.applyEventWiring(wireOrbitals, wireEntries);
        } else {
          this.logUnsupported("behavior/wire");
        }
        break;
      }
      case "behavior/detect-layout": {
        if (this.handlers.detectLayoutStrategy) {
          const layoutOrbitals = args[0];
          const layoutWiring = args[1];
          await this.handlers.detectLayoutStrategy(layoutOrbitals, layoutWiring);
        } else {
          this.logUnsupported("behavior/detect-layout");
        }
        break;
      }
      case "behavior/pipe": {
        if (this.handlers.pipeBehaviors) {
          const [pipeSeed, ...pipeSteps] = args;
          await this.handlers.pipeBehaviors(
            pipeSeed,
            ...pipeSteps
          );
        } else {
          this.logUnsupported("behavior/pipe");
        }
        break;
      }
      // === Language trio (server-side, effect-position) ===
      //
      // behavior/catalog|describe|source and program/read|print|eval honour
      // a trailing `{ emit: { success, failure } }`: success fires with
      // `{ result }`, failure with `{ error }`. A missing program host is an
      // explicit failure, never a silent no-op. Without an emit config the
      // call is fire-and-forget and errors propagate.
      case "behavior/catalog":
      case "behavior/describe":
      case "behavior/source":
      case "program/read":
      case "program/print":
      case "program/eval":
      case "program/compose": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        await this.runProgramEffectArm(operator, positional, emitCfg);
        break;
      }
      // === Browser device operators (client-side, user-gesture) ===
      // Async host APIs routed to dedicated handler methods. Uniform
      // `{ result }` / `{ error }` payload via runSubstrate. On the
      // server (or any host without the API) the handler is absent →
      // unsupported warning; a thrown error fires `emit.failure`.
      case "browser/open-file-picker": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const options = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserOpenFilePicker) {
            this.logUnsupported("browser/open-file-picker");
            return null;
          }
          return this.handlers.browserOpenFilePicker(options);
        }, emitCfg);
        break;
      }
      case "browser/clipboard-read": {
        const [, emitCfg] = this.splitSubstrateEmit(args);
        await this.runSubstrate(async () => {
          if (!this.handlers.browserClipboardRead) {
            this.logUnsupported("browser/clipboard-read");
            return null;
          }
          return this.handlers.browserClipboardRead();
        }, emitCfg);
        break;
      }
      case "browser/clipboard-write": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const text = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserClipboardWrite) {
            this.logUnsupported("browser/clipboard-write");
            return null;
          }
          return this.handlers.browserClipboardWrite(text);
        }, emitCfg);
        break;
      }
      case "browser/geolocation-current": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const options = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserGeolocationCurrent) {
            this.logUnsupported("browser/geolocation-current");
            return null;
          }
          return this.handlers.browserGeolocationCurrent(options);
        }, emitCfg);
        break;
      }
      case "browser/push-subscribe": {
        const [, emitCfg] = this.splitSubstrateEmit(args);
        await this.runSubstrate(async () => {
          if (!this.handlers.browserPushSubscribe) {
            this.logUnsupported("browser/push-subscribe");
            return null;
          }
          return this.handlers.browserPushSubscribe();
        }, emitCfg);
        break;
      }
      default: {
        if (operator.includes("/")) {
          const [positional, emitCfg] = this.splitSubstrateEmit(args);
          await this.runSubstrate(async () => {
            const ctx = createContextFromBindings(
              this.bindings,
              this.strictBindings,
              this.contextExtensions
            );
            const result = this.getEvaluator().evaluate(
              [operator, ...positional],
              ctx
            );
            return result instanceof Promise ? await result : result;
          }, emitCfg);
        } else if (this.debug) {
          effectLog.warn("unknown-operator", { operator });
        }
      }
    }
  }
  logUnsupported(operator) {
    if (this.debug) {
      effectLog.warn("unsupported-on-platform", { operator });
    }
  }
};
function createTestExecutor(overrides = {}) {
  const noopAsync = async () => void 0;
  const noop = () => {
  };
  return new EffectExecutor({
    handlers: {
      emit: overrides.emit ?? noop,
      persist: overrides.persist ?? noopAsync,
      set: overrides.set ?? noop,
      callService: overrides.callService ?? (async () => ({})),
      ...overrides
    },
    bindings: {},
    context: {
      traitName: "TestTrait",
      state: "test",
      transition: "test->test"
    },
    debug: true
  });
}

// src/effects/defaultCallService.ts
var loaded = null;
async function load() {
  const { RuntimeIntegrationManager } = await import('@almadar/integrations/runtime');
  const manager = new RuntimeIntegrationManager();
  manager.configureFromEnv();
  const factory = manager.getFactory();
  return async (service, action, params, context) => {
    const result = await factory.execute(service, action, params ?? {}, context);
    if (!result.success) throw result.error ?? new Error(`${service}.${action} failed`);
    return result.data == null ? null : result.data;
  };
}
async function defaultCallService(service, action, params, context) {
  loaded ??= load();
  return (await loaded)(service, action, params, context);
}
var effectLog2 = createLogger("almadar:runtime:effects");
var busLog = createLogger("almadar:runtime:bus");
var renderLog = createLogger("almadar:runtime:render-ui");
var xOrbitalLog = createLogger("almadar:runtime:cross-orbital");
var dynamicLog = createLogger("almadar:runtime:dynamic");
var NO_ROW_KEY_IS_FATAL = true;
function findEntityDefByName(registeredOrbitals, name) {
  const registered = Array.from(registeredOrbitals);
  return findEntityAmongOrbitals(
    registered.map((reg) => reg.entity),
    name
  ) ?? findEntityAmongOrbitals(
    registered.flatMap(
      (reg) => orbitalInlineEntities(reg.schema).filter((e) => e.name !== reg.entity.name)
    ),
    name
  );
}
async function injectRelationOptions(deps, pattern, traitName) {
  if (pattern === null || typeof pattern !== "object") return;
  const linkedEntityName = deps.irTraits.find((t) => t.name === traitName)?.linkedEntity ?? deps.entity?.name;
  if (!linkedEntityName) return;
  const entityDef = findEntityDefByName(deps.registeredOrbitals, linkedEntityName);
  if (!entityDef?.fields) return;
  const relationTargets = /* @__PURE__ */ new Map();
  for (const field of entityDef.fields) {
    if (field.type === "relation" && field.relation?.entity && field.name) {
      relationTargets.set(field.name, field.relation.entity);
    }
  }
  if (relationTargets.size === 0) return;
  const visit = async (node) => {
    if (node === null || node === void 0 || typeof node !== "object") return;
    if (Array.isArray(node)) {
      for (const child of node) await visit(child);
      return;
    }
    const record = node;
    const nodeType = record["type"];
    const fields = record["fields"] ?? record["columns"];
    if (typeof nodeType === "string" && // Which patterns consume entity-bound field lists is DECLARED per
    // component via `@fieldsContract` and read from the pattern registry
    // — never a hardcoded name list. Both contracts get relation options
    // (form selects need choices; display fields need label resolution).
    getPatternFieldsContract(nodeType) !== void 0 && Array.isArray(fields) && record["relationsData"] === void 0) {
      const relationsData = {};
      for (const fieldEntry of fields) {
        const fieldName = typeof fieldEntry === "string" ? fieldEntry : fieldEntry !== null && typeof fieldEntry === "object" && !Array.isArray(fieldEntry) ? String(
          // `field` is TableView's column-to-entity-field override
          // (defaults to `key`); `name`/`key` cover every other
          // field-def shape (DetailPanel, DataList, DataGrid, DataTable).
          fieldEntry["field"] ?? fieldEntry["name"] ?? fieldEntry["key"] ?? ""
        ) : "";
        const targetEntity = fieldName ? relationTargets.get(fieldName) : void 0;
        if (!targetEntity) continue;
        try {
          const rows = await deps.persistence.list(targetEntity);
          relationsData[fieldName] = rows.map((r) => ({
            value: String(r.id ?? ""),
            label: String(r.name ?? r.title ?? r.id ?? "")
          }));
        } catch {
        }
      }
      if (Object.keys(relationsData).length > 0) {
        record["relationsData"] = relationsData;
      }
    }
    const children = record["children"];
    if (children !== void 0) await visit(children);
  };
  await visit(pattern);
}
function viewerI18n(locale, messages) {
  return locale !== void 0 ? { locale, messages: messages?.[locale] ?? {} } : {};
}
async function runServerEffectStage(deps, args) {
  const {
    traitName,
    effects,
    payload,
    entityData,
    entityId,
    emittedEvents,
    fetchedData,
    clientEffects,
    effectResults,
    user,
    now,
    locale,
    clientEffectsByTrait,
    firing,
    onPush,
    originClientId,
    followOn,
    callsitePayload,
    dispatch
  } = args;
  const entityType = deps.entity.name;
  for (const eff of effects) {
    if (isEffectTuple(eff) && eff[0] === "fetch") {
      xOrbitalLog.debug("fetch:pre-exec-keys", () => ({
        trait: traitName,
        entity: String(eff[1]),
        optKeys: Object.keys(eff[2] ?? {}).join("+")
      }));
    }
  }
  const pushClientEffect = (effect) => {
    clientEffects.push(effect);
    clientEffectsByTrait?.push({ traitName, effect, ...firing });
    onPush?.({ type: "effect", data: effect });
  };
  let bindingsRef = null;
  let contextRef = null;
  const { callService: _delegatedCallService, ...hostOverrides } = deps.extraEffectHandlers ?? {};
  const hostProgramEffect = hostOverrides.programEffect;
  const handlers = {
    emit: (event, eventPayload, source, fromPersistSuccess) => {
      if (deps.debug) {
        busLog.debug("emit:dispatch", () => ({
          event,
          payloadJson: JSON.stringify(eventPayload ?? null),
          sourceOrbital: source?.orbital,
          sourceTrait: source?.trait
        }));
      }
      const emittingTrait = deps.irTraits.find((t) => t.name === traitName);
      const emitContract = emittingTrait?.emits?.find((e) => e.event === event);
      const stamp = stampEmitSource(source, {
        orbitalName: deps.orbitalName,
        traitName,
        orbitalId: deps.orbitalId,
        traitId: emittingTrait?.id,
        emitContractEventId: emitContract?.eventId,
        originClientId
      });
      deps.deliverEmit?.(event, eventPayload, stamp, fromPersistSuccess === true);
      const emittedItem = { event, payload: eventPayload, source: stamp };
      emittedEvents.push(emittedItem);
      onPush?.({ type: "event", data: emittedItem });
      effectLog2.debug("emit:push", {
        event,
        cumulativeEmittedCount: emittedEvents.length,
        sourceTrait: stamp.trait,
        sourceOrbital: stamp.orbital
      });
      xOrbitalLog.info("emit:server", {
        event,
        sourceOrbital: stamp.orbital,
        sourceTrait: stamp.trait,
        dispatchOrbital: deps.orbitalName
      });
      if (fromPersistSuccess) {
        busLog.debug("live-broadcast:sink-call", {
          event,
          sourceOrbital: stamp.orbital,
          sourceTrait: stamp.trait,
          originClientId,
          sinkWired: deps.liveBroadcastSinkWired === true
        });
      }
    },
    set: async (targetId, field, value) => {
      const fieldKey = deps.frameKeyFor(traitName);
      let fieldState = deps.frames.get(fieldKey);
      if (!fieldState) {
        fieldState = {};
        deps.frames.set(fieldKey, fieldState);
      }
      fieldState[field] = value;
      effectResults.push({
        effect: "set",
        entityType,
        data: { id: targetId || entityId || "", field, value },
        success: true
      });
    },
    persist: async (action, targetEntityType, data) => {
      if (action === "batch") {
        const operations = data?.operations;
        if (!Array.isArray(operations) || operations.length === 0) {
          effectResults.push({
            effect: "persist",
            action: "batch",
            success: false,
            error: "Batch requires a non-empty operations array"
          });
          return;
        }
        const batchResults = [];
        const completed = [];
        let batchFailed = false;
        let batchError = "";
        const ops = operations;
        for (const op of ops) {
          if (!isPersistBatchOperation(op)) {
            batchFailed = true;
            batchError = `Invalid batch operation format: ${JSON.stringify(op)}`;
            break;
          }
          const opAction = op[0];
          const opEntityType = op[1];
          try {
            switch (op[0]) {
              case "create": {
                const createIntrinsicFields = deps.intrinsicFieldNames(opEntityType);
                const createData = createIntrinsicFields.length > 0 ? omitFrameFields(op[2] ?? {}, createIntrinsicFields) : op[2] ?? {};
                const { id: newId } = await deps.persistence.create(opEntityType, createData);
                batchResults.push({ ...createData, action: "create", entityType: opEntityType, id: newId });
                completed.push({ action: "create", entityType: opEntityType, id: newId });
                break;
              }
              case "update": {
                const updateId = op[2];
                const updateIntrinsicFields = deps.intrinsicFieldNames(opEntityType);
                const updateData = updateIntrinsicFields.length > 0 ? omitFrameFields(op[3] ?? {}, updateIntrinsicFields) : op[3] ?? {};
                await deps.persistence.update(opEntityType, updateId, updateData);
                const updated = await deps.persistence.getById(opEntityType, updateId);
                batchResults.push({ ...updated || updateData, action: "update", entityType: opEntityType, id: updateId });
                completed.push({ action: "update", entityType: opEntityType, id: updateId });
                break;
              }
              case "delete": {
                const deleteId = op[2];
                await deps.persistence.delete(opEntityType, deleteId);
                batchResults.push({ action: "delete", entityType: opEntityType, id: deleteId, deleted: true });
                completed.push({ action: "delete", entityType: opEntityType, id: deleteId });
                break;
              }
              default:
                batchFailed = true;
                batchError = `Unknown batch operation action: ${opAction}`;
                break;
            }
          } catch (err) {
            batchFailed = true;
            batchError = `Batch operation [${opAction}, ${opEntityType}] failed: ${err instanceof Error ? err.message : String(err)}`;
            break;
          }
          if (batchFailed) break;
        }
        const batchSummary = {
          operations: batchResults,
          completedCount: completed.length,
          totalCount: operations.length
        };
        effectResults.push({
          effect: "persist",
          action: "batch",
          data: batchSummary,
          success: !batchFailed,
          ...batchFailed ? { error: batchError } : {}
        });
        return batchSummary;
      }
      const type = targetEntityType || entityType;
      if ((action === "create" || action === "update") && data !== void 0) {
        const intrinsicFields = deps.intrinsicFieldNames(type);
        if (intrinsicFields.length > 0) data = omitFrameFields(data, intrinsicFields);
      }
      let resultData;
      const sizeBefore = (await deps.persistence.list(type)).length;
      let deniedReason;
      try {
        if (action === "create" || action === "update") {
          deps.validateRelationCardinality?.(type, data || {});
        }
        const accessBindings = { user: bindingsRef?.user, payload: bindingsRef?.payload, config: bindingsRef?.config, now: bindingsRef?.now };
        const mutationPolicy = deps.resolvedSchema ? entityAccessPolicies(deps.resolvedSchema, type)?.[action === "create" ? "create" : action === "update" ? "update" : "delete"] : void 0;
        switch (action) {
          case "create": {
            if (!checkMutationAccess(data || {}, mutationPolicy, accessBindings)) {
              deniedReason = "access-denied";
              throw new Error(accessDeniedMessage("create", type));
            }
            const { id } = await deps.persistence.create(type, data || {});
            resultData = await deps.persistence.getById(type, id) ?? { ...data || {}, id };
            break;
          }
          case "update":
            if (data?.id || entityId) {
              const updateId = data?.id || entityId;
              if (mutationPolicy !== void 0) {
                const existing = await deps.persistence.getById(type, updateId);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings)) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("update", type));
                }
              }
              await deps.persistence.update(type, updateId, data || {});
              const updated = await deps.persistence.getById(type, updateId);
              resultData = updated || { ...data || {}, id: updateId };
            } else {
              effectLog2.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          case "delete": {
            const directId = typeof data === "string" ? data : void 0;
            const nestedId = typeof data === "object" && data !== null ? data.id : void 0;
            const deleteId = directId ?? nestedId ?? entityId;
            if (deleteId) {
              if (mutationPolicy !== void 0) {
                const existing = await deps.persistence.getById(type, deleteId);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings)) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("delete", type));
                }
              }
              await deps.enforceOnDeleteRules?.(type, deleteId);
              await deps.persistence.delete(type, deleteId);
              resultData = { id: deleteId, deleted: true };
            } else {
              effectLog2.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          }
        }
        if (resultData === void 0 && (action === "update" || action === "delete")) {
          effectResults.push({
            effect: "persist",
            action,
            entityType: type,
            success: false,
            denied: true,
            error: `persist ${action} ${type} resolved no row key`
          });
          return void 0;
        }
        const sizeAfter = (await deps.persistence.list(type)).length;
        effectLog2.debug("persist:store-mutate", {
          action,
          entityType: type,
          resultId: resultData?.id,
          sizeBefore,
          sizeAfter,
          delta: sizeAfter - sizeBefore
        });
        effectResults.push({
          effect: "persist",
          action,
          entityType: type,
          data: resultData,
          success: true
        });
        return resultData;
      } catch (err) {
        effectLog2.error("persist:store-mutate-error", {
          action,
          entityType: type,
          error: err instanceof Error ? err.message : String(err)
        });
        effectResults.push({
          effect: "persist",
          action,
          entityType: type,
          success: false,
          ...deniedReason !== void 0 ? { denied: true } : {},
          error: err instanceof Error ? err.message : String(err)
        });
      }
      return void 0;
    },
    callService: async (service, action, params, context2, route) => {
      try {
        let result = null;
        const awaitProvider = context2?.signal === void 0 && context2?.outsideRequest !== true && deps.outsideEventQueue !== void 0 ? deps.outsideEventQueue : (work) => work();
        const message = (payload2) => {
          if (route === void 0) return;
          const callingTrait = deps.irTraits.find((t) => t.name === traitName);
          const stamp = stampEmitSource(route.source, {
            orbitalName: deps.orbitalName,
            traitName,
            orbitalId: deps.orbitalId,
            traitId: callingTrait?.id,
            emitContractEventId: callingTrait?.emits?.find((e) => e.event === route.event)?.eventId,
            originClientId
          });
          deps.deliverLive?.(route.event, payload2, stamp);
        };
        const host = deps.servicePorts?.({ orbital: deps.orbitalName, trait: traitName }, user, message);
        const signal = context2?.signal;
        const callContext = user !== void 0 || host !== void 0 || signal !== void 0 ? {
          ...user !== void 0 ? { principal: user.id, role: user.role } : {},
          ...host !== void 0 ? { host } : {},
          ...signal !== void 0 ? { signal } : {}
        } : void 0;
        if (deps.extraEffectHandlers?.callService) {
          const provider = deps.extraEffectHandlers.callService;
          result = await awaitProvider(() => provider(service, action, params, callContext));
        } else if (deps.mockMode === true) {
          const mockId = `mock_${service}_${action}_${Math.random().toString(36).slice(2, 10)}`;
          const paramsEcho = {};
          if (params) {
            for (const [k, v] of Object.entries(params)) {
              if (v !== void 0 && (typeof v === "string" || typeof v === "number" || typeof v === "boolean" || v === null || v instanceof Date)) {
                paramsEcho[k] = v;
              }
            }
          }
          result = {
            id: mockId,
            clientSecret: `secret_${mockId}`,
            success: true,
            status: "succeeded",
            ...paramsEcho
          };
        } else {
          result = await awaitProvider(
            () => defaultCallService(service, action, params, callContext)
          );
        }
        effectResults.push({
          effect: "call-service",
          action: `${service}.${action}`,
          data: result,
          success: true
        });
        return result;
      } catch (err) {
        effectResults.push({
          effect: "call-service",
          action: `${service}.${action}`,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        throw err;
      }
    },
    fetch: async (fetchEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        const readPolicy = deps.resolvedSchema ? entityAccessPolicies(deps.resolvedSchema, fetchEntityType)?.read : void 0;
        const accessBindings = { user: bindingsRef?.user, payload: bindingsRef?.payload, config: bindingsRef?.config, now: bindingsRef?.now };
        if (options?.id) {
          const stored = await deps.persistence.getById(fetchEntityType, options.id);
          if (stored && applyRowAccess([stored], readPolicy, void 0, accessBindings).length > 0) {
            const entity = { ...stored };
            if (options?.include && options.include.length > 0) {
              await deps.populateRelations?.([entity], fetchEntityType, options.include);
            }
            fetchedData[fetchEntityType] = [entity];
            result = entity;
            total = 1;
          }
        } else {
          let entities = await deps.persistence.list(fetchEntityType);
          entities = applyRowAccess(
            entities,
            readPolicy,
            options?.filter !== void 0 && options.filter !== null ? options.filter : void 0,
            accessBindings
          );
          total = entities.length;
          entities = orderFetchedRows(entities, options?.orderBy);
          if (options?.offset && options.offset > 0) {
            entities = entities.slice(options.offset);
          }
          if (options?.limit && options.limit > 0) {
            entities = entities.slice(0, options.limit);
          }
          if (options?.include && options.include.length > 0) {
            entities = entities.map((row) => ({ ...row }));
            await deps.populateRelations?.(entities, fetchEntityType, options.include);
          }
          fetchedData[fetchEntityType] = entities;
          result = entities;
        }
        return result === null ? null : { rows: result, total };
      } catch (error) {
        effectLog2.error("fetch:error", {
          entityType: fetchEntityType,
          error: error instanceof Error ? error : String(error)
        });
        return null;
      }
    },
    // Resource operators: ref, deref, swap, watch, atomic
    ref: async (refEntityType, options) => {
      try {
        return await handlers.fetch(refEntityType, options);
      } catch (error) {
        effectLog2.error("ref:error", {
          entityType: refEntityType,
          error: error instanceof Error ? error : String(error)
        });
        return null;
      }
    },
    deref: async (derefEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        if (options?.id) {
          const entity = await deps.persistence.getById(derefEntityType, options.id);
          if (entity) {
            fetchedData[derefEntityType] = [entity];
            result = entity;
            total = 1;
          }
        } else {
          const entities = await deps.persistence.list(derefEntityType);
          fetchedData[derefEntityType] = entities;
          result = entities;
          total = entities.length;
        }
        effectResults.push({
          effect: "deref",
          entityType: derefEntityType,
          success: true
        });
        return result === null ? null : { rows: result, total };
      } catch (error) {
        effectResults.push({
          effect: "deref",
          entityType: derefEntityType,
          success: false,
          error: error instanceof Error ? error.message : String(error)
        });
        return null;
      }
    },
    swap: async (swapEntityType, swapEntityId, transform) => {
      try {
        const current = await deps.persistence.getById(swapEntityType, swapEntityId);
        if (!current) {
          effectResults.push({
            effect: "swap",
            entityType: swapEntityType,
            success: false,
            error: `Entity ${swapEntityType}/${swapEntityId} not found`
          });
          return null;
        }
        const ctx = createContextFromBindings({
          current,
          entity: entityData,
          payload
        }, false, deps.contextExtensions);
        let newData;
        if (Array.isArray(transform)) {
          const result = evaluate(
            transform,
            ctx
          );
          if (result && typeof result === "object" && !Array.isArray(result)) {
            newData = result;
          } else {
            newData = current;
          }
        } else if (typeof transform === "object" && transform !== null) {
          newData = { ...current, ...transform };
        } else {
          effectResults.push({
            effect: "swap",
            entityType: swapEntityType,
            success: false,
            error: "swap transform must be an S-expression or object"
          });
          return null;
        }
        await deps.persistence.update(swapEntityType, swapEntityId, newData);
        effectResults.push({
          effect: "swap",
          entityType: swapEntityType,
          data: { id: swapEntityId, ...newData },
          success: true
        });
        return newData;
      } catch (error) {
        effectResults.push({
          effect: "swap",
          entityType: swapEntityType,
          success: false,
          error: error instanceof Error ? error.message : String(error)
        });
        return null;
      }
    },
    watch: (_watchEntityType, _watchOptions) => {
      if (deps.debug) {
        effectLog2.debug("watch:noop-server", { entityType: _watchEntityType });
      }
    },
    atomic: async (atomicEffects) => {
      let atomicFailed = false;
      let atomicError = "";
      const atomicExecutor = new EffectExecutor({
        handlers,
        bindings: bindingsRef ?? {},
        context: contextRef ?? { traitName, orbitalName: deps.orbitalName, state: "unknown", transition: "unknown" },
        debug: deps.debug,
        contextExtensions: deps.contextExtensions,
        // Same render-time marker boundary as the outer executor
        // (including the [shared]-defers-regardless-of-persistence rule).
        deferRenderBindings: clientResolvesRenderBindings(deps.entity),
        resolveIntrinsicFields: (type) => deps.intrinsicFieldNames(type),
        resolveEntityFields: (type) => deps.entityFieldsFor(type),
        inFlightCalls: deps.inFlightCalls,
        ...deps.outsideEventQueue !== void 0 ? { outsideEventQueue: deps.outsideEventQueue } : {},
        ...deps.outsideEventQueue !== void 0 ? { outsideEventQueue: deps.outsideEventQueue } : {}
      });
      for (const innerEffect of atomicEffects) {
        if (atomicFailed) break;
        try {
          await atomicExecutor.execute(innerEffect);
        } catch (err) {
          atomicFailed = true;
          atomicError = err instanceof Error ? err.message : String(err);
        }
      }
      if (atomicFailed) {
        effectResults.push({
          effect: "atomic",
          success: false,
          error: `Atomic block failed: ${atomicError}`
        });
      } else {
        effectResults.push({
          effect: "atomic",
          success: true,
          data: { innerCount: atomicEffects.length }
        });
      }
    },
    // Client-side effects - collect for forwarding to client
    renderUI: async (slot, pattern, props, priority) => {
      await injectRelationOptions(deps, pattern, traitName);
      const patternNode = pattern !== null && typeof pattern === "object" && !Array.isArray(pattern) ? pattern : null;
      const patternEntity = patternNode?.entity;
      const entityRow = patternEntity !== null && typeof patternEntity === "object" && !Array.isArray(patternEntity) ? patternEntity : null;
      const patternTypeRaw = patternNode?.["type"];
      renderLog.debug("renderUI:push", {
        trait: traitName,
        slot,
        patternType: typeof patternTypeRaw === "string" ? patternTypeRaw : void 0,
        entityRowId: typeof entityRow?.id === "string" ? entityRow.id : void 0,
        entityIsObject: entityRow !== null
      });
      pushClientEffect(["render-ui", slot, pattern, props, priority]);
    },
    navigate: (path, params, crumb) => {
      if (crumb !== void 0) {
        pushClientEffect(["navigate", path, params, { crumb }]);
      } else {
        pushClientEffect(["navigate", path, params]);
      }
    },
    navigateBack: () => {
      pushClientEffect(["navigate-back"]);
    },
    log: (message, level) => {
      if (level === "error") {
        dynamicLog.error(message);
      } else if (level === "warn") {
        dynamicLog.warn(message);
      } else {
        dynamicLog.debug(message);
      }
    },
    // Allow custom handlers to override — except `callService`, which the
    // stage's own handler above already delegates to (recording the result and
    // awaiting it outside the event queue).
    ...hostOverrides,
    ...hostProgramEffect !== void 0 ? { programEffect: (op, args2) => hostProgramEffect(op, args2, user !== void 0 ? { principal: user.id, role: user.role } : void 0) } : {}
  };
  const state = deps.getTraitState(traitName);
  const bindings = {
    entity: entityData,
    payload,
    state: dispatch?.toState ?? (state?.currentState || "unknown"),
    user,
    ...now !== void 0 ? { now } : {},
    ...viewerI18n(locale ?? deps.defaultLocale, deps.messages),
    ...dispatch !== void 0 ? dispatch : {}
  };
  if (callsitePayload) {
    bindings.callsitePayload = callsitePayload;
  }
  const config = buildConfigBinding({
    traitDef: deps.irTraits.find((t) => t.name === traitName),
    resolvedDefaults: deps.resolvedTraitConfigs[traitName],
    callSiteOverride: deps.configByTrait.get(traitName),
    user
  });
  if (config !== void 0) bindings.config = config;
  if (deps.sigilPages.length > 0) {
    bindings.pages = deps.sigilPages;
  }
  bindings.currentTheme = deps.sigilTheme;
  const traitEntityName = deps.irTraits.find((t) => t.name === traitName)?.linkedEntity ?? entityType;
  const entityBinding = buildEntityBinding({
    entity: { fields: deps.entityFieldsFor(traitEntityName) },
    persisted: bindings.entity,
    frame: deps.frames.get(deps.frameKeyFor(traitName))
  });
  if (entityBinding !== void 0) bindings.entity = entityBinding;
  if (entityType) {
    bindings[entityType] = bindings.entity ?? entityData;
  }
  bindingsRef = bindings;
  const context = {
    traitName,
    orbitalName: deps.orbitalName,
    state: state?.currentState || "unknown",
    transition: "unknown",
    entityId
  };
  contextRef = context;
  const executor = new EffectExecutor({
    // A persist refused before its handler ran was never recorded by it.
    onPersistRefused: ({ args: args2, error }) => {
      const [action, entityType2] = args2;
      effectResults.push({
        effect: "persist",
        action: typeof action === "string" ? action : "persist",
        ...typeof entityType2 === "string" && entityType2 !== "" ? { entityType: entityType2 } : {},
        success: false,
        error
      });
    },
    handlers,
    bindings,
    context,
    debug: deps.debug,
    contextExtensions: deps.contextExtensions,
    // Carry `@entity`-dependent render-ui leaves as render-time markers
    // for runtime-only (and unlinked) entities: the client's local state
    // machines execute the same `(set)` writes, so the renderer resolves
    // markers against its live store — otherwise these SSE-pushed eager
    // props clobber the client's live marker frames on every event.
    // Persistent NON-shared entities stay eager: their `@entity` merges
    // persistence rows the client does not hold. A `[shared]` entity is
    // deferred REGARDLESS of persistence — its trait frame's scalars are
    // client-held by contract (one live frame across bound traits), and
    // the eager clobber pinned the chat composer's controlled input to
    // the server's flush-time "" on every keystroke round-trip.
    deferRenderBindings: clientResolvesRenderBindings(deps.entity),
    resolveIntrinsicFields: (type) => deps.intrinsicFieldNames(type),
    resolveEntityFields: (type) => deps.entityFieldsFor(type),
    inFlightCalls: deps.inFlightCalls,
    ...deps.outsideEventQueue !== void 0 ? { outsideEventQueue: deps.outsideEventQueue } : {},
    ...followOn === true && deps.deferCallOutcome !== void 0 ? { deferCallOutcome: (event, eventPayload) => deps.deferCallOutcome?.({ traitName, event, payload: eventPayload, entityId, originClientId }) } : {}
  });
  await executor.executeAll(effects);
}

// src/evaluation/stage-runner.ts
function createIndexStageRunner(options) {
  const { traitIndex, persistence, frames, manager } = options;
  const entityFieldsFor = (entityType) => findEntityAmongOrbitals(traitIndex.allEntities, entityType)?.fields ?? [];
  return async (traitName, args) => {
    const entry = traitIndex.byName.get(traitName);
    if (!entry) {
      return;
    }
    await runServerEffectStage(
      {
        persistence,
        frames,
        frameKeyFor: (t) => traitIndex.byName.get(t)?.frameKey ?? t,
        orbitalName: entry.orbitalName,
        ...entry.orbitalId !== void 0 ? { orbitalId: entry.orbitalId } : {},
        irTraits: [entry.irTrait],
        entity: entry.entity,
        configByTrait: new Map(
          entry.config !== void 0 ? [[traitName, entry.config]] : []
        ),
        resolvedTraitConfigs: {},
        resolvedSchema: options.schema ?? null,
        registeredOrbitals: traitIndex.orbitals,
        getTraitState: (t) => manager.getState(t),
        intrinsicFieldNames: (entityType) => entityFieldsFor(entityType).filter((field) => field.intrinsic === true && typeof field.name === "string").map((field) => field.name),
        entityFieldsFor,
        sigilPages: appNavItems(traitIndex.orbitals.map((o) => o.schema)),
        sigilTheme: sigilThemeKey(
          traitIndex.orbitals.find((o) => o.schema.name === entry.orbitalName)?.schema.theme,
          themeDataKey(options.schema?.theme) || void 0
        ),
        ...options.extraEffectHandlers !== void 0 ? { extraEffectHandlers: options.extraEffectHandlers } : {},
        ...options.deliverEmit !== void 0 ? { deliverEmit: options.deliverEmit } : {},
        ...options.debug !== void 0 ? { debug: options.debug } : {},
        ...options.mockMode !== void 0 ? { mockMode: options.mockMode } : {},
        ...options.messages !== void 0 ? { messages: options.messages } : {},
        ...options.servicePorts !== void 0 ? { servicePorts: options.servicePorts } : {},
        ...options.inFlightCalls !== void 0 ? { inFlightCalls: options.inFlightCalls } : {},
        ...options.outsideEventQueue !== void 0 ? { outsideEventQueue: options.outsideEventQueue } : {},
        ...options.schema?.locales?.[0] !== void 0 ? { defaultLocale: options.schema.locales[0] } : {}
      },
      { ...args, traitName }
    );
  };
}
var log2 = createLogger("almadar:runtime:effects:client");
var _ws = null;
var _wsQueue = [];
function getOrOpenWs() {
  if (typeof WebSocket === "undefined" || typeof location === "undefined") return null;
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) {
    return _ws;
  }
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const url = `${protocol}//${location.host}/ws/events`;
  try {
    _ws = new WebSocket(url);
    _ws.addEventListener("open", () => {
      const queued = _wsQueue.splice(0);
      for (const msg of queued) {
        _ws.send(msg);
      }
    });
    _ws.addEventListener("error", () => {
      _ws = null;
    });
    _ws.addEventListener("close", () => {
      _ws = null;
    });
  } catch {
    _ws = null;
  }
  return _ws;
}
function urlBase64ToArrayBuffer(base64Url) {
  const padding = "=".repeat((4 - base64Url.length % 4) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) {
    view[i] = raw.charCodeAt(i);
  }
  return buffer;
}
function sendServerEvent(orbital, event, payload) {
  const msg = JSON.stringify({
    type: "ORBITAL_EVENT",
    payload: { orbital, event, payload: payload ?? null }
  });
  const ws = getOrOpenWs();
  if (!ws) {
    log2.warn("send-server:no-ws", { event });
    return;
  }
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(msg);
  } else {
    _wsQueue.push(msg);
  }
}
function createClientEffectHandlers(options) {
  const {
    eventBus,
    slotSetter,
    navigate,
    navigateBack,
    sendServer,
    orbitalName = "",
    callService: consumerCallService,
    liveEntity,
    persistDelegated,
    callServiceDelegated
  } = options;
  return {
    emit: (event, payload, source) => {
      const prefixedEvent = event.startsWith("UI:") ? event : `UI:${event}`;
      eventBus.emit(prefixedEvent, payload, source);
    },
    persist: async () => {
      log2.debug("persist is server-side only, ignored on client");
      return void 0;
    },
    // Bridge mode: the server runs every persist and the response
    // carries its outcome — tell the executor so the placeholder above is
    // never read as a denial (see `EffectHandlers.persistDelegated`).
    ...persistDelegated === true ? { persistDelegated: true } : {},
    ...callServiceDelegated === true ? { callServiceDelegated: true } : {},
    // `[runtime]` entities live only in the browser — `(set @entity.X)`
    // must land in the live client store so the same `executeAll`'s
    // render-ui, the next tick, and guards all read the advanced value.
    // Without a `liveEntity` we are in bridge mode (server persists),
    // so the write is a no-op here.
    set: (_entityId, field, value) => {
      if (!liveEntity) {
        log2.warn("set is server-side only, ignored on client (no live entity)");
        return;
      }
      liveEntity[field] = value;
    },
    callService: async (service, action, params) => {
      if (consumerCallService) return consumerCallService(service, action, params);
      const mockId = `mock_${service}_${action}_${Math.random().toString(36).slice(2, 10)}`;
      const paramsEcho = {};
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          if (v !== void 0 && (typeof v === "string" || typeof v === "number" || typeof v === "boolean" || v === null || v instanceof Date)) {
            paramsEcho[k] = v;
          }
        }
      }
      return {
        id: mockId,
        clientSecret: `secret_${mockId}`,
        success: true,
        status: "succeeded",
        ...paramsEcho
      };
    },
    renderUI: (slot, pattern, props, priority) => {
      if (pattern === null) {
        slotSetter.clearSlot(slot);
        return;
      }
      slotSetter.addPattern(slot, pattern, props, priority);
    },
    navigate: navigate ?? ((path) => {
      if (typeof window !== "undefined" && /^https?:\/\//.test(path)) {
        window.location.href = path;
        return;
      }
      log2.warn("navigate-no-handler", { path });
    }),
    navigateBack: navigateBack ?? (() => {
      log2.warn("navigate-back-no-handler");
    }),
    sendServer: sendServer ?? ((event, payload) => {
      sendServerEvent(orbitalName, event, payload);
    }),
    // === Browser device handlers (client host path) ===
    // Each throws when the underlying API is unavailable so the executor's
    // runSubstrate wrapper fires `emit.failure` with `{ error }`.
    browserOpenFilePicker: async (options2) => {
      if (typeof window === "undefined" || !("showOpenFilePicker" in window)) {
        throw new Error("File picker API is not available in this environment");
      }
      const host = window;
      const pickerOptions = {};
      if (options2?.multiple === true) pickerOptions.multiple = true;
      if (typeof options2?.accept === "string" && options2.accept.length > 0) {
        pickerOptions.types = [{ accept: { [options2.accept]: [] } }];
      }
      const handles = await host.showOpenFilePicker(pickerOptions);
      const files = await Promise.all(handles.map(async (handle) => {
        const file = await handle.getFile();
        return { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified };
      }));
      return { files };
    },
    browserClipboardRead: async () => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API is not available in this environment");
      }
      const text = await navigator.clipboard.readText();
      return { text };
    },
    browserClipboardWrite: async (text) => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API is not available in this environment");
      }
      await navigator.clipboard.writeText(text);
      return { text };
    },
    browserPushSubscribe: async () => {
      if (typeof navigator === "undefined" || !("serviceWorker" in navigator) || typeof window === "undefined" || !("PushManager" in window)) {
        throw new Error("Push API is not available in this environment");
      }
      const keyResponse = await fetch("/api/push/vapid-public-key");
      if (!keyResponse.ok) {
        throw new Error("Push is not configured on this host (no VAPID public key)");
      }
      const { publicKey } = await keyResponse.json();
      const registration = await navigator.serviceWorker.register("/almadar-push-sw.js");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(publicKey)
      });
      const json = subscription.toJSON();
      const endpoint = json.endpoint ?? "";
      const p256dh = json.keys?.p256dh ?? "";
      const auth = json.keys?.auth ?? "";
      if (!endpoint || !p256dh || !auth) {
        throw new Error("Push subscription resolved without endpoint/keys");
      }
      return { endpoint, p256dh, auth };
    },
    browserGeolocationCurrent: async (options2) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        throw new Error("Geolocation API is not available in this environment");
      }
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options2);
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    }
  };
}

// src/effects/server-leg.ts
var ServerLegCollector = class {
  effects = [];
  source;
  sources = /* @__PURE__ */ new Set();
  /** Attribute every effect delegated while `run` executes to `trait`. */
  async attribute(trait, run) {
    const previous = this.source;
    this.source = trait;
    try {
      await run();
    } finally {
      this.source = previous;
    }
  }
  /** Traits whose own effects were delegated to the server. */
  delegatingTraits() {
    return this.sources;
  }
  delegate(effect, target) {
    if (target === "server") {
      this.effects.push(effect);
      if (this.source !== void 0) this.sources.add(this.source);
    }
  }
  /** Take every effect collected since the last drain, leaving the collector empty. */
  drain() {
    const drained = this.effects;
    this.effects = [];
    return drained;
  }
};

// src/evaluation/awaiting.ts
var AwaitingRegistry = class {
  entries = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Map();
  anyListeners = /* @__PURE__ */ new Set();
  version = 0;
  begin(list) {
    for (const entry of list) {
      this.entries.set(entry.trait, entry);
      this.emit(entry.trait);
    }
  }
  end(traits) {
    for (const trait of traits) {
      if (!this.entries.delete(trait)) continue;
      this.emit(trait);
    }
  }
  get(trait) {
    return this.entries.get(trait);
  }
  list() {
    return [...this.entries.values()];
  }
  subscribe(trait, listener) {
    let set = this.listeners.get(trait);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.listeners.set(trait, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
      if (set && set.size === 0) this.listeners.delete(trait);
    };
  }
  /** Any trait's awaiting change (an empty slot does not know which trait will fill it). */
  subscribeAll(listener) {
    this.anyListeners.add(listener);
    return () => {
      this.anyListeners.delete(listener);
    };
  }
  /** Bumped on every change — a stable snapshot key for `useSyncExternalStore`. */
  getVersion() {
    return this.version;
  }
  emit(trait) {
    this.version += 1;
    this.listeners.get(trait)?.forEach((listener) => listener());
    this.anyListeners.forEach((listener) => listener());
  }
};

// src/evaluation/circuit-store.ts
function createMemoryCircuitStore(traits = [], config = {}, observer) {
  const manager = new StateMachineManager(traits, config, observer);
  const frames = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  let version = 0;
  return {
    manager,
    frames,
    mount: new MountLifecycle(),
    callsitePayloads: /* @__PURE__ */ new Map(),
    awaiting: new AwaitingRegistry(),
    snapshot(trait, entityId, frameKey) {
      const key = frameKey ?? trait;
      const frame = frames.get(key);
      return {
        state: manager.getState(trait, entityId)?.currentState ?? "",
        frame: frame !== void 0 ? { ...frame } : void 0
      };
    },
    restore(trait, entityId, snap, frameKey, cause) {
      const key = frameKey ?? trait;
      if (snap.state !== "") {
        manager.rollbackState(trait, snap.state, entityId, cause);
      }
      if (snap.frame === void 0) {
        frames.delete(key);
      } else {
        frames.set(key, { ...snap.frame });
      }
    },
    notify() {
      version += 1;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getVersion() {
      return version;
    }
  };
}

// src/evaluation/trait-mount-error.ts
var TraitMountError = class extends Error {
  trait;
  event;
  original;
  constructor(trait, event, original) {
    super(original.message);
    this.name = "TraitMountError";
    this.trait = trait;
    this.event = event;
    this.original = original;
  }
};
var clientRoleLog = createLogger("almadar:runtime:client-role");
function alreadyDeliveredFrom(dispatch) {
  const set = /* @__PURE__ */ new Set();
  for (const emitted of dispatch.response.emittedEvents) {
    set.add(alreadyDeliveredKey(emitted.source?.trait ?? "", emitted.event));
  }
  if (dispatch.serverLeg?.targetTrait !== void 0) {
    set.add(alreadyDeliveredKey(dispatch.serverLeg.targetTrait, dispatch.serverLeg.event));
  }
  return set;
}
function sameEntityFrameKeys(traitIndex, store, traitName, rowId) {
  const entry = traitIndex.byName.get(traitName);
  const keys = /* @__PURE__ */ new Set([entry?.frameKey ?? traitName]);
  if (entry === void 0) return keys;
  for (const sibling of traitIndex.byName.values()) {
    if (sibling.entity.name !== entry.entity.name) continue;
    const siblingId = store.frames.get(sibling.frameKey)?.["id"];
    if (rowId === void 0 || siblingId === void 0 || siblingId === rowId) keys.add(sibling.frameKey);
  }
  return keys;
}
function rowIdOf(row) {
  const id = row["id"];
  return typeof id === "string" && id !== "" ? id : void 0;
}
function fanOutEntityRows(store, traitIndex, entityByTrait, writtenTraits) {
  const merge = (frameKey, row) => {
    const existing = store.frames.get(frameKey);
    if (existing === row) return;
    store.frames.set(frameKey, existing !== void 0 ? { ...existing, ...row } : { ...row });
  };
  for (const [traitName, row] of Object.entries(entityByTrait)) {
    merge(traitIndex.byName.get(traitName)?.frameKey ?? traitName, row);
  }
  for (const traitName of writtenTraits) {
    const row = entityByTrait[traitName];
    if (row === void 0) continue;
    for (const frameKey of sameEntityFrameKeys(traitIndex, store, traitName, rowIdOf(row))) merge(frameKey, row);
  }
}
function restoreDispatch(store, dispatch, cause) {
  if (dispatch.snapshot === void 0) return;
  for (const [frameKey, row] of dispatch.siblingSnapshots ?? []) {
    if (row === void 0) store.frames.delete(frameKey);
    else store.frames.set(frameKey, { ...row });
  }
  store.restore(dispatch.trait, dispatch.entityId, dispatch.snapshot, dispatch.frameKey, cause);
  store.notify();
}
function i18nOpts(opts) {
  return {
    ...opts.locale !== void 0 ? { locale: opts.locale } : {},
    ...opts.messages !== void 0 ? { messages: opts.messages } : {}
  };
}
function createClientEffectRunner(deps, collector, onExecuted) {
  return async (traitName, args) => {
    onExecuted?.(traitName, args.firing, {
      ...args.payload !== void 0 ? { payload: args.payload } : {},
      ...args.entityId !== void 0 ? { entityId: args.entityId } : {},
      ...args.received !== void 0 ? args.received : {}
    });
    const entry = deps.traitIndex.byName.get(traitName);
    const frameKey = entry?.frameKey ?? traitName;
    let frame = deps.store.frames.get(frameKey);
    if (frame === void 0) {
      frame = {};
      deps.store.frames.set(frameKey, frame);
    }
    const capture = args.callsitePayload ?? deps.store.callsitePayloads.get(traitName);
    if (args.callsitePayload !== void 0) deps.store.callsitePayloads.set(traitName, args.callsitePayload);
    const pushClientEffect = (effect) => {
      args.clientEffects.push(effect);
      args.clientEffectsByTrait?.push({ traitName, effect, ...args.firing, ...capture !== void 0 ? { callsitePayload: capture } : {} });
      args.onPush?.({ type: "effect", data: effect });
    };
    const clientHandlers = createClientEffectHandlers({
      eventBus: {
        emit: (type, payload, source) => {
          const event = type.startsWith("UI:") ? type.slice(3) : type;
          const item = { event, payload, source };
          args.emittedEvents.push(item);
          args.onPush?.({ type: "event", data: item });
        }
      },
      slotSetter: {
        addPattern: (slot, pattern, props, priority) => {
          pushClientEffect(
            priority !== void 0 ? ["render-ui", slot, pattern, props, priority] : props !== void 0 ? ["render-ui", slot, pattern, props] : ["render-ui", slot, pattern]
          );
        },
        clearSlot: (slot) => {
          pushClientEffect(["render-ui", slot, null]);
        }
      },
      navigate: (path, params, crumb) => {
        pushClientEffect(crumb !== void 0 ? ["navigate", path, params, { crumb }] : ["navigate", path, params]);
      },
      navigateBack: () => {
        pushClientEffect(["navigate-back"]);
      },
      liveEntity: frame,
      orbitalName: entry?.orbitalName ?? deps.orbitalName
    });
    const state = args.dispatch?.toState ?? deps.store.manager.getState(traitName, args.entityId)?.currentState ?? "unknown";
    const config = buildConfigBinding({
      traitDef: entry?.irTrait,
      resolvedDefaults: void 0,
      callSiteOverride: entry?.config,
      user: args.user
    });
    const executor = new EffectExecutor({
      handlers: clientHandlers,
      bindings: {
        entity: buildEntityBinding({ entity: entry?.entity, persisted: args.entityData, frame }),
        payload: args.payload,
        state,
        ...config !== void 0 ? { config } : {},
        ...args.user !== void 0 ? { user: args.user } : {},
        ...args.now !== void 0 ? { now: args.now } : {},
        ...deps.locale !== void 0 ? { locale: deps.locale, messages: deps.messages?.[deps.locale] ?? {} } : {},
        ...capture !== void 0 ? { callsitePayload: capture } : {},
        pages: appNavItems((deps.fullTraitIndex ?? deps.traitIndex).orbitals.map((o) => o.schema)),
        currentTheme: sigilThemeKey(deps.traitIndex.orbitals.find((o) => o.schema.name === (entry?.orbitalName ?? deps.orbitalName))?.schema.theme),
        ...args.dispatch !== void 0 ? args.dispatch : {}
      },
      context: {
        traitName,
        orbitalName: entry?.orbitalName ?? deps.orbitalName,
        state,
        transition: "unknown",
        ...args.entityId !== void 0 ? { entityId: args.entityId } : {},
        ...entry?.orbitalId !== void 0 ? { orbitalId: entry.orbitalId } : {},
        ...entry?.irTrait.id !== void 0 ? { traitId: entry.irTrait.id } : {},
        ...entry?.irTrait.emits !== void 0 ? { emits: entry.irTrait.emits } : {}
      },
      environment: "client",
      delegate: collector,
      deferRenderBindings: clientResolvesRenderBindings(entry?.entity)
    });
    await collector.attribute(traitName, () => executor.executeAll(args.effects).then(() => void 0));
  };
}
function resolvePersistence(opts) {
  return opts.persistence ?? new InMemoryPersistence();
}
function baseEvaluateDeps(opts, runEffects) {
  return {
    traitIndex: opts.traitIndex,
    manager: opts.store.manager,
    persistence: resolvePersistence(opts),
    frames: opts.store.frames,
    mount: opts.store.mount,
    runEffects,
    ...opts.user !== void 0 ? { user: opts.user } : {},
    ...opts.guardMode !== void 0 ? { guardMode: opts.guardMode } : {},
    ...opts.strictBindings !== void 0 ? { strictBindings: opts.strictBindings } : {},
    ...opts.contextExtensions !== void 0 ? { contextExtensions: opts.contextExtensions } : {},
    ...opts.debug !== void 0 ? { debug: opts.debug } : {},
    ...opts.logContext !== void 0 ? { logContext: opts.logContext } : {}
  };
}
async function dispatchWithServerLeg(opts, request) {
  const traitName = request.targetTrait;
  if (traitName === void 0) {
    throw new Error("dispatchWithServerLeg requires request.targetTrait (the seed trait)");
  }
  const entry = opts.traitIndex.byName.get(traitName);
  if (entry === void 0) {
    throw new Error(`dispatchWithServerLeg: unknown trait "${traitName}"`);
  }
  const mode = entry.dispatchMode;
  const entityId = request.entityId;
  const snapshot = mode === "runtimeOptimistic" ? opts.store.snapshot(traitName, entityId, entry.frameKey) : void 0;
  let siblingSnapshots;
  if (snapshot !== void 0) {
    siblingSnapshots = /* @__PURE__ */ new Map();
    for (const frameKey of sameEntityFrameKeys(opts.traitIndex, opts.store, traitName, void 0)) {
      if (frameKey === entry.frameKey) continue;
      const row = opts.store.frames.get(frameKey);
      siblingSnapshots.set(frameKey, row !== void 0 ? { ...row } : void 0);
    }
  }
  const beforeFrame = opts.store.frames.get(entry.frameKey);
  const seedRow = beforeFrame !== void 0 ? { ...beforeFrame } : void 0;
  const beforeStates = /* @__PURE__ */ new Map();
  for (const [name, traitState] of opts.store.manager.getAllStates()) {
    beforeStates.set(name, traitState.currentState);
  }
  const executedTraitNames = /* @__PURE__ */ new Set();
  const firings = /* @__PURE__ */ new Map();
  const cascadeFirings = [];
  const collector = new ServerLegCollector();
  const runEffects = createClientEffectRunner(
    { traitIndex: opts.traitIndex, ...opts.fullTraitIndex !== void 0 ? { fullTraitIndex: opts.fullTraitIndex } : {}, store: opts.store, orbitalName: opts.orbitalName, ...i18nOpts(opts) },
    collector,
    (name, firing, step) => {
      executedTraitNames.add(name);
      if (firing === void 0) return;
      if (!firings.has(name)) firings.set(name, firing);
      const row = opts.store.frames.get(opts.traitIndex.byName.get(name)?.frameKey ?? name);
      cascadeFirings.push({ trait: name, ...firing, ...step, ...row !== void 0 ? { row: { ...row } } : {} });
    }
  );
  const response = await evaluateOrbitalEvent(
    baseEvaluateDeps(opts, runEffects),
    { event: request.event, payload: request.payload, entityId, targetTrait: traitName }
  );
  if (response.entityByTrait) fanOutEntityRows(opts.store, opts.traitIndex, response.entityByTrait, executedTraitNames);
  opts.store.notify();
  const drained = collector.drain();
  const hybrid = mode === "hybridClientOnly";
  const full = opts.fullTraitIndex;
  const offPage = full === void 0 ? [] : response.emittedEvents.flatMap((emitted) => collectListenerTargets(full, emitted.source, emitted.event, emitted.payload).filter((target) => !opts.traitIndex.byName.has(target.listenerTrait)).map((target) => ({ emitted, target })));
  const reachesOffPage = offPage.length > 0;
  const downstreamNeedsServer = [...collector.delegatingTraits()].some((t) => t !== traitName);
  const browserStored = new Set(opts.traitIndex.allEntities.filter(storesRowsInBrowser).map((e) => e.name));
  const browserWrites = cascadeFirings.flatMap((f) => {
    const indexed = opts.traitIndex.byName.get(f.trait);
    return f.trait !== traitName && indexed !== void 0 && browserLegEvents(indexed.traitDef, (t) => browserStored.has(t), indexed.config).has(f.event) ? [{ firing: f, orbital: indexed.orbitalName }] : [];
  });
  const writingTraits = new Set(browserWrites.map((w) => w.firing.trait));
  const delegating = [...collector.delegatingTraits()];
  let serverLeg;
  let continuations;
  if (hybrid && delegating.length > 0 && delegating.every((t) => writingTraits.has(t))) {
    continuations = [
      ...offPage.map(({ emitted, target }) => offPageContinuation(emitted, target, request)),
      ...browserWrites.map((w) => browserWriteContinuation(w.firing, w.orbital, request))
    ];
  } else if (hybrid && !downstreamNeedsServer && drained.length === 0 && reachesOffPage) {
    continuations = offPage.map(({ emitted, target }) => offPageContinuation(emitted, target, request));
  } else if (drained.length > 0 || reachesOffPage) {
    const seedBrowserLeg = browserLegEvents(entry.traitDef, (t) => browserStored.has(t), entry.config).has(request.event);
    if (hybrid && !downstreamNeedsServer && !reachesOffPage && !seedBrowserLeg) {
      clientRoleLog.error("hybrid-trait-produced-server-leg", {
        trait: traitName,
        event: request.event,
        drained: drained.length
      });
    } else {
      if (hybrid && browserWrites.length > 0) {
        continuations = browserWrites.map((w) => browserWriteContinuation(w.firing, w.orbital, request));
      }
      const executedTraits = [];
      for (const name of executedTraitNames) {
        if (continuations !== void 0 && writingTraits.has(name)) continue;
        const indexed = opts.traitIndex.byName.get(name);
        const from = beforeStates.get(name) ?? (indexed !== void 0 ? findInitialState(indexed.traitDef) : "");
        const event = firings.get(name)?.event;
        executedTraits.push({ trait: name, from, ...event !== void 0 ? { event } : {} });
      }
      serverLeg = {
        event: request.event,
        ...request.payload !== void 0 ? { payload: request.payload } : {},
        ...entityId !== void 0 ? { entityId } : {},
        targetTrait: traitName,
        sourceTrait: traitName,
        ...request.clientId !== void 0 ? { clientId: request.clientId } : {},
        ...request.locale !== void 0 ? { locale: request.locale } : {},
        ...executedTraits.length > 0 ? { traits: executedTraits } : {},
        ...seedRow !== void 0 ? { entityByTrait: { [traitName]: seedRow } } : {},
        ...request.delivery !== void 0 ? { delivery: request.delivery } : {},
        ...request.dispatchLog !== void 0 ? { dispatchLog: request.dispatchLog } : {}
      };
    }
  }
  return {
    response,
    serverLeg,
    ...continuations !== void 0 ? { continuations } : {},
    mode,
    snapshot,
    ...siblingSnapshots !== void 0 ? { siblingSnapshots } : {},
    writtenTraits: executedTraitNames,
    trait: traitName,
    entityId,
    frameKey: entry.frameKey,
    firings
  };
}
function offPageContinuation(emitted, target, request) {
  return {
    orbital: target.entry.orbitalName,
    request: {
      event: target.triggers,
      ...target.payload !== void 0 ? { payload: target.payload } : {},
      ...target.entityId !== void 0 ? { entityId: target.entityId } : {},
      targetTrait: target.listenerTrait,
      ...request.clientId !== void 0 ? { clientId: request.clientId } : {},
      delivery: deliveryOf(emitted)
    }
  };
}
function browserWriteContinuation(f, orbital, request) {
  return {
    orbital,
    request: {
      event: f.event,
      ...f.payload !== void 0 ? { payload: f.payload } : {},
      ...f.entityId !== void 0 ? { entityId: f.entityId } : {},
      targetTrait: f.trait,
      sourceTrait: f.trait,
      traits: [{ trait: f.trait, from: f.fromState, event: f.event }],
      ...f.row !== void 0 ? { entityByTrait: { [f.trait]: f.row } } : {},
      ...request.clientId !== void 0 ? { clientId: request.clientId } : {},
      ...request.locale !== void 0 ? { locale: request.locale } : {},
      ...f.delivery !== void 0 ? { delivery: f.delivery } : {},
      ...f.log !== void 0 ? { dispatchLog: f.log } : {}
    }
  };
}
function foldBase(store, carried, seeded, traitIndex) {
  if (carried === void 0) return void 0;
  const out = { ...carried };
  for (const trait of seeded) {
    if (!(trait in carried)) continue;
    const frame = store.frames.get(traitIndex.byName.get(trait)?.frameKey ?? trait);
    if (frame !== void 0) out[trait] = { ...frame };
  }
  return out;
}
function changedSinceCarried(response, carried, traitIndex) {
  if (carried === void 0 || response.entityByTrait === void 0) return response;
  const carriedByFrame = /* @__PURE__ */ new Map();
  for (const [trait, row] of Object.entries(carried)) {
    carriedByFrame.set(traitIndex.byName.get(trait)?.frameKey ?? trait, row);
  }
  const entityByTrait = {};
  for (const [trait, row] of Object.entries(response.entityByTrait)) {
    const base = carriedByFrame.get(traitIndex.byName.get(trait)?.frameKey ?? trait);
    if (base === void 0) {
      entityByTrait[trait] = row;
      continue;
    }
    const delta = {};
    for (const [field, value] of Object.entries(row)) {
      if (field === "id" || JSON.stringify(value) !== JSON.stringify(base[field])) delta[field] = value;
    }
    entityByTrait[trait] = delta;
  }
  return { ...response, entityByTrait };
}
function withMountedSet(request, opts) {
  if (opts.fullTraitIndex === void 0) return request;
  return {
    ...request,
    payload: {
      ...request.payload ?? {},
      _activeTraits: [...opts.traitIndex.byName.keys()],
      // Only traits whose dispatches reach the host: a client-only trait's
      // INIT never posts, so the host would hold its deliveries forever.
      _awaitingInit: opts.store.mount.awaitingTraits().filter((trait) => opts.traitIndex.byName.get(trait)?.dispatchMode !== "hybridClientOnly")
    }
  };
}
function stripCircuitState(request) {
  const stripped = { ...request };
  delete stripped.traits;
  delete stripped.entityByTrait;
  return stripped;
}
function clientOnlyTraits(traitIndex) {
  const out = /* @__PURE__ */ new Set();
  for (const [name, entry] of traitIndex.byName) if (entry.dispatchMode === "hybridClientOnly") out.add(name);
  return out;
}
function withoutClientOnlyRuns(response, clientOnly) {
  if (clientOnly.size === 0) return response;
  const keep = (record) => record === void 0 ? void 0 : Object.fromEntries(Object.entries(record).filter(([trait]) => !clientOnly.has(trait)));
  const byTrait = response.clientEffectsByTrait?.filter((e) => !clientOnly.has(e.traitName) || e.callsitePayload !== void 0);
  return {
    ...response,
    states: keep(response.states) ?? {},
    ...response.entityByTrait !== void 0 ? { entityByTrait: keep(response.entityByTrait) } : {},
    ...byTrait !== void 0 ? { clientEffectsByTrait: byTrait, clientEffects: byTrait.map((e) => e.effect) } : {}
  };
}
function withoutTraits(response, traits) {
  const keep = (record) => record === void 0 ? void 0 : Object.fromEntries(Object.entries(record).filter(([trait]) => !traits.has(trait)));
  const byTrait = response.clientEffectsByTrait?.filter((e) => !traits.has(e.traitName));
  return {
    ...response,
    states: keep(response.states) ?? {},
    ...response.entityByTrait !== void 0 ? { entityByTrait: keep(response.entityByTrait) } : {},
    ...byTrait !== void 0 ? { clientEffectsByTrait: byTrait, clientEffects: byTrait.map((e) => e.effect) } : {}
  };
}
async function applyOrbitalEventResponse(store, serverResponse, alreadyDelivered, opts, writtenTraits = /* @__PURE__ */ new Set()) {
  const heldHere = new Set(store.mount.awaitingTraits().filter((trait) => opts.traitIndex.byName.get(trait)?.dispatchMode === "hybridClientOnly"));
  const clientOnly = clientOnlyTraits(opts.traitIndex);
  const browserStored = new Set(opts.traitIndex.allEntities.filter(storesRowsInBrowser).map((e) => e.name));
  const response = withoutClientOnlyRuns(heldHere.size > 0 ? withoutTraits(serverResponse, heldHere) : serverResponse, clientOnly);
  const clientEffects = [...response.clientEffects ?? []];
  const clientEffectsByTrait = [...response.clientEffectsByTrait ?? []];
  const ownContinuations = [];
  const browserWrites = [];
  clientRoleLog.debug("fold:apply", {
    rows: Object.keys(response.entityByTrait ?? {}),
    serverClientEffects: clientEffects.length,
    emitted: response.emittedEvents.map((e) => e.event)
  });
  if (response.entityByTrait) fanOutEntityRows(store, opts.traitIndex, response.entityByTrait, writtenTraits);
  for (const frame of serverResponse.clientEffectsByTrait ?? []) {
    if (frame.callsitePayload !== void 0) store.callsitePayloads.set(frame.traitName, frame.callsitePayload);
  }
  const traitEntityIds = /* @__PURE__ */ new Map();
  if (response.entityByTrait) {
    for (const [traitName, row] of Object.entries(response.entityByTrait)) {
      const id = row["id"];
      if (typeof id === "string" && id !== "") traitEntityIds.set(traitName, id);
    }
  }
  for (const [traitName, stateName] of Object.entries(response.states)) {
    store.manager.seedState(traitName, stateName, traitEntityIds.get(traitName));
  }
  for (const emitted of response.emittedEvents) {
    const sourceTrait = emitted.source?.trait ?? "";
    if (alreadyDelivered.has(alreadyDeliveredKey(sourceTrait, emitted.event))) continue;
    const delivery = deliveryOf(emitted);
    for (const target of collectListenerTargets(opts.traitIndex, emitted.source, emitted.event, emitted.payload, void 0, { deferGuards: true })) {
      if (!heldHere.has(target.listenerTrait)) continue;
      store.mount.hold(target.listenerTrait, {
        trait: target.listenerTrait,
        from: findInitialState(target.entry.traitDef),
        fromAtDelivery: true,
        event: target.triggers,
        payload: target.payload,
        ...target.entityId !== void 0 ? { entityId: target.entityId } : {},
        onPage: true,
        delivery,
        ...target.listener.guard !== void 0 ? { listenGuard: target.listener.guard } : {}
      });
    }
    const hostRan = emitted.source?.dispatched === true;
    const targets = collectListenerTargets(opts.traitIndex, emitted.source, emitted.event, emitted.payload, void 0, {
      guardBindings: freshDeliveryGuardBindings(delivery, (listener) => {
        const entry = opts.traitIndex.byName.get(listener);
        return store.manager.getState(listener)?.currentState ?? (entry !== void 0 ? findInitialState(entry.traitDef) : "");
      })
    }).filter((target) => !heldHere.has(target.listenerTrait) && (!hostRan || clientOnly.has(target.listenerTrait)));
    for (const target of targets) {
      if (browserLegEvents(target.entry.traitDef, (t) => browserStored.has(t), target.entry.config).has(target.triggers)) {
        ownContinuations.push({
          event: target.triggers,
          ...target.payload !== void 0 ? { payload: target.payload } : {},
          ...target.entityId !== void 0 ? { entityId: target.entityId } : {},
          targetTrait: target.listenerTrait,
          delivery
        });
        continue;
      }
      const collector = new ServerLegCollector();
      const fannedFirings = [];
      const runEffects = createClientEffectRunner(
        { traitIndex: opts.traitIndex, ...opts.fullTraitIndex !== void 0 ? { fullTraitIndex: opts.fullTraitIndex } : {}, store, orbitalName: opts.orbitalName, ...i18nOpts(opts) },
        collector,
        (name, firing, step) => {
          if (firing === void 0) return;
          const row = store.frames.get(opts.traitIndex.byName.get(name)?.frameKey ?? name);
          fannedFirings.push({ trait: name, ...firing, ...step, ...row !== void 0 ? { row: { ...row } } : {} });
        }
      );
      const fanned = await evaluateOrbitalEvent(
        { ...baseEvaluateDeps(opts, runEffects), seedVisited: alreadyDelivered, seedDelivery: delivery },
        {
          event: target.triggers,
          payload: target.payload,
          entityId: target.entityId,
          targetTrait: target.listenerTrait
        }
      );
      if (fanned.clientEffects) clientEffects.push(...fanned.clientEffects);
      if (fanned.clientEffectsByTrait) clientEffectsByTrait.push(...fanned.clientEffectsByTrait);
      for (const f of fannedFirings) {
        const indexed = opts.traitIndex.byName.get(f.trait);
        if (indexed !== void 0 && browserLegEvents(indexed.traitDef, (t) => browserStored.has(t), indexed.config).has(f.event)) {
          browserWrites.push(browserWriteContinuation(f, indexed.orbitalName, { event: f.event, ...opts.locale !== void 0 ? { locale: opts.locale } : {} }));
        }
      }
      if (fanned.entityByTrait) {
        fanOutEntityRows(store, opts.traitIndex, fanned.entityByTrait, /* @__PURE__ */ new Set([target.listenerTrait]));
      }
    }
    const own = emitted.source?.trait;
    const ownEntry = own !== void 0 ? opts.traitIndex.byName.get(own) : void 0;
    if (own !== void 0 && ownEntry !== void 0 && !hostRan && clientOnly.has(own) && !heldHere.has(own)) {
      const from = store.manager.getState(own)?.currentState ?? findInitialState(ownEntry.traitDef);
      if (findMatchingTransitions(ownEntry.traitDef, from, emitted.event).length > 0) {
        ownContinuations.push({ event: emitted.event, ...emitted.payload !== void 0 ? { payload: emitted.payload } : {}, targetTrait: own });
      }
    }
  }
  store.notify();
  return { clientEffects, clientEffectsByTrait, ownContinuations, browserWrites };
}
async function dispatchOwnContinuations(transport, orbitalName, folded, store, opts) {
  const out = { clientEffects: [], clientEffectsByTrait: [] };
  for (const write of folded.browserWrites) {
    const posted = await transport.send(write.orbital, write.request);
    const written = await applyOrbitalEventResponse(store, posted, /* @__PURE__ */ new Set(), opts, /* @__PURE__ */ new Set([write.request.targetTrait ?? ""]));
    const continued = await dispatchOwnContinuations(transport, write.orbital, written, store, opts);
    out.clientEffects.push(...written.clientEffects, ...continued.clientEffects);
    out.clientEffectsByTrait.push(...written.clientEffectsByTrait, ...continued.clientEffectsByTrait);
  }
  for (const request of folded.ownContinuations) {
    const continued = await dispatchWithServerLeg(opts, request);
    const presented = await postServerLeg(transport, orbitalName, continued, store, opts);
    out.clientEffects.push(...presented.clientEffects ?? []);
    out.clientEffectsByTrait.push(...presented.clientEffectsByTrait ?? []);
  }
  return out;
}
function awaitingEntries(dispatch, opts) {
  const out = [];
  for (const [trait, firing] of dispatch.firings ?? []) {
    if (opts.traitIndex.byName.get(trait)?.dispatchMode === "hybridClientOnly") continue;
    out.push({ trait, event: firing.event, from: firing.fromState });
  }
  return out;
}
function serverEffectResults(local, posted) {
  const merged = [...local.effectResults ?? [], ...posted.effectResults ?? []];
  return merged.length > 0 ? { effectResults: merged } : {};
}
async function postServerLeg(transport, orbitalName, dispatch, store, opts, request, onBeforeSend, progress = false) {
  if (dispatch.continuations !== void 0 && dispatch.serverLeg === void 0) {
    return postContinuations(transport, dispatch, store, opts);
  }
  if (dispatch.mode === "hybridClientOnly" && dispatch.serverLeg === void 0) {
    return dispatch.response;
  }
  let legToSend;
  if (dispatch.serverLeg !== void 0) {
    legToSend = opts.carriesCircuitState || dispatch.mode === "hybridClientOnly" ? dispatch.serverLeg : stripCircuitState(dispatch.serverLeg);
  } else if (!opts.carriesCircuitState && request !== void 0) {
    legToSend = {
      event: request.event,
      ...request.payload !== void 0 ? { payload: request.payload } : {},
      ...dispatch.entityId !== void 0 ? { entityId: dispatch.entityId } : {},
      targetTrait: dispatch.trait,
      sourceTrait: dispatch.trait,
      ...request.clientId !== void 0 ? { clientId: request.clientId } : {},
      ...request.locale !== void 0 ? { locale: request.locale } : {}
    };
  } else {
    return dispatch.response;
  }
  legToSend = withMountedSet(legToSend, opts);
  const awaiting = request?.tick === void 0 && !progress ? awaitingEntries(dispatch, opts) : [];
  store.awaiting.begin(awaiting);
  try {
    onBeforeSend?.();
    const shownAtSend = foldBase(store, legToSend.entityByTrait, dispatch.mode === "hybridClientOnly" ? [dispatch.trait] : [], opts.traitIndex);
    let posted;
    try {
      posted = await transport.send(orbitalName, legToSend);
    } catch (err) {
      restoreDispatch(store, dispatch, "transport-error");
      throw err;
    }
    if (dispatch.mode === "runtimeOptimistic" && !posted.success) {
      restoreDispatch(store, dispatch, "server-rejected");
      return posted;
    }
    const delivered = alreadyDeliveredFrom(dispatch);
    const folded = await applyOrbitalEventResponse(
      store,
      changedSinceCarried(posted, shownAtSend, opts.traitIndex),
      delivered,
      opts,
      dispatch.writtenTraits
    );
    const local = dispatch.response;
    const continued = await dispatchOwnContinuations(transport, orbitalName, folded, store, opts);
    const answer = await answerContinuations(transport, dispatch, store, opts);
    return {
      ...local,
      emittedEvents: [
        ...local.emittedEvents,
        ...posted.emittedEvents.filter((e) => !delivered.has(alreadyDeliveredKey(e.source?.trait ?? "", e.event))),
        ...answer.emittedEvents
      ],
      clientEffects: [...local.clientEffects ?? [], ...folded.clientEffects, ...continued.clientEffects, ...answer.clientEffects],
      clientEffectsByTrait: [...local.clientEffectsByTrait ?? [], ...folded.clientEffectsByTrait, ...continued.clientEffectsByTrait, ...answer.clientEffectsByTrait],
      ...serverEffectResults(local, posted)
    };
  } finally {
    store.awaiting.end(awaiting.map((a) => a.trait));
  }
}
async function answerContinuations(transport, dispatch, store, opts) {
  const delivered = alreadyDeliveredFrom(dispatch);
  const answer = { emittedEvents: [], clientEffects: [], clientEffectsByTrait: [] };
  for (const continuation of dispatch.continuations ?? []) {
    const posted = await transport.send(continuation.orbital, continuation.request);
    const folded = await applyOrbitalEventResponse(store, posted, delivered, opts, dispatch.writtenTraits);
    const continued = await dispatchOwnContinuations(transport, continuation.orbital, folded, store, opts);
    answer.emittedEvents.push(...posted.emittedEvents.filter((e) => !delivered.has(alreadyDeliveredKey(e.source?.trait ?? "", e.event))));
    answer.clientEffects.push(...folded.clientEffects, ...continued.clientEffects);
    answer.clientEffectsByTrait.push(...folded.clientEffectsByTrait, ...continued.clientEffectsByTrait);
  }
  return answer;
}
async function postContinuations(transport, dispatch, store, opts) {
  const local = dispatch.response;
  const answer = await answerContinuations(transport, dispatch, store, opts);
  return {
    ...local,
    emittedEvents: [...local.emittedEvents, ...answer.emittedEvents],
    clientEffects: [...local.clientEffects ?? [], ...answer.clientEffects],
    clientEffectsByTrait: [...local.clientEffectsByTrait ?? [], ...answer.clientEffectsByTrait]
  };
}
async function postMountLeg(transport, orbitalName, mounted, store, opts, base = {}) {
  const traits = [];
  const entityByTrait = {};
  const carried = mounted.filter(({ dispatch }) => opts.carriesCircuitState || dispatch.mode === "hybridClientOnly");
  {
    const seen = /* @__PURE__ */ new Set();
    for (const { dispatch } of carried) {
      for (const t of dispatch.serverLeg?.traits ?? []) {
        if (seen.has(t.trait)) continue;
        seen.add(t.trait);
        traits.push(t);
      }
      for (const [trait, row] of Object.entries(dispatch.serverLeg?.entityByTrait ?? {})) {
        if (!(trait in entityByTrait)) entityByTrait[trait] = row;
      }
    }
  }
  const mount = mounted.map(({ seed }) => seed);
  const request = withMountedSet({
    event: mount[0]?.event ?? "INIT",
    ...base.payload !== void 0 ? { payload: base.payload } : {},
    ...base.entityId !== void 0 ? { entityId: base.entityId } : {},
    ...base.clientId !== void 0 ? { clientId: base.clientId } : {},
    ...base.locale !== void 0 ? { locale: base.locale } : {},
    mount,
    ...traits.length > 0 ? { traits } : {},
    ...Object.keys(entityByTrait).length > 0 ? { entityByTrait } : {}
  }, opts);
  const awaiting = mounted.flatMap(({ dispatch }) => awaitingEntries(dispatch, opts));
  store.awaiting.begin(awaiting);
  try {
    const shownAtMountSend = foldBase(store, request.entityByTrait, mounted.filter((m) => m.dispatch.mode === "hybridClientOnly").map((m) => m.dispatch.trait), opts.traitIndex);
    let posted;
    try {
      posted = await transport.send(orbitalName, request);
    } catch (err) {
      for (const { dispatch } of mounted) restoreDispatch(store, dispatch, "transport-error");
      throw err;
    }
    if (!posted.success) {
      for (const { dispatch } of mounted) {
        if (dispatch.mode === "runtimeOptimistic") restoreDispatch(store, dispatch, "server-rejected");
      }
    }
    const delivered = /* @__PURE__ */ new Set();
    const written = /* @__PURE__ */ new Set();
    for (const { dispatch } of mounted) {
      for (const key of alreadyDeliveredFrom(dispatch)) delivered.add(key);
      for (const trait of dispatch.writtenTraits) written.add(trait);
    }
    const folded = await applyOrbitalEventResponse(
      store,
      changedSinceCarried(posted, shownAtMountSend, opts.traitIndex),
      delivered,
      opts,
      written
    );
    const continued = await dispatchOwnContinuations(transport, orbitalName, folded, store, opts);
    return {
      success: posted.success,
      states: posted.states,
      rejections: (posted.rejections ?? []).filter((r) => r.code === "effect-failed"),
      emittedEvents: posted.emittedEvents.filter((e) => !delivered.has(alreadyDeliveredKey(e.source?.trait ?? "", e.event))),
      clientEffects: [...folded.clientEffects, ...continued.clientEffects],
      clientEffectsByTrait: [...folded.clientEffectsByTrait, ...continued.clientEffectsByTrait],
      ...posted.error !== void 0 ? { error: posted.error } : {}
    };
  } finally {
    store.awaiting.end(awaiting.map((a) => a.trait));
  }
}
function serverOnlyEffects(local, response) {
  if (!response.success) return { clientEffects: response.clientEffects, clientEffectsByTrait: response.clientEffectsByTrait };
  return {
    clientEffects: (response.clientEffects ?? []).slice((local.clientEffects ?? []).length),
    clientEffectsByTrait: response.clientEffectsByTrait?.slice((local.clientEffectsByTrait ?? []).length)
  };
}
function createClientKernel(opts) {
  const { transport, topology, ...roleOpts } = opts;
  const queue = [];
  let pumping = false;
  let postRoleOpts = topology === void 0 ? roleOpts : void 0;
  const postRole = async () => {
    if (postRoleOpts !== void 0) return postRoleOpts;
    const confirmed = await (topology ?? Promise.resolve({ carriesCircuitState: roleOpts.carriesCircuitState }));
    postRoleOpts = { ...roleOpts, carriesCircuitState: confirmed.carriesCircuitState };
    return postRoleOpts;
  };
  const orbitalFor = (request) => (request.targetTrait !== void 0 ? roleOpts.traitIndex.byName.get(request.targetTrait)?.orbitalName : void 0) ?? roleOpts.orbitalName;
  const tickLanes = /* @__PURE__ */ new Map();
  const flushTickLane = (key) => {
    const lane = tickLanes.get(key);
    if (lane === void 0 || transport === void 0) return;
    const next = lane.pending;
    lane.pending = void 0;
    if (next === void 0) {
      lane.inFlight = false;
      return;
    }
    lane.inFlight = true;
    void postRole().then((role) => postServerLeg(transport, orbitalFor(next.request), next.dispatch, roleOpts.store, role, next.request)).then((response) => next.settle(response)).catch((err) => {
      clientRoleLog.warn("tick-post-failed", { event: next.request.event, trait: next.request.targetTrait, error: String(err) });
      next.settle(void 0);
    }).then(() => flushTickLane(key));
  };
  const postTick = (dispatch, request) => {
    const key = `${request.targetTrait ?? ""}\0${request.event}`;
    const lane = tickLanes.get(key) ?? { inFlight: false };
    tickLanes.set(key, lane);
    const { snapshot: _snapshot, siblingSnapshots: _siblings, ...unrollable } = dispatch;
    return new Promise((settle) => {
      lane.pending?.settle(void 0);
      lane.pending = { dispatch: unrollable, request, settle };
      if (!lane.inFlight) flushTickLane(key);
    });
  };
  let progressChain = Promise.resolve();
  const runHostDispatch = async (request, response) => {
    const mode = (request.targetTrait !== void 0 ? roleOpts.traitIndex.byName.get(request.targetTrait)?.dispatchMode : void 0) ?? "persistedAwaited";
    if (!response.success) return { response: { ...response, clientEffects: [], clientEffectsByTrait: [] }, mode };
    const folded = await applyOrbitalEventResponse(roleOpts.store, response, /* @__PURE__ */ new Set(), roleOpts);
    roleOpts.store.notify();
    return { response: { ...response, clientEffects: folded.clientEffects, clientEffectsByTrait: folded.clientEffectsByTrait }, mode };
  };
  const runProgress = async (request) => {
    const dispatch = await dispatchWithServerLeg(roleOpts, request);
    const hasServerWork = dispatch.serverLeg !== void 0 || dispatch.continuations !== void 0;
    const response = transport !== void 0 && hasServerWork ? await postServerLeg(transport, orbitalFor(request), dispatch, roleOpts.store, await postRole(), request, void 0, true) : dispatch.response;
    return { response, mode: dispatch.mode };
  };
  async function runMount(entry) {
    try {
      const mounted = [];
      for (const seed of entry.seeds) {
        const dispatch = await dispatchWithServerLeg(roleOpts, { ...entry.base, event: seed.event, targetTrait: seed.trait }).catch((err) => {
          throw new TraitMountError(seed.trait, seed.event, err instanceof Error ? err : new Error(String(err)));
        });
        mounted.push({ seed, dispatch });
      }
      for (const { seed, dispatch } of mounted) entry.hooks?.onLocal?.(seed.trait, dispatch.response);
      const seeds = mounted.map(({ seed, dispatch }) => ({ trait: seed.trait, event: seed.event, mode: dispatch.mode, local: dispatch.response }));
      const outcome = { seeds, posted: false, success: true, rejections: [], serverEffects: { clientEffects: [], clientEffectsByTrait: [] }, emittedEvents: [] };
      if (transport !== void 0) {
        const role = await postRole();
        const byOrbital = /* @__PURE__ */ new Map();
        for (const m of mounted) {
          const posts = m.dispatch.serverLeg !== void 0 || !role.carriesCircuitState && m.dispatch.mode !== "hybridClientOnly";
          if (!posts) continue;
          const orbital = orbitalFor({ event: m.seed.event, targetTrait: m.seed.trait });
          byOrbital.set(orbital, [...byOrbital.get(orbital) ?? [], m]);
        }
        for (const [orbital, group] of byOrbital) {
          const leg = await postMountLeg(transport, orbital, group, roleOpts.store, role, entry.base);
          outcome.posted = true;
          outcome.success = outcome.success && leg.success;
          if (leg.error !== void 0) outcome.error = leg.error;
          outcome.rejections.push(...leg.rejections);
          outcome.serverEffects.clientEffects?.push(...leg.clientEffects);
          outcome.serverEffects.clientEffectsByTrait?.push(...leg.clientEffectsByTrait);
          outcome.emittedEvents.push(...leg.emittedEvents);
        }
        for (const m of mounted) {
          if (m.dispatch.continuations === void 0) continue;
          const answer = await answerContinuations(transport, m.dispatch, roleOpts.store, role);
          outcome.posted = true;
          outcome.serverEffects.clientEffects?.push(...answer.clientEffects);
          outcome.serverEffects.clientEffectsByTrait?.push(...answer.clientEffectsByTrait);
          outcome.emittedEvents.push(...answer.emittedEvents);
        }
      }
      entry.resolve(outcome);
    } catch (err) {
      entry.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }
  function pump() {
    if (pumping) return;
    pumping = true;
    void (async () => {
      try {
        while (queue.length > 0) {
          const entry = queue.shift();
          if (entry.kind === "mount") {
            await runMount(entry);
            continue;
          }
          if (entry.kind === "host") {
            await runHostDispatch(entry.request, entry.response).then(entry.resolve, entry.reject);
            continue;
          }
          try {
            const dispatch = await dispatchWithServerLeg(roleOpts, entry.request);
            let response = dispatch.response;
            let localPainted = false;
            let tickSettled;
            if (transport !== void 0) {
              if (entry.request.tick !== void 0) {
                const local = dispatch.response;
                tickSettled = postTick(dispatch, entry.request).then((posted) => posted === void 0 ? void 0 : serverOnlyEffects(local, posted));
              } else {
                const painters = entry.hooks.filter((h) => h.onLocal !== void 0);
                if (painters.length > 0) {
                  localPainted = true;
                  for (const h of painters) h.onLocal?.(dispatch.response);
                }
                response = await postServerLeg(transport, orbitalFor(entry.request), dispatch, roleOpts.store, await postRole(), entry.request);
              }
            }
            const outcome = localPainted ? { response, mode: dispatch.mode, localPainted, serverEffects: serverOnlyEffects(dispatch.response, response) } : { response, mode: dispatch.mode, ...tickSettled !== void 0 ? { tickSettled } : {} };
            for (const resolve of entry.resolvers) resolve(outcome);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            for (const reject of entry.rejecters) reject(error);
          }
        }
      } finally {
        pumping = false;
      }
    })();
  }
  return {
    store: roleOpts.store,
    dispatchProgress(request) {
      const outcome = progressChain.then(() => runProgress(request));
      progressChain = outcome.then(() => void 0, () => void 0);
      return outcome;
    },
    foldHostDispatch(request, response) {
      return new Promise((resolve, reject) => {
        queue.push({ kind: "host", request, response, resolve, reject });
        pump();
      });
    },
    dispatchMount(seeds, hooks, base = {}) {
      return new Promise((resolve, reject) => {
        queue.push({ kind: "mount", seeds, ...hooks !== void 0 ? { hooks } : {}, base, resolve, reject });
        pump();
      });
    },
    dispatch(request, hooks) {
      return new Promise((resolve, reject) => {
        if (request.tick !== void 0) {
          const pending = queue.find(
            (e) => e.kind === void 0 && e.request.tick !== void 0 && e.request.event === request.event && e.request.targetTrait === request.targetTrait
          );
          if (pending !== void 0) {
            pending.request = { ...pending.request, payload: request.payload };
            pending.resolvers.push(resolve);
            pending.rejecters.push(reject);
            if (hooks !== void 0) pending.hooks.push(hooks);
            return;
          }
        }
        queue.push({ request, hooks: hooks !== void 0 ? [hooks] : [], resolvers: [resolve], rejecters: [reject] });
        pump();
      });
    }
  };
}
function seedRows(entity, locale) {
  const written = instancesForLocale(entity, locale);
  if (written.length > 0) return written;
  if (!entity.seedMock) return [];
  const mock = new MockPersistenceAdapter();
  mock.registerEntity(entitySchemaOf(entity));
  return mock.rowsOf(entity.name);
}
async function isEmpty(store, entityType) {
  return store.countRows ? await store.countRows(entityType) === 0 : (await store.list(entityType)).length === 0;
}
async function seedBrowserStore(store, entities, locale) {
  for (const entity of entities) {
    if (entity.local !== true || !await isEmpty(store, entity.name)) continue;
    for (const row of seedRows(entity, locale)) {
      await store.create(entity.name, row);
    }
  }
}

// src/evaluation/local-store-transport.ts
function createLocalStoreTransport(options) {
  const { traitIndex, persistence, store } = options;
  const inFlightCalls = new InFlightCalls();
  return createInProcessTransport(
    async (_orbitalName, request) => {
      const runEffects = createIndexStageRunner({
        traitIndex,
        persistence,
        frames: store.frames,
        manager: store.manager,
        ...options.schema !== void 0 ? { schema: options.schema } : {},
        ...options.callService !== void 0 ? { extraEffectHandlers: { callService: options.callService } } : {},
        ...options.servicePorts !== void 0 ? { servicePorts: options.servicePorts } : {},
        ...options.outsideEventQueue !== void 0 ? { outsideEventQueue: options.outsideEventQueue } : {},
        inFlightCalls,
        ...options.debug !== void 0 ? { debug: options.debug } : {},
        ...options.messages !== void 0 ? { messages: options.messages } : {}
      });
      return evaluateOrbitalEvent(
        {
          traitIndex,
          manager: store.manager,
          persistence,
          frames: store.frames,
          runEffects,
          ...options.user !== void 0 ? { user: options.user } : {},
          ...options.guardMode !== void 0 ? { guardMode: options.guardMode } : {},
          ...options.strictBindings !== void 0 ? { strictBindings: options.strictBindings } : {},
          ...options.contextExtensions !== void 0 ? { contextExtensions: options.contextExtensions } : {},
          ...options.debug !== void 0 ? { debug: options.debug } : {},
          ...options.logContext !== void 0 ? { logContext: options.logContext } : {},
          ...options.clientRelays !== void 0 ? { clientRelays: options.clientRelays } : {},
          ...request.locale !== void 0 ? { locale: request.locale } : {}
        },
        request
      );
    },
    { carriesCircuitState: false }
  );
}
async function dispatchDeclaredInput(schema, orbitalName, request, send) {
  const declared = schema ? findExternalInput(schema, orbitalName, request.targetTrait, request.event) : void 0;
  if (!declared) {
    return {
      success: false,
      transitioned: false,
      states: {},
      emittedEvents: [],
      error: `'${request.event}' is not an external input of ${orbitalName}.${request.targetTrait}`,
      rejections: [{ code: "not-an-external-input", trait: request.targetTrait, event: request.event }]
    };
  }
  return send(orbitalName, {
    event: request.event,
    payload: request.payload ?? {},
    targetTrait: request.targetTrait,
    ...request.user !== void 0 ? { user: request.user } : {},
    ...request.entityId !== void 0 ? { entityId: request.entityId } : {}
  });
}

// src/server/channel-transport.ts
var CLOSED = "The host closed the channel before answering";
function createChannelTransport(channel, options = {}) {
  let next = 1;
  const pending = /* @__PURE__ */ new Map();
  channel.onMessage((message) => {
    if (message.almadarChannel !== "registered" && message.almadarChannel !== "reply") return;
    const settle = pending.get(message.id);
    if (!settle) return;
    pending.delete(message.id);
    settle(message);
  });
  let closed = false;
  const closedReply = (id) => ({
    almadarChannel: "reply",
    id,
    response: { success: false, transitioned: false, states: {}, emittedEvents: [], error: CLOSED }
  });
  channel.onClose?.(() => {
    closed = true;
    for (const [id, settle] of pending) settle(closedReply(id));
    pending.clear();
  });
  const request = (build) => new Promise((resolve) => {
    const id = next++;
    if (closed) {
      resolve(closedReply(id));
      return;
    }
    pending.set(id, resolve);
    channel.post(build(id));
  });
  return {
    hostsBrowserStore: options.hostsBrowserStore === true,
    async register(schema) {
      const reply = await request((id) => ({ almadarChannel: "register", id, schemaName: schema.name }));
      const held = reply.almadarChannel === "registered" && reply.held;
      return { success: held, carriesCircuitState: false };
    },
    async unregister() {
      pending.clear();
    },
    async send(orbitalName, req) {
      const reply = await request((id) => ({ almadarChannel: "send", id, orbitalName, request: req }));
      if (reply.almadarChannel !== "reply") {
        return { success: false, transitioned: false, states: {}, emittedEvents: [], error: "The host answered out of protocol" };
      }
      return reply.response;
    },
    subscribe(onPush) {
      return channel.onMessage((message) => {
        if (message.almadarChannel === "push") onPush(message.emitted, message.target);
      });
    },
    subscribeHostDispatches(onDispatch) {
      return channel.onMessage((message) => {
        if (message.almadarChannel === "dispatched") onDispatch(message.orbitalName, message.request, message.response);
      });
    }
  };
}
function serveChannel(channel, transport, options) {
  const stop = channel.onMessage((message) => {
    if (message.almadarChannel === "register") {
      channel.post({ almadarChannel: "registered", id: message.id, held: message.schemaName === options.schemaName });
      return;
    }
    if (message.almadarChannel !== "send") return;
    transport.send(message.orbitalName, message.request).then(
      (response) => channel.post({ almadarChannel: "reply", id: message.id, response }),
      (err) => channel.post({
        almadarChannel: "reply",
        id: message.id,
        response: { success: false, transitioned: false, states: {}, emittedEvents: [], error: err.message }
      })
    );
  });
  return {
    push: (emitted, target) => channel.post({ almadarChannel: "push", emitted, target }),
    pushDispatch: (orbitalName, request, response) => channel.post({ almadarChannel: "dispatched", orbitalName, request, response }),
    stop
  };
}
function holdUntilServed(channel) {
  let held = [];
  const listeners = [];
  channel.onMessage((message) => {
    if (held !== void 0) held.push(message);
    else listeners.forEach((l) => l(message));
  });
  return {
    post: (message) => channel.post(message),
    onMessage(listener) {
      listeners.push(listener);
      if (held !== void 0) {
        const early = held;
        held = void 0;
        early.forEach((m) => listeners.forEach((l) => l(m)));
      }
      return () => {
        const i = listeners.indexOf(listener);
        if (i >= 0) listeners.splice(i, 1);
      };
    },
    ...channel.onClose !== void 0 ? { onClose: channel.onClose.bind(channel) } : {}
  };
}
function legTraits(request, orbitalName, index) {
  const events = /* @__PURE__ */ new Map();
  for (const t of request.traits ?? []) events.set(t.trait, t.event ?? request.event);
  const named = [
    request.targetTrait,
    request.sourceTrait,
    ...(request.traits ?? []).map((t) => t.trait),
    ...(request.mount ?? []).map((m) => m.trait)
  ].filter((t) => typeof t === "string");
  const names = named.length > 0 ? new Set(named) : null;
  return [...index.byName.values()].filter((entry) => names ? names.has(entry.traitDef.name) : entry.orbitalName === orbitalName).map((entry) => ({ entry, event: events.get(entry.traitDef.name) ?? request.event }));
}
function createResidenceTransport(options) {
  const { local, remote, traitIndex } = options;
  const browserStored = new Set(traitIndex.allEntities.filter(storesRowsInBrowser).map((e) => e.name));
  const stores = (entityType) => browserStored.has(entityType);
  const route = (orbitalName, request) => {
    const traits = legTraits(request, orbitalName, traitIndex);
    const server = traits.filter(({ entry: e, event }) => touchesServer(e.traitDef, e.irTrait, e.config, stores, event)).map(({ entry }) => entry.traitDef.name);
    const browser = traits.filter(({ entry: e, event }) => browserLegEvents(e.traitDef, stores, e.config).has(event)).map(({ entry }) => entry.traitDef.name);
    if (server.length > 0 && browser.length > 0) {
      throw new Error(`residence: one dispatch reaches browser-stored data (${browser.join(", ")}) and server data (${server.join(", ")}); split it with an emit and a listen`);
    }
    if (browser.length > 0) return local;
    if (remote) return remote;
    if (server.length > 0) {
      throw new Error(`residence: ${server.join(", ")} needs server data, and this host has no server`);
    }
    return local;
  };
  const mountLegs = (orbitalName, request) => {
    const seeds = request.mount ?? [];
    const groups = /* @__PURE__ */ new Map();
    for (const seed of seeds) {
      const transport = route(orbitalName, { ...request, mount: [seed], traits: void 0, targetTrait: void 0, sourceTrait: void 0 });
      groups.set(transport, [...groups.get(transport) ?? [], seed.trait]);
    }
    return [...groups].map(([transport, traits]) => {
      const keep = new Set(traits);
      const entityByTrait = request.entityByTrait === void 0 ? void 0 : Object.fromEntries(Object.entries(request.entityByTrait).filter(([trait]) => keep.has(trait)));
      return {
        transport,
        request: {
          ...request,
          mount: seeds.filter((seed) => keep.has(seed.trait)),
          ...request.traits !== void 0 ? { traits: request.traits.filter((t) => keep.has(t.trait)) } : {},
          ...entityByTrait !== void 0 ? { entityByTrait } : {}
        }
      };
    });
  };
  return {
    async register(schema) {
      const result = await local.register(schema);
      return remote ? remote.register(schema) : result;
    },
    async unregister() {
      await local.unregister();
      await remote?.unregister();
    },
    async send(orbitalName, request) {
      if ((request.mount?.length ?? 0) > 1) {
        const legs = mountLegs(orbitalName, request);
        if (legs.length > 1) {
          const responses = [];
          for (const leg of legs) responses.push(await leg.transport.send(orbitalName, leg.request));
          return mergeMountResponses(responses);
        }
      }
      return route(orbitalName, request).send(orbitalName, request);
    },
    ...remote?.subscribe ? { subscribe: remote.subscribe.bind(remote) } : {}
  };
}
function mergeMountResponses(responses) {
  const concat = (pick) => {
    const all = responses.flatMap((r) => pick(r) ?? []);
    return responses.some((r) => pick(r) !== void 0) ? all : void 0;
  };
  const data = {};
  for (const r of responses) for (const [entity, rows] of Object.entries(r.data ?? {})) data[entity] = [...data[entity] ?? [], ...rows];
  const entityByTrait = Object.assign({}, ...responses.map((r) => r.entityByTrait ?? {}));
  const errors = responses.flatMap((r) => r.error !== void 0 ? [r.error] : []);
  const merged = {
    success: responses.every((r) => r.success),
    transitioned: responses.some((r) => r.transitioned),
    states: Object.assign({}, ...responses.map((r) => r.states)),
    emittedEvents: responses.flatMap((r) => r.emittedEvents)
  };
  if (responses.some((r) => r.data !== void 0)) merged.data = data;
  if (responses.some((r) => r.entityByTrait !== void 0)) merged.entityByTrait = entityByTrait;
  const clientEffects = concat((r) => r.clientEffects);
  if (clientEffects !== void 0) merged.clientEffects = clientEffects;
  const clientEffectsByTrait = concat((r) => r.clientEffectsByTrait);
  if (clientEffectsByTrait !== void 0) merged.clientEffectsByTrait = clientEffectsByTrait;
  const effectResults = concat((r) => r.effectResults);
  if (effectResults !== void 0) merged.effectResults = effectResults;
  const rejections = concat((r) => r.rejections);
  if (rejections !== void 0) merged.rejections = rejections;
  const cascadeTruncated = concat((r) => r.cascadeTruncated);
  if (cascadeTruncated !== void 0) merged.cascadeTruncated = cascadeTruncated;
  const guardFailed = responses.find((r) => r.guardFailed !== void 0)?.guardFailed;
  if (guardFailed !== void 0) merged.guardFailed = guardFailed;
  if (errors.length > 0) merged.error = errors.join("; ");
  return merged;
}
function browserStoreName(appName, locale) {
  return locale !== void 0 ? `almadar:${appName}:${locale}` : `almadar:${appName}`;
}
async function openBrowserStore(databaseName, orbitals, factory, locale) {
  const entities = orbitals.flatMap(orbitalInlineEntities).filter(storesRowsInBrowser);
  if (entities.length === 0) return null;
  const store = await IndexedDbPersistence.open({
    databaseName,
    entityTypes: entities.map((e) => e.name),
    ...factory !== void 0 ? { factory } : {}
  });
  await seedBrowserStore(store, entities, locale);
  return store;
}
async function openBrowserStoreTransport(options) {
  const stored = await openBrowserStore(options.databaseName, options.schema.orbitals, options.factory, options.locale);
  if (stored === null && options.callService === void 0) {
    throw new Error(`openBrowserStoreTransport: schema "${options.schema.name}" declares no browser-stored entity`);
  }
  const persistence = stored ?? new InMemoryPersistence();
  const traitIndex = buildTraitIndex(options.schema.orbitals);
  const store = createMemoryCircuitStore(Array.from(traitIndex.byName.values(), (e) => e.traitDef));
  return createLocalStoreTransport({
    traitIndex,
    persistence,
    store,
    schema: options.schema,
    ...options.clientRelays !== void 0 ? { clientRelays: options.clientRelays } : {},
    ...options.callService !== void 0 ? { callService: options.callService } : {},
    ...options.messages !== void 0 ? { messages: options.messages } : {}
  });
}
function openBundledBrowserStore(bundled, locale, callService, messages) {
  const schema = parseOrbitalSchema(bundled);
  return openBrowserStoreTransport({
    databaseName: browserStoreName(schema.name, locale),
    schema,
    clientRelays: true,
    ...locale !== void 0 ? { locale } : {},
    ...callService !== void 0 ? { callService } : {},
    ...messages !== void 0 ? { messages } : {}
  });
}
var tickLog = createLogger("almadar:runtime:browser-host:ticks");
var queueLog = createLogger("almadar:runtime:browser-host:queue");
function isPayloadObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date) && isEventPayloadValue(value);
}
function refuseUnhostedTicks(traitIndex) {
  for (const entry of traitIndex.byName.values()) {
    for (const tick of entry.irTrait.ticks ?? []) {
      for (const effect of tick.effects) {
        const op = Array.isArray(effect) ? effect[0] : void 0;
        if (op !== "emit") {
          throw new Error(`openBrowserHost runs ticks that emit events; ${entry.orbitalName}.${entry.traitDef.name} tick '${tick.name}' has a '${String(op)}' effect`);
        }
      }
      if (tick.interval === "frame") {
        throw new Error(`openBrowserHost has no frame clock; ${entry.orbitalName}.${entry.traitDef.name} tick '${tick.name}' runs every frame`);
      }
    }
  }
}
async function openBrowserHost(options) {
  const persistence = await openBrowserStore(options.databaseName, options.schema.orbitals, options.factory, options.locale) ?? new InMemoryPersistence();
  const traitIndex = buildTraitIndex(options.schema.orbitals);
  refuseUnhostedTicks(traitIndex);
  const store = createMemoryCircuitStore(Array.from(traitIndex.byName.values(), (e) => e.traitDef));
  const { schema } = options;
  let transport;
  const listeners = /* @__PURE__ */ new Set();
  let turns = 0;
  const foreground = [];
  const background = [];
  let pumping = false;
  let running;
  const pump = async () => {
    if (pumping) return;
    pumping = true;
    for (let grant = foreground.shift() ?? background.shift(); grant !== void 0; grant = foreground.shift() ?? background.shift()) {
      await new Promise(grant);
    }
    pumping = false;
  };
  const take = (turn) => new Promise((taken) => {
    queueLog.debug("queue:wait", { turn: turn.id, lane: turn.lane === foreground ? "foreground" : "background", running: running?.id });
    turn.lane.push((free) => {
      turn.held = true;
      running = turn;
      queueLog.debug("queue:take", { turn: turn.id });
      turn.release = () => {
        queueLog.debug("queue:release", { turn: turn.id });
        turn.held = false;
        if (running === turn) running = void 0;
        free();
      };
      taken();
    });
    void pump();
  });
  const enqueue = async (lane, work) => {
    const turn = { id: ++turns, lane, held: false, done: false, release: () => void 0 };
    await take(turn);
    try {
      return await work();
    } finally {
      turn.done = true;
      if (turn.held) turn.release();
    }
  };
  const serially = (work) => enqueue(foreground, work);
  const inBackground = (work) => enqueue(background, work);
  const outsideEventQueue = async (work) => {
    const turn = running;
    queueLog.debug("queue:outside", { turn: turn?.id, held: turn?.held });
    if (turn === void 0 || !turn.held) return work();
    turn.release();
    try {
      return await work();
    } finally {
      await take(turn);
    }
  };
  const servicePorts = (caller, user, message) => {
    const lentBy = running;
    return {
      caller,
      inputs: () => externalInputsOf(schema),
      readableEntities: () => readableEntitiesOf(schema),
      dispatchInput: (orbital, request) => {
        const host = transport;
        if (!host) return Promise.reject(new Error("openBrowserHost: the host is not open yet"));
        const send = (o, r) => lentBy !== void 0 && lentBy.held && !lentBy.done ? host.send(o, r) : inBackground(() => host.send(o, r));
        return dispatchDeclaredInput(schema, orbital, { ...request, ...user !== void 0 ? { user } : {} }, async (o, r) => {
          const response = await send(o, r);
          for (const listener of listeners) listener(o, r, response);
          return response;
        });
      },
      read: async (entity) => applyRowAccess(await persistence.list(entity), entityAccessPolicies(schema, entity)?.read, void 0, { user }),
      message
    };
  };
  const local = createLocalStoreTransport({ traitIndex, persistence, store, schema, callService: options.callService, servicePorts, outsideEventQueue });
  transport = local;
  const scheduler = createTickScheduler();
  const tickHandles = [];
  for (const entry of traitIndex.byName.values()) {
    for (const tick of entry.irTrait.ticks ?? []) {
      let firing = false;
      const fire = () => {
        if (firing) return;
        firing = true;
        void inBackground(async () => {
          const state = store.manager.getState(entry.traitDef.name)?.currentState ?? "";
          const ctx = createContextFromBindings({ entity: store.frames.get(entry.frameKey) ?? {}, payload: {}, state, now: Date.now() });
          if (entry.config !== void 0) ctx.config = entry.config;
          if (tick.guard !== void 0 && !evaluateGuard(tick.guard, ctx)) return;
          const emitted = [];
          ctx.emit = (event, payload) => {
            if (payload === void 0 || payload === null) emitted.push({ event, payload: {} });
            else if (isPayloadObject(payload)) emitted.push({ event, payload });
            else throw new Error(`${entry.orbitalName}.${entry.traitDef.name} tick '${tick.name}' emitted ${event} with a payload that is not an object`);
          };
          executeEffects(tick.effects, ctx);
          const source = { orbital: entry.orbitalName, trait: entry.traitDef.name, tick: tick.name, ...entry.orbitalId !== void 0 ? { orbitalId: entry.orbitalId } : {}, ...entry.traitDef.id !== void 0 ? { traitId: entry.traitDef.id } : {} };
          for (const { event, payload } of emitted) {
            const deliveries = [];
            if (entry.traitDef.transitions.some((t) => t.event === event)) {
              deliveries.push({ orbital: entry.orbitalName, request: { event, payload, targetTrait: entry.traitDef.name } });
            }
            for (const target of collectListenerTargets(traitIndex, source, event, payload)) {
              deliveries.push({ orbital: target.entry.orbitalName, request: { event: target.triggers, payload: target.payload ?? {}, targetTrait: target.listenerTrait } });
            }
            for (const { orbital, request } of deliveries) {
              const response = await local.send(orbital, request);
              for (const listener of listeners) listener(orbital, request, response);
            }
          }
        }).catch((error) => {
          tickLog.error("tick:failed", { orbital: entry.orbitalName, trait: entry.traitDef.name, tick: tick.name, error: error.message });
        }).finally(() => {
          firing = false;
        });
      };
      tickHandles.push(
        typeof tick.interval === "string" && isValidCronExpression(tick.interval) ? scheduler.addCron(tick.interval, fire) : scheduler.add(typeof tick.interval === "number" ? tick.interval : parseDurationString(tick.interval), fire)
      );
    }
  }
  return {
    ...local,
    send: (orbitalName, request) => serially(() => local.send(orbitalName, request)),
    onInputDispatched(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close() {
      for (const handle of tickHandles) handle.stop();
    }
  };
}
var NO_ROW_KEY_IS_FATAL2 = true;
var effectLog3 = createLogger("almadar:runtime:effects");
function createServerEffectHandlers(opts) {
  const {
    persistence,
    eventBus,
    entityType,
    entityId,
    bindings,
    context,
    effectResults,
    fetchedData,
    emittedEvents,
    clientEffects,
    clientEffectsByTrait,
    source,
    callService: consumerCallService,
    entityAccess,
    debug
  } = opts;
  const pushClientEffect = (effect) => {
    clientEffects?.push(effect);
    clientEffectsByTrait?.push({ traitName: context?.traitName ?? "", effect });
  };
  const accessBindings = (forEntityType) => ({
    user: bindings?.user,
    payload: bindings?.payload,
    config: bindings?.config,
    onPredicateError: (err) => {
      effectLog3.error("access-predicate-eval-error", {
        entityType: forEntityType,
        error: err instanceof Error ? err : String(err)
      });
    }
  });
  const record = (entry) => {
    effectResults?.push(entry);
  };
  const handlers = {
    emit: (event, eventPayload, emitSource) => {
      if (debug) {
        effectLog3.debug("emit", {
          event,
          payloadKeys: eventPayload ? Object.keys(eventPayload).join(",") : ""
        });
      }
      const stamp = emitSource ?? source;
      eventBus.emit(event, eventPayload, stamp);
      emittedEvents?.push({ event, payload: eventPayload });
      effectLog3.debug("emit:push", {
        event,
        cumulativeEmittedCount: emittedEvents?.length,
        sourceTrait: stamp?.trait,
        sourceOrbital: stamp?.orbital,
        emittedEventsBound: emittedEvents !== void 0
      });
    },
    set: async (targetId, field, value) => {
      const id = targetId || entityId;
      if (!id) return;
      try {
        await persistence.update(entityType, id, { [field]: value });
        record({
          effect: "set",
          entityType,
          data: { id, field, value },
          success: true
        });
      } catch (err) {
        record({
          effect: "set",
          entityType,
          data: { id, field, value },
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    },
    persist: async (action, targetEntityType, data) => {
      if (action === "batch") {
        const operations = data?.operations;
        if (!Array.isArray(operations) || operations.length === 0) {
          record({
            effect: "persist",
            action: "batch",
            success: false,
            error: "Batch requires a non-empty operations array"
          });
          return;
        }
        const batchResults = [];
        const completed = [];
        let batchFailed = false;
        let batchError = "";
        const ops = operations;
        for (const op of ops) {
          if (!isPersistBatchOperation(op)) {
            batchFailed = true;
            batchError = `Invalid batch operation format: ${JSON.stringify(op)}`;
            break;
          }
          const opAction = op[0];
          const opEntityType = op[1];
          try {
            switch (op[0]) {
              case "create": {
                const createData = op[2] ?? {};
                const { id: newId } = await persistence.create(
                  opEntityType,
                  createData
                );
                batchResults.push({
                  ...createData,
                  action: "create",
                  entityType: opEntityType,
                  id: newId
                });
                completed.push({
                  action: "create",
                  entityType: opEntityType,
                  id: newId
                });
                break;
              }
              case "update": {
                const updateId = op[2];
                const updateData = op[3] ?? {};
                await persistence.update(opEntityType, updateId, updateData);
                const updated = await persistence.getById(opEntityType, updateId);
                batchResults.push({
                  ...updated || updateData,
                  action: "update",
                  entityType: opEntityType,
                  id: updateId
                });
                completed.push({
                  action: "update",
                  entityType: opEntityType,
                  id: updateId
                });
                break;
              }
              case "delete": {
                const deleteId = op[2];
                await persistence.delete(opEntityType, deleteId);
                batchResults.push({
                  action: "delete",
                  entityType: opEntityType,
                  id: deleteId,
                  deleted: true
                });
                completed.push({
                  action: "delete",
                  entityType: opEntityType,
                  id: deleteId
                });
                break;
              }
              default:
                batchFailed = true;
                batchError = `Unknown batch operation action: ${opAction}`;
                break;
            }
          } catch (err) {
            batchFailed = true;
            batchError = `Batch operation [${opAction}, ${opEntityType}] failed: ${err instanceof Error ? err.message : String(err)}`;
            break;
          }
          if (batchFailed) break;
        }
        const batchSummary = {
          operations: batchResults,
          completedCount: completed.length,
          totalCount: operations.length
        };
        record({
          effect: "persist",
          action: "batch",
          data: batchSummary,
          success: !batchFailed,
          ...batchFailed ? { error: batchError } : {}
        });
        return batchSummary;
      }
      const type = targetEntityType || entityType;
      let resultData;
      const sizeBefore = (await persistence.list(type)).length;
      let deniedReason;
      try {
        const mutationPolicy = entityAccess?.get(type)?.[action];
        switch (action) {
          case "create": {
            if (!checkMutationAccess(data ?? {}, mutationPolicy, accessBindings(type))) {
              deniedReason = "access-denied";
              throw new Error(accessDeniedMessage("create", type));
            }
            const { id } = await persistence.create(type, data ?? {});
            resultData = { ...data ?? {}, id };
            break;
          }
          case "update": {
            const row = data ?? {};
            const idOrFallback = row.id ?? entityId;
            if (idOrFallback) {
              if (mutationPolicy !== void 0) {
                const existing = await persistence.getById(type, idOrFallback);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings(type))) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("update", type));
                }
              }
              await persistence.update(type, idOrFallback, row);
              const updated = await persistence.getById(type, idOrFallback);
              resultData = updated ?? { ...row, id: idOrFallback };
            } else {
              effectLog3.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL2) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          }
          case "delete": {
            const directId = typeof data === "string" ? data : void 0;
            const nestedId = typeof data === "object" && data !== null ? data.id : void 0;
            const deleteId = directId ?? nestedId ?? entityId;
            if (deleteId) {
              if (mutationPolicy !== void 0) {
                const existing = await persistence.getById(type, deleteId);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings(type))) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("delete", type));
                }
              }
              await persistence.delete(type, deleteId);
              resultData = { id: deleteId, deleted: true };
            } else {
              effectLog3.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL2) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          }
        }
        if (resultData === void 0 && (action === "update" || action === "delete")) {
          record({
            effect: "persist",
            action,
            entityType: type,
            success: false,
            denied: true,
            error: `persist ${action} ${type} resolved no row key`
          });
          return void 0;
        }
        const sizeAfter = (await persistence.list(type)).length;
        effectLog3.debug("persist:store-mutate", {
          action,
          entityType: type,
          resultId: resultData?.id,
          sizeBefore,
          sizeAfter,
          delta: sizeAfter - sizeBefore
        });
        record({
          effect: "persist",
          action,
          entityType: type,
          data: resultData,
          success: true
        });
        return resultData;
      } catch (err) {
        effectLog3.error("persist:store-mutate-error", {
          action,
          entityType: type,
          error: err instanceof Error ? err.message : String(err)
        });
        record({
          effect: "persist",
          action,
          entityType: type,
          ...deniedReason !== void 0 ? { denied: true } : {},
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
      return void 0;
    },
    callService: async (service, action, params, callerContext) => {
      try {
        let result = null;
        const signal = callerContext?.signal;
        const identity = () => {
          const viewer = bindings?.user;
          return viewer || signal ? { ...viewer ? { principal: viewer.id, role: viewer.role } : {}, ...signal ? { signal } : {} } : void 0;
        };
        if (consumerCallService) {
          result = await consumerCallService(service, action, params, identity());
        } else {
          result = await defaultCallService(service, action, params, identity());
        }
        record({
          effect: "call-service",
          action: `${service}.${action}`,
          data: result,
          success: true
        });
        return result;
      } catch (err) {
        record({
          effect: "call-service",
          action: `${service}.${action}`,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        throw err;
      }
    },
    fetch: async (fetchEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        if (options?.id) {
          const entity = await persistence.getById(fetchEntityType, options.id);
          const visible = entity ? applyRowAccess(
            [entity],
            entityAccess?.get(fetchEntityType)?.read,
            void 0,
            accessBindings(fetchEntityType)
          ) : [];
          if (visible.length > 0) {
            if (fetchedData) fetchedData[fetchEntityType] = visible;
            result = visible[0] ?? null;
            total = 1;
          }
        } else {
          let entities = await persistence.list(fetchEntityType);
          entities = applyRowAccess(
            entities,
            entityAccess?.get(fetchEntityType)?.read,
            options?.filter,
            accessBindings(fetchEntityType)
          );
          total = entities.length;
          entities = orderFetchedRows(entities, options?.orderBy);
          if (options?.offset && options.offset > 0) {
            entities = entities.slice(options.offset);
          }
          if (options?.limit && options.limit > 0) {
            entities = entities.slice(0, options.limit);
          }
          if (fetchedData) fetchedData[fetchEntityType] = entities;
          result = entities;
        }
        return result === null ? null : { rows: result, total };
      } catch (err) {
        effectLog3.error("fetch-error", {
          entityType: fetchEntityType,
          error: err instanceof Error ? err : String(err)
        });
        return null;
      }
    },
    fetchStream: async (streamEntityType, options, onChunk) => {
      try {
        const rows = await persistence.list(streamEntityType);
        const visible = applyRowAccess(
          rows,
          entityAccess?.get(streamEntityType)?.read,
          void 0,
          accessBindings(streamEntityType)
        );
        const matched = options?.id ? visible.filter((r) => r["id"] === options.id) : visible;
        for (const row of matched) {
          onChunk(row);
        }
        return matched;
      } catch (err) {
        effectLog3.error("fetch-stream-error", {
          entityType: streamEntityType,
          error: err instanceof Error ? err : String(err)
        });
        return null;
      }
    },
    ref: async (refEntityType, options) => {
      return handlers.fetch(refEntityType, options);
    },
    deref: async (derefEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        if (options?.id) {
          const entity = await persistence.getById(derefEntityType, options.id);
          if (entity) {
            if (fetchedData) fetchedData[derefEntityType] = [entity];
            result = entity;
            total = 1;
          }
        } else {
          const entities = await persistence.list(derefEntityType);
          if (fetchedData) fetchedData[derefEntityType] = entities;
          result = entities;
          total = entities.length;
        }
        record({
          effect: "deref",
          entityType: derefEntityType,
          success: true
        });
        return result === null ? null : { rows: result, total };
      } catch (err) {
        record({
          effect: "deref",
          entityType: derefEntityType,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        return null;
      }
    },
    swap: async (swapEntityType, swapEntityId, transform) => {
      try {
        const current = await persistence.getById(swapEntityType, swapEntityId);
        if (!current) {
          record({
            effect: "swap",
            entityType: swapEntityType,
            success: false,
            error: `Entity ${swapEntityType}/${swapEntityId} not found`
          });
          return null;
        }
        const ctx = createContextFromBindings(
          { current, entity: bindings?.entity, payload: bindings?.payload },
          false
        );
        let newData;
        if (Array.isArray(transform)) {
          const evalResult = evaluate(transform, ctx);
          if (evalResult && typeof evalResult === "object" && !Array.isArray(evalResult)) {
            newData = evalResult;
          } else {
            newData = current;
          }
        } else if (typeof transform === "object" && transform !== null) {
          newData = { ...current, ...transform };
        } else {
          record({
            effect: "swap",
            entityType: swapEntityType,
            success: false,
            error: "swap transform must be an S-expression or object"
          });
          return null;
        }
        await persistence.update(swapEntityType, swapEntityId, newData);
        record({
          effect: "swap",
          entityType: swapEntityType,
          data: { id: swapEntityId, ...newData },
          success: true
        });
        return newData;
      } catch (err) {
        record({
          effect: "swap",
          entityType: swapEntityType,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        return null;
      }
    },
    watch: (_watchEntityType) => {
    },
    atomic: async (atomicEffects) => {
      const atomicExecutor = new EffectExecutor({
        handlers,
        bindings: bindings ?? {},
        context: context ?? {
          traitName: "atomic",
          state: "unknown",
          transition: "unknown"
        },
        inFlightCalls: opts.inFlightCalls
      });
      try {
        await atomicExecutor.executeAll(atomicEffects);
        record({ effect: "atomic", success: true });
      } catch (err) {
        record({
          effect: "atomic",
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    },
    renderUI: (slot, pattern, props, priority) => {
      pushClientEffect(["render-ui", slot, pattern, props, priority]);
    },
    navigate: (path, params, crumb) => {
      if (crumb !== void 0) {
        pushClientEffect(["navigate", path, params, { crumb }]);
      } else {
        pushClientEffect(["navigate", path, params]);
      }
    },
    navigateBack: () => {
      pushClientEffect(["navigate-back"]);
    }
  };
  return handlers;
}

// src/ui/splice-lambda-traits.ts
var TRAIT_BINDING_PREFIX = "@trait.";
var CONFIG_BINDING_PREFIX = "@config.";
var LambdaSpliceError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "LambdaSpliceError";
  }
  code;
};
function isRenderUiEffect(effect) {
  return Array.isArray(effect) && effect.length >= 1 && effect[0] === "render-ui";
}
function renderOnlyTemplate(trait) {
  if (trait.listens && trait.listens.length > 0) return null;
  if (trait.ticks && trait.ticks.length > 0) return null;
  const sm = trait.stateMachine;
  const states = sm?.states ?? [];
  if (states.length > 1) return null;
  let template = null;
  for (const transition of sm?.transitions ?? []) {
    const selfLoop = transition.from === transition.to;
    const guarded = transition.guard !== void 0 && transition.guard !== null;
    if (!selfLoop || guarded) return null;
    for (const effect of transition.effects ?? []) {
      if (!isRenderUiEffect(effect) || !Array.isArray(effect)) return null;
      const pattern = effect[2];
      if (pattern === void 0) return null;
      if (template !== null) return null;
      template = pattern;
    }
  }
  return template;
}
function resolveObjectPath(value, path) {
  let cur = value;
  for (const seg of path.split(".")) {
    if (cur === null || typeof cur !== "object") return void 0;
    cur = cur[seg];
    if (cur === void 0) return void 0;
  }
  return cur;
}
function substituteConfig(expr, subs) {
  if (typeof expr === "string") {
    if (!expr.startsWith(CONFIG_BINDING_PREFIX)) return expr;
    const rest = expr.slice(CONFIG_BINDING_PREFIX.length);
    const dot = rest.indexOf(".");
    const param = dot === -1 ? rest : rest.slice(0, dot);
    const trailing = dot === -1 ? void 0 : rest.slice(dot + 1);
    if (!(param in subs)) return expr;
    const value = subs[param];
    if (trailing === void 0) {
      if (typeof value === "string" && value === expr) return expr;
      return substituteConfig(value, subs);
    }
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const leaf = resolveObjectPath(value, trailing);
      if (leaf !== void 0) return substituteConfig(leaf, subs);
    }
    return expr;
  }
  if (Array.isArray(expr)) {
    return expr.map((item) => substituteConfig(item, subs));
  }
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const [k, v] of Object.entries(expr)) {
      out[k] = substituteConfig(v, subs);
    }
    return out;
  }
  return expr;
}
function buildTemplate(trait) {
  const raw = renderOnlyTemplate(trait);
  const emits = trait.emits ?? [];
  if (raw === null) return { template: null, emits, traitName: trait.name, id: trait.id, embedIds: trait.traitEmbedIds };
  const defaults = collectDeclaredConfigDefaults(trait);
  const template = defaults ? substituteConfig(raw, defaults) : raw;
  return { template, emits, traitName: trait.name, id: trait.id, embedIds: trait.traitEmbedIds };
}
function isLambdaForm(expr) {
  return expr.length === 3 && (expr[0] === "fn" || expr[0] === "lambda");
}
function spliceExpr(expr, inLambda, st) {
  if (typeof expr === "string") {
    if (!inLambda || !expr.startsWith(TRAIT_BINDING_PREFIX)) return expr;
    const name = expr.slice(TRAIT_BINDING_PREFIX.length);
    if (name.length === 0 || name.includes(".")) return expr;
    const embedId = st.embedIds?.[name];
    const entry = (embedId !== void 0 ? st.templatesById.get(embedId) : void 0) ?? st.templates.get(name);
    if (entry === void 0) return expr;
    if (entry.template === null) {
      throw new LambdaSpliceError(
        "LAMBDA_STATEFUL_TRAIT",
        `Trait "${name}" keeps its own state machine and cannot be expanded per list item \u2014 use the list pattern's item events (referenced inside a lambda in trait "${st.hostName}")`
      );
    }
    if (st.visiting.includes(name)) return expr;
    st.visiting.push(name);
    const prevEmbedIds = st.embedIds;
    st.embedIds = entry.embedIds;
    const expanded = spliceExpr(entry.template, true, st);
    st.embedIds = prevEmbedIds;
    st.visiting.pop();
    for (const emit of entry.emits) st.merged.push([entry.traitName, emit]);
    st.spliced.add(entry.traitName);
    st.changed = true;
    return expanded;
  }
  if (Array.isArray(expr)) {
    if (isLambdaForm(expr)) {
      return [expr[0], expr[1], spliceExpr(expr[2], true, st)];
    }
    return expr.map((item) => spliceExpr(item, inLambda, st));
  }
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const [k, v] of Object.entries(expr)) {
      out[k] = spliceExpr(v, inLambda, st);
    }
    return out;
  }
  return expr;
}
function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value !== null && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      out[key] = canonical(value[key]);
    }
    return out;
  }
  return value;
}
function mergeWrapperEmits(host, incoming) {
  const emits = [...host.emits ?? []];
  for (const [wrapper, emit] of incoming) {
    const existing = emits.find((e) => e.event === emit.event);
    if (existing) {
      const same = JSON.stringify(canonical(existing)) === JSON.stringify(canonical(emit));
      if (!same) {
        throw new LambdaSpliceError(
          "EMIT_MERGE_CONFLICT",
          `Emit "${emit.event}" merged from spliced wrapper "${wrapper}" conflicts with an existing "${emit.event}" on host "${host.name}" (different payload shape)`
        );
      }
    } else {
      emits.push(emit);
    }
  }
  return emits;
}
function removeConsumedWrappers(traits, pages, spliced) {
  const idToName = /* @__PURE__ */ new Map();
  for (const { trait } of traits) {
    if (trait.id) idToName.set(trait.id, trait.name);
  }
  for (; ; ) {
    const stillReferenced = /* @__PURE__ */ new Set();
    for (const { trait } of traits) {
      const rawTokens = /* @__PURE__ */ new Set();
      for (const t of trait.stateMachine?.transitions ?? []) {
        if (t.guard !== void 0 && t.guard !== null) {
          collectTraitRefsFromValue(t.guard, rawTokens);
        }
        collectTraitRefsFromEffects(t.effects, rawTokens);
      }
      for (const tick of trait.ticks ?? []) {
        if (tick.guard !== void 0 && tick.guard !== null) {
          collectTraitRefsFromValue(tick.guard, rawTokens);
        }
        collectTraitRefsFromEffects(tick.effects, rawTokens);
      }
      if (trait.config) {
        for (const field of Object.values(trait.config)) {
          if (field && typeof field === "object" && "default" in field && field.default !== void 0) {
            collectTraitRefsFromValue(field.default, rawTokens);
          }
        }
      }
      for (const token of rawTokens) {
        const embedId = trait.traitEmbedIds?.[token];
        const resolvedName = (embedId !== void 0 ? idToName.get(embedId) : void 0) ?? token;
        stillReferenced.add(resolvedName);
      }
    }
    const removable = /* @__PURE__ */ new Set();
    for (const { trait } of traits) {
      if (spliced.has(trait.name) && !stillReferenced.has(trait.name)) {
        removable.add(trait.name);
      }
    }
    if (removable.size === 0) return;
    for (const { page } of pages) {
      const pageTraits = page.traits;
      if (!pageTraits) continue;
      const survivors2 = pageTraits.filter((pt) => !removable.has(pt.ref)).length;
      if (survivors2 === 0) {
        for (const pt of pageTraits) removable.delete(pt.ref);
      }
    }
    if (removable.size === 0) return;
    for (const resolvedPage of pages) {
      const pageTraits = resolvedPage.page.traits;
      if (pageTraits) {
        resolvedPage.page.traits = pageTraits.filter((pt) => !removable.has(pt.ref));
      }
    }
    const survivors = traits.filter(({ trait }) => !removable.has(trait.name));
    traits.length = 0;
    traits.push(...survivors);
  }
}
function spliceLambdaTraitRefs(traits, pages) {
  if (traits.length === 0) return;
  const templates = /* @__PURE__ */ new Map();
  const templatesById = /* @__PURE__ */ new Map();
  for (const { trait } of traits) {
    const entry = buildTemplate(trait);
    templates.set(trait.name, entry);
    if (trait.id) templatesById.set(trait.id, entry);
  }
  const spliced = /* @__PURE__ */ new Set();
  for (const resolved of traits) {
    const host = resolved.trait;
    const st = {
      templates,
      templatesById,
      embedIds: host.traitEmbedIds,
      // Seed with the host itself so a self-reference never self-splices.
      visiting: [host.name],
      merged: [],
      spliced,
      hostName: host.name,
      changed: false
    };
    const sm = host.stateMachine;
    const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
      ...t,
      effects: t.effects ? t.effects.map((e) => spliceExpr(e, false, st)) : t.effects
    })) : sm?.transitions;
    const nextTicks = host.ticks ? host.ticks.map((tick) => ({
      ...tick,
      effects: tick.effects.map((e) => spliceExpr(e, false, st))
    })) : host.ticks;
    if (!st.changed) {
      continue;
    }
    const nextEmits = st.merged.length > 0 ? mergeWrapperEmits(host, st.merged) : host.emits;
    resolved.trait = {
      ...host,
      ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
      ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
      ...nextEmits !== void 0 ? { emits: nextEmits } : {}
    };
  }
  if (spliced.size > 0) {
    removeConsumedWrappers(traits, pages, spliced);
  }
}

// src/entities/resolver/sentinel-resolution.ts
var PRIMITIVE_TYPE_ARG_NAMES = /* @__PURE__ */ new Set([
  "string",
  "money",
  "any",
  "icon",
  "component",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "node",
  "json",
  "render-ui",
  "relation",
  "int",
  "float",
  "number",
  "duration",
  "bool",
  "boolean",
  "date",
  "datetime",
  "timestamp",
  "object",
  "sexpr",
  "scalar"
]);
var BARE_PAYLOAD_CARRIER = "value";
function resolveTypeArgValue(value, entitiesByName, typesByName) {
  if (PRIMITIVE_TYPE_ARG_NAMES.has(value)) {
    return { kind: "scalar", primitive: value };
  }
  const entity = entitiesByName.get(value);
  if (entity) {
    return { kind: "entity", entity };
  }
  const shape = typesByName[value];
  if (shape) {
    return { kind: "struct", shape };
  }
  return void 0;
}
function entityFieldToPayloadField(f) {
  if (f.type === "object" && f.items) {
    const valueType = fieldTypeTagOf(f.items);
    return {
      name: f.name ?? "",
      type: `Map<string,${valueType}>`,
      ...f.required !== void 0 ? { required: f.required } : {}
    };
  }
  let properties;
  if (f.type === "object" && f.properties) {
    properties = Object.values(f.properties).map(entityFieldToPayloadField);
  } else if (f.type === "union" && f.properties) {
    const ordered = f.values ?? Object.keys(f.properties);
    properties = ordered.map((k) => f.properties?.[k]).filter((v) => v !== void 0).map(entityFieldToPayloadField);
  } else if (f.type === "array" && f.items) {
    properties = entityFieldToPayloadField(f.items).properties;
  }
  const isArrayOfStruct = f.type === "array" && properties !== void 0;
  return {
    name: f.name ?? "",
    type: isArrayOfStruct ? "[object]" : fieldTypeTagOf(f),
    ...f.required !== void 0 ? { required: f.required } : {},
    ...properties && properties.length > 0 ? { properties } : {}
  };
}
function fieldTypeTagOf(f) {
  return f.type;
}
function entityFieldsToPayloadFields(entity) {
  return entity.fields.map(entityFieldToPayloadField);
}
function flattenBareEntityPayload(fields, entityDef, subs) {
  if (fields.length !== 1) return false;
  const field = fields[0];
  if (field.name !== BARE_PAYLOAD_CARRIER) return false;
  let entity;
  if (field.type === "@entity") {
    entity = entityDef;
  } else if (field.type === "object" && field.properties) {
    entity = entityDef;
  } else if (field.type.startsWith("$")) {
    const sub = subs.get(field.type.slice(1));
    entity = sub?.kind === "entity" ? sub.entity : void 0;
  }
  if (!entity) return false;
  fields.length = 0;
  fields.push(...entityFieldsToPayloadFields(entity));
  return true;
}
function resolveSentinelFields(fields, entityDef, subs, declaredNames) {
  let changed = false;
  for (const field of fields) {
    const isSelfRef = field.type === "@entity" || field.type === "[@entity]";
    if (isSelfRef) {
      if (entityDef) {
        const isArray = field.type.startsWith("[");
        field.type = isArray ? "[object]" : "object";
        field.properties = entityFieldsToPayloadFields(entityDef);
        field.entity = entityDef.name;
        changed = true;
      }
      continue;
    }
    const isArraySentinel = field.type.startsWith("[$") && field.type.endsWith("]");
    const isBareSentinel = !isArraySentinel && field.type.startsWith("$");
    if (isBareSentinel || isArraySentinel) {
      const paramName = isArraySentinel ? field.type.slice(2, -1) : field.type.slice(1);
      const sub = subs.get(paramName);
      if (sub) {
        switch (sub.kind) {
          case "scalar":
            field.type = isArraySentinel ? `[${sub.primitive}]` : sub.primitive;
            field.properties = void 0;
            break;
          case "entity":
            field.type = isArraySentinel ? "[object]" : "object";
            field.properties = entityFieldsToPayloadFields(sub.entity);
            field.entity = sub.entity.name;
            break;
          case "struct":
            field.type = isArraySentinel ? "[object]" : "object";
            field.properties = [...sub.shape];
            break;
        }
        changed = true;
        continue;
      }
      if (declaredNames.has(paramName)) {
        continue;
      }
      if (entityDef) {
        field.type = isArraySentinel ? "[object]" : "object";
        field.properties = entityFieldsToPayloadFields(entityDef);
        field.entity = entityDef.name;
        changed = true;
      }
      continue;
    }
    if (field.properties) {
      const mutableProps = field.properties;
      changed = resolveSentinelFields(mutableProps, entityDef, subs, declaredNames) || changed;
    }
  }
  return changed;
}
function resolvePayloadEntitySchema(payloadEntity, setPayloadSchema, entitiesByName) {
  if (!payloadEntity) return false;
  const entity = entitiesByName.get(payloadEntity);
  if (!entity) return false;
  setPayloadSchema(entityFieldsToPayloadFields(entity));
  return true;
}
function resolveTraitTypeParamSentinels(trait, entityDef, entitiesByName, typesByName, typeArgs) {
  const declared = trait.typeParams ?? [];
  const subs = /* @__PURE__ */ new Map();
  const declaredNames = /* @__PURE__ */ new Set();
  for (const p of declared) {
    declaredNames.add(p.name);
    const argValue = typeArgs?.[p.name];
    let sub;
    if (argValue !== void 0) {
      sub = resolveTypeArgValue(argValue, entitiesByName, typesByName);
    } else if (p.kind === "Entity" && entityDef) {
      sub = { kind: "entity", entity: entityDef };
    } else if (p.default) {
      sub = resolveTypeArgValue(p.default, entitiesByName, typesByName);
    }
    if (sub) subs.set(p.name, sub);
  }
  let changed = false;
  for (const emit of trait.emits ?? []) {
    if (!emit.payloadSchema) continue;
    if (resolvePayloadEntitySchema(
      emit.payloadEntity,
      (schema2) => {
        emit.payloadSchema = schema2;
      },
      entitiesByName
    )) {
      changed = true;
      continue;
    }
    const schema = emit.payloadSchema;
    if (flattenBareEntityPayload(schema, entityDef, subs)) {
      changed = true;
    } else {
      changed = resolveSentinelFields(schema, entityDef, subs, declaredNames) || changed;
    }
  }
  for (const ev of trait.stateMachine?.events ?? []) {
    if (!ev.payloadSchema) continue;
    if (resolvePayloadEntitySchema(
      ev.payloadEntity,
      (schema2) => {
        ev.payloadSchema = schema2;
      },
      entitiesByName
    )) {
      changed = true;
      continue;
    }
    const schema = ev.payloadSchema;
    if (flattenBareEntityPayload(schema, entityDef, subs)) {
      changed = true;
    } else {
      changed = resolveSentinelFields(schema, entityDef, subs, declaredNames) || changed;
    }
  }
  return changed;
}
function resolveOrbitalTypeParamSentinels(traits, primaryEntity, auxiliaryEntities, orbitalTypes) {
  const entitiesByName = /* @__PURE__ */ new Map();
  entitiesByName.set(primaryEntity.name, primaryEntity);
  for (const e of auxiliaryEntities) entitiesByName.set(e.name, e);
  const typesByName = orbitalTypes ?? {};
  for (const rt of traits) {
    const entityDef = rt.trait.linkedEntity ? entitiesByName.get(rt.trait.linkedEntity) : void 0;
    resolveTraitTypeParamSentinels(rt.trait, entityDef, entitiesByName, typesByName, rt.typeArgs);
  }
}

// src/entities/resolver/reference-resolver.ts
var refResolverLog = createLogger("almadar:runtime:ref-resolver");
function indexOrbitalNodes(orbital, idIndex) {
  for (const traitRef of orbital.traits ?? []) {
    if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
      const trait = traitRef;
      if (trait.id) {
        idIndex.set(trait.id, { kind: "trait", node: trait });
      }
      for (const ev of trait.stateMachine?.events ?? []) {
        if (ev.id) {
          idIndex.set(ev.id, { kind: "event", node: ev });
        }
      }
    }
  }
  const entityRef = orbital.entity;
  if (entityRef && typeof entityRef !== "string" && !("extends" in entityRef) && entityRef.id) {
    const entity = entityRef;
    idIndex.set(entity.id, { kind: "entity", node: entity });
  }
  for (const pageRef of orbital.pages ?? []) {
    if (typeof pageRef !== "string" && !("ref" in pageRef)) {
      const page = pageRef;
      if (page.id) {
        idIndex.set(page.id, { kind: "page", node: page });
      }
    }
  }
}
function buildIdIndex(orbital, orbitals) {
  const idIndex = /* @__PURE__ */ new Map();
  indexOrbitalNodes(orbital, idIndex);
  for (const imported of orbitals.values()) {
    for (const o of importedOrbitals(imported)) indexOrbitalNodes(o, idIndex);
  }
  return idIndex;
}
function importedOrbitals(imported) {
  return imported.orbitals?.length ? imported.orbitals : [imported.orbital];
}
function resolvedAliasView(imported) {
  return importedOrbitals(imported).map((o) => {
    const resolved = o.reference ? imported.resolvedOrbitals?.find((r) => r.name === o.name) : void 0;
    if (!resolved) return o;
    const aux = [...resolved.original.auxiliaryEntities ?? [], ...resolved.auxiliaryEntities ?? []].filter(isInlineEntity);
    return { ...o, entity: resolved.entity, auxiliaryEntities: aux };
  });
}
function renameEventsInRenderUiConfig(node, rename) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    return node.map((item) => renameEventsInRenderUiConfig(item, rename));
  }
  if (typeof node !== "object") return node;
  const obj = node;
  const next = { ...obj };
  const rawType = obj.type;
  const patternType = typeof rawType === "string" ? rawType : void 0;
  for (const [key, value] of Object.entries(obj)) {
    if (key === "action" && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    if (/^on[A-Z]/.test(key) && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    if (key.endsWith("Event") && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    const eventField = patternType !== void 0 ? eventListPropsOf(patternType).get(key) : void 0;
    if (eventField !== void 0 && Array.isArray(value)) {
      const rewrittenArray = value.map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
        const action = entry;
        const current = action[eventField];
        if (typeof current === "string" && !current.startsWith("@")) {
          return { ...action, [eventField]: rename(current) ?? current };
        }
        return action;
      });
      next[key] = rewrittenArray;
      continue;
    }
    next[key] = renameEventsInRenderUiConfig(value, rename);
  }
  return next;
}
function renameEventTypedMembers(value, items, rename) {
  if (items === void 0 || value === null || value === void 0) return value;
  if (Array.isArray(value)) {
    return value.map((entry2) => renameEventTypedMembers(entry2, items, rename));
  }
  if (typeof value !== "object") return value;
  const properties = items.type === "union" ? Object.fromEntries(Object.values(items.properties ?? {}).flatMap(
    (variant) => Object.entries(variant.properties ?? {}).filter(([, member]) => member.type === "event" || member.type === "EventAddress")
  )) : items.properties;
  if (properties === void 0) return value;
  const entry = value;
  const next = { ...entry };
  let touched = false;
  for (const [member, decl] of Object.entries(properties)) {
    const current = entry[member];
    if (decl.type === "event" || decl.type === "EventAddress") {
      if (typeof current === "string" && current !== "" && !current.startsWith("@")) {
        const renamed = rename(current);
        if (renamed !== void 0 && renamed !== current) {
          next[member] = renamed;
          touched = true;
        }
      }
      continue;
    }
    if (decl.items !== void 0 && current !== void 0) {
      const nested = renameEventTypedMembers(current, decl.items, rename);
      if (nested !== current) {
        next[member] = nested;
        touched = true;
      }
    }
  }
  return touched ? next : value;
}
function renameEventsInDeclaredConfig(config, rename) {
  if (config === void 0) return config;
  let touched = false;
  const next = {};
  for (const [knob, field] of Object.entries(config)) {
    if (field.default === void 0) {
      next[knob] = field;
      continue;
    }
    const renamedDefault = field.type === "event" || field.type === "EventAddress" ? typeof field.default === "string" && field.default !== "" && !field.default.startsWith("@") ? rename(field.default) ?? field.default : field.default : renameEventTypedMembers(field.default, field.items, rename);
    if (renamedDefault !== field.default) {
      next[knob] = { ...field, default: renamedDefault };
      touched = true;
    } else {
      next[knob] = field;
    }
  }
  return touched ? next : config;
}
function renameEventsInEffects(effects, rename) {
  return effects.map((effect) => renameEventRefsInNode(effect, rename));
}
var EMIT_OPTION_SLOTS = /* @__PURE__ */ new Set(["success", "failure", "on_message", "on_change"]);
function renameEventRefsInNode(node, rename) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    const arr = node;
    if (arr[0] === "render-ui" && arr.length >= 3) {
      const nextConfig = renameEventsInRenderUiConfig(arr[2], rename);
      return [
        arr[0],
        arr[1],
        nextConfig,
        ...arr.slice(3).map((x) => renameEventRefsInNode(x, rename))
      ];
    }
    const out = arr.map((x) => renameEventRefsInNode(x, rename));
    if (arr[0] === "emit" && typeof arr[1] === "string") {
      out[1] = rename(arr[1]) ?? arr[1];
    }
    return out;
  }
  if (typeof node === "object") {
    const obj = node;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === "emit") {
        if (typeof v === "string") {
          next[k] = rename(v) ?? v;
        } else if (v !== null && typeof v === "object" && !Array.isArray(v)) {
          const slots = {};
          for (const [slot, slotV] of Object.entries(v)) {
            slots[slot] = EMIT_OPTION_SLOTS.has(slot) && typeof slotV === "string" ? rename(slotV) ?? slotV : renameEventRefsInNode(slotV, rename);
          }
          next[k] = slots;
        } else {
          next[k] = renameEventRefsInNode(v, rename);
        }
        continue;
      }
      next[k] = renameEventRefsInNode(v, rename);
    }
    return next;
  }
  return node;
}
var REBIND_ENTITY_PROPS = /* @__PURE__ */ new Set(["entity"]);
var ID_ENTITY_PROPS = /* @__PURE__ */ new Set(["entity", "entityType", "source"]);
function renameEntityInRenderUiConfig(node, rename, props) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    return node.map((item) => renameEntityInRenderUiConfig(item, rename, props));
  }
  if (typeof node !== "object") return node;
  const obj = node;
  const next = { ...obj };
  for (const [key, value] of Object.entries(obj)) {
    if (props.has(key) && typeof value === "string") {
      const replaced = rename(value);
      if (replaced !== void 0) {
        next[key] = replaced;
        continue;
      }
    }
    next[key] = renameEntityInRenderUiConfig(value, rename, props);
  }
  return next;
}
function renameEntityInEffects(effects, rename, props) {
  return effects.map((effect) => renameEntityInEffect(effect, rename, props));
}
var ENTITY_AT_POS_1 = /* @__PURE__ */ new Set(["fetch", "ref", "deref", "spawn"]);
var ALL_ARGS_ARE_EFFECTS = /* @__PURE__ */ new Set([
  "do",
  "atomic",
  "async/race",
  "async/all",
  "async/sequence"
]);
var ARGS_FROM_POS_2_ARE_EFFECTS = /* @__PURE__ */ new Set([
  "if",
  "when",
  "let",
  "async/delay",
  "async/debounce",
  "async/throttle",
  "async/interval"
]);
function renameEntityInEffect(effect, rename, props) {
  if (!Array.isArray(effect) || effect.length === 0) return effect;
  const arr = effect;
  const op = arr[0];
  if (typeof op !== "string") return effect;
  if (op === "render-ui" && arr.length >= 3) {
    const [, slot, config, ...rest] = arr;
    const nextConfig = renameEntityInRenderUiConfig(config, rename, props);
    return [op, slot, nextConfig, ...rest];
  }
  if (op === "persist" && arr.length >= 3 && typeof arr[2] === "string") {
    const replaced = rename(arr[2]);
    if (replaced !== void 0) return [op, arr[1], replaced, ...arr.slice(3)];
  }
  if (ENTITY_AT_POS_1.has(op) && typeof arr[1] === "string") {
    const replaced = rename(arr[1]);
    if (replaced !== void 0) return [op, replaced, ...arr.slice(2)];
  }
  const skipFirstNonEffectArg = ARGS_FROM_POS_2_ARE_EFFECTS.has(op);
  const recurseAll = ALL_ARGS_ARE_EFFECTS.has(op);
  if (recurseAll || skipFirstNonEffectArg) {
    const startIndex = skipFirstNonEffectArg ? 2 : 1;
    return arr.map((arg, i) => {
      if (i < startIndex) return arg;
      if (Array.isArray(arg)) {
        return renameEntityInEffect(arg, rename, props);
      }
      return arg;
    });
  }
  return effect;
}
var TRAIT_EMBED_PREFIX = "@trait.";
function renameTraitEmbedsInValue(node, subs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    if (!node.startsWith(TRAIT_EMBED_PREFIX)) return node;
    const rest = node.slice(TRAIT_EMBED_PREFIX.length);
    const dot = rest.search(/[.[]/);
    const name = dot === -1 ? rest : rest.slice(0, dot);
    const suffix = dot === -1 ? "" : rest.slice(dot);
    const to = subs.get(name);
    return to === void 0 ? node : `${TRAIT_EMBED_PREFIX}${to}${suffix}`;
  }
  if (Array.isArray(node)) return node.map((item) => renameTraitEmbedsInValue(item, subs));
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = renameTraitEmbedsInValue(value, subs);
  }
  return next;
}
function renameEntityNameTokensInValue(node, entitySubs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    if (!node.startsWith("@")) return node;
    const rest = node.slice(1);
    const dot = rest.search(/[.[]/);
    const name = dot === -1 ? rest : rest.slice(0, dot);
    const suffix = dot === -1 ? "" : rest.slice(dot);
    const to = entitySubs.get(name);
    return to === void 0 ? node : `@${to}${suffix}`;
  }
  if (Array.isArray(node)) return node.map((item) => renameEntityNameTokensInValue(item, entitySubs));
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = renameEntityNameTokensInValue(value, entitySubs);
  }
  return next;
}
function renameTraitEmbeds(trait, subs) {
  if (subs.size === 0) return trait;
  const sm = trait.stateMachine;
  const next = { ...trait };
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map(
        (t) => t.effects ? { ...t, effects: renameTraitEmbedsInValue(t.effects, subs) } : t
      )
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map(
      (tick) => tick.effects ? { ...tick, effects: renameTraitEmbedsInValue(tick.effects, subs) } : tick
    );
  }
  if (trait.config) {
    const nextConfig = {};
    for (const [key, field] of Object.entries(trait.config)) {
      nextConfig[key] = field.default === void 0 ? field : {
        ...field,
        default: renameTraitEmbedsInValue(
          field.default,
          subs
        )
      };
    }
    next.config = nextConfig;
  }
  return next;
}
function findTraitEntryInOrbital(orbital, localName) {
  for (const traitRef of orbital.traits ?? []) {
    if (typeof traitRef === "string") continue;
    if ("stateMachine" in traitRef) {
      if (traitRef.name === localName) return traitRef;
      continue;
    }
    if (!("ref" in traitRef)) continue;
    const refObj = traitRef;
    const declared = refObj.name ?? parseImportedTraitRef(refObj.ref)?.traitName;
    if (declared === localName) return traitRef;
  }
  return null;
}
function homeOrbitalConfigForTrait(imported, traitName) {
  for (const o of importedOrbitals(imported)) {
    if (findTraitEntryInOrbital(o, traitName)) return o.config;
  }
  return void 0;
}
function findPageEntryInOrbital(orbital, localName) {
  for (const pageRef of orbital.pages ?? []) {
    if (typeof pageRef === "string") continue;
    if (!("ref" in pageRef)) {
      if (pageRef.name === localName) return pageRef;
      continue;
    }
    const refObj = pageRef;
    if (parsePageRef(refObj.ref)?.pageName === localName) return refObj;
  }
  return null;
}
function toPageTraitRef(traitRef) {
  if (typeof traitRef === "string") return { ref: traitRef };
  if ("stateMachine" in traitRef) return { ref: traitRef.name };
  const refObj = traitRef;
  return {
    ref: refObj.name ?? refObj.ref,
    ...refObj.refId !== void 0 ? { refId: refObj.refId } : {},
    ...refObj.linkedEntity !== void 0 ? { linkedEntity: refObj.linkedEntity } : {},
    ...refObj.linkedEntityId !== void 0 ? { linkedEntityId: refObj.linkedEntityId } : {},
    ...refObj.config !== void 0 ? { config: refObj.config } : {}
  };
}
function traitEmbedNamesOf(trait) {
  const found = /* @__PURE__ */ new Set();
  const walk = (node) => {
    if (node === null || node === void 0) return;
    if (typeof node === "string") {
      if (!node.startsWith(TRAIT_EMBED_PREFIX)) return;
      const rest = node.slice(TRAIT_EMBED_PREFIX.length);
      const dot = rest.search(/[.[]/);
      const name = dot === -1 ? rest : rest.slice(0, dot);
      if (name.length > 0) found.add(name);
      return;
    }
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    if (typeof node !== "object") return;
    for (const value of Object.values(node)) walk(value);
  };
  for (const t of trait.stateMachine?.transitions ?? []) {
    walk(t.guard);
    walk(t.effects);
  }
  for (const tick of trait.ticks ?? []) {
    walk(tick.guard);
    walk(tick.effects);
  }
  for (const field of Object.values(trait.config ?? {})) walk(field.default);
  return [...found].sort();
}
function resolveForwardedSiblingConfigFrom(trait, parentChain, orbitalConfig, schemaConfig, schemaOnlyKeys) {
  const declared = trait.config;
  if (!declared) return trait;
  const next = resolveConfigForwards(
    declared,
    parentChain,
    orbitalConfig,
    void 0,
    void 0,
    schemaConfig,
    schemaOnlyKeys
  );
  return next ? { ...trait, config: next } : trait;
}
function callSiteRungValue(rungConfig, knob) {
  const entry = rungConfig[knob];
  if (entry === void 0) return void 0;
  return isCallSiteConfigDeclaration(entry) ? entry.default : entry;
}
function walkConfigForwardChain(forward, parentChain, orbitalConfig, upstreamOrbitalConfig, upstreamSchemaConfig, schemaConfig) {
  const unresolved = { value: void 0, reachedSchemaConfigOnly: false };
  let knob = forward.slice("@config.".length);
  if (knob.length === 0 || knob.includes(".")) return unresolved;
  let value;
  let currentForward = forward;
  for (const rungConfig of parentChain) {
    const rungValue = callSiteRungValue(rungConfig, knob);
    if (rungValue === void 0 || rungValue === currentForward) continue;
    if (typeof rungValue === "string" && rungValue.startsWith("@config.")) {
      const nextKnob = rungValue.slice("@config.".length);
      if (nextKnob.length === 0 || nextKnob.includes(".") || nextKnob === knob) continue;
      knob = nextKnob;
      currentForward = rungValue;
      continue;
    }
    value = rungValue;
    break;
  }
  if (value === void 0 || value === currentForward) value = orbitalConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) value = upstreamOrbitalConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) value = upstreamSchemaConfig?.[knob]?.default;
  const reachesSchemaConfigRung = value === void 0 || value === currentForward;
  if (reachesSchemaConfigRung) value = schemaConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) return unresolved;
  return { value, reachedSchemaConfigOnly: reachesSchemaConfigRung, target: currentForward };
}
function substituteNestedForwards(value, own, resolve, tokens) {
  if (typeof value === "string") {
    if (!value.startsWith("@config.") || own.has(value.slice("@config.".length))) return void 0;
    const hit = resolve(value);
    if (hit.value === void 0) return void 0;
    tokens.push(hit.target ?? value);
    return hit.value;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const v = substituteNestedForwards(item, own, resolve, tokens);
      if (v === void 0) return item;
      changed = true;
      return v;
    });
    return changed ? next : void 0;
  }
  if (value !== null && typeof value === "object") {
    let changed = false;
    const next = {};
    for (const [k, item] of Object.entries(value)) {
      const v = substituteNestedForwards(item, own, resolve, tokens);
      next[k] = v === void 0 ? item : v;
      if (v !== void 0) changed = true;
    }
    return changed ? next : void 0;
  }
  return void 0;
}
function foldNestedForwards(field, own, resolve) {
  const value = field.default;
  if (value === void 0 || typeof value !== "object" || value === null) return void 0;
  const tokens = [];
  const next = substituteNestedForwards(value, own, resolve, tokens);
  if (next === void 0) return void 0;
  const nestedForwardedFrom = [.../* @__PURE__ */ new Set([...field.nestedForwardedFrom ?? [], ...tokens])];
  return { ...field, default: next, nestedForwardedFrom };
}
function resolveConfigForwards(declared, parentChain, orbitalConfig, upstreamOrbitalConfig, upstreamSchemaConfig, schemaConfig, schemaOnlyKeys) {
  let next;
  const own = new Set(Object.keys(declared));
  const resolveNested = (forward) => walkConfigForwardChain(forward, parentChain, orbitalConfig, upstreamOrbitalConfig, upstreamSchemaConfig, schemaConfig);
  for (const [key, field] of Object.entries(declared)) {
    const forward = field.default;
    if (typeof forward !== "string") {
      const folded = foldNestedForwards(field, own, resolveNested);
      if (folded) {
        next ??= { ...declared };
        next[key] = folded;
      }
      continue;
    }
    if (!forward.startsWith("@config.")) continue;
    const { value, reachedSchemaConfigOnly } = walkConfigForwardChain(
      forward,
      parentChain,
      orbitalConfig,
      upstreamOrbitalConfig,
      upstreamSchemaConfig,
      schemaConfig
    );
    if (value === void 0) continue;
    if (reachedSchemaConfigOnly) schemaOnlyKeys?.add(key);
    next ??= { ...declared };
    next[key] = { ...field, default: value, forwardedFrom: forward };
  }
  return next;
}
function resolveCallSiteConfigForwards(config, parentChain, orbitalConfig, schemaConfig) {
  let next;
  for (const [key, entry] of Object.entries(config)) {
    const forward = isCallSiteConfigDeclaration(entry) ? entry.default : entry;
    if (typeof forward !== "string" || !forward.startsWith("@config.")) continue;
    const { value } = walkConfigForwardChain(forward, parentChain, orbitalConfig, void 0, void 0, schemaConfig);
    if (value === void 0) continue;
    next ??= { ...config };
    next[key] = isCallSiteConfigDeclaration(entry) ? { ...entry, default: value, forwardedFrom: forward } : value;
  }
  return next;
}
function resolveNestedCallSiteForwards(config, own, parentChain, orbitalConfig, schemaConfig) {
  const resolve = (forward) => walkConfigForwardChain(forward, parentChain, orbitalConfig, void 0, void 0, schemaConfig);
  let next;
  for (const [key, entry] of Object.entries(config)) {
    if (isCallSiteConfigDeclaration(entry)) {
      const folded = foldNestedForwards(entry, own, resolve);
      if (!folded) continue;
      next ??= { ...config };
      next[key] = folded;
      continue;
    }
    if (entry === null || typeof entry !== "object") continue;
    const value = substituteNestedForwards(entry, own, resolve, []);
    if (value === void 0) continue;
    next ??= { ...config };
    next[key] = value;
  }
  return next;
}
function traitRefEmbedNamesOf(traitRef) {
  if (typeof traitRef === "string") return [];
  return traitEmbedNamesOf(traitRef);
}
function traitRefLocalName(traitRef) {
  if (typeof traitRef === "string") {
    return parseImportedTraitRef(traitRef)?.traitName ?? traitRef;
  }
  if ("stateMachine" in traitRef) return traitRef.name;
  const refObj = traitRef;
  return refObj.name ?? parseImportedTraitRef(refObj.ref)?.traitName ?? refObj.ref;
}
function traitRefConfigOf(traitRef) {
  if (typeof traitRef === "string") return void 0;
  return traitRef.config;
}
function buildEmbedGraph(traitRefs) {
  const out = /* @__PURE__ */ new Map();
  for (const traitRef of traitRefs) {
    const referrerName = traitRefLocalName(traitRef);
    if (!referrerName) continue;
    for (const child of traitRefEmbedNamesOf(traitRef)) {
      if (child === referrerName) continue;
      if (!out.has(child)) out.set(child, referrerName);
    }
  }
  return out;
}
function embedderChain(localName, traitRefs, graph) {
  const chain = [];
  const visited = /* @__PURE__ */ new Set([localName]);
  let cur = graph.get(localName);
  let hops = 0;
  while (cur !== void 0 && !visited.has(cur) && hops < traitRefs.length) {
    visited.add(cur);
    hops++;
    const entry = traitRefs.find((t) => traitRefLocalName(t) === cur);
    const entryConfig = entry ? traitRefConfigOf(entry) : void 0;
    if (entryConfig) chain.push(entryConfig);
    cur = graph.get(cur);
  }
  return chain;
}
function resolveEmbedderConfigForward(config, localName, embedCtx, schemaConfig) {
  const chain = embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph);
  const next = resolveCallSiteConfigForwards(config, chain, embedCtx.orbitalConfig, schemaConfig);
  return next ? next : config;
}
function consumerKnownEntityRebindSubs(trait, entityIds) {
  const out = /* @__PURE__ */ new Map();
  const refs = trait.entityRefIds;
  if (!refs) return out;
  const idToConsumerName = /* @__PURE__ */ new Map();
  for (const [name, id] of entityIds) {
    if (!idToConsumerName.has(id)) idToConsumerName.set(id, name);
  }
  for (const [tokenName, entityId] of Object.entries(refs)) {
    const consumerName = idToConsumerName.get(entityId);
    if (consumerName !== void 0 && consumerName !== tokenName) {
      out.set(tokenName, consumerName);
    }
  }
  return out;
}
function staleEntityRebindSubs(trait, newEntity) {
  const out = /* @__PURE__ */ new Map();
  const boundId = trait.linkedEntityId;
  const refs = trait.entityRefIds;
  if (boundId && refs) {
    for (const [tokenName, entityId] of Object.entries(refs)) {
      if (entityId === boundId) out.set(tokenName, newEntity);
    }
  }
  return out;
}
function applyLinkedEntityRename(trait, linkedEntity, entityIds) {
  const atomLinked = trait.linkedEntity;
  const entitySubs = consumerKnownEntityRebindSubs(trait, entityIds);
  if (!linkedEntity || !atomLinked || linkedEntity === atomLinked) {
    return rewriteEntityTypedConfigValues(rewriteEntityRefIds(trait, entitySubs, entityIds), entitySubs, entityIds);
  }
  for (const [from, to] of staleEntityRebindSubs(trait, linkedEntity)) {
    entitySubs.set(from, to);
  }
  entitySubs.set(atomLinked, linkedEntity);
  const rename = (name) => name === atomLinked ? linkedEntity : void 0;
  const sm = trait.stateMachine;
  const rebound = {
    ...trait,
    linkedEntity,
    linkedEntityId: entityIds.get(linkedEntity)
  };
  if (!sm) {
    return rewriteEntityTypedConfigValues(
      renamePayloadEntityMarkers(rewriteEntityRefIds(rebound, entitySubs, entityIds), rename),
      entitySubs,
      entityIds
    );
  }
  const nextTransitions = (sm.transitions ?? []).map((t) => {
    const nextEffects = t.effects ? renameEntityInEffects(t.effects, rename, REBIND_ENTITY_PROPS) : t.effects;
    return { ...t, effects: nextEffects };
  });
  refResolverLog.info("linkedEntity:rename", {
    trait: trait.name,
    from: atomLinked,
    to: linkedEntity,
    transitionCount: nextTransitions.length
  });
  return rewriteEntityTypedConfigValues(
    renamePayloadEntityMarkers(
      rewriteEntityRefIds(
        { ...rebound, stateMachine: { ...sm, transitions: nextTransitions } },
        entitySubs,
        entityIds
      ),
      rename
    ),
    entitySubs,
    entityIds
  );
}
function resolveEntityTokensById(trait, idIndex) {
  const map = trait.entityRefIds;
  if (!map) return trait;
  const rewrites = /* @__PURE__ */ new Map();
  for (const [tokenName, entityId] of Object.entries(map)) {
    const entry = idIndex.get(entityId);
    if (entry && entry.kind === "entity") {
      const currentName = entry.node.name;
      if (currentName && currentName !== tokenName) {
        rewrites.set(tokenName, currentName);
      }
    }
  }
  if (rewrites.size === 0) return trait;
  const rename = (name) => rewrites.get(name);
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    effects: t.effects ? renameEntityInEffects(t.effects, rename, ID_ENTITY_PROPS) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    effects: renameEntityInEffects(tick.effects, rename, ID_ENTITY_PROPS)
  })) : trait.ticks;
  const nextInitial = trait.initialEffects ? renameEntityInEffects(trait.initialEffects, rename, ID_ENTITY_PROPS) : trait.initialEffects;
  const nextLinked = trait.linkedEntity !== void 0 ? rewrites.get(trait.linkedEntity) ?? trait.linkedEntity : trait.linkedEntity;
  refResolverLog.info("entity-ref:id-resolve", {
    trait: trait.name,
    rewrites: Object.fromEntries(rewrites)
  });
  return {
    ...trait,
    linkedEntity: nextLinked,
    ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
    ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
    ...nextInitial !== void 0 ? { initialEffects: nextInitial } : {}
  };
}
var REFERENCE_CONFIG_TYPE_TO_ID_KIND = {
  entity: "entity",
  trait: "trait",
  event: "event"
};
function resolveConfigRefsById(trait, idIndex) {
  const schema = trait.config;
  if (!schema) return trait;
  let nextSchema;
  const rewrites = [];
  for (const [key, field] of Object.entries(schema)) {
    if (!field.refId || !isReferenceConfigType(field.type)) continue;
    const expectedKind = REFERENCE_CONFIG_TYPE_TO_ID_KIND[field.type];
    if (!expectedKind) continue;
    const entry = idIndex.get(field.refId);
    if (!entry || entry.kind !== expectedKind) continue;
    const currentName = entry.kind === "entity" ? entry.node.name : entry.kind === "event" ? entry.node.key : entry.node.name;
    if (!currentName) continue;
    const isTraitBinding = entry.kind === "trait" && typeof field.default === "string" && field.default.startsWith("@trait.");
    const nextDefault = isTraitBinding ? `@trait.${currentName}` : currentName;
    if (nextDefault === field.default) continue;
    nextSchema ??= { ...schema };
    nextSchema[key] = { ...field, default: nextDefault };
    rewrites.push({ key, from: field.default, to: nextDefault });
  }
  if (!nextSchema) return trait;
  refResolverLog.info("config-ref:id-resolve", {
    trait: trait.name,
    rewrites
  });
  return { ...trait, config: nextSchema };
}
function applyEventRenames(trait, renames, keptFinalNames) {
  if (!renames || Object.keys(renames).length === 0) return trait;
  const rename = (k) => k !== void 0 && k in renames ? renames[k] : k;
  const sm = trait.stateMachine;
  const nextTransitions = (sm?.transitions ?? []).map((t) => {
    const nextEvent = rename(t.event) ?? t.event;
    const nextEffects = t.effects ? renameEventsInEffects(t.effects, rename) : t.effects;
    return { ...t, event: nextEvent, effects: nextEffects };
  });
  const nextEvents = (sm?.events ?? []).map((e) => {
    const newKey = rename(e.key);
    if (newKey === e.key) return e;
    return { ...e, key: newKey ?? e.key };
  });
  const nextEmits = (trait.emits ?? []).map((em) => {
    if (typeof em === "string") return rename(em) ?? em;
    const newEvent = rename(em.event);
    return newEvent === em.event ? em : { ...em, event: newEvent ?? em.event };
  });
  const nextTicks = (trait.ticks ?? []).map(
    (tk) => tk.effects ? {
      ...tk,
      effects: renameEventsInEffects(tk.effects, rename)
    } : tk
  );
  const nextListens = (trait.listens ?? []).map((l) => {
    const sourced = l.source !== void 0 && l.source.kind !== "any";
    const sourceTraitName = l.source && l.source.kind !== "any" ? l.source.trait : void 0;
    const fromThisImport = sourceTraitName !== void 0 && keptFinalNames !== void 0 && keptFinalNames.has(sourceTraitName);
    const nextEvent = sourced && !fromThisImport ? l.event : rename(l.event) ?? l.event;
    const nextTriggers = rename(l.triggers) ?? l.triggers;
    return nextEvent === l.event && nextTriggers === l.triggers ? l : { ...l, event: nextEvent, triggers: nextTriggers };
  });
  const renamedConfig = renameEventsInDeclaredConfig(trait.config, rename);
  const nextConfig = mapConfigEventAddresses(
    renamedConfig,
    (event, source) => source.kind !== "any" && (source.trait === trait.name || keptFinalNames?.has(source.trait) === true) && event in renames ? { event: renames[event], source } : void 0
  );
  return {
    ...trait,
    stateMachine: sm ? {
      ...sm,
      transitions: nextTransitions,
      events: nextEvents
    } : sm,
    ...trait.ticks ? { ticks: nextTicks } : {},
    ...trait.listens ? { listens: nextListens } : {},
    ...nextConfig !== void 0 ? { config: nextConfig } : {},
    emits: nextEmits
  };
}
function applyEventRenamesToCallSiteConfig(config, declared, renames) {
  if (config === void 0 || declared === void 0) return config;
  if (!renames || Object.keys(renames).length === 0) return config;
  const rename = (k) => k !== void 0 && k in renames ? renames[k] : k;
  let touched = false;
  const next = { ...config };
  for (const [knob, value] of Object.entries(config)) {
    const field = declared[knob];
    if (field === void 0) continue;
    const renamed = field.type === "event" ? typeof value === "string" && value !== "" && !value.startsWith("@") ? rename(value) ?? value : value : renameEventTypedMembers(value, field.items, rename);
    if (renamed !== value) {
      next[knob] = renamed;
      touched = true;
    }
  }
  return touched ? next : config;
}
function resolvedKnobValues(before, after, knobs) {
  const out = /* @__PURE__ */ new Map();
  if (knobs.length === 0) return out;
  const knobSet = new Set(knobs);
  const beforeEmits = before.emits ?? [];
  const afterEmits = after.emits ?? [];
  for (let i = 0; i < beforeEmits.length; i++) {
    const knob = configRefEventKnob(beforeEmits[i].event);
    if (knob !== void 0 && knobSet.has(knob)) out.set(knob, afterEmits[i].event);
  }
  return out;
}
function foldEventRenameOntoDeclaredKnobs(trait, resolved, eventRenames) {
  if (!eventRenames || resolved.size === 0 || !trait.config) return trait;
  let nextConfig;
  for (const [knob, resolvedValue] of resolved) {
    const renamed = eventRenames[resolvedValue];
    if (renamed === void 0 || renamed === resolvedValue) continue;
    const field = trait.config[knob];
    if (!field) continue;
    nextConfig ??= { ...trait.config };
    nextConfig[knob] = { ...field, default: renamed };
  }
  return nextConfig ? { ...trait, config: nextConfig } : trait;
}
function resolveConfigRefEmitNames(trait, callSiteConfig) {
  const emits = trait.emits ?? [];
  const hasRef = emits.some((em) => configRefEventKnob(em.event) !== void 0);
  if (!hasRef) return { trait, errors: [], resolvedKnobs: [] };
  const effectiveConfig = {
    ...normalizeCallSiteConfigToValues(trait.config) ?? {},
    ...normalizeCallSiteConfigToValues(callSiteConfig) ?? {}
  };
  const errors = [];
  const resolvedTriples = [];
  const nextEmits = emits.map((em) => {
    const knob = configRefEventKnob(em.event);
    if (knob === void 0) return em;
    const result = resolveConfigRefEventName(em.event, trait.config, effectiveConfig);
    if (!result.ok) {
      errors.push(
        `Trait "${trait.name}" emits \`${em.event}\` but the reference is invalid (${result.error}): the knob must be a declared string-typed config field with a default.`
      );
      return em;
    }
    refResolverLog.debug("emit-config-ref:resolved", {
      trait: trait.name,
      ref: em.event,
      resolved: result.value
    });
    resolvedTriples.push({ knob, from: em.event, to: result.value });
    return { ...em, event: result.value };
  });
  const sm = trait.stateMachine;
  const nextEvents = resolvedTriples.length > 0 && sm ? sm.events.map((ev) => {
    const hit = resolvedTriples.find((t) => t.from === ev.key);
    return hit ? { ...ev, key: hit.to } : ev;
  }) : sm?.events;
  return {
    trait: {
      ...trait,
      emits: nextEmits,
      ...sm && nextEvents !== sm.events ? { stateMachine: { ...sm, events: nextEvents } } : {}
    },
    errors,
    resolvedKnobs: resolvedTriples.map((t) => t.knob)
  };
}
function foldCallSiteConfigOntoTrait(trait, callSiteConfig) {
  if (!callSiteConfig) return trait;
  const declared = trait.config;
  const overrideValues = {};
  const overriddenKeys = /* @__PURE__ */ new Set();
  const newDeclarations = {};
  for (const [key, entry] of Object.entries(callSiteConfig)) {
    const isDecl = isCallSiteConfigDeclaration(entry);
    const value = isDecl ? entry.default : entry;
    if (declared && key in declared) {
      if (value !== void 0) {
        overrideValues[key] = value;
        overriddenKeys.add(key);
      }
    } else if (isDecl) {
      newDeclarations[key] = entry;
    }
  }
  if (overriddenKeys.size === 0 && Object.keys(newDeclarations).length === 0) {
    return trait;
  }
  let nextConfig = overrideDeclaredKnobs(declared ?? {}, overrideValues);
  if (overriddenKeys.size > 0) {
    const patched = { ...nextConfig };
    for (const key of overriddenKeys) {
      const field = patched[key];
      if (!field) continue;
      const entry = callSiteConfig[key];
      const entryDecl = isCallSiteConfigDeclaration(entry) ? entry : void 0;
      patched[key] = {
        ...field,
        forwardedFrom: entryDecl?.forwardedFrom,
        // The atom's OWN `refId` (a reference-config field's declared
        // default names a SPECIFIC node by id) describes the ATOM's OWN
        // `default`, not whatever this override just replaced it with —
        // carrying it forward unchanged left a STALE id bound to the NEW
        // literal string, so the id-index's "current name" correction
        // (`resolveConfigRefsById`) silently rewrote the override BACK to
        // the atom's own default target — found via std-notes'
        // `NoteGlobalSearch`: `idleContent`'s override says `@trait.
        // NoteCatalog` but the atom's OWN stale `refId` (still pointing at
        // `AppSearchIdleHint`, the atom's default) rewrote the sibling-pull
        // target back to it (rung 2: an override with no id of its own
        // names no id, so it must clear the atom's). An override that
        // carries ITS OWN `refId` (an annotated, id-aware override) wins
        // instead — same precedence a `default` override already has.
        refId: entryDecl?.refId,
        ...entryDecl?.label !== void 0 ? { label: entryDecl.label } : {},
        ...entryDecl?.description !== void 0 ? { description: entryDecl.description } : {},
        ...entryDecl?.synonyms !== void 0 ? { synonyms: entryDecl.synonyms } : {},
        ...entryDecl?.tier !== void 0 ? { tier: entryDecl.tier } : {}
      };
    }
    nextConfig = patched;
  }
  if (Object.keys(newDeclarations).length > 0) {
    nextConfig = { ...nextConfig, ...newDeclarations };
  }
  return { ...trait, config: nextConfig };
}
var ROW_ALIAS_TOKEN_PREFIXES = ["@entity.", "@item.", "@row."];
var ROW_ALIAS_ROOTS = /* @__PURE__ */ new Set(["@entity", "@item", "@row"]);
var FIELD_NAME_MEMBER_KEYS = ["key", "name", "field"];
function rewriteEntityFieldTokensInValue(node, subs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    for (const prefix of ROW_ALIAS_TOKEN_PREFIXES) {
      if (!node.startsWith(prefix)) continue;
      const rest = node.slice(prefix.length);
      const dot = rest.search(/[.[]/);
      const name = dot === -1 ? rest : rest.slice(0, dot);
      const suffix = dot === -1 ? "" : rest.slice(dot);
      const to = subs.get(name);
      return to === void 0 ? node : `${prefix}${to}${suffix}`;
    }
    return node;
  }
  if (Array.isArray(node)) {
    const arr = node;
    if (arr.length >= 3 && arr[0] === "object/get" && typeof arr[1] === "string" && ROW_ALIAS_ROOTS.has(arr[1]) && typeof arr[2] === "string") {
      const to = subs.get(arr[2]);
      if (to !== void 0) {
        const rewritten = arr.map((item) => rewriteEntityFieldTokensInValue(item, subs));
        rewritten[2] = to;
        return rewritten;
      }
    }
    return arr.map((item) => rewriteEntityFieldTokensInValue(item, subs));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    if (FIELD_NAME_MEMBER_KEYS.includes(key) && typeof value === "string") {
      const to = subs.get(value);
      next[key] = to === void 0 ? value : to;
      continue;
    }
    next[key] = rewriteEntityFieldTokensInValue(value, subs);
  }
  return next;
}
function rewriteBareFieldNameConfigDefault(value, fieldSubs) {
  if (typeof value === "string") {
    if (value === "" || value.startsWith("@")) return value;
    return fieldSubs.get(value) ?? value;
  }
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return value.map((v) => v === "" || v.startsWith("@") ? v : fieldSubs.get(v) ?? v);
  }
  return value;
}
function rewriteEntityFieldsInConfig(config, fieldSubs) {
  if (fieldSubs.size === 0 || !config) return config;
  const nextConfig = {};
  for (const [key, field] of Object.entries(config)) {
    if (field.default === void 0) {
      nextConfig[key] = field;
      continue;
    }
    const afterSigils = rewriteEntityFieldTokensInValue(field.default, fieldSubs);
    nextConfig[key] = {
      ...field,
      default: rewriteBareFieldNameConfigDefault(afterSigils, fieldSubs)
    };
  }
  return nextConfig;
}
function applyTraitCallSiteOverrides(base, o, entityIds, aliasSubs) {
  const { trait: configResolved, errors, resolvedKnobs } = resolveConfigRefEmitNames(base, o.config);
  if (errors.length > 0) return { ok: false, errors };
  const rebound = applyLinkedEntityRename(configResolved, o.linkedEntity, entityIds);
  const aliasRebound = aliasSubs ? applyAliasEntityRenames(rebound, aliasSubs, entityIds) : rebound;
  const renamed = applyEventRenames(aliasRebound, o.events);
  const folded = foldEventRenameOntoDeclaredKnobs(renamed, resolvedKnobValues(base, configResolved, resolvedKnobs), o.events);
  const config = applyEventRenamesToCallSiteConfig(o.config, rebound.config, o.events);
  let next = foldCallSiteConfigOntoTrait(folded, config);
  if (o.fields !== void 0) next = rewriteEntityFieldsInTrait(next, new Map(Object.entries(o.fields)));
  if (o.listens !== void 0) next = { ...next, listens: [...o.listens] };
  if (o.emitsScope !== void 0 && next.emits) {
    const scope = o.emitsScope;
    next = { ...next, emits: next.emits.map((e) => ({ ...e, scope, scopeOverridden: true })) };
  }
  return { ok: true, trait: next, config };
}
function rewriteEntityFieldsInTrait(trait, fieldSubs) {
  if (fieldSubs.size === 0) return trait;
  const rewrite = (v) => rewriteEntityFieldTokensInValue(v, fieldSubs);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map((t) => ({
        ...t,
        ...t.guard !== void 0 ? { guard: rewrite(t.guard) } : {},
        ...t.effects ? { effects: rewrite(t.effects) } : {}
      }))
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({
      ...tick,
      ...tick.guard !== void 0 ? { guard: rewrite(tick.guard) } : {},
      effects: rewrite(tick.effects)
    }));
  }
  if (trait.listens) {
    next.listens = trait.listens.map((l) => ({
      ...l,
      ...l.guard !== void 0 ? { guard: rewrite(l.guard) } : {},
      ...l.payloadMapping ? { payloadMapping: rewrite(l.payloadMapping) } : {}
    }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  if (trait.config) {
    next.config = rewriteEntityFieldsInConfig(trait.config, fieldSubs);
  }
  return next;
}
function rewriteEntityFieldsInEntity(entity, fieldSubs) {
  if (fieldSubs.size === 0) return entity;
  const rewrite = (v) => rewriteEntityFieldTokensInValue(v, fieldSubs);
  return {
    ...entity,
    fields: entity.fields.map((f) => f.name && fieldSubs.has(f.name) ? { ...f, name: fieldSubs.get(f.name) } : f),
    ...entity.read_policy !== void 0 ? { read_policy: rewrite(entity.read_policy) } : {},
    ...entity.create_policy !== void 0 ? { create_policy: rewrite(entity.create_policy) } : {},
    ...entity.update_policy !== void 0 ? { update_policy: rewrite(entity.update_policy) } : {},
    ...entity.delete_policy !== void 0 ? { delete_policy: rewrite(entity.delete_policy) } : {}
  };
}
var USER_FIELD_PREFIX = "@user.";
function directUserField(node) {
  if (typeof node !== "string" || !node.startsWith(USER_FIELD_PREFIX)) return void 0;
  const rest = node.slice(USER_FIELD_PREFIX.length);
  return rest.length > 0 && !/[.[]/.test(rest) ? rest : void 0;
}
var EQUALITY_OPS = /* @__PURE__ */ new Set(["=", "==", "!=", "!==", "eq", "neq"]);
var NEGATED_EQUALITY_OPS = /* @__PURE__ */ new Set(["!=", "!==", "neq"]);
function literalHaystackMembers(items) {
  const isListCall = items[0] === "list";
  const members = isListCall ? items.slice(1) : items;
  return members.every((v) => typeof v === "string") ? [...members] : void 0;
}
function rewriteRoleLiteralsInValue(node, roleFields, roles) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    const arr = node;
    const head = arr[0];
    if (typeof head === "string" && EQUALITY_OPS.has(head) && arr.length === 3) {
      const [, left, right] = arr;
      const leftField = directUserField(left);
      const rightField = directUserField(right);
      const userField = leftField !== void 0 && rightField === void 0 ? leftField : rightField !== void 0 && leftField === void 0 ? rightField : void 0;
      if (userField !== void 0 && roleFields.has(userField)) {
        const literalSide = leftField !== void 0 ? right : left;
        if (typeof literalSide === "string" && literalSide in roles) {
          const targets = roles[literalSide];
          if (targets.length === 1) {
            return leftField !== void 0 ? [head, left, targets[0]] : [head, targets[0], right];
          }
          const includesExpr = ["array/includes", [...targets], leftField !== void 0 ? left : right];
          return NEGATED_EQUALITY_OPS.has(head) ? ["not", includesExpr] : includesExpr;
        }
      }
    }
    if (head === "array/includes" && arr.length === 3 && Array.isArray(arr[1])) {
      const needleField = directUserField(arr[2]);
      if (needleField !== void 0 && roleFields.has(needleField)) {
        const members = literalHaystackMembers(arr[1]);
        if (members !== void 0) {
          const expanded = [];
          for (const literal of members) {
            const mapped = roles[literal];
            if (mapped) {
              for (const target of mapped) if (!expanded.includes(target)) expanded.push(target);
            } else if (!expanded.includes(literal)) {
              expanded.push(literal);
            }
          }
          const isListCall = arr[1][0] === "list";
          return ["array/includes", isListCall ? ["list", ...expanded] : expanded, arr[2]];
        }
      }
    }
    return arr.map((item) => rewriteRoleLiteralsInValue(item, roleFields, roles));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = rewriteRoleLiteralsInValue(value, roleFields, roles);
  }
  return next;
}
function rewriteRoleLiteralsInTrait(trait, roleFields, roles) {
  if (roleFields.size === 0 || Object.keys(roles).length === 0) return trait;
  const rewrite = (v) => rewriteRoleLiteralsInValue(v, roleFields, roles);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map((t) => ({
        ...t,
        ...t.guard !== void 0 ? { guard: rewrite(t.guard) } : {},
        ...t.effects ? { effects: rewrite(t.effects) } : {}
      }))
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({
      ...tick,
      ...tick.guard !== void 0 ? { guard: rewrite(tick.guard) } : {},
      effects: rewrite(tick.effects)
    }));
  }
  if (trait.listens) {
    next.listens = trait.listens.map((l) => ({
      ...l,
      ...l.guard !== void 0 ? { guard: rewrite(l.guard) } : {},
      ...l.payloadMapping ? { payloadMapping: rewrite(l.payloadMapping) } : {}
    }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  if (trait.config) {
    const nextConfig = {};
    for (const [key, field] of Object.entries(trait.config)) {
      nextConfig[key] = field.default === void 0 ? field : { ...field, default: rewrite(field.default) };
    }
    next.config = nextConfig;
  }
  if (trait.sourceEntityDefinition) {
    next.sourceEntityDefinition = rewriteRoleLiteralsInEntity(trait.sourceEntityDefinition, roleFields, roles);
  }
  return next;
}
function rewriteRoleLiteralsInEntity(entity, roleFields, roles) {
  if (roleFields.size === 0 || Object.keys(roles).length === 0) return entity;
  const rewrite = (v) => rewriteRoleLiteralsInValue(v, roleFields, roles);
  return {
    ...entity,
    ...entity.read_policy !== void 0 ? { read_policy: rewrite(entity.read_policy) } : {},
    ...entity.create_policy !== void 0 ? { create_policy: rewrite(entity.create_policy) } : {},
    ...entity.update_policy !== void 0 ? { update_policy: rewrite(entity.update_policy) } : {},
    ...entity.delete_policy !== void 0 ? { delete_policy: rewrite(entity.delete_policy) } : {}
  };
}
function renameRelationTargetsInField(field, entitySubs, entityIds) {
  let next = field;
  if (next.type === "relation") {
    const to = entitySubs.get(next.relation.entity);
    if (to !== void 0) {
      next = { ...next, relation: { ...next.relation, entity: to, entityId: entityIds.get(to) } };
    }
  }
  if ((next.type === "array" || next.type === "object") && next.items) {
    next = { ...next, items: renameRelationTargetsInField(next.items, entitySubs, entityIds) };
  }
  if (next.properties) {
    const nextProperties = {};
    for (const [key, value] of Object.entries(next.properties)) {
      nextProperties[key] = renameRelationTargetsInField(value, entitySubs, entityIds);
    }
    next = { ...next, properties: nextProperties };
  }
  return next;
}
function renameEntityRelationTargets(entity, entitySubs, entityIds) {
  if (entitySubs.size === 0) return entity;
  return { ...entity, fields: entity.fields.map((f) => renameRelationTargetsInField(f, entitySubs, entityIds)) };
}
function renameSourceEntityDefinition(trait, subs, entityIds) {
  const src = trait.sourceEntityDefinition;
  if (!src || subs.size === 0) return trait;
  const renamedName = subs.get(src.name) ?? src.name;
  const nextSrc = renameEntityRelationTargets({ ...src, name: renamedName }, subs, entityIds);
  return { ...trait, sourceEntityDefinition: nextSrc };
}
function renamePayloadEntity(fields, rename) {
  return fields.map((field) => {
    const renamedEntity = field.entity !== void 0 ? rename(field.entity) : void 0;
    const renamedProps = field.properties ? renamePayloadEntity(field.properties, rename) : field.properties;
    return {
      ...field,
      ...renamedEntity !== void 0 ? { entity: renamedEntity } : {},
      ...renamedProps !== void 0 ? { properties: renamedProps } : {}
    };
  });
}
function renamePayloadEntityMarkers(trait, rename) {
  const sm = trait.stateMachine;
  const nextEvents = sm?.events ? sm.events.map((ev) => {
    const renamedEntity = ev.payloadEntity !== void 0 ? rename(ev.payloadEntity) : void 0;
    if (!ev.payloadSchema && renamedEntity === void 0) return ev;
    return {
      ...ev,
      ...ev.payloadSchema ? { payloadSchema: renamePayloadEntity(ev.payloadSchema, rename) } : {},
      ...renamedEntity !== void 0 ? { payloadEntity: renamedEntity } : {}
    };
  }) : void 0;
  const nextEmits = trait.emits ? trait.emits.map((em) => {
    const renamedEntity = em.payloadEntity !== void 0 ? rename(em.payloadEntity) : void 0;
    if (!em.payloadSchema && renamedEntity === void 0) return em;
    return {
      ...em,
      ...em.payloadSchema ? { payloadSchema: renamePayloadEntity(em.payloadSchema, rename) } : {},
      ...renamedEntity !== void 0 ? { payloadEntity: renamedEntity } : {}
    };
  }) : void 0;
  return {
    ...trait,
    ...sm && nextEvents ? { stateMachine: { ...sm, events: nextEvents } } : {},
    ...nextEmits ? { emits: nextEmits } : {}
  };
}
function walkEntityInPayloadMapping(payloadMapping, rename, props) {
  const next = {};
  for (const [key, value] of Object.entries(payloadMapping)) {
    next[key] = renameEntityInEffect(value, rename, props);
  }
  return next;
}
function renameEntitiesInTrait(trait, entitySubs, entityIds) {
  if (entitySubs.size === 0) return trait;
  const rename = (name) => entitySubs.get(name);
  const withTokens = (value) => renameEntityNameTokensInValue(value, entitySubs);
  const nextLinked = trait.linkedEntity !== void 0 ? entitySubs.get(trait.linkedEntity) ?? trait.linkedEntity : trait.linkedEntity;
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    ...t.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(t.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: t.effects ? withTokens(renameEntityInEffects(t.effects, rename, REBIND_ENTITY_PROPS)) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    ...tick.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(tick.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: withTokens(renameEntityInEffects(
      tick.effects,
      rename,
      REBIND_ENTITY_PROPS
    ))
  })) : trait.ticks;
  const nextListens = trait.listens ? trait.listens.map((l) => ({
    ...l,
    ...l.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(l.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    ...l.payloadMapping ? { payloadMapping: withTokens(walkEntityInPayloadMapping(l.payloadMapping, rename, REBIND_ENTITY_PROPS)) } : {}
  })) : trait.listens;
  const nextInitial = trait.initialEffects ? withTokens(renameEntityInEffects(
    trait.initialEffects,
    rename,
    REBIND_ENTITY_PROPS
  )) : trait.initialEffects;
  return rewriteEntityTypedConfigValues(
    renamePayloadEntityMarkers(
      {
        ...trait,
        linkedEntity: nextLinked,
        ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
        ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
        ...nextListens !== void 0 ? { listens: nextListens } : {},
        ...nextInitial !== void 0 ? { initialEffects: nextInitial } : {}
      },
      rename
    ),
    entitySubs,
    entityIds
  );
}
function rewriteEntityRefIds(trait, entitySubs, entityIds) {
  const refs = trait.entityRefIds;
  if (!refs) return trait;
  const out = {};
  for (const [from, oldId] of Object.entries(refs)) {
    const name = entitySubs.get(from) ?? from;
    const id = entityIds.get(name);
    if (id !== void 0) out[name] = id;
    else if (name === from) out[name] = oldId;
  }
  return { ...trait, entityRefIds: out };
}
function rewriteEntityTypedConfigValues(trait, entitySubs, entityIds) {
  if (entitySubs.size === 0 || !trait.config) return trait;
  let changed = false;
  const nextConfig = { ...trait.config };
  for (const [key, field] of Object.entries(trait.config)) {
    if (field.type !== "entity" || typeof field.default !== "string") continue;
    const to = entitySubs.get(field.default);
    if (to === void 0) continue;
    changed = true;
    nextConfig[key] = { ...field, default: to, refId: entityIds.get(to) };
  }
  return changed ? { ...trait, config: nextConfig } : trait;
}
function applyAliasEntityRenames(trait, subs, entityIds) {
  if (subs.size === 0) return trait;
  const rename = (name) => subs.get(name);
  const withTokens = (value) => renameEntityNameTokensInValue(value, subs);
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    ...t.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(t.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: t.effects ? withTokens(renameEntityInEffects(t.effects, rename, REBIND_ENTITY_PROPS)) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    ...tick.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(tick.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: withTokens(
      renameEntityInEffects(tick.effects, rename, REBIND_ENTITY_PROPS)
    )
  })) : trait.ticks;
  const next = {
    ...trait,
    ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
    ...nextTicks !== void 0 ? { ticks: nextTicks } : {}
  };
  return rewriteEntityRefIds(next, subs, entityIds);
}
function collectEntityNamesInTrait(trait, candidates) {
  const collected = /* @__PURE__ */ new Set();
  const collect = (name) => {
    if (candidates.has(name)) collected.add(name);
    return void 0;
  };
  const sm = trait.stateMachine;
  for (const t of sm?.transitions ?? []) {
    if (t.guard !== void 0) renameEntityInEffect(t.guard, collect, REBIND_ENTITY_PROPS);
    if (t.effects) renameEntityInEffects(t.effects, collect, REBIND_ENTITY_PROPS);
  }
  for (const tick of trait.ticks ?? []) {
    if (tick.guard !== void 0) renameEntityInEffect(tick.guard, collect, REBIND_ENTITY_PROPS);
    renameEntityInEffects(tick.effects, collect, REBIND_ENTITY_PROPS);
  }
  for (const l of trait.listens ?? []) {
    if (l.guard !== void 0) renameEntityInEffect(l.guard, collect, REBIND_ENTITY_PROPS);
    if (l.payloadMapping) walkEntityInPayloadMapping(l.payloadMapping, collect, REBIND_ENTITY_PROPS);
  }
  if (trait.initialEffects) {
    renameEntityInEffects(trait.initialEffects, collect, REBIND_ENTITY_PROPS);
  }
  return collected;
}
function collectRelationTargets(field, candidates, out) {
  if (field.type === "relation" && candidates.has(field.relation.entity)) out.add(field.relation.entity);
  if ((field.type === "array" || field.type === "object") && field.items) {
    collectRelationTargets(field.items, candidates, out);
  }
  if (field.properties) {
    for (const value of Object.values(field.properties)) collectRelationTargets(value, candidates, out);
  }
}
function relationTargetsOfEntity(entity, candidates) {
  const out = /* @__PURE__ */ new Set();
  for (const field of entity.fields) collectRelationTargets(field, candidates, out);
  return out;
}
function rewriteListenSources(trait, subs, idByName, orbitalRename, alias, listenSourceTargets) {
  const rewrite = (source) => rewriteImportSource(source, subs, idByName, orbitalRename, alias, listenSourceTargets);
  let changed = false;
  const nextListens = (trait.listens ?? []).map((listen) => {
    const moved = listen.source ? rewrite(listen.source) : void 0;
    if (!moved) return listen;
    changed = true;
    return { ...listen, source: moved };
  });
  const nextConfig = mapConfigEventAddresses(trait.config, (event, source) => {
    const moved = rewrite(source);
    return moved ? { event, source: moved } : void 0;
  });
  if (!changed && nextConfig === trait.config) return trait;
  return {
    ...trait,
    ...trait.listens ? { listens: nextListens } : {},
    ...nextConfig !== void 0 ? { config: nextConfig } : {}
  };
}
function rewriteImportSource(source, subs, idByName, orbitalRename, alias, listenSourceTargets) {
  if (source.kind === "trait") {
    const target = subs.get(source.trait);
    if (target === void 0 || target === source.trait) return void 0;
    const traitId = idByName.get(target);
    return { kind: "trait", trait: target, ...traitId ? { traitId } : {} };
  }
  if (source.kind === "orbital") {
    if (orbitalRename && source.orbital === orbitalRename.from) {
      const targetTrait = subs.get(source.trait) ?? source.trait;
      const traitId = idByName.get(targetTrait);
      return { kind: "orbital", orbital: orbitalRename.to, trait: targetTrait, ...traitId ? { traitId } : {} };
    }
    const info = alias && listenSourceTargets ? listenSourceTargets.get(siblingImportKey(alias, source.orbital)) : void 0;
    if (info) {
      const target = info.traitSubs.get(source.trait);
      return {
        kind: "orbital",
        orbital: info.localName,
        trait: target?.finalName ?? source.trait,
        ...target?.finalId ? { traitId: target.finalId } : {}
      };
    }
  }
  return void 0;
}
function mapConfigEventAddresses(config, f) {
  return renameEventsInDeclaredConfig(config, (value) => {
    if (value === void 0 || value.startsWith("@") || !value.includes(".")) return value;
    const { event, source } = splitEventAddress(value);
    if (!source || source.kind === "any") return value;
    const next = f(event, source);
    return next ? joinEventAddress(next.event, next.source) : value;
  });
}
function isInlineEntity(ref) {
  return !isEntityReference(ref) && !isEntityCall(ref);
}
function inlineEntityRefsOf(orbital) {
  return [orbital.entity, ...orbital.auxiliaryEntities ?? []].filter(isInlineEntity);
}
function entityByNameForAlias(imports, alias, entityName) {
  const imported = imports.orbitals.get(alias);
  if (!imported) return void 0;
  for (const o of importedOrbitals(imported)) {
    const found = inlineEntityRefsOf(o).find((e) => e.name === entityName);
    if (found) return found;
  }
  return void 0;
}
function entityByNameAnyAlias(imports, entityName) {
  const aliases = Array.from(imports.orbitals.keys()).sort();
  for (const alias of aliases) {
    const found = entityByNameForAlias(imports, alias, entityName);
    if (found) return found;
  }
  return void 0;
}
function ownedTraitEntryFor(imports, alias, traitName) {
  const imported = imports.orbitals.get(alias);
  if (!imported) return void 0;
  const owner = importedOrbitals(imported).find(
    (o) => findTraitEntryInOrbital(o, traitName) !== null
  );
  if (!owner || !isInlineEntity(owner.entity)) return void 0;
  return { owner, ownerEntity: owner.entity, entry: findTraitEntryInOrbital(owner, traitName) };
}
function boundTraitOrbitalBinding(imports, alias, traitName) {
  const imported = imports.orbitals.get(alias);
  if (imported?.resolvedOrbitals) {
    for (const ro of imported.resolvedOrbitals) {
      const entry = findTraitEntryInOrbital(ro.original, traitName);
      if (!entry) continue;
      const bound2 = entry.linkedEntity;
      const resolvedEntities = [ro.entity, ...ro.auxiliaryEntities ?? []];
      const boundEntity2 = bound2 !== void 0 ? resolvedEntities.find((e) => e.name === bound2) : void 0;
      const resolvedTrait = ro.traits.find((rt) => rt.trait.name === traitName)?.trait;
      const requiredData2 = new Set(resolvedTrait?.entityContract?.requires ?? []);
      return {
        owner: ro.original,
        ownerEntity: ro.entity,
        entity: boundEntity2 ?? ro.entity,
        requiredData: requiredData2,
        trait: resolvedTrait
      };
    }
  }
  const owned = ownedTraitEntryFor(imports, alias, traitName);
  if (!owned) return void 0;
  const ownerEntities = inlineEntityRefsOf(owned.owner);
  const bound = owned.entry?.linkedEntity;
  const boundEntity = bound !== void 0 ? ownerEntities.find((e) => e.name === bound) : void 0;
  const rawTrait = owned.entry && "stateMachine" in owned.entry ? owned.entry : void 0;
  const requiredData = new Set(rawTrait?.entityContract?.requires ?? []);
  return {
    owner: owned.owner,
    ownerEntity: owned.ownerEntity,
    entity: boundEntity ?? owned.ownerEntity,
    requiredData,
    trait: rawTrait
  };
}
function auxEntitiesFromUnrebindTraitRefs(orbitalTraits, imports) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (e) => {
    if (!seen.has(e.name)) {
      seen.add(e.name);
      out.push(e);
    }
  };
  for (const tr of orbitalTraits) {
    let ref;
    let linkedEntity;
    if (typeof tr === "string") {
      ref = tr;
    } else if ("ref" in tr) {
      ref = tr.ref;
      linkedEntity = tr.linkedEntity;
    } else {
      continue;
    }
    const parsed = parseImportedTraitRef(ref);
    if (!parsed) continue;
    const binding = boundTraitOrbitalBinding(imports, parsed.alias, parsed.traitName);
    if (!binding) continue;
    if (linkedEntity === void 0) {
      push(binding.entity);
    }
    for (const e of inlineEntityRefsOf(binding.owner)) {
      if (e.name !== binding.ownerEntity.name) push(e);
    }
  }
  return out;
}
function rewriteSelfRelationTarget(field, oldName, newName, newId) {
  if (field.type === "relation" && field.relation.entity === oldName) {
    field.relation = { ...field.relation, entity: newName, entityId: newId };
  }
  if (field.type === "array" && field.items) {
    rewriteSelfRelationTarget(field.items, oldName, newName, newId);
  }
  if (field.type === "object" && field.items) {
    rewriteSelfRelationTarget(field.items, oldName, newName, newId);
  }
  if (field.properties) {
    for (const key of Object.keys(field.properties)) {
      rewriteSelfRelationTarget(field.properties[key], oldName, newName, newId);
    }
  }
}
function collectEntityFieldRefs(node, referencedFields, setFields, site) {
  if (typeof node === "string" && node.startsWith("@entity.")) {
    const path = node.slice("@entity.".length);
    const field = (path.split(".")[0] ?? "").split("[")[0] ?? "";
    if (field !== "" && field !== "id" && field !== "createdAt" && field !== "updatedAt") {
      if (!referencedFields.has(field)) referencedFields.set(field, site);
    }
    return;
  }
  if (Array.isArray(node)) {
    if (node.length >= 2 && node[0] === "set" && typeof node[1] === "string" && node[1].startsWith("@entity.")) {
      const setterPath = node[1].slice("@entity.".length);
      const setterField = (setterPath.split(".")[0] ?? "").split("[")[0] ?? "";
      if (setterField !== "") setFields.add(setterField);
    }
    for (const item of node) collectEntityFieldRefs(item, referencedFields, setFields, site);
    return;
  }
  if (node !== null && typeof node === "object") {
    for (const value of Object.values(node)) {
      collectEntityFieldRefs(value, referencedFields, setFields, site);
    }
  }
}
function referencedEntityFieldNames(trait) {
  const referenced = /* @__PURE__ */ new Map();
  const setFields = /* @__PURE__ */ new Set();
  for (const transition of trait.stateMachine?.transitions ?? []) {
    if (transition.guard) collectEntityFieldRefs(transition.guard, referenced, setFields, "GUARD");
    for (const effect of transition.effects ?? []) {
      collectEntityFieldRefs(effect, referenced, setFields, "EFFECT");
    }
  }
  for (const tick of trait.ticks ?? []) {
    if (tick.guard) collectEntityFieldRefs(tick.guard, referenced, setFields, "TICK_GUARD");
    for (const effect of tick.effects ?? []) {
      collectEntityFieldRefs(effect, referenced, setFields, "TICK");
    }
  }
  for (const field of Object.values(trait.config ?? {})) {
    if (field.default !== void 0) {
      collectEntityFieldRefs(field.default, referenced, setFields, "CONFIG_DEFAULT");
    }
  }
  const out = new Set(referenced.keys());
  for (const f of setFields) out.add(f);
  return out;
}
function fetchIncludeLiterals(includeExpr) {
  if (!Array.isArray(includeExpr)) return [];
  const arr = includeExpr[0] === "list" ? includeExpr.slice(1) : includeExpr;
  return arr.filter((item) => typeof item === "string");
}
function resolveFetchIncludeField(entityDef, includeField) {
  const exact = entityDef.fields.find((f) => f.type === "relation" && f.name === includeField);
  if (exact?.name !== void 0) return exact.name;
  for (const f of entityDef.fields) {
    if (f.type !== "relation" || f.name === void 0) continue;
    const baseName = f.name.endsWith("Id") ? f.name.slice(0, -2) : f.name;
    if (baseName === includeField) return f.name;
  }
  return void 0;
}
function collectFetchPersistEntityFields(node, entityDef, out) {
  if (Array.isArray(node)) {
    if (node[0] === "fetch" && node.length >= 2 && node[1] === entityDef.name) {
      const opts = node[2];
      if (opts !== null && typeof opts === "object" && !Array.isArray(opts)) {
        const include = opts["include"];
        for (const literal of fetchIncludeLiterals(include)) {
          const resolved = resolveFetchIncludeField(entityDef, literal);
          if (resolved !== void 0) out.add(resolved);
        }
      }
    } else if (node[0] === "persist") {
      const entityIndex = typeof node[1] === "string" && (node[1] === "create" || node[1] === "update" || node[1] === "delete") ? 2 : 1;
      if (node[entityIndex] === entityDef.name) {
        const data = node[entityIndex + 1];
        if (data !== null && typeof data === "object" && !Array.isArray(data)) {
          for (const key of Object.keys(data)) out.add(key);
        }
      }
    }
    for (const item of node) collectFetchPersistEntityFields(item, entityDef, out);
    return;
  }
  if (node !== null && typeof node === "object") {
    for (const value of Object.values(node)) {
      collectFetchPersistEntityFields(value, entityDef, out);
    }
  }
}
function traitReferencesEntityFields(trait, entityDef) {
  const fields = referencedEntityFieldNames(trait);
  for (const transition of trait.stateMachine?.transitions ?? []) {
    if (transition.guard) collectFetchPersistEntityFields(transition.guard, entityDef, fields);
    for (const effect of transition.effects ?? []) {
      collectFetchPersistEntityFields(effect, entityDef, fields);
    }
  }
  for (const tick of trait.ticks ?? []) {
    if (tick.guard) collectFetchPersistEntityFields(tick.guard, entityDef, fields);
    for (const effect of tick.effects ?? []) {
      collectFetchPersistEntityFields(effect, entityDef, fields);
    }
  }
  for (const field of Object.values(trait.config ?? {})) {
    if (field.default !== void 0) collectFetchPersistEntityFields(field.default, entityDef, fields);
  }
  return fields;
}
function collectTraitReferencedEntityFields(trait, importedEntity) {
  const fields = traitReferencesEntityFields(trait, importedEntity);
  for (const r of trait.entityContract?.requires ?? []) fields.add(r);
  return fields;
}
function mergeImportedEntityFields(callerEntity, importedEntity, intrinsicOnly, requiredData, referencedFields) {
  const existingNames = new Set(
    callerEntity.fields.map((f) => f.name).filter((n) => n !== void 0)
  );
  const oldName = importedEntity.name;
  const newName = callerEntity.name;
  const newId = callerEntity.id;
  for (const field of importedEntity.fields) {
    if (intrinsicOnly && field.intrinsic !== true) continue;
    if (field.name === void 0) continue;
    if (requiredData.has(field.name) && field.intrinsic !== true) continue;
    if (!intrinsicOnly && !referencedFields.has(field.name)) continue;
    if (existingNames.has(field.name)) continue;
    const cloned = structuredClone(field);
    rewriteSelfRelationTarget(cloned, oldName, newName, newId);
    cloned.mergedFrom = oldName;
    callerEntity.fields.push(cloned);
  }
}
function mergeImportedEntityFieldsIntoOrbital(orbitalTraits, entity, declaredAuxEntities, imports) {
  const mergedAliases = /* @__PURE__ */ new Set();
  const pending = [];
  for (const tr of orbitalTraits) {
    let ref;
    let linkedEntity;
    if (typeof tr === "string") {
      ref = tr;
    } else if ("ref" in tr) {
      ref = tr.ref;
      linkedEntity = tr.linkedEntity;
    } else {
      continue;
    }
    const parsed = parseImportedTraitRef(ref);
    if (!parsed) continue;
    const binding = boundTraitOrbitalBinding(imports, parsed.alias, parsed.traitName);
    if (!binding) continue;
    const carriesFields = linkedEntity !== void 0 ? true : binding.entity.name === entity.name;
    if (!carriesFields) continue;
    const key = `${parsed.alias}\0${parsed.traitName}`;
    if (mergedAliases.has(key)) continue;
    mergedAliases.add(key);
    pending.push({
      target: linkedEntity,
      imported: binding.entity,
      intrinsicOnly: linkedEntity === void 0,
      requiredData: binding.requiredData,
      trait: binding.trait
    });
  }
  const deferred = [];
  for (const pm of pending) {
    const referencedFields = pm.intrinsicOnly || pm.trait === void 0 ? /* @__PURE__ */ new Set() : collectTraitReferencedEntityFields(pm.trait, pm.imported);
    if (pm.target === void 0 || pm.target === entity.name) {
      refResolverLog.debug("entity-merge", {
        into: entity.name,
        from: pm.imported.name,
        target: pm.target,
        intrinsicOnly: pm.intrinsicOnly,
        fields: pm.imported.fields.map((f) => f.name)
      });
      mergeImportedEntityFields(entity, pm.imported, pm.intrinsicOnly, pm.requiredData, referencedFields);
      continue;
    }
    const auxTarget = declaredAuxEntities.find((e) => e.name === pm.target);
    if (auxTarget) {
      refResolverLog.debug("entity-merge", {
        into: auxTarget.name,
        from: pm.imported.name,
        target: pm.target,
        intrinsicOnly: pm.intrinsicOnly,
        fields: pm.imported.fields.map((f) => f.name)
      });
      mergeImportedEntityFields(auxTarget, pm.imported, pm.intrinsicOnly, pm.requiredData, referencedFields);
      continue;
    }
    refResolverLog.debug("entity-merge:deferred", {
      from: pm.imported.name,
      target: pm.target,
      intrinsicOnly: pm.intrinsicOnly
    });
    deferred.push({
      target: pm.target,
      imported: pm.imported,
      intrinsicOnly: pm.intrinsicOnly,
      requiredData: pm.requiredData,
      referencedFields
    });
  }
  return deferred;
}
function applyDeferredEntityFieldMerges(resolved) {
  for (const orbital of resolved) {
    for (const pm of orbital.pendingEntityFieldMerges ?? []) {
      let landed = false;
      for (const candidate of resolved) {
        if (candidate.entity.name === pm.target) {
          mergeImportedEntityFields(candidate.entity, pm.imported, pm.intrinsicOnly, pm.requiredData, pm.referencedFields);
          landed = true;
          break;
        }
        const auxMatch = (candidate.auxiliaryEntities ?? []).find((e) => e.name === pm.target);
        if (auxMatch) {
          mergeImportedEntityFields(auxMatch, pm.imported, pm.intrinsicOnly, pm.requiredData, pm.referencedFields);
          landed = true;
          break;
        }
      }
      refResolverLog.debug(landed ? "entity-merge:deferred-landed" : "entity-merge:unresolved", {
        from: pm.imported.name,
        target: pm.target
      });
    }
  }
}
function consumerEntityIdsOf(orbitals) {
  const out = /* @__PURE__ */ new Map();
  for (const orbital of orbitals) {
    for (const entity of inlineEntityRefsOf(orbital)) {
      if (entity.id) out.set(entity.name, entity.id);
    }
  }
  return out;
}
function materializedPrimaryEntity(upstreamEntity, upstreamOrbitalId, ref, localName) {
  const name = ref.entity ?? `${localName}${upstreamEntity.name}`;
  const id = asEntityId(
    deriveMaterializedId(upstreamEntity.id, upstreamOrbitalId, upstreamEntity.name, localName, "entity")
  );
  return { name, id };
}
function materializedTraitSubs(upstreamId, traits, ref, localName) {
  const onlySet = ref.only ? new Set(ref.only) : void 0;
  const omitSet = new Set(ref.omit ?? []);
  const out = /* @__PURE__ */ new Map();
  for (const rt of traits) {
    const name = rt.trait.name;
    if (!name) continue;
    const keep = onlySet ? onlySet.has(name) : !omitSet.has(name);
    if (!keep) continue;
    out.set(name, {
      finalName: `${localName}${name}`,
      finalId: asTraitId(deriveMaterializedId(rt.trait.id, upstreamId, name, localName, "trait"))
    });
  }
  return out;
}
function addressImportedTraits(orbitals, imported) {
  if (imported.size === 0) return;
  const present = /* @__PURE__ */ new Set();
  for (const o of orbitals) {
    for (const t of o.traits ?? []) if (isInlineTrait(t) && t.name) present.add(`${o.name} ${t.name}`);
  }
  for (const orbital of orbitals) {
    const resolve = (source) => {
      if (source.kind === "any") return void 0;
      const scope = source.kind === "orbital" ? source.orbital : orbital.name;
      if (present.has(`${scope} ${source.trait}`)) return void 0;
      const target = imported.get(scope)?.get(source.trait);
      if (!target) return void 0;
      return { ...source, trait: target.finalName, ...target.finalId ? { traitId: target.finalId } : {} };
    };
    orbital.traits = (orbital.traits ?? []).map((t) => {
      if (!isInlineTrait(t)) return t;
      const trait = t;
      let changed = false;
      const listens = (trait.listens ?? []).map((l) => {
        const moved = l.source ? resolve(l.source) : void 0;
        if (!moved) return l;
        changed = true;
        return { ...l, source: moved };
      });
      const config = mapConfigEventAddresses(trait.config, (event, source) => {
        const moved = resolve(source);
        return moved ? { event, source: moved } : void 0;
      });
      if (!changed && config === trait.config) return t;
      return {
        ...trait,
        ...trait.listens ? { listens } : {},
        ...config !== void 0 ? { config } : {}
      };
    });
  }
}
function siblingImportKey(alias, upstreamOrbitalName) {
  return `${alias}\0${upstreamOrbitalName}`;
}
function applyExtendFields(entity, extend, fieldSubs, consumerEntityIds, localName) {
  if (!extend || extend.length === 0) return { entity, errors: [] };
  const errors = [];
  const taken = new Set(entity.fields.map((f) => f.name).filter((n) => Boolean(n)));
  for (const target of fieldSubs.values()) taken.add(target);
  const added = [];
  for (const field of extend) {
    const name = field.name;
    if (!name) continue;
    if (taken.has(name)) {
      errors.push(
        `Orbital "${localName}" declares extend field "${name}" but the upstream primary entity already has a field of that name after "fields {}" renames (ORB_O_EXTEND_FIELD_COLLISION)`
      );
      continue;
    }
    taken.add(name);
    if (field.type === "relation" && !field.relation.entityId) {
      const targetId = consumerEntityIds.get(field.relation.entity);
      added.push(targetId ? { ...field, relation: { ...field.relation, entityId: targetId } } : field);
      continue;
    }
    added.push(field);
  }
  if (added.length === 0) return { entity, errors };
  return { entity: { ...entity, fields: [...entity.fields, ...added] }, errors };
}
function applyRetypeAndPersistence(entity, ref, consumerEntityIds, localName) {
  const errors = [];
  let fields = [...entity.fields];
  const extended = new Set((ref.extend ?? []).map((f) => f.name));
  const seen = /* @__PURE__ */ new Set();
  for (const rf of ref.retype ?? []) {
    const name = rf.name;
    if (!name) continue;
    if (extended.has(name) || seen.has(name)) {
      errors.push(
        `Orbital "${localName}" retypes field "${name}", which is ${extended.has(name) ? "also added by `extend {}`" : "retyped more than once"} (ORB_O_RETYPE_FIELD_CONFLICT)`
      );
      continue;
    }
    seen.add(name);
    const index = fields.findIndex((f) => f.name === name);
    if (index < 0) {
      errors.push(`Orbital "${localName}" retypes field "${name}", which the upstream primary entity does not have after "fields {}" renames (ORB_O_RETYPE_UNKNOWN_FIELD)`);
      continue;
    }
    const old = fields[index];
    const relation = rf.type === "relation" && !rf.relation.entityId ? consumerEntityIds.get(rf.relation.entity) : void 0;
    const retyped = {
      ...rf,
      ...relation !== void 0 && rf.type === "relation" ? { relation: { ...rf.relation, entityId: relation } } : {},
      ...rf.description === void 0 && old.description !== void 0 ? { description: old.description } : {},
      ...rf.synonyms === void 0 && old.synonyms !== void 0 ? { synonyms: old.synonyms } : {},
      ...rf.mock === void 0 && old.mock !== void 0 ? { mock: old.mock } : {},
      ...rf.primaryKey === void 0 && old.primaryKey !== void 0 ? { primaryKey: old.primaryKey } : {},
      ...rf.intrinsic === void 0 && old.intrinsic !== void 0 ? { intrinsic: old.intrinsic } : {},
      ...old.mergedFrom !== void 0 ? { mergedFrom: old.mergedFrom } : {}
    };
    fields = fields.map((f, i) => i === index ? retyped : f);
  }
  let next = { ...entity, fields };
  if (ref.persistence !== void 0 || ref.collection !== void 0) {
    if (entity.persistence === void 0 || entity.persistence === "persistent") {
      next = {
        ...next,
        ...ref.persistence !== void 0 ? { persistence: ref.persistence } : {},
        ...ref.collection !== void 0 ? { collection: ref.collection } : {}
      };
    } else {
      errors.push(
        `Orbital "${localName}" overrides the persistence of upstream entity "${entity.name}", declared \`${entity.persistence}\` \u2014 only a persistent entity takes an override (ORB_O_ENTITY_PERSISTENCE_LOCKED)`
      );
    }
  }
  if (ref.shared) next = { ...next, shared: true };
  if (ref.identity) next = { ...next, identity: true };
  if (ref.local) next = { ...next, local: true };
  if (ref.seedMock) next = { ...next, seedMock: true };
  return { entity: next, errors };
}
function applyImportTraitOverrides(kept, upstreamNames, removedNames, ref, consumerEntityIds, localName) {
  const overridden = /* @__PURE__ */ new Map();
  const errors = [];
  const entries = ref.traits ?? {};
  for (const name of Object.keys(entries).sort()) {
    const ov = entries[name];
    if (!upstreamNames.has(name)) {
      errors.push(`Orbital "${localName}" overrides trait "${name}", which is not a trait of the imported orbital (ORB_O_TRAITS_UNKNOWN_TRAIT)`);
      continue;
    }
    if (removedNames.has(name)) {
      errors.push(`Orbital "${localName}" overrides trait "${name}", which omit {}/only {} dropped (ORB_O_TRAITS_OMITTED_TRAIT)`);
      continue;
    }
    const trait = kept.find((t) => t.name === name);
    if (!trait || !ov) continue;
    for (const key of Object.keys(ov.config ?? {}).sort()) {
      const value = ov.config?.[key];
      if (!(key in (trait.config ?? {})) && !(value !== void 0 && isConfigRedeclaration(value))) {
        errors.push(`Orbital "${localName}" sets traits(${name}).config(${key}), a knob trait "${name}" does not declare (ORB_O_CONFIG_UNKNOWN_KEY)`);
      }
    }
    const applied = applyTraitCallSiteOverrides(trait, ov, ov.linkedEntity !== void 0 ? consumerEntityIds : /* @__PURE__ */ new Map());
    if (!applied.ok) {
      errors.push(...applied.errors);
      continue;
    }
    overridden.set(name, applied.trait);
  }
  return { overridden, errors };
}
function weakensPageAccess(page, mods) {
  return mods?.access === "public" && page.access === "authenticated";
}
function withPageModifiers(page, mods) {
  if (!mods) return page;
  return {
    ...page,
    ...mods.access !== void 0 ? { access: mods.access } : {},
    ...mods.indexing !== void 0 ? { indexing: mods.indexing } : {},
    ...mods.title !== void 0 ? { title: mods.title } : {},
    ...mods.description !== void 0 ? { description: mods.description } : {},
    ...mods.translationOf !== void 0 ? { translationOf: mods.translationOf } : {},
    ...mods.label !== void 0 ? { label: mods.label } : {},
    ...mods.icon !== void 0 ? { icon: mods.icon } : {},
    ...mods.roles !== void 0 ? { roles: [...mods.roles] } : {}
  };
}
function deriveMaterializedId(sourceId, upstreamOrbitalId, sourceName, localName, kind) {
  const parent = sourceId ?? `${idPrefix(kind)}orbital-import-seed:${upstreamOrbitalId ?? "no-orbital-id"}:${sourceName}`;
  return deriveId(parent, localName);
}
function navigatePathSegments(path) {
  return path.split("/").filter((segment) => segment.length > 0);
}
function navigateParamStrippedPrefix(path) {
  const segments = navigatePathSegments(path);
  const last = segments[segments.length - 1];
  if (last === void 0 || !last.startsWith(":")) return void 0;
  return `/${segments.slice(0, -1).join("/")}/`;
}
function rewriteConcatPrefix(literalPrefix, pathMap) {
  const normalized = literalPrefix.endsWith("/") ? literalPrefix : `${literalPrefix}/`;
  for (const [upstreamPath, finalPath] of pathMap) {
    const stripped = navigateParamStrippedPrefix(upstreamPath);
    if (stripped === void 0 || stripped !== normalized) continue;
    const finalStripped = navigateParamStrippedPrefix(finalPath);
    if (finalStripped !== void 0) return finalStripped;
  }
  return void 0;
}
function rewriteNavigateTargets(node, pathMap) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    const arr = node;
    if (arr[0] === "navigate" && arr.length >= 2) {
      const target = arr[1];
      if (typeof target === "string" && pathMap.has(target)) {
        return [arr[0], pathMap.get(target), ...arr.slice(2).map((v) => rewriteNavigateTargets(v, pathMap))];
      }
      if (Array.isArray(target) && target[0] === "str/concat" && typeof target[1] === "string" && target[1].startsWith("/")) {
        const rewrittenPrefix = rewriteConcatPrefix(target[1], pathMap);
        const nextTarget = rewrittenPrefix !== void 0 ? [target[0], rewrittenPrefix, ...target.slice(2)] : target;
        return [arr[0], nextTarget, ...arr.slice(2).map((v) => rewriteNavigateTargets(v, pathMap))];
      }
    }
    return arr.map((item) => rewriteNavigateTargets(item, pathMap));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = rewriteNavigateTargets(value, pathMap);
  }
  return next;
}
function rewriteTraitNavigateTargets(trait, pathMap) {
  if (pathMap.size === 0) return trait;
  const rewrite = (v) => rewriteNavigateTargets(v, pathMap);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map(
        (t) => t.effects ? { ...t, effects: rewrite(t.effects) } : t
      )
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({ ...tick, effects: rewrite(tick.effects) }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  return next;
}
function foldUseConfigOverride(declared, override, context) {
  if (!override) return declared;
  const values = {};
  for (const [key, field] of Object.entries(override)) {
    if (!(key in declared)) {
      refResolverLog.warn("use-config:unknown-knob", { ...context, key });
      continue;
    }
    if (field.default !== void 0) values[key] = field.default;
  }
  return Object.keys(values).length > 0 ? overrideDeclaredKnobs(declared, values) : declared;
}
function forwardConsumerAppKnobs(folded, consumer) {
  if (!consumer) return folded;
  const forwards = {};
  for (const [key, field] of Object.entries(folded)) {
    const token = field.default;
    if (typeof token !== "string" || !token.startsWith("@config.")) continue;
    const source = token.slice("@config.".length);
    if (source.includes(".") || !(source in consumer)) continue;
    if (consumer[source].default === void 0) continue;
    forwards[key] = source;
  }
  if (Object.keys(forwards).length === 0) return folded;
  const next = { ...folded };
  for (const [key, source] of Object.entries(forwards)) {
    next[key] = { ...folded[key], default: consumer[source].default, forwardedFrom: `@config.${source}` };
  }
  return next;
}
var ReferenceResolver = class _ReferenceResolver {
  /** `entities {}` targets awaiting the flattened program, keyed by the flattening call's consumer-entity map. */
  pendingEntityTargets = /* @__PURE__ */ new Map();
  loader;
  options;
  localTraits;
  /** id-keyed mirror of `localTraits`, populated wherever the trait carries an `id`. */
  localTraitsById = /* @__PURE__ */ new Map();
  /** Import scope of each loaded source orbital, keyed by its source path. */
  sourceImportsCache = /* @__PURE__ */ new Map();
  /**
   * {@link preResolveImportFile}'s cache, keyed by absolute source path —
   * `null` for a file whose own pre-resolution failed (best-effort, cached
   * too, so a repeat import doesn't retry a doomed resolve). Distinct from
   * {@link sourceImportsCache} (raw import SCOPE, no trait resolution) —
   * this holds the file's fully RESOLVED orbitals.
   */
  importFileResolveCache = /* @__PURE__ */ new Map();
  loaderInitialized = false;
  /** The schema's declared `config {}` — outermost rung of the forwarded-config chain (§4.5). */
  schemaConfig;
  /**
   * Schema-wide CONSUMER entity NAME → id, growing as each of THIS
   * schema's own orbitals finishes resolving. JS twin of the compiled
   * path's `consumer_entity_ids` (`orbital-compiler/src/phases/inline/
   * mod.rs`), extended the same way: once per orbital, right after it
   * resolves. Extended ONLY by {@link noteResolvedEntityIds} — called by
   * the free function {@link resolveSchema} after each of ITS OWN orbitals
   * finishes — never automatically inside `resolve()` itself, so the
   * nested `this.resolve()` call {@link materializeOrbitalRef} makes for an
   * external upstream orbital (a DIFFERENT file, never this consumer
   * schema) cannot pollute it.
   */
  schemaEntityIds = /* @__PURE__ */ new Map();
  /**
   * Entity NAME → id visible to the orbital CURRENTLY being resolved:
   * {@link schemaEntityIds} (every name an EARLIER orbital in this schema
   * already established) plus the current orbital's own declared primary +
   * auxiliary entities — JS twin of the compiled path's per-orbital `entity_
   * ids = consumer_entity_ids.clone(); entity_ids.extend(collect_orbital_
   * entity_ids(orbital))`, timed the same way (before this orbital's own
   * trait composition runs). Read by {@link applyLinkedEntityRename}'s call
   * sites so a call-site `-> Entity` rebind can REFRESH (never merely drop)
   * the trait's `entityRefIds` side-map when the rebind target's id is
   * already known — the gap `std-api-gateway` exposed: `RouteOrbital`'s
   * no-rebind `Audit.traits.AuditCaptureListener` pull establishes
   * `AuditEntry`'s id; `GatewayUserOrbital`, resolved LATER in the same
   * schema, rebinds `Browse.traits.BrowseItemBrowse -> AuditEntry` and must
   * see it. `resolve()` re-sets this fresh at its own entry (no restore
   * needed once it returns): nothing reads it once `resolve()` has
   * returned, and no `resolve()` call is ever active while another is on
   * this instance — the only recursive `this.resolve()` call
   * (`materializeOrbitalRef`'s upstream re-resolve) runs strictly BEFORE
   * `resolveSchema`'s own per-orbital loop begins, never nested inside it.
   */
  entityIdsInScope = /* @__PURE__ */ new Map();
  /**
   * Per-orbital name → the FINAL trait names {@link pullSiblingTraits}
   * auto-pulled for it (`ResolvedOrbital.name` keyed, set once per `resolve()`
   * call). JS twin of Rust's `pulled_by_orbital` (`inline/mod.rs`) — read by
   * {@link uniquifyCrossOrbitalPulledSiblings}, run once by the free
   * function {@link resolveSchema} AFTER every orbital in the schema has
   * resolved, because the rename decision (does this pulled name collide
   * with ANOTHER orbital's own declared trait name?) needs the WHOLE
   * schema's roster, not just this orbital's — exactly why Rust's own
   * `uniquify_cross_orbital_pulled_siblings` runs once, after its per-orbital
   * inline loop, not inside `inline_orbital` itself.
   */
  pulledTraitNamesByOrbital = /* @__PURE__ */ new Map();
  /**
   * Seed {@link schemaEntityIds} with EVERY orbital's PRIMARY (+ any
   * already-declared auxiliary) entity name → id, from the RAW pre-resolve
   * schema — JS twin of the compiled path's `consumer_entity_ids` INITIAL
   * build (`orbital-compiler/src/phases/inline/mod.rs`, `schema.orbitals
   * .iter().flat_map(...)`), which scans ALL orbitals' primaries up front,
   * before ANY of them inlines — not just the ones resolved so far. Without
   * this, an EARLY-resolved orbital's own `entityRefIds` secondary
   * references to a LATER orbital's primary entity (`RouteOrbital`'s
   * `entityRefIds["RateBucket"]`, `RateBucket` being a DIFFERENT, not-yet-
   * resolved orbital's own primary in `std-api-gateway`) would see nothing
   * in {@link entityIdsInScope} and {@link consumerKnownEntityRebindSubs}
   * would never refresh the stale atom-baked id. Called by {@link
   * resolveSchema} ONCE, before its per-orbital loop begins; {@link
   * noteResolvedEntityIds} then keeps widening past this seed with each
   * orbital's post-resolve auxiliary discoveries (Gap #22, sibling pulls),
   * same as before.
   */
  seedSchemaEntityIds(orbitals) {
    const next = new Map(this.schemaEntityIds);
    for (const orbital of orbitals) {
      for (const e of inlineEntityRefsOf(orbital)) {
        if (e.id) next.set(e.name, e.id);
      }
    }
    this.schemaEntityIds = next;
  }
  /**
   * Extend {@link schemaEntityIds} with a just-resolved orbital's FINAL
   * entity set — primary + every auxiliary entity resolution carried in
   * (including a no-rebind sibling pull's own bound entity,
   * `ResolvedOrbital.auxiliaryEntities`). Mirrors the compiled path's
   * `consumer_entity_ids.extend(collect_orbital_entity_ids(orbital))`,
   * called the same way: once per orbital, immediately after it resolves.
   * Called by {@link resolveSchema}'s own per-orbital loop only.
   */
  noteResolvedEntityIds(resolved) {
    const next = new Map(this.schemaEntityIds);
    if (resolved.entity.id) next.set(resolved.entity.name, resolved.entity.id);
    for (const aux of resolved.auxiliaryEntities ?? []) {
      if (aux.id) next.set(aux.name, aux.id);
    }
    this.schemaEntityIds = next;
  }
  /**
   * Make auto-pulled sibling traits app-unique across orbitals — JS twin of
   * Rust's `uniquify_cross_orbital_pulled_siblings` (`inline/mod.rs`), called
   * by the free function {@link resolveSchema} once every orbital in the
   * schema has resolved (mirroring Rust's own post-per-orbital-loop timing:
   * the rename decision needs the WHOLE schema's trait-name roster, built
   * fresh here rather than grown incrementally like {@link schemaEntityIds}).
   * An auto-pulled sibling keeps its source name within its OWN orbital, so a
   * `@trait.<Sibling>` reference inside that orbital still resolves — but
   * when TWO orbitals in the same schema each declare (pulled OR explicit) a
   * trait of the SAME name, that name collides app-wide. Only renames a
   * PULLED copy (never an explicitly-authored trait ref —
   * {@link pulledTraitNamesByOrbital} is the eligibility set) whose name is
   * ALSO declared by at least one OTHER orbital, prefixing it with the
   * owning orbital's own name; the prefixed name must itself be free
   * schema-wide or the rename is skipped (matches Rust: a collision on the
   * PREFIXED name is left for the validator, never silently double-renamed).
   * Mutates `orbitals`' traits in place, same mutation style as
   * {@link pullSiblingTraits}.
   */
  uniquifyCrossOrbitalPulledSiblings(orbitals) {
    if (this.pulledTraitNamesByOrbital.size === 0) return;
    const nameOrbitalCount = /* @__PURE__ */ new Map();
    for (const orbital of orbitals) {
      const local = new Set(
        orbital.traits.map((rt) => rt.trait.name).filter((n) => Boolean(n))
      );
      for (const name of local) {
        nameOrbitalCount.set(name, (nameOrbitalCount.get(name) ?? 0) + 1);
      }
    }
    for (const orbital of orbitals) {
      const pulledNames = this.pulledTraitNamesByOrbital.get(orbital.name);
      if (!pulledNames || pulledNames.size === 0) continue;
      const renames = /* @__PURE__ */ new Map();
      for (const oldName of pulledNames) {
        if ((nameOrbitalCount.get(oldName) ?? 0) <= 1) continue;
        const newName = `${orbital.name}${oldName}`;
        if (nameOrbitalCount.has(newName)) continue;
        renames.set(oldName, newName);
      }
      if (renames.size === 0) continue;
      for (const rt of orbital.traits) {
        if (rt.trait.name && renames.has(rt.trait.name)) {
          rt.trait = { ...rt.trait, name: renames.get(rt.trait.name) };
        }
      }
      const idByName = /* @__PURE__ */ new Map();
      for (const rt of orbital.traits) {
        if (rt.trait.name) idByName.set(rt.trait.name, rt.trait.id);
      }
      for (const rt of orbital.traits) {
        rt.trait = renameTraitEmbeds(rt.trait, renames);
        rt.trait = rewriteListenSources(rt.trait, renames, idByName);
      }
    }
  }
  constructor(options) {
    this.options = options;
    this.loader = options.loader;
    this.schemaConfig = options.schemaConfig;
    this.localTraits = options.localTraits ?? /* @__PURE__ */ new Map();
    for (const trait of this.localTraits.values()) {
      if (trait.id) {
        this.localTraitsById.set(trait.id, trait);
      }
    }
  }
  async ensureLoader() {
    if (this.loader || this.loaderInitialized) return;
    this.loaderInitialized = true;
    try {
      const { ExternalOrbitalLoader } = await import('./external-loader-2MKPT5PS.js');
      this.loader = new ExternalOrbitalLoader(this.options);
    } catch {
    }
  }
  /**
   * Resolve all references in an orbital.
   */
  async resolve(orbital, sourcePath, chain, opts) {
    const errors = [];
    const warnings = [];
    const importChain = chain ?? { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    this.entityIdsInScope = new Map([...this.schemaEntityIds, ...inlineEntityRefsOf(orbital).flatMap((e) => e.id ? [[e.name, e.id]] : [])]);
    const traitsList = orbital.traits ?? [];
    const alreadyResolved = traitsList.length > 0 && traitsList.every((t) => isInlineTrait(t));
    const importsResult = alreadyResolved ? { success: true, data: { orbitals: /* @__PURE__ */ new Map(), idIndex: /* @__PURE__ */ new Map() }} : await this.resolveImports(orbital.uses ?? [], sourcePath, importChain);
    if (!importsResult.success) {
      return { success: false, errors: importsResult.errors };
    }
    const imports = importsResult.data;
    imports.idIndex = buildIdIndex(orbital, imports.orbitals);
    const gap22AuxEntities = auxEntitiesFromUnrebindTraitRefs(orbital.traits ?? [], imports);
    if (gap22AuxEntities.length > 0) {
      const ownNames = new Set(inlineEntityRefsOf(orbital).map((e) => e.name));
      const next = new Map(this.entityIdsInScope);
      const pulledNames2 = /* @__PURE__ */ new Set();
      for (const e of gap22AuxEntities) {
        if (e.id && !ownNames.has(e.name) && !pulledNames2.has(e.name)) {
          next.set(e.name, e.id);
          pulledNames2.add(e.name);
        }
      }
      this.entityIdsInScope = next;
    }
    let pendingEntityFieldMerges;
    if (isInlineEntity(orbital.entity)) {
      pendingEntityFieldMerges = mergeImportedEntityFieldsIntoOrbital(
        orbital.traits ?? [],
        orbital.entity,
        (orbital.auxiliaryEntities ?? []).filter(isInlineEntity),
        imports
      );
    }
    const entityResult = this.resolveEntity(orbital.entity, imports);
    if (!entityResult.success) {
      errors.push(...entityResult.errors);
    }
    const traitsResult = await this.resolveTraits(orbital.traits, imports, importChain, orbital.config);
    if (!traitsResult.success) {
      errors.push(...traitsResult.errors);
    }
    const pagesResult = await this.resolvePages(orbital.pages, imports, importChain);
    if (!pagesResult.success) {
      errors.push(...pagesResult.errors);
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    if (!entityResult.success || !traitsResult.success || !pagesResult.success) {
      return { success: false, errors: ["Internal error: unexpected failure state"] };
    }
    const { errors: pullErrors, auxEntities: pulledAuxEntities, pulledNames } = await this.pullSiblingTraits(
      traitsResult.data,
      imports,
      importChain,
      orbital.config
    );
    if (pullErrors.length > 0) {
      return { success: false, errors: pullErrors };
    }
    this.pulledTraitNamesByOrbital.set(orbital.name, pulledNames);
    const known = /* @__PURE__ */ new Set([entityResult.data.entity.name]);
    const declaredAux = (orbital.auxiliaryEntities ?? []).filter(isInlineEntity);
    for (const e of declaredAux) known.add(e.name);
    const combined = [...declaredAux];
    for (const e of [...gap22AuxEntities, ...pulledAuxEntities]) {
      if (!known.has(e.name)) {
        known.add(e.name);
        combined.push(e);
      }
    }
    const resolvedAuxEntities = combined.length > 0 ? combined : void 0;
    for (const resolvedTrait of traitsResult.data) {
      resolvedTrait.trait = resolveEntityTokensById(resolvedTrait.trait, imports.idIndex);
      resolvedTrait.trait = resolveConfigRefsById(resolvedTrait.trait, imports.idIndex);
    }
    try {
      spliceLambdaTraitRefs(traitsResult.data, pagesResult.data);
    } catch (e) {
      if (e instanceof LambdaSpliceError) {
        return { success: false, errors: [e.message] };
      }
      throw e;
    }
    if (!opts?.skipTypeParamSentinels) {
      resolveOrbitalTypeParamSentinels(
        traitsResult.data,
        entityResult.data.entity,
        resolvedAuxEntities ?? [],
        orbital.types
      );
    }
    return {
      success: true,
      data: {
        name: orbital.name,
        entity: entityResult.data.entity,
        entitySource: entityResult.data.source,
        traits: traitsResult.data,
        ...resolvedAuxEntities ? { auxiliaryEntities: resolvedAuxEntities } : {},
        ...pendingEntityFieldMerges && pendingEntityFieldMerges.length > 0 ? { pendingEntityFieldMerges } : {},
        pages: pagesResult.data,
        imports,
        original: orbital
      },
      warnings
    };
  }
  /**
   * Pre-resolve one imported FILE's own orbitals to completion — its own
   * `uses` (recursively, through THIS same method), its own trait refs,
   * its own sibling pulls, its own entity-ref-id rebinds — BEFORE the
   * consumer that names this alias ever folds or pulls from it. JS twin
   * of Rust's per-`uses` recursive `inline_orbital` call (`orbital-
   * compiler/src/phases/inline/mod.rs:1001-1013`): every `uses` alias's
   * OWN orbitals are inlined to completion, ahead of `ctx.add_alias_multi`
   * registering the alias for the consumer's own lookups. Without this, a
   * sibling-pull collision that is a genuine top-level PEER collision
   * INSIDE the imported file (`std-approval-request.orb`'s
   * `InlineBrowseItemBrowse6`/`…10`, both `ref: Dense.traits.
   * BrowseItemBrowse`, each embedding `@trait.DataGrid1`) never becomes
   * visible as a peer collision to the CONSUMER's own {@link
   * pullSiblingTraits} call — it arrives already flattened one level
   * deeper, sharing one JS-visible owner (see that method's own
   * drain-order doc for the full trace this ports).
   *
   * Cached per absolute `sourcePath` ({@link importFileResolveCache}) —
   * "a file imported twice resolves once" — so the cache must not bake in
   * any ONE call site's `uses { config }` override: the child resolver
   * below is seeded with this FILE's own raw, un-folded `config {}`
   * (`fileSchemaConfig`), never a folded override. A genuinely
   * call-site-configured explicit ref (`Alias.traits.X { config: {…} }`)
   * still resolves through the pre-existing lazy path
   * ({@link resolveTraitEntry} / {@link resolveTraitRefString}), which
   * this cache does not touch.
   *
   * Runs on a FRESH child `ReferenceResolver`, never `this` — `resolve()`'s
   * own `entityIdsInScope` / `schemaEntityIds` / `pulledTraitNamesByOrbital`
   * are single mutable instance state scoped to ONE active `resolve()` call
   * tree (see {@link entityIdsInScope}'s own doc: "no `resolve()` call is
   * ever active while another is on this instance"); reusing `this` here
   * would nest a `resolve()` call inside the CONSUMER's own still-active
   * `resolve()` → `resolveImports` call and corrupt that state once this
   * nested call returns. The child reuses `this.loader` (and therefore its
   * underlying `LoaderCache`) so raw parsing still dedupes exactly as
   * before. `chain` is threaded through unchanged (not cloned, matching
   * every other cross-call `chain` hand-off in this file) so genuine `uses`
   * cycles are still caught by the loader.
   *
   * Mirrors {@link resolveSchema}'s own orchestration (flatten orbital
   * references → collect local traits → seed schema entity ids →
   * per-orbital `resolve()` → `uniquifyCrossOrbitalPulledSiblings`) rather
   * than calling that free function directly — it accepts no `chain`
   * parameter, so a direct call would silently drop cycle protection one
   * level in.
   *
   * Best-effort: returns `null` (never throws) on any failure — a caller
   * falls back to the pre-existing raw-ref walk, matching this file's
   * other best-effort pre-passes (e.g. {@link precomputeReferenceFormPrimaryIds}).
   */
  async preResolveImportFile(fileOrbitals, sourcePath, fileSchemaConfig, chain) {
    const cached = this.importFileResolveCache.get(sourcePath);
    if (cached !== void 0) return cached;
    let result;
    try {
      result = await this.preResolveImportFileUncached(fileOrbitals, sourcePath, fileSchemaConfig, chain);
    } catch {
      result = null;
    }
    this.importFileResolveCache.set(sourcePath, result);
    return result;
  }
  async preResolveImportFileUncached(fileOrbitals, sourcePath, fileSchemaConfig, chain) {
    const child = new _ReferenceResolver({
      ...this.options,
      loader: this.loader,
      schemaConfig: fileSchemaConfig
    });
    const fileSchema = {
      name: sourcePath,
      orbitals: fileOrbitals,
      ...fileSchemaConfig ? { config: fileSchemaConfig } : {}
    };
    const flattenResult = await child.resolveOrbitalImports(fileSchema, { skipTypeParamSentinels: true });
    if (!flattenResult.success) return null;
    const orbitals = flattenResult.data;
    const inlineTraits = orbitals.flatMap(
      (o) => o.traits.filter((t) => typeof t !== "string" && "stateMachine" in t)
    );
    child.addLocalTraits(inlineTraits);
    child.seedSchemaEntityIds(orbitals);
    const resolved = [];
    for (const orbital of orbitals) {
      const result = await child.resolve(orbital, sourcePath, chain, { skipTypeParamSentinels: true });
      if (!result.success) return null;
      resolved.push(result.data);
      child.noteResolvedEntityIds(result.data);
    }
    applyDeferredEntityFieldMerges(resolved);
    return resolved;
  }
  /**
   * Resolve `uses` declarations to loaded orbitals.
   */
  async resolveImports(uses, sourcePath, chain) {
    const errors = [];
    const orbitals = /* @__PURE__ */ new Map();
    if (this.options.skipExternalLoading) {
      return {
        success: true,
        data: { orbitals, idIndex: /* @__PURE__ */ new Map() },
        warnings: ["External loading skipped"]
      };
    }
    const effectiveChain = chain ?? { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    for (const use of uses) {
      if (orbitals.has(use.as)) {
        errors.push(`Duplicate import alias: ${use.as}`);
        continue;
      }
      await this.ensureLoader();
      if (!this.loader) {
        errors.push(`No loader available to resolve import: ${use.from}`);
        continue;
      }
      const loadResult = await this.loader.loadOrbital(
        use.from,
        void 0,
        sourcePath,
        effectiveChain
      );
      if (!loadResult.success) {
        errors.push(`Failed to load "${use.from}" as "${use.as}": ${loadResult.error}`);
        continue;
      }
      const fileOrbitals = loadResult.data.orbitals ?? [loadResult.data.orbital];
      const resolvedOrbitals = await this.preResolveImportFile(
        fileOrbitals,
        loadResult.data.sourcePath,
        loadResult.data.schemaConfig,
        effectiveChain
      );
      orbitals.set(use.as, {
        alias: use.as,
        from: use.from,
        orbital: loadResult.data.orbital,
        orbitals: fileOrbitals,
        sourcePath: loadResult.data.sourcePath,
        ...resolvedOrbitals ? { resolvedOrbitals } : {},
        ...loadResult.data.schemaConfig ? {
          schemaConfig: forwardConsumerAppKnobs(
            foldUseConfigOverride(loadResult.data.schemaConfig, use.config, use),
            this.schemaConfig
          )
        } : {}
      });
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: { orbitals, idIndex: /* @__PURE__ */ new Map() }, warnings: [] };
  }
  /**
   * Resolve entity reference.
   */
  resolveEntity(entityRef, imports) {
    if (isEntityCall(entityRef)) {
      const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
      return {
        success: true,
        data: {
          entity: {
            name: fallbackName,
            fields: entityRef.fields ?? [],
            ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
            ...entityRef.collection ? { collection: entityRef.collection } : {}
          }
        },
        warnings: []
      };
    }
    if (!isEntityReference(entityRef)) {
      return {
        success: true,
        data: { entity: entityRef },
        warnings: []
      };
    }
    const parsed = parseEntityRef(entityRef);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid entity reference format: ${entityRef}. Expected "Alias.entity"`]
      };
    }
    const imported = imports.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in entity reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    const importedEntity = this.getEntityFromOrbital(imported.orbital);
    if (!importedEntity) {
      return {
        success: false,
        errors: [
          `Imported orbital "${parsed.alias}" does not have an inline entity. Entity references cannot be chained.`
        ]
      };
    }
    const persistence = importedEntity.persistence ?? "persistent";
    return {
      success: true,
      data: {
        entity: importedEntity,
        source: {
          alias: parsed.alias,
          persistence
        }
      },
      warnings: []
    };
  }
  /**
   * Get the entity from an orbital (handling EntityRef).
   */
  getEntityFromOrbital(orbital) {
    const entityRef = orbital.entity;
    if (typeof entityRef === "string") {
      return null;
    }
    if (isEntityCall(entityRef)) {
      const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
      return {
        name: fallbackName,
        fields: entityRef.fields ?? [],
        ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
        ...entityRef.collection ? { collection: entityRef.collection } : {}
      };
    }
    return entityRef;
  }
  /**
   * The ATOM's OWN trait `alias.traits.traitName` resolves to — no call-site
   * overrides applied (`callSiteConfig` omitted) — so its DECLARED
   * `linkedEntity` can drive {@link buildAliasEntityRenames}. Same lookup
   * `resolveTraitRefString`'s imported branch performs to find the base
   * `trait` before any rebind, factored out so the pre-pass and the main
   * resolve share one lookup rather than two. `null` when the alias or
   * trait cannot be found — the pre-pass simply skips that entry, same as a
   * lookup failure anywhere else in this best-effort pass.
   */
  async atomOwnTrait(alias, traitName, imports, chain) {
    const imported = imports.orbitals.get(alias);
    if (!imported) return null;
    for (const o of importedOrbitals(imported)) {
      const entry = findTraitEntryInOrbital(o, traitName);
      if (!entry) continue;
      const entryResolved = await this.resolveTraitEntry(imported, entry, traitName, chain, imports);
      return entryResolved.success ? entryResolved.data.trait : null;
    }
    return null;
  }
  /**
   * Alias-scoped cross-trait entity-rebind propagation — JS twin of Rust's
   * `alias_entity_renames` pre-pass (`inline/trait.rs::inline_traits`). One
   * explicit pull's `linkedEntity` override (`Button.traits.ButtonRender ->
   * Route`) establishes a rebind (the atom's OWN declared `linkedEntity` →
   * the override) that a SIBLING pull from the SAME alias with NO rebind of
   * its own (`Button.traits.ButtonRender`, no `->`) never sees through
   * {@link applyLinkedEntityRename} — that call only ever knows ITS OWN
   * delta. But the no-rebind pull's `entityRefIds` side-map can still carry
   * a SECONDARY, positional reference to that same atom-local name (a
   * "self" entry keyed by the atom's own bound entity), which nothing else
   * rewrites — `std-api-gateway`'s `RouteOrbital.InlineButtonRender3`
   * (`Button.traits.ButtonRender`, no rebind) recovers `entityRefIds`'
   * `ButtonItem` key to `Route` only because `InlineButtonRender9` (`Button.
   * traits.ButtonRender -> Route`) established the rebind earlier in the
   * SAME orbital's `traits` list. Scanned once per orbital, over every
   * EXPLICIT `Alias.traits.X -> Y` entry in `traitRefs`: `(alias, atom's
   * declared linkedEntity) -> override`. First rebind for a given `(alias,
   * atomLocal)` pair wins (`Map.set` only on a fresh key), matching Rust's
   * `.or_insert_with`.
   */
  async buildAliasEntityRenames(traitRefs, imports, chain) {
    const out = /* @__PURE__ */ new Map();
    for (const traitRef of traitRefs) {
      if (typeof traitRef === "string" || "stateMachine" in traitRef || !("ref" in traitRef)) continue;
      const refObj = traitRef;
      if (!refObj.linkedEntity) continue;
      const parsed = parseImportedTraitRef(refObj.ref);
      if (!parsed) continue;
      const atomTrait = await this.atomOwnTrait(parsed.alias, parsed.traitName, imports, chain);
      const atomLocal = atomTrait?.linkedEntity;
      if (!atomLocal || atomLocal === refObj.linkedEntity) continue;
      const forAlias = out.get(parsed.alias) ?? /* @__PURE__ */ new Map();
      if (!forAlias.has(atomLocal)) forAlias.set(atomLocal, refObj.linkedEntity);
      out.set(parsed.alias, forAlias);
    }
    return out;
  }
  /**
   * Resolve trait references.
   */
  async resolveTraits(traitRefs, imports, chain, orbitalConfig) {
    const errors = [];
    const resolved = [];
    const embedCtx = {
      traitRefs,
      embedGraph: buildEmbedGraph(traitRefs),
      orbitalConfig
    };
    const aliasEntityRenames = await this.buildAliasEntityRenames(traitRefs, imports, chain);
    for (const traitRef of traitRefs) {
      const result = await this.resolveTraitRef(traitRef, imports, chain, embedCtx, aliasEntityRenames);
      if (!result.success) {
        errors.push(...result.errors);
      } else {
        resolved.push(result.data);
      }
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: resolved, warnings: [] };
  }
  /**
   * The import scope of an already-loaded orbital, memoised per source path.
   * A sibling that is itself a ref names its target through the SOURCE atom's
   * aliases, so it can only be resolved against those.
   */
  async importsOfSource(imported, chain, entry) {
    const declares = (o) => entry !== void 0 && (o.traits.some((t) => t === entry) || (o.pages ?? []).some((p) => p === entry));
    const declaring = importedOrbitals(imported).find(declares) ?? imported.orbital;
    const key = `${imported.sourcePath ?? `${imported.alias}:${imported.from}`}#${declaring.name}`;
    const cached = this.sourceImportsCache.get(key);
    if (cached) return cached;
    const result = await this.resolveImports(
      declaring.uses ?? [],
      imported.sourcePath,
      chain
    );
    if (!result.success) return null;
    result.data.idIndex = buildIdIndex(declaring, result.data.orbitals);
    this.sourceImportsCache.set(key, result.data);
    return result.data;
  }
  /**
   * The concrete base `Trait` a trait entry (found under `localName` in
   * `imported`) resolves to — an inline entry resolves its own
   * `@config.<knob>` emit refs against `callSiteConfig` (the ref hop that
   * led here, e.g. `std-approval-gate`'s `CloseButton = Button.traits.
   * ButtonRender { config: { action: CLOSE } }` resolving `ButtonRender`
   * itself); a ref-shaped entry (an atom's sub-view declared `trait X =
   * Y.traits.Z {…}` INSIDE the source atom, e.g. `std-note`'s
   * `NoteSubpages = RecordDetail.traits.RecordItemDetail`) recurses through
   * that source's own import scope via {@link importsOfSource} — the
   * entry's own declared overrides are its call site. Single owner for
   * `pullSiblingTraits`' sibling pull and `resolveTraitRefString`'s
   * imported branch, both of which used to reach `findTraitInOrbital`'s
   * `stateMachine`-gated lookup (its ref arm did nothing), silently failing
   * to resolve a nested ref (ledger (i)).
   *
   * `callSiteConfig` MUST be threaded by any caller that goes on to fold a
   * call-site config of its own onto the returned trait
   * (`resolveTraitRefString`'s ref-imported branch, ledger (o)) — the
   * `@config.<knob>` emit-name substitution is destructive
   * (`resolveConfigRefEmitNames` rewrites the marker to a concrete literal
   * in place), so resolving it here with the WRONG effective config
   * (the atom's own bare default, absent the override) permanently loses
   * the override: a second `resolveConfigRefEmitNames` call downstream sees
   * no more `@config.<knob>` marker to substitute and silently no-ops.
   * `pullSiblingTraits` (no further fold of its own) omits it, matching its
   * prior no-callSiteConfig behavior exactly.
   */
  async resolveTraitEntry(imported, entry, localName, chain, homeImports, callSiteConfig) {
    if ("stateMachine" in entry) {
      const { trait, errors } = resolveConfigRefEmitNames(entry, callSiteConfig);
      if (errors.length > 0) return { success: false, errors };
      return {
        success: true,
        data: { trait, embedScope: { imports: homeImports, alias: imported.alias } },
        warnings: []
      };
    }
    const refObj = entry;
    const srcImports = await this.importsOfSource(imported, chain, entry);
    if (!srcImports) {
      return {
        success: false,
        errors: [
          `Trait "${localName}" in imported behavior could not resolve its own import scope to follow ref "${refObj.ref}"`
        ]
      };
    }
    const nested = await this.resolveTraitRefString(
      refObj.ref,
      srcImports,
      chain,
      refObj.config,
      refObj.linkedEntity,
      refObj.name ?? localName,
      refObj.events,
      refObj.listens,
      refObj.refId,
      refObj.typeArgs,
      void 0,
      void 0,
      refObj.fields,
      refObj.emitsScope
    );
    if (!nested.success) return nested;
    return {
      success: true,
      data: {
        trait: nested.data.trait,
        embedScope: nested.data.embedScope ?? { imports: srcImports, alias: imported.alias },
        ...nested.data.typeArgs !== void 0 ? { typeArgs: nested.data.typeArgs } : {}
      },
      warnings: nested.warnings
    };
  }
  /**
   * The concrete `Page` a page entry (found under `localName` in `imported`)
   * resolves to — an inline `Page` passes through; a `PageRefObject` (a
   * re-exported page declared inside the SOURCE orbital's own `pages {}`,
   * {@link findPageEntryInOrbital}'s ref arm) recurses through that source's
   * own import scope via {@link importsOfSource} and
   * {@link resolvePageRefObject} — the page twin of {@link resolveTraitEntry}
   * (nested refs, ledger (i)).
   */
  async resolvePageEntry(imported, entry, chain) {
    if (!("ref" in entry)) return { success: true, data: { page: entry }, warnings: [] };
    const srcImports = await this.importsOfSource(imported, chain, entry);
    if (!srcImports) {
      return {
        success: false,
        errors: [`Page in imported behavior could not resolve its own import scope to follow ref "${entry.ref}"`]
      };
    }
    const nested = await this.resolvePageRefObject(entry, srcImports, chain);
    if (!nested.success) return nested;
    return { success: true, data: { page: nested.data.page }, warnings: nested.warnings };
  }
  /**
   * Sibling-trait auto-pull — the JS twin of the compiler's
   * `phases/inline/trait.rs` pass, appending to `resolved` in place.
   *
   * An atom's main trait embeds its own sub-views as `@trait.<Sibling>` string
   * literals (`std-browse`'s `bodyContent: {children: [@trait.DataGrid1]}`).
   * A consumer that imports only the main trait never instantiates those
   * siblings, so the token dangles: no state machine, no fetch, an empty list
   * where the grid should be. The compiled path materialises them; without this
   * pass the interpreted path did not, so a raw `.orb` handed straight to
   * `OrbitalServerRuntime` rendered a different app than the same `.orb` run
   * through `orbital resolve`.
   *
   * Pulls are keyed per EMBEDDER (`owner` = the top-level consumer trait that
   * started the chain), not per atom. Two rebinds of one atom in one orbital
   * (`ChannelRail -> Channel` and `ChatThread -> ChatMessage`, both std-browse)
   * carry different entity rebinds and different hosts to listen to, so one
   * shared copy can only ever serve one of them. The first owner keeps the
   * source name; later owners pull under `<Owner><Sibling>` — unique by
   * construction, since owner names are unique within the orbital.
   */
  async pullSiblingTraits(resolved, imports, chain, orbitalConfig) {
    const auxEntities = [];
    const work = [];
    const ownerSubs = /* @__PURE__ */ new Map();
    const subsFor = (owner) => {
      let m = ownerSubs.get(owner);
      if (!m) {
        m = /* @__PURE__ */ new Map();
        ownerSubs.set(owner, m);
      }
      return m;
    };
    for (const rt of resolved) {
      if (rt.source.type !== "imported" || !rt.trait.name) continue;
      const owner = rt.trait.name;
      subsFor(owner).set(rt.source.traitName, owner);
      const rebindFrom = rt.linkedEntity ? (await this.atomOwnTrait(rt.source.alias, rt.source.traitName, imports, chain))?.linkedEntity : void 0;
      for (const sibling of traitEmbedNamesOf(rt.trait)) {
        work.push({
          alias: rt.source.alias,
          fallback: rt.embedScope,
          sibling,
          linkedEntity: rt.linkedEntity,
          parent: owner,
          owner,
          // Top-level SEED — always starts with the raw-walk-first gate
          // (see {@link PullItem.preResolvedScope}'s own doc).
          preResolvedScope: void 0,
          rebindFrom
        });
      }
    }
    const renameMap = /* @__PURE__ */ new Map();
    for (const rt of resolved) {
      if (rt.source.type === "imported" && rt.trait.name && rt.trait.name !== rt.source.traitName) {
        renameMap.set(rt.source.traitName, rt.trait.name);
      }
    }
    const renameDeclared = (traits, all) => {
      if (renameMap.size === 0) return;
      const ids = /* @__PURE__ */ new Map();
      for (const rt of all) if (rt.trait.name) ids.set(rt.trait.name, rt.trait.id);
      for (const rt of traits) rt.trait = rewriteListenSources(rt.trait, renameMap, ids);
    };
    if (work.length === 0) {
      renameDeclared(resolved, resolved);
      return { errors: [], auxEntities: [], pulledNames: /* @__PURE__ */ new Set() };
    }
    const errors = [];
    const consumerDeclared = new Set(resolved.map((r) => r.trait.name).filter(Boolean));
    const consumerDeclaredProvenance = /* @__PURE__ */ new Map();
    for (const rt of resolved) {
      if (rt.source.type === "imported" && rt.trait.name) {
        consumerDeclaredProvenance.set(rt.trait.name, { alias: rt.source.alias, traitName: rt.source.traitName });
      }
    }
    const seen = new Set(consumerDeclared);
    const visited = /* @__PURE__ */ new Set();
    const pulledAs = /* @__PURE__ */ new Map();
    const ownerOf = /* @__PURE__ */ new Map();
    const parentOf = /* @__PURE__ */ new Map();
    const parentRewrites = /* @__PURE__ */ new Map();
    const pulled = [];
    const embedderChain2 = (finalName) => {
      const chain2 = [];
      const seenNames = /* @__PURE__ */ new Set();
      let cur = finalName;
      while (cur !== void 0 && !seenNames.has(cur)) {
        seenNames.add(cur);
        const t = resolved.find((r) => r.trait.name === cur)?.trait ?? pulled.find((r) => r.trait.name === cur)?.trait;
        if (!t) break;
        if (t.config) chain2.push(t.config);
        cur = parentOf.get(cur);
      }
      return chain2;
    };
    const noteRewrite = (parent, from, to) => {
      let m = parentRewrites.get(parent);
      if (!m) {
        m = /* @__PURE__ */ new Map();
        parentRewrites.set(parent, m);
      }
      m.set(from, to);
    };
    for (let item = work.pop(); item !== void 0; item = work.pop()) {
      const { alias, fallback, sibling, linkedEntity, parent, owner, preResolvedScope, rebindFrom } = item;
      const pullKey = `${alias} ${sibling} ${owner} ${parent}`;
      const existing = pulledAs.get(pullKey);
      if (existing !== void 0) {
        if (existing !== sibling) noteRewrite(parent, sibling, existing);
        continue;
      }
      let finalName = sibling;
      if (seen.has(sibling)) {
        const provenance = consumerDeclaredProvenance.get(sibling);
        if (consumerDeclared.has(sibling) && provenance?.alias === alias && provenance.traitName === sibling) {
          continue;
        }
        finalName = `${parent}${sibling}`;
        if (seen.has(finalName)) continue;
      }
      if (visited.has(pullKey)) continue;
      visited.add(pullKey);
      let searchImports = imports;
      let searchAlias = alias;
      let atomTrait;
      let childScope;
      let foundTypeArgs;
      let childPreResolvedScope;
      if (preResolvedScope) {
        const found = preResolvedScope.traits.find((rt) => rt.trait.name === sibling);
        if (found) {
          atomTrait = found.trait;
          childScope = { imports: searchImports, alias: searchAlias };
          foundTypeArgs = found.typeArgs;
          childPreResolvedScope = preResolvedScope;
        }
      }
      if (!atomTrait) {
        let imported = imports.orbitals.get(alias);
        let atomEntry = null;
        let atomOrbitalIndex = -1;
        if (imported) {
          const list = importedOrbitals(imported);
          for (let i = 0; i < list.length; i++) {
            atomEntry = findTraitEntryInOrbital(list[i], sibling);
            if (atomEntry) {
              atomOrbitalIndex = i;
              break;
            }
          }
        }
        if (!atomEntry && fallback) {
          const fbImported = fallback.imports.orbitals.get(fallback.alias);
          if (fbImported) {
            const list = importedOrbitals(fbImported);
            for (let i = 0; i < list.length; i++) {
              atomEntry = findTraitEntryInOrbital(list[i], sibling);
              if (atomEntry) {
                imported = fbImported;
                searchImports = fallback.imports;
                searchAlias = fallback.alias;
                atomOrbitalIndex = i;
                break;
              }
            }
          }
        }
        if (!imported || !atomEntry) continue;
        const preResolvedOrbital = imported.resolvedOrbitals?.[atomOrbitalIndex];
        const preResolved = preResolvedOrbital?.traits.find((rt) => rt.trait.name === sibling);
        if (preResolved) {
          atomTrait = preResolved.trait;
          childScope = { imports: searchImports, alias: searchAlias };
          foundTypeArgs = preResolved.typeArgs;
          childPreResolvedScope = preResolvedOrbital;
        } else {
          const entryResolved = await this.resolveTraitEntry(imported, atomEntry, sibling, chain, searchImports);
          if (!entryResolved.success) {
            errors.push(...entryResolved.errors);
            continue;
          }
          atomTrait = entryResolved.data.trait;
          childScope = entryResolved.data.embedScope;
          foundTypeArgs = entryResolved.data.typeArgs;
        }
      }
      if (!atomTrait || !childScope) continue;
      const nextFallback = childScope.alias === searchAlias && childScope.imports === searchImports ? void 0 : childScope;
      const siblingRebind = atomTrait.linkedEntity && rebindFrom && atomTrait.linkedEntity !== rebindFrom ? void 0 : linkedEntity;
      let copy = resolveForwardedSiblingConfigFrom(
        applyLinkedEntityRename(atomTrait, siblingRebind, this.entityIdsInScope),
        embedderChain2(parent),
        orbitalConfig,
        this.schemaConfig
      );
      if (finalName !== sibling) {
        copy = { ...copy, name: finalName };
        noteRewrite(parent, sibling, finalName);
      }
      const childParent = childPreResolvedScope ? parent : finalName;
      for (const next of traitEmbedNamesOf(copy)) {
        work.push({
          alias,
          fallback: nextFallback,
          sibling: next,
          linkedEntity,
          parent: childParent,
          owner,
          preResolvedScope: childPreResolvedScope,
          rebindFrom
        });
      }
      pulledAs.set(pullKey, finalName);
      subsFor(owner).set(sibling, finalName);
      ownerOf.set(finalName, owner);
      parentOf.set(finalName, parent);
      seen.add(finalName);
      pulled.push({
        trait: copy,
        source: { type: "imported", alias, traitName: sibling },
        ...siblingRebind !== void 0 ? { linkedEntity: siblingRebind } : {},
        ...foundTypeArgs !== void 0 ? { typeArgs: foundTypeArgs } : {}
      });
      if (copy.linkedEntity !== void 0) {
        const aux = entityByNameForAlias(imports, alias, copy.linkedEntity) ?? entityByNameAnyAlias(imports, copy.linkedEntity);
        if (aux) auxEntities.push(aux);
      }
    }
    if (pulled.length === 0 && parentRewrites.size === 0) {
      renameDeclared(resolved, resolved);
      return { errors, auxEntities, pulledNames: /* @__PURE__ */ new Set() };
    }
    const applyRewrites = (rt) => {
      const subs = rt.trait.name ? parentRewrites.get(rt.trait.name) : void 0;
      if (subs) rt.trait = renameTraitEmbeds(rt.trait, subs);
    };
    for (const rt of resolved) applyRewrites(rt);
    for (const rt of pulled) applyRewrites(rt);
    const idByName = /* @__PURE__ */ new Map();
    for (const rt of [...resolved, ...pulled]) {
      if (rt.trait.name) idByName.set(rt.trait.name, rt.trait.id);
    }
    for (const rt of pulled) {
      const owner = rt.trait.name ? ownerOf.get(rt.trait.name) : void 0;
      const subs = new Map([...renameMap, ...owner ? ownerSubs.get(owner) ?? [] : []]);
      if (subs.size === 0) continue;
      rt.trait = rewriteListenSources(rt.trait, subs, idByName);
    }
    renameDeclared(resolved, [...resolved, ...pulled]);
    refResolverLog.info("sibling-pull", {
      pulled: pulled.map((p) => ({
        trait: p.trait.name,
        owner: p.trait.name ? ownerOf.get(p.trait.name) : void 0,
        parent: p.trait.name ? parentOf.get(p.trait.name) : void 0,
        linkedEntity: p.linkedEntity ?? p.trait.linkedEntity
      }))
    });
    resolved.push(...pulled);
    const pulledNames = new Set(
      pulled.map((p) => p.trait.name).filter((n) => Boolean(n))
    );
    return { errors, auxEntities, pulledNames };
  }
  /**
   * Resolve a single trait reference.
   */
  async resolveTraitRef(traitRef, imports, chain, embedCtx, aliasEntityRenames) {
    if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
      const { trait: resolvedInline, errors } = resolveConfigRefEmitNames(traitRef);
      if (errors.length > 0) {
        return { success: false, errors };
      }
      return {
        success: true,
        data: {
          trait: resolvedInline,
          source: { type: "inline" }
        },
        warnings: []
      };
    }
    if (typeof traitRef !== "string" && "ref" in traitRef) {
      const refObj = traitRef;
      return withCallSiteTraitId(refObj.id, await this.resolveTraitRefString(
        refObj.ref,
        imports,
        chain,
        refObj.config,
        refObj.linkedEntity,
        refObj.name,
        refObj.events,
        refObj.listens,
        refObj.refId,
        refObj.typeArgs,
        embedCtx,
        aliasEntityRenames,
        refObj.fields,
        refObj.emitsScope
      ));
    }
    if (typeof traitRef === "string") {
      return this.resolveTraitRefString(
        traitRef,
        imports,
        chain,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        embedCtx,
        aliasEntityRenames
      );
    }
    return {
      success: false,
      errors: [`Unknown trait reference format: ${JSON.stringify(traitRef)}`]
    };
  }
  /**
   * Resolve a trait reference string.
   */
  async resolveTraitRefString(ref, imports, chain, config, linkedEntity, overrideName, eventRenames, listensOverride, refId, typeArgs, embedCtx, aliasEntityRenames, fieldRenames, emitsScope) {
    const parsed = parseImportedTraitRef(ref);
    const localName = overrideName ?? (parsed ? parsed.traitName : ref);
    let resolvedConfig = config && embedCtx ? resolveEmbedderConfigForward(config, localName, embedCtx, this.schemaConfig) : config;
    if (parsed) {
      const imported = imports.orbitals.get(parsed.alias);
      if (!imported) {
        return {
          success: false,
          errors: [
            `Unknown import alias in trait reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
          ]
        };
      }
      let trait = null;
      let embedScope = { imports, alias: parsed.alias };
      const idEntry = refId ? imports.idIndex?.get(refId) : void 0;
      if (idEntry && idEntry.kind === "trait") {
        trait = idEntry.node;
      } else {
        for (const o of importedOrbitals(imported)) {
          const entry = findTraitEntryInOrbital(o, parsed.traitName);
          if (!entry) continue;
          const entryResolved = await this.resolveTraitEntry(
            imported,
            entry,
            parsed.traitName,
            chain,
            imports,
            resolvedConfig
          );
          if (!entryResolved.success) return entryResolved;
          trait = entryResolved.data.trait;
          embedScope = entryResolved.data.embedScope;
          break;
        }
      }
      if (!trait) {
        const available = importedOrbitals(imported).flatMap((o) => this.listTraitsInOrbital(o));
        return {
          success: false,
          errors: [
            `Trait "${parsed.traitName}" not found in imported orbital "${parsed.alias}". Available traits: ${available.join(", ") || "none"}`
          ]
        };
      }
      if (embedCtx && trait.config) {
        const homeResolvedConfig = resolveConfigForwards(
          trait.config,
          embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph),
          embedCtx.orbitalConfig,
          homeOrbitalConfigForTrait(imported, parsed.traitName),
          imported.schemaConfig,
          this.schemaConfig
        );
        if (homeResolvedConfig) {
          trait = { ...trait, config: homeResolvedConfig };
        }
      }
      if (resolvedConfig && embedCtx) {
        const nested = resolveNestedCallSiteForwards(
          resolvedConfig,
          /* @__PURE__ */ new Set([...Object.keys(trait.config ?? {}), ...Object.keys(resolvedConfig)]),
          embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph),
          embedCtx.orbitalConfig,
          this.schemaConfig
        );
        if (nested) resolvedConfig = nested;
      }
      const baseTrait = overrideName && overrideName !== trait.name ? {
        ...trait,
        name: overrideName,
        ...trait.config ? {
          config: mapConfigEventAddresses(
            trait.config,
            (event, source) => source.kind === "trait" && source.trait === trait.name ? { event, source: { kind: "trait", trait: overrideName } } : void 0
          )
        } : {}
      } : trait;
      const applied = applyTraitCallSiteOverrides(
        baseTrait,
        { config: resolvedConfig, linkedEntity, events: eventRenames, listens: listensOverride, fields: fieldRenames, emitsScope },
        this.entityIdsInScope,
        aliasEntityRenames?.get(parsed.alias)
      );
      if (!applied.ok) return { success: false, errors: applied.errors };
      const finalTrait = applied.trait;
      const renamedConfig = applied.config;
      if (listensOverride !== void 0) {
        refResolverLog.info("listens-override:imported", {
          trait: finalTrait.name,
          ref,
          atomListens: trait.listens?.length ?? 0,
          callSiteListens: listensOverride.length
        });
      }
      return {
        success: true,
        data: {
          trait: finalTrait,
          source: { type: "imported", alias: parsed.alias, traitName: parsed.traitName },
          config: renamedConfig,
          linkedEntity,
          embedScope,
          ...typeArgs !== void 0 ? { typeArgs } : {}
        },
        warnings: []
      };
    }
    const localTrait = (refId && this.localTraitsById.get(refId)) ?? this.localTraits.get(ref);
    if (localTrait) {
      if (resolvedConfig && embedCtx) {
        const nested = resolveNestedCallSiteForwards(
          resolvedConfig,
          /* @__PURE__ */ new Set([...Object.keys(localTrait.config ?? {}), ...Object.keys(resolvedConfig)]),
          embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph),
          embedCtx.orbitalConfig,
          this.schemaConfig
        );
        if (nested) resolvedConfig = nested;
      }
      const baseLocal = overrideName ? { ...localTrait, name: overrideName } : localTrait;
      const appliedLocal = applyTraitCallSiteOverrides(
        baseLocal,
        { config: resolvedConfig, linkedEntity, events: eventRenames, listens: listensOverride, fields: fieldRenames, emitsScope },
        this.entityIdsInScope
      );
      if (!appliedLocal.ok) return { success: false, errors: appliedLocal.errors };
      const finalLocalTrait = appliedLocal.trait;
      const renamedLocalConfig = appliedLocal.config;
      if (listensOverride !== void 0) {
        refResolverLog.info("listens-override:local", {
          trait: finalLocalTrait.name,
          ref,
          atomListens: localTrait.listens?.length ?? 0,
          callSiteListens: listensOverride.length
        });
      }
      return {
        success: true,
        data: {
          trait: finalLocalTrait,
          source: { type: "local", name: ref },
          config: renamedLocalConfig,
          linkedEntity,
          ...typeArgs !== void 0 ? { typeArgs } : {}
        },
        warnings: []
      };
    }
    return {
      success: false,
      errors: [
        `Trait "${ref}" not found. For imported traits, use format "Alias.traits.TraitName". Local traits available: ${Array.from(this.localTraits.keys()).join(", ") || "none"}`
      ]
    };
  }
  /**
   * List trait names in an orbital.
   */
  listTraitsInOrbital(orbital) {
    const names = [];
    for (const traitRef of orbital.traits) {
      if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
        names.push(traitRef.name);
      }
    }
    return names;
  }
  /**
   * Resolve page references.
   */
  async resolvePages(pageRefs, imports, chain) {
    const errors = [];
    const resolved = [];
    for (const pageRef of pageRefs) {
      const result = await this.resolvePageRef(pageRef, imports, chain);
      if (!result.success) {
        errors.push(...result.errors);
      } else {
        resolved.push(result.data);
      }
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: resolved, warnings: [] };
  }
  /**
   * Resolve a single page reference.
   */
  async resolvePageRef(pageRef, imports, chain) {
    if (!isPageReference(pageRef)) {
      return {
        success: true,
        data: {
          page: pageRef,
          source: { type: "inline" },
          pathOverridden: false
        },
        warnings: []
      };
    }
    if (isPageReferenceString(pageRef)) {
      return this.resolvePageRefString(pageRef, imports, chain);
    }
    if (isPageReferenceObject(pageRef)) {
      return this.resolvePageRefObject(pageRef, imports, chain);
    }
    return {
      success: false,
      errors: [`Unknown page reference format: ${JSON.stringify(pageRef)}`]
    };
  }
  /**
   * Resolve a page reference string.
   */
  async resolvePageRefString(ref, imports, chain, refId) {
    const parsed = parsePageRef(ref);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid page reference format: ${ref}. Expected "Alias.pages.PageName"`]
      };
    }
    const imported = imports.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in page reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    let page = null;
    const idEntry = refId ? imports.idIndex?.get(refId) : void 0;
    if (idEntry && idEntry.kind === "page") {
      page = { ...idEntry.node };
    } else {
      for (const o of importedOrbitals(imported)) {
        const entry = findPageEntryInOrbital(o, parsed.pageName);
        if (!entry) continue;
        const entryResolved = await this.resolvePageEntry(imported, entry, chain);
        if (!entryResolved.success) return entryResolved;
        page = entryResolved.data.page;
        break;
      }
    }
    if (!page) {
      const available = importedOrbitals(imported).flatMap((o) => this.listPagesInOrbital(o));
      return {
        success: false,
        errors: [
          `Page "${parsed.pageName}" not found in imported orbital "${parsed.alias}". Available pages: ${available.join(", ") || "none"}`
        ]
      };
    }
    const sourcePage = page.sourcePage ?? page.id;
    if (sourcePage !== void 0) page = { ...page, sourcePage };
    return {
      success: true,
      data: {
        page,
        source: { type: "imported", alias: parsed.alias, pageName: parsed.pageName },
        pathOverridden: false
      },
      warnings: []
    };
  }
  /**
   * Resolve a page reference object, applying every call-site override —
   * `path` (direct assignment), `linkedEntity` (rebind `primaryEntity` and
   * every page-level trait pointer that named the old entity), and `traits`
   * (full replacement) — mirroring the compiler's `apply_overrides_to_page`
   * (`phases/inline/page.rs:21-93`). `Page.traits` is a `PageTraitRef[]`
   * pointer list (a trait's own body lives in the orbital's `traits {}`, not
   * on the page), so unlike Rust's `TraitReference` the `linkedEntity` rebind
   * only ever touches each pointer's OWN `linkedEntity` field — there is no
   * inline trait body here to walk with `renameEntitiesInTrait`.
   */
  async resolvePageRefObject(refObj, imports, chain) {
    const baseResult = await this.resolvePageRefString(refObj.ref, imports, chain, refObj.refId);
    if (!baseResult.success) {
      return baseResult;
    }
    const resolved = baseResult.data;
    const mods = {
      ...refObj.access !== void 0 ? { access: refObj.access } : {},
      ...refObj.indexing !== void 0 ? { indexing: refObj.indexing } : {},
      ...refObj.title !== void 0 ? { title: refObj.title } : {},
      ...refObj.description !== void 0 ? { description: refObj.description } : {},
      ...refObj.translationOf !== void 0 ? { translationOf: refObj.translationOf } : {},
      ...refObj.label !== void 0 ? { label: refObj.label } : {},
      ...refObj.icon !== void 0 ? { icon: refObj.icon } : {},
      ...refObj.roles !== void 0 ? { roles: [...refObj.roles] } : {}
    };
    if (weakensPageAccess(resolved.page, mods)) {
      return {
        success: false,
        errors: [
          `Page reference '${refObj.ref}' at ${refObj.path ?? resolved.page.path} sets \`access: public\` on an upstream page declared \`access: authenticated\` \u2014 an import cannot weaken inherited protection (ORB_O_PAGE_ACCESS_WEAKENED)`
        ]
      };
    }
    resolved.page = withPageModifiers(resolved.page, mods);
    if (refObj.path) {
      const originalPath = resolved.page.path;
      resolved.page = {
        ...resolved.page,
        path: refObj.path
      };
      resolved.pathOverridden = true;
      resolved.originalPath = originalPath;
    }
    if (refObj.linkedEntity) {
      const oldEntity = resolved.page.primaryEntity;
      const newEntity = refObj.linkedEntity;
      const rebindPointers = (pointers) => oldEntity && oldEntity !== newEntity ? pointers?.map((pt) => pt.linkedEntity === oldEntity ? { ...pt, linkedEntity: newEntity } : pt) : pointers;
      resolved.page = {
        ...resolved.page,
        primaryEntity: newEntity,
        traits: rebindPointers(resolved.page.traits)
      };
    }
    if (refObj.traits) {
      resolved.page = { ...resolved.page, traits: refObj.traits.map(toPageTraitRef) };
    }
    return {
      success: true,
      data: resolved,
      warnings: baseResult.warnings
    };
  }
  /**
   * List page names in an orbital.
   */
  listPagesInOrbital(orbital) {
    const pages = orbital.pages;
    if (!pages) return [];
    const names = [];
    for (const pageRef of pages) {
      if (typeof pageRef !== "string" && !("ref" in pageRef)) {
        names.push(pageRef.name);
      }
    }
    return names;
  }
  /**
   * Flatten every reference-form orbital (`orbital X = Alias.orbitals.Y
   * { … }`, docs/Almadar_Orbital_Import.md §4) in a schema BEFORE the
   * ordinary per-orbital {@link resolve} pass runs — the interpreter-path
   * twin of the (not-yet-landed) compiler's inline-phase flatten. After this
   * call, no returned `OrbitalDefinition` carries `.reference`.
   */
  async resolveOrbitalImports(schema, opts) {
    const errors = [];
    const flattened = [];
    const chain = { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    const visiting = /* @__PURE__ */ new Set();
    const consumerEntityIds = consumerEntityIdsOf(schema.orbitals);
    const pendingTargets = [];
    this.pendingEntityTargets.set(consumerEntityIds, pendingTargets);
    for (const [name, id] of await this.precomputeReferenceFormPrimaryIds(schema.orbitals, chain)) {
      if (!consumerEntityIds.has(name)) consumerEntityIds.set(name, id);
    }
    const { targets: listenSourceTargets, byLocal: importedTraitNames } = await this.precomputeReferenceFormListenTargets(schema.orbitals, chain);
    const consumerRoster = identityEntitiesOf(schema.orbitals);
    for (const orbital of schema.orbitals) {
      if (!orbital.reference) {
        if (!opts?.skipTypeParamSentinels && isInlineEntity(orbital.entity)) {
          resolveOrbitalTypeParamSentinels(
            orbital.traits.filter(isInlineTrait).map((t) => ({ trait: t })),
            orbital.entity,
            (orbital.auxiliaryEntities ?? []).filter(isInlineEntity),
            orbital.types
          );
        }
        flattened.push(orbital);
        continue;
      }
      const result = await this.resolveOrbitalRefChain(
        orbital,
        void 0,
        chain,
        visiting,
        consumerEntityIds,
        consumerRoster,
        listenSourceTargets
      );
      if (!result.success) {
        errors.push(...result.errors);
        continue;
      }
      if (!opts?.skipTypeParamSentinels && isInlineEntity(result.data.entity)) {
        resolveOrbitalTypeParamSentinels(
          result.data.traits.filter(isInlineTrait).map((t) => ({ trait: t })),
          result.data.entity,
          (result.data.auxiliaryEntities ?? []).filter(isInlineEntity),
          result.data.types
        );
      }
      flattened.push(result.data);
    }
    this.pendingEntityTargets.delete(consumerEntityIds);
    const declared = new Set(flattened.flatMap((o) => [o.entity, ...o.auxiliaryEntities ?? []].filter(isInlineEntity).map((e) => e.name)));
    for (const t of pendingTargets) {
      if (declared.has(t.consumerName)) continue;
      errors.push(
        `Orbital "${t.localName}" entities { ${t.upstreamKey}: ${t.consumerName} } targets "${t.consumerName}", which the consumer schema does not declare (ORB_O_ENTITY_TARGET_UNKNOWN)`
      );
    }
    if (errors.length > 0) return { success: false, errors };
    addressImportedTraits(flattened, importedTraitNames);
    reconcileOrbitalRefEventIdRenames(schema, flattened);
    return { success: true, data: flattened, warnings: [] };
  }
  /**
   * (B) The materialized primary name → derived id of every REFERENCE-FORM
   * orbital this schema declares (`orbital X = Alias.orbitals.Y { … }`),
   * computed order-free — WITHOUT requiring any other orbital in `schema`
   * to have resolved first, and without recursively flattening the
   * upstream. Each reference's alias is loaded via the ordinary
   * `resolveImports` path, which the loader caches by absolute path (see
   * `ExternalOrbitalLoader`'s `LoaderCache`) — the REAL flatten
   * (`resolveOrbitalRefChain`) moments later is then a cache hit, no
   * duplicate work — and its named orbital looked up directly in the
   * loaded (un-flattened) schema: an upstream's OWN primary entity id/name
   * never change through resolution (only a CONSUMER-side CLONE gets a
   * fresh derived id), so this shallow load's entity is exactly what the
   * real flatten pass would see too. Uses the SAME `materializedPrimaryEntity`
   * pair `materializeOrbitalRef` computes for itself — one owner. JS twin
   * of the compiled path's `precompute_reference_form_primary_ids`.
   *
   * Best-effort: a reference this pre-pass cannot resolve (unknown alias,
   * malformed reference string, upstream orbital not found, load failure)
   * is silently skipped here — the REAL `resolveOrbitalRefChain` call
   * moments later still surfaces the actual error for it.
   */
  async precomputeReferenceFormPrimaryIds(orbitals, chain, sourcePath) {
    const out = /* @__PURE__ */ new Map();
    for (const orbital of orbitals) {
      const ref = orbital.reference;
      if (!ref) continue;
      const parsed = parseOrbitalRef(ref.ref);
      if (!parsed) continue;
      const importsResult = await this.resolveImports(orbital.uses ?? [], sourcePath, chain);
      if (!importsResult.success) continue;
      const imported = importsResult.data.orbitals.get(parsed.alias);
      if (!imported) continue;
      const upstream = importedOrbitals(imported).find((o) => o.name === parsed.orbitalName);
      if (!upstream || !isInlineEntity(upstream.entity)) continue;
      const { name, id } = materializedPrimaryEntity(upstream.entity, upstream.id, ref, orbital.name);
      out.set(name, id);
    }
    return out;
  }
  /**
   * G1 (`docs/Almadar_Compiler_Gaps.md` §82): every reference-form
   * orbital's OWN materialized trait set, keyed by {@link siblingImportKey}
   * (upstream alias + upstream orbital name), mapped to that import's local
   * orbital name + every kept trait's final name/id ({@link
   * materializedTraitSubs}). Computed order-free — WITHOUT requiring any
   * other orbital in `schema` to have resolved first — same precompute
   * shape as {@link precomputeReferenceFormPrimaryIds}, but for LISTEN-
   * SOURCE rewriting: consulted by {@link rewriteListenSources} so a source
   * naming ANY reference-form orbital of this schema resolves regardless of
   * declaration order. Unlike the entity-id precompute, this resolves the
   * upstream's OWN traits via the ordinary `this.resolve()` pass (the same
   * call {@link materializeOrbitalRef} makes on its own upstream, with the
   * same `schemaConfig` save/restore) rather than reading the raw unresolved
   * schema — the JS identity model has no pre-resolve declaration id for a
   * COMPOSED trait reference (unlike the compiled path's V4 dual-carry
   * `TraitReference::Reference.id`), so a shallow read would silently
   * under-report every composed trait's id.
   *
   * Best-effort: a reference this pre-pass cannot resolve (unknown alias,
   * malformed reference string, upstream orbital not found, itself still
   * reference-form, load/resolve failure) is silently skipped here — the
   * REAL `resolveOrbitalRefChain`/`materializeOrbitalRef` call moments
   * later still surfaces the actual error for it.
   */
  async precomputeReferenceFormListenTargets(orbitals, chain) {
    const out = /* @__PURE__ */ new Map();
    const byLocal = /* @__PURE__ */ new Map();
    for (const orbital of orbitals) {
      const ref = orbital.reference;
      if (!ref) continue;
      const parsed = parseOrbitalRef(ref.ref);
      if (!parsed) continue;
      const importsResult = await this.resolveImports(orbital.uses ?? [], void 0, chain);
      if (!importsResult.success) continue;
      const imported = importsResult.data.orbitals.get(parsed.alias);
      if (!imported) continue;
      const upstream = importedOrbitals(imported).find((o) => o.name === parsed.orbitalName);
      if (!upstream || upstream.reference) continue;
      const savedSchemaConfig = this.schemaConfig;
      this.schemaConfig = imported.schemaConfig;
      let resolvedUpstream;
      try {
        resolvedUpstream = await this.resolve(upstream, imported.sourcePath, chain);
      } finally {
        this.schemaConfig = savedSchemaConfig;
      }
      if (!resolvedUpstream.success) continue;
      const traitSubs = materializedTraitSubs(upstream.id, resolvedUpstream.data.traits, ref, orbital.name);
      out.set(siblingImportKey(parsed.alias, parsed.orbitalName), { localName: orbital.name, traitSubs });
      byLocal.set(orbital.name, traitSubs);
    }
    return { targets: out, byLocal };
  }
  /**
   * Resolve one reference-form orbital, recursing into the upstream first
   * when IT is also reference-form ("already fully inlined because inline
   * recursed into every loaded orbital" — docs/Almadar_Orbital_Import.md §9
   * step 3 — the JS resolver needs its own cycle guard since it has no
   * file-level inline-order pass to lean on).
   */
  async resolveOrbitalRefChain(orbital, sourcePath, chain, visiting, consumerEntityIds, consumerRoster, listenSourceTargets) {
    const ref = orbital.reference;
    if (!ref) return { success: true, data: orbital, warnings: [] };
    const parsed = parseOrbitalRef(ref.ref);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid orbital reference format: ${ref.ref}. Expected "Alias.orbitals.OrbitalName"`]
      };
    }
    const importsResult = await this.resolveImports(orbital.uses ?? [], sourcePath, chain);
    if (!importsResult.success) return { success: false, errors: importsResult.errors };
    const imported = importsResult.data.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in orbital reference: ${parsed.alias}. Available aliases: ${Array.from(importsResult.data.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    const candidates = importedOrbitals(imported);
    let upstream = candidates.find((o) => o.name === parsed.orbitalName);
    if (!upstream) {
      return {
        success: false,
        errors: [
          `Orbital "${parsed.orbitalName}" not found in imported behavior "${parsed.alias}". Available orbitals: ${candidates.map((o) => o.name).join(", ") || "none"}`
        ]
      };
    }
    const cycleKey = `${imported.sourcePath}::${parsed.orbitalName}`;
    if (visiting.has(cycleKey)) {
      return {
        success: false,
        errors: [
          `Circular orbital reference: "${orbital.name}" imports "${parsed.alias}.orbitals.${parsed.orbitalName}" while that orbital is itself being resolved`
        ]
      };
    }
    visiting.add(cycleKey);
    if (upstream.reference) {
      const { targets: nestedListenSourceTargets } = await this.precomputeReferenceFormListenTargets(candidates, chain);
      const nestedEntityIds = consumerEntityIdsOf(candidates);
      for (const [name, id] of await this.precomputeReferenceFormPrimaryIds(candidates, chain, imported.sourcePath)) {
        if (!nestedEntityIds.has(name)) nestedEntityIds.set(name, id);
      }
      const nested = await this.resolveOrbitalRefChain(
        upstream,
        imported.sourcePath,
        chain,
        visiting,
        nestedEntityIds,
        consumerRoster,
        nestedListenSourceTargets
      );
      if (!nested.success) {
        visiting.delete(cycleKey);
        return nested;
      }
      upstream = nested.data;
    }
    visiting.delete(cycleKey);
    const materialized = await this.materializeOrbitalRef(
      upstream,
      ref,
      orbital.name,
      orbital.id,
      imported.sourcePath,
      chain,
      resolvedAliasView(imported),
      consumerEntityIds,
      consumerRoster,
      imported.schemaConfig,
      parsed.alias,
      listenSourceTargets
    );
    if (!materialized.success) return materialized;
    const siblingTraits = orbital.traits ?? [];
    return {
      success: true,
      data: {
        ...materialized.data,
        ...siblingTraits.length > 0 ? { traits: [...materialized.data.traits, ...siblingTraits] } : {},
        uses: orbital.uses
      },
      warnings: materialized.warnings
    };
  }
  /**
   * Materialize a reference-form orbital: flatten the (already-inlined)
   * upstream orbital's whole trait/entity/page closure into the consumer,
   * unconditionally prefixed by `localName` (docs/Almadar_Orbital_Import.md
   * §4.3). JS twin of the (not-yet-landed) compiler's
   * `resolve_orbital_reference`.
   */
  async materializeOrbitalRef(upstream, ref, localName, localId, sourcePath, chain, aliasOrbitals, consumerEntityIds, consumerRoster, importedSchemaConfig, alias, listenSourceTargets) {
    const errors = [];
    const declaredConfig = upstream.config;
    if (ref.config) {
      for (const key of Object.keys(ref.config)) {
        if (!declaredConfig || !(key in declaredConfig)) {
          errors.push(
            `Orbital "${localName}" overrides config "${key}" but upstream orbital "${upstream.name}" does not declare that knob (ORB_O_CONFIG_UNKNOWN_KEY)`
          );
        }
      }
      if (errors.length > 0) return { success: false, errors };
    }
    const configOverrideValues = {};
    for (const [key, field] of Object.entries(ref.config ?? {})) {
      if (field.default !== void 0) configOverrideValues[key] = field.default;
    }
    const foldedConfig = declaredConfig ? overrideDeclaredKnobs(declaredConfig, configOverrideValues) : void 0;
    const savedSchemaConfig = this.schemaConfig;
    this.schemaConfig = importedSchemaConfig;
    let resolvedUpstream;
    try {
      resolvedUpstream = await this.resolve({ ...upstream, config: foldedConfig }, sourcePath, chain);
    } finally {
      this.schemaConfig = savedSchemaConfig;
    }
    if (!resolvedUpstream.success) {
      return {
        success: false,
        errors: resolvedUpstream.errors.map(
          (e) => `Orbital reference "${localName}" (${ref.ref}): ${e}`
        )
      };
    }
    const {
      entity: upstreamEntity,
      traits: upstreamTraits,
      pages: upstreamPages,
      // (C) the RESOLVED aux-entity set — includes whatever sibling-pull
      // surfaced (`pullSiblingTraits`'s aux-entity carry), never just
      // `upstream.auxiliaryEntities` (the pre-resolve INPUT, which misses
      // every un-rebound composed atom's own entity).
      auxiliaryEntities: upstreamAuxiliaryEntities
    } = resolvedUpstream.data;
    const onlySet = ref.only ? new Set(ref.only) : void 0;
    const omitSet = new Set(ref.omit ?? []);
    const keptTraits = upstreamTraits.filter(
      (rt) => onlySet ? onlySet.has(rt.trait.name) : !omitSet.has(rt.trait.name)
    );
    const keptNames = new Set(keptTraits.map((rt) => rt.trait.name));
    const removedNames = new Set(
      upstreamTraits.map((rt) => rt.trait.name).filter((n) => !keptNames.has(n))
    );
    for (const rt of keptTraits) {
      for (const embed of traitEmbedNamesOf(rt.trait)) {
        if (removedNames.has(embed)) {
          errors.push(
            `Orbital "${localName}" omits trait "${embed}" but surviving trait "${rt.trait.name}" still embeds it via @trait.${embed} (ORB_O_OMIT_EMBEDDED_TRAIT)`
          );
        }
      }
    }
    if (errors.length > 0) return { success: false, errors };
    const traitOverrides = applyImportTraitOverrides(
      keptTraits.map((rt) => rt.trait),
      new Set(upstreamTraits.map((rt) => rt.trait.name)),
      removedNames,
      ref,
      consumerEntityIds,
      localName
    );
    if (traitOverrides.errors.length > 0) return { success: false, errors: traitOverrides.errors };
    const siblingPrimaries = new Set(
      aliasOrbitals.filter((o) => o.name !== upstream.name && isInlineEntity(o.entity)).flatMap((o) => isInlineEntity(o.entity) ? [o.entity.name] : [])
    );
    let auxEntities = (upstreamAuxiliaryEntities ?? []).filter((e) => !siblingPrimaries.has(e.name));
    const subs = /* @__PURE__ */ new Map();
    for (const rt of keptTraits) subs.set(rt.trait.name, `${localName}${rt.trait.name}`);
    const keptFinalNames = new Set(subs.values());
    const { name: primaryFinalName, id: primaryId } = materializedPrimaryEntity(
      upstreamEntity,
      upstream.id,
      ref,
      localName
    );
    subs.set(upstreamEntity.name, primaryFinalName);
    for (const e of auxEntities) subs.set(e.name, `${localName}${e.name}`);
    const fieldSubs = new Map(Object.entries(ref.fields ?? {}));
    const closure = /* @__PURE__ */ new Set([upstreamEntity.name, ...auxEntities.map((e) => e.name)]);
    const upstreamUniverse = /* @__PURE__ */ new Set();
    const upstreamUniverseEntities = /* @__PURE__ */ new Map();
    const primaryEntityNames = /* @__PURE__ */ new Set();
    for (const o of aliasOrbitals) {
      if (o.name === upstream.name) continue;
      if (isInlineEntity(o.entity)) primaryEntityNames.add(o.entity.name);
      for (const entity of inlineEntityRefsOf(o)) {
        if (!closure.has(entity.name)) {
          upstreamUniverse.add(entity.name);
          upstreamUniverseEntities.set(entity.name, entity);
        }
      }
    }
    const entityIds = /* @__PURE__ */ new Map();
    const expectedIdentities = new Set(
      (upstream.expects ?? []).flatMap((e) => e.kind === "identity" && e.name !== void 0 ? [e.name] : [])
    );
    if (ref.entities) {
      for (const [upstreamKey, consumerName] of Object.entries(ref.entities)) {
        if (!upstreamUniverse.has(upstreamKey) && !expectedIdentities.has(upstreamKey)) {
          errors.push(
            `Orbital "${localName}" declares entities { ${upstreamKey}: ${consumerName} } but "${upstreamKey}" is not an out-of-orbital entity of upstream alias "${upstream.name}" (ORB_O_ENTITY_UNKNOWN_KEY)`
          );
          continue;
        }
        const consumerId = consumerEntityIds.get(consumerName);
        if (consumerId === void 0) {
          const pending = this.pendingEntityTargets.get(consumerEntityIds);
          if (pending === void 0) {
            errors.push(
              `Orbital "${localName}" entities { ${upstreamKey}: ${consumerName} } targets "${consumerName}", which the consumer schema does not declare (ORB_O_ENTITY_TARGET_UNKNOWN)`
            );
            continue;
          }
          pending.push({ localName, upstreamKey, consumerName });
          subs.set(upstreamKey, consumerName);
          continue;
        }
        subs.set(upstreamKey, consumerName);
        entityIds.set(consumerName, consumerId);
      }
      if (errors.length > 0) return { success: false, errors };
    }
    const referenced = /* @__PURE__ */ new Set();
    for (const rt of keptTraits) {
      if (rt.trait.linkedEntity && upstreamUniverse.has(rt.trait.linkedEntity)) {
        referenced.add(rt.trait.linkedEntity);
      }
      for (const name of collectEntityNamesInTrait(rt.trait, upstreamUniverse)) referenced.add(name);
      if (rt.trait.sourceEntityDefinition) {
        for (const name of relationTargetsOfEntity(rt.trait.sourceEntityDefinition, upstreamUniverse)) {
          referenced.add(name);
        }
      }
    }
    for (const name of relationTargetsOfEntity(upstreamEntity, upstreamUniverse)) referenced.add(name);
    for (const aux of auxEntities) {
      for (const name of relationTargetsOfEntity(aux, upstreamUniverse)) referenced.add(name);
    }
    for (const rp of upstreamPages) {
      if (rp.page.primaryEntity && upstreamUniverse.has(rp.page.primaryEntity)) {
        referenced.add(rp.page.primaryEntity);
      }
      for (const tr of rp.page.traits ?? []) {
        if (tr.linkedEntity && upstreamUniverse.has(tr.linkedEntity)) referenced.add(tr.linkedEntity);
      }
    }
    for (const name of referenced) {
      if (subs.has(name)) continue;
      const definition = upstreamUniverseEntities.get(name);
      const auxOnly = definition !== void 0 && !primaryEntityNames.has(name);
      if (definition && auxOnly && !definition.identity) {
        auxEntities = [...auxEntities, definition];
        subs.set(name, `${localName}${name}`);
        continue;
      }
      errors.push(
        `Orbital "${localName}" references out-of-orbital entity "${name}" with no "${name}: <consumer entity>" mapping in entities {} (ORB_O_ENTITY_UNMAPPED)`
      );
    }
    if (errors.length > 0) return { success: false, errors };
    const roleFields = /* @__PURE__ */ new Map();
    for (const entity of identityEntitiesOf(aliasOrbitals)) {
      for (const field of entity.fields) {
        if (!field.name) continue;
        const vocab = roleVocabularyOf(entity, field.name);
        if (vocab) roleFields.set(field.name, vocab);
      }
    }
    if (roleFields.size === 0) {
      for (const expect of upstream.expects ?? []) {
        if (expect.kind !== "identity") continue;
        const declared = { name: expect.name ?? "Viewer", fields: expect.shape ?? [] };
        for (const field of declared.fields) {
          if (!field.name) continue;
          const vocab = roleVocabularyOf(declared, field.name);
          if (vocab) roleFields.set(field.name, vocab);
        }
      }
    }
    const consumerRoleFields = /* @__PURE__ */ new Map();
    for (const entity of consumerRoster) {
      for (const field of entity.fields) {
        if (!field.name) continue;
        const vocab = roleVocabularyOf(entity, field.name);
        if (!vocab) continue;
        let values = consumerRoleFields.get(field.name);
        if (!values) {
          values = /* @__PURE__ */ new Set();
          consumerRoleFields.set(field.name, values);
        }
        for (const value of vocab) values.add(value);
      }
    }
    for (const [literal, targets] of Object.entries(ref.roles ?? {})) {
      const matchingFields = [...roleFields.entries()].filter(([, vocab]) => vocab.includes(literal)).map(([field]) => field).sort();
      if (matchingFields.length === 0) {
        errors.push(
          `Orbital "${localName}" declares roles { ${literal}: ... } but upstream alias "${upstream.name}"'s roster declares no role field with literal "${literal}" in its vocabulary (ORB_O_ROLE_UNKNOWN_UPSTREAM)`
        );
        continue;
      }
      for (const field of matchingFields) {
        const allowed = consumerRoleFields.get(field);
        for (const target of targets) {
          if (allowed?.has(target)) continue;
          errors.push(
            `Orbital "${localName}" roles { ${literal}: ${target} } targets "${target}", which is not a member of consumer field "${field}" on the roster (values: ${allowed ? [...allowed].sort().join(", ") : "(none)"}) (ORB_O_ROLE_TARGET_NOT_MEMBER)`
          );
        }
      }
    }
    if (errors.length > 0) return { success: false, errors };
    const idByName = /* @__PURE__ */ new Map();
    const materializedTraits = keptTraits.map((rt) => {
      const finalName = subs.get(rt.trait.name);
      const finalId = asTraitId(deriveMaterializedId(rt.trait.id, upstream.id, rt.trait.name, localName, "trait"));
      idByName.set(finalName, finalId);
      return { ...traitOverrides.overridden.get(rt.trait.name) ?? rt.trait, name: finalName, id: finalId };
    });
    const embeddersOf = /* @__PURE__ */ new Map();
    for (const t of materializedTraits) {
      for (const embed of traitEmbedNamesOf(t)) {
        const embedFinal = subs.get(embed);
        if (!embedFinal) continue;
        let embedders = embeddersOf.get(embedFinal);
        if (!embedders) {
          embedders = [];
          embeddersOf.set(embedFinal, embedders);
        }
        embedders.push(t.name);
      }
    }
    const parentConfigs = new Map(
      materializedTraits.map((t) => [t.name, t.config])
    );
    const transitiveEmbedderChain = (name) => {
      const chain2 = [];
      const seenNames = /* @__PURE__ */ new Set([name]);
      let cur = embeddersOf.get(name)?.[0];
      while (cur !== void 0 && !seenNames.has(cur)) {
        seenNames.add(cur);
        const cfg = parentConfigs.get(cur);
        if (cfg) chain2.push(cfg);
        cur = embeddersOf.get(cur)?.[0];
      }
      return chain2;
    };
    entityIds.set(primaryFinalName, primaryId);
    for (const e of auxEntities) {
      entityIds.set(
        subs.get(e.name),
        asEntityId(deriveMaterializedId(e.id, upstream.id, e.name, localName, "entity"))
      );
    }
    const upstreamPathSet = new Set(upstreamPages.map((rp) => rp.page.path));
    for (const path of [...Object.keys(ref.pages ?? {}), ...Object.keys(ref.mounts ?? {}), ...Object.keys(ref.pageModifiers ?? {})]) {
      if (!upstreamPathSet.has(path)) {
        errors.push(
          `Orbital "${localName}" names page path "${path}" but upstream orbital "${upstream.name}" has no page at that path (ORB_O_PAGE_UNKNOWN_PATH)`
        );
      }
    }
    for (const rp of upstreamPages) {
      if (weakensPageAccess(rp.page, ref.pageModifiers?.[rp.page.path])) {
        errors.push(
          `Orbital "${localName}" sets \`access: public\` on upstream page "${rp.page.path}", declared \`access: authenticated\` \u2014 an import cannot weaken inherited protection (ORB_O_PAGE_ACCESS_WEAKENED)`
        );
      }
    }
    if (errors.length > 0) return { success: false, errors };
    const pathMap = new Map(
      upstreamPages.map((rp) => [rp.page.path, ref.pages?.[rp.page.path] ?? rp.page.path])
    );
    const orbitalRename = upstream.name !== localName ? { from: upstream.name, to: localName } : void 0;
    const finalTraits = materializedTraits.map((trait) => {
      let next = renameTraitEmbeds(trait, subs);
      next = renameEntitiesInTrait(next, subs, entityIds);
      if (next.linkedEntity !== void 0) {
        const linkedEntityId = entityIds.get(next.linkedEntity);
        if (linkedEntityId !== void 0) next = { ...next, linkedEntityId };
      }
      next = rewriteEntityRefIds(next, subs, entityIds);
      next = renameSourceEntityDefinition(next, subs, entityIds);
      next = rewriteRoleLiteralsInTrait(next, roleFields, ref.roles ?? {});
      if (fieldSubs.size > 0) next = rewriteEntityFieldsInTrait(next, fieldSubs);
      next = rewriteListenSources(next, subs, idByName, orbitalRename, alias, listenSourceTargets);
      if (pathMap.size > 0) {
        next = rewriteTraitNavigateTargets(next, pathMap);
      }
      const schemaOnlyKeys = /* @__PURE__ */ new Set();
      for (const embedderName of embeddersOf.get(trait.name) ?? []) {
        const embedderConfig = parentConfigs.get(embedderName);
        next = resolveForwardedSiblingConfigFrom(
          next,
          embedderConfig ? [embedderConfig] : [],
          void 0,
          void 0,
          schemaOnlyKeys
        );
      }
      next = resolveForwardedSiblingConfigFrom(
        next,
        transitiveEmbedderChain(trait.name),
        void 0,
        void 0,
        schemaOnlyKeys
      );
      next = resolveForwardedSiblingConfigFrom(next, [], foldedConfig, importedSchemaConfig, schemaOnlyKeys);
      if (fieldSubs.size > 0 && next.config) {
        next = { ...next, config: rewriteEntityFieldsInConfig(next.config, fieldSubs) };
      }
      next = applyEventRenames(next, ref.events, keptFinalNames);
      return next;
    });
    for (const { trait: target, ...listen } of ref.listens ?? []) {
      const finalName = subs.get(target);
      const index = finalName === void 0 ? -1 : finalTraits.findIndex((t) => t.name === finalName);
      if (index < 0) {
        return {
          success: false,
          errors: [
            `Orbital "${localName}" listens routes into "${target}", which is not a trait of the imported set (after omit/only) (ORB_O_LISTEN_TARGET_UNKNOWN)`
          ]
        };
      }
      const source = listen.source;
      const imported = source?.kind === "trait" ? subs.get(source.trait) : void 0;
      const routed = imported !== void 0 ? { ...listen, source: { kind: "trait", trait: imported } } : listen;
      const trait = finalTraits[index];
      finalTraits[index] = { ...trait, listens: [...trait.listens ?? [], routed] };
    }
    const renamedPrimaryEntity = rewriteRoleLiteralsInEntity(
      renameEntityRelationTargets(
        rewriteEntityFieldsInEntity(
          {
            ...upstreamEntity,
            name: primaryFinalName,
            id: primaryId
          },
          fieldSubs
        ),
        subs,
        entityIds
      ),
      roleFields,
      ref.roles ?? {}
    );
    const extendResult = applyExtendFields(
      renamedPrimaryEntity,
      ref.extend,
      fieldSubs,
      consumerEntityIds,
      localName
    );
    if (extendResult.errors.length > 0) return { success: false, errors: extendResult.errors };
    const retypeResult = applyRetypeAndPersistence(extendResult.entity, ref, consumerEntityIds, localName);
    if (retypeResult.errors.length > 0) return { success: false, errors: retypeResult.errors };
    const primaryEntity = retypeResult.entity;
    const finalAuxEntities = auxEntities.map((e) => {
      const finalName = subs.get(e.name);
      return rewriteRoleLiteralsInEntity(
        renameEntityRelationTargets(
          rewriteEntityFieldsInEntity(
            {
              ...e,
              name: finalName,
              id: entityIds.get(finalName)
            },
            fieldSubs
          ),
          subs,
          entityIds
        ),
        roleFields,
        ref.roles ?? {}
      );
    });
    const finalPages = upstreamPages.map((rp) => {
      const original = rp.page;
      const mountNames = ref.mounts?.[original.path] ?? [];
      const finalPageTraits = [
        ...(original.traits ?? []).filter((tr) => !removedNames.has(tr.ref)).map((tr) => {
          const finalRef = subs.get(tr.ref) ?? tr.ref;
          const refId = idByName.get(finalRef);
          return {
            ...tr,
            ref: finalRef,
            ...refId !== void 0 ? { refId } : {},
            ...tr.linkedEntity !== void 0 ? { linkedEntity: subs.get(tr.linkedEntity) ?? tr.linkedEntity } : {}
          };
        }),
        ...mountNames.map((name) => ({ ref: name }))
      ];
      const sourcePage = original.sourcePage ?? original.id;
      return {
        ...withPageModifiers(original, ref.pageModifiers?.[original.path]),
        // Gap (E) — unconditionally prefixed, same as traits/entities (§4.3):
        // two imports of the same upstream orbital with distinct `pages {}`
        // path maps must not collide on page NAME (`Duplicate page name`).
        // `id` is the page-scope sibling of `deriveMaterializedId` (never
        // `mintId`) — fresh and pairwise-disjoint across imports, same as
        // trait/entity ids above.
        name: `${localName}${original.name}`,
        id: asPageId(deriveMaterializedId(original.id, upstream.id, original.name, localName, "page")),
        path: pathMap.get(original.path) ?? original.path,
        ...sourcePage !== void 0 ? { sourcePage } : {},
        ...original.primaryEntity !== void 0 ? { primaryEntity: subs.get(original.primaryEntity) ?? original.primaryEntity } : {},
        traits: finalPageTraits
      };
    });
    const forwardedKnobs = /* @__PURE__ */ new Set();
    for (const rt of keptTraits) {
      for (const field of Object.values(rt.trait.config ?? {})) {
        if (typeof field.default === "string" && field.default.startsWith("@config.")) {
          const knob = field.default.slice("@config.".length);
          if (knob.length > 0 && !knob.includes(".")) forwardedKnobs.add(knob);
        }
      }
    }
    const prunedConfig = foldedConfig ? Object.fromEntries(Object.entries(foldedConfig).filter(([key]) => forwardedKnobs.has(key))) : void 0;
    const orbitalId = localId ?? (upstream.id ? asOrbitalId(deriveId(upstream.id, localName)) : void 0);
    refResolverLog.info("orbital-import:materialize", {
      local: localName,
      upstream: upstream.name,
      traits: finalTraits.length,
      pages: finalPages.length
    });
    return {
      success: true,
      data: {
        ...upstream,
        name: localName,
        ...orbitalId ? { id: orbitalId } : {},
        entity: primaryEntity,
        ...finalAuxEntities.length > 0 ? { auxiliaryEntities: finalAuxEntities } : {},
        traits: finalTraits,
        pages: finalPages,
        emits: void 0,
        listens: void 0,
        exposes: void 0,
        ...prunedConfig ? { config: prunedConfig } : { config: void 0 },
        uses: void 0,
        reference: void 0
      },
      warnings: []
    };
  }
  /**
   * Add local traits for resolution.
   */
  addLocalTraits(traits) {
    for (const trait of traits) {
      this.localTraits.set(trait.name, trait);
      if (trait.id) {
        this.localTraitsById.set(trait.id, trait);
      }
    }
  }
  /**
   * Clear loader cache.
   */
  clearCache() {
    this.loader?.clearCache();
  }
};
function isInlineTraitWithStateMachine(tr) {
  return isInlineTrait(tr);
}
function reconcileOrbitalRefEventIdRenames(schema, flattened) {
  if (!schema.ledger) return;
  const renamedRefs = schema.orbitals.filter((o) => o.reference?.events && Object.keys(o.reference.events).length > 0).map((o) => ({ localName: o.name, eventSubs: o.reference.events }));
  if (renamedRefs.length === 0) return;
  const sites = [];
  for (const { localName, eventSubs } of renamedRefs) {
    const newKeys = new Set(Object.values(eventSubs));
    const orbital = flattened.find((o) => o.name === localName);
    if (!orbital) continue;
    for (const tr of orbital.traits) {
      if (!isInlineTraitWithStateMachine(tr)) continue;
      for (const ev of tr.stateMachine?.events ?? []) {
        if (!ev.id || !newKeys.has(ev.key)) continue;
        const entry = schema.ledger.entries[ev.id];
        if (!entry || entry.curName === ev.key) continue;
        sites.push({ orbitalName: orbital.name, traitName: tr.name, oldId: ev.id, newKey: ev.key });
      }
    }
  }
  if (sites.length === 0) return;
  const candidateIds = new Set(sites.map((s) => s.oldId));
  const owners = /* @__PURE__ */ new Map();
  for (const orbital of flattened) {
    for (const tr of orbital.traits) {
      if (!isInlineTraitWithStateMachine(tr)) continue;
      for (const ev of tr.stateMachine?.events ?? []) {
        if (ev.id && candidateIds.has(ev.id)) {
          const ownerKey = `${orbital.name}\0${tr.name}`;
          const set = owners.get(ev.id) ?? /* @__PURE__ */ new Set();
          set.add(ownerKey);
          owners.set(ev.id, set);
        }
      }
    }
  }
  const renameInPlace = /* @__PURE__ */ new Map();
  const deriveSites = [];
  const seen = /* @__PURE__ */ new Set();
  for (const site of sites) {
    const ownerCount = owners.get(site.oldId)?.size ?? 1;
    if (ownerCount <= 1) {
      renameInPlace.set(site.oldId, site.newKey);
    } else if (!seen.has(site.oldId)) {
      seen.add(site.oldId);
      deriveSites.push({ site, newId: asEventId(deriveId(site.oldId, site.newKey)) });
    }
  }
  for (const { site, newId } of deriveSites) {
    const orbital = flattened.find((o) => o.name === site.orbitalName);
    if (!orbital) continue;
    orbital.traits = orbital.traits.map((tr) => {
      if (!isInlineTraitWithStateMachine(tr) || tr.name !== site.traitName) return tr;
      let next = tr;
      if (next.stateMachine?.events) {
        next = {
          ...next,
          stateMachine: {
            ...next.stateMachine,
            events: next.stateMachine.events.map((ev) => ev.id === site.oldId ? { ...ev, id: newId } : ev),
            transitions: (next.stateMachine.transitions ?? []).map(
              (t) => t.eventId === site.oldId ? { ...t, eventId: newId } : t
            )
          }
        };
      }
      if (next.emits) {
        next = {
          ...next,
          emits: next.emits.map(
            (e) => typeof e !== "string" && e.eventId === site.oldId ? { ...e, eventId: newId } : e
          )
        };
      }
      if (next.listens) {
        next = {
          ...next,
          listens: next.listens.map((l) => {
            const eventId = l.eventId === site.oldId ? newId : l.eventId;
            const triggersId = l.triggersId === site.oldId ? newId : l.triggersId;
            return eventId === l.eventId && triggersId === l.triggersId ? l : { ...l, eventId, triggersId };
          })
        };
      }
      return next;
    });
  }
  const at = (/* @__PURE__ */ new Date()).toISOString();
  let ledger = schema.ledger;
  for (const [oldId, newKey] of renameInPlace) {
    ledger = ledgerRename(ledger, oldId, newKey, at);
  }
  for (const { site, newId } of deriveSites) {
    const src = ledger.entries[site.oldId];
    if (!src) continue;
    const ownerTrait = flattened.find((o) => o.name === site.orbitalName)?.traits.find((tr) => isInlineTraitWithStateMachine(tr) && tr.name === site.traitName);
    const newEntry = {
      id: newId,
      kind: src.kind,
      bakedName: src.bakedName,
      curName: site.newKey,
      renames: [...src.renames, { from: src.curName, to: site.newKey, at }],
      owner: src.owner,
      ...ownerTrait?.id !== void 0 ? { parent: ownerTrait.id } : {}
    };
    ledger = { ...ledger, entries: { ...ledger.entries, [newId]: newEntry } };
  }
  schema.ledger = ledger;
}
async function resolveSchema(schema, options) {
  const resolver = new ReferenceResolver({ ...options, schemaConfig: schema.config });
  const errors = [];
  const warnings = [];
  const resolved = [];
  const flattenResult = await resolver.resolveOrbitalImports(schema);
  if (!flattenResult.success) {
    return { success: false, errors: flattenResult.errors };
  }
  const orbitals = flattenResult.data;
  for (const orbital of orbitals) {
    const inlineTraits = orbital.traits.filter(
      (t) => typeof t !== "string" && "stateMachine" in t
    );
    resolver.addLocalTraits(inlineTraits);
  }
  resolver.seedSchemaEntityIds(orbitals);
  for (const orbital of orbitals) {
    const result = await resolver.resolve(orbital);
    if (!result.success) {
      errors.push(`Orbital "${orbital.name}": ${result.errors.join(", ")}`);
    } else {
      resolved.push(result.data);
      resolver.noteResolvedEntityIds(result.data);
      warnings.push(...result.warnings.map((w) => `Orbital "${orbital.name}": ${w}`));
    }
  }
  if (errors.length > 0) {
    return { success: false, errors };
  }
  applyDeferredEntityFieldMerges(resolved);
  resolver.uniquifyCrossOrbitalPulledSiblings(resolved);
  return { success: true, data: resolved, warnings };
}
function withCallSiteTraitId(id, result) {
  if (id === void 0 || !result.success) return result;
  return { ...result, data: { ...result.data, trait: { ...result.data.trait, id } } };
}

// src/entities/loader/schema-loader.ts
function isElectron() {
  return typeof process !== "undefined" && !!process.versions?.electron;
}
function isBrowser() {
  return typeof window !== "undefined" && !isElectron();
}
function isNode() {
  return typeof process !== "undefined" && !isBrowser();
}

// src/entities/loader/lazy-page.ts
async function loadLazyPage(loader, page, importerPath) {
  const loaded2 = await loader.load(page.orbRef, importerPath);
  if (!loaded2.success) return loaded2;
  const { schema, sourcePath } = loaded2.data;
  if (!schema.orbitals.some((o) => o.name === page.orbital)) {
    return {
      success: false,
      error: `Lazy page ${page.path} expects orbital "${page.orbital}" in ${sourcePath}, found: ${schema.orbitals.map((o) => o.name).join(", ")}`
    };
  }
  return { success: true, data: schema };
}
var HttpImportChain = class _HttpImportChain {
  chain = [];
  /**
   * Try to add a path to the chain.
   * @returns Error message if circular, null if OK
   */
  push(absolutePath) {
    if (this.chain.includes(absolutePath)) {
      const cycle = [
        ...this.chain.slice(this.chain.indexOf(absolutePath)),
        absolutePath
      ];
      return `Circular import detected: ${cycle.join(" -> ")}`;
    }
    this.chain.push(absolutePath);
    return null;
  }
  /**
   * Remove the last path from the chain.
   */
  pop() {
    this.chain.pop();
  }
  /**
   * Clone the chain for nested loading.
   */
  clone() {
    const newChain = new _HttpImportChain();
    newChain.chain = [...this.chain];
    return newChain;
  }
};
var HttpLoaderCache = class {
  cache = /* @__PURE__ */ new Map();
  get(url) {
    return this.cache.get(url);
  }
  set(url, schema) {
    this.cache.set(url, schema);
  }
  has(url) {
    return this.cache.has(url);
  }
  clear() {
    this.cache.clear();
  }
  get size() {
    return this.cache.size;
  }
};
var HttpLoader = class {
  options;
  cache;
  constructor(options) {
    this.options = {
      basePath: options.basePath,
      stdLibPath: options.stdLibPath ?? "",
      scopedPaths: options.scopedPaths ?? {},
      fetchOptions: options.fetchOptions,
      timeout: options.timeout ?? 3e4,
      credentials: options.credentials ?? "same-origin"
    };
    this.cache = new HttpLoaderCache();
  }
  /**
   * Load a schema from an import path.
   */
  async load(importPath, fromPath, chain) {
    const importChain = chain ?? new HttpImportChain();
    const resolveResult = this.resolvePath(importPath, fromPath);
    if (!resolveResult.success) {
      return resolveResult;
    }
    const absoluteUrl = resolveResult.data;
    const circularError = importChain.push(absoluteUrl);
    if (circularError) {
      return { success: false, error: circularError };
    }
    try {
      const cached = this.cache.get(absoluteUrl);
      if (cached) {
        return { success: true, data: cached };
      }
      const loadResult = await this.fetchSchema(absoluteUrl);
      if (!loadResult.success) {
        return loadResult;
      }
      const loaded2 = {
        schema: loadResult.data,
        sourcePath: absoluteUrl,
        importPath
      };
      this.cache.set(absoluteUrl, loaded2);
      return { success: true, data: loaded2 };
    } finally {
      importChain.pop();
    }
  }
  /**
   * Load a specific orbital from a schema by name.
   */
  async loadOrbital(importPath, orbitalName, fromPath, chain) {
    const schemaResult = await this.load(importPath, fromPath, chain);
    if (!schemaResult.success) {
      return schemaResult;
    }
    const schema = schemaResult.data.schema;
    let orbital;
    if (orbitalName) {
      const found = schema.orbitals.find(
        (o) => o.name === orbitalName
      );
      if (!found) {
        return {
          success: false,
          error: `Orbital "${orbitalName}" not found in ${importPath}. Available: ${schema.orbitals.map((o) => o.name).join(", ")}`
        };
      }
      orbital = found;
    } else {
      if (schema.orbitals.length === 0) {
        return {
          success: false,
          error: `No orbitals found in ${importPath}`
        };
      }
      orbital = schema.orbitals[0];
    }
    return {
      success: true,
      data: {
        orbital,
        orbitals: schema.orbitals,
        ...schema.config ? { schemaConfig: schema.config } : {},
        sourcePath: schemaResult.data.sourcePath,
        importPath
      }
    };
  }
  /**
   * Resolve an import path to an absolute URL.
   */
  resolvePath(importPath, fromPath) {
    if (importPath.startsWith("http://") || importPath.startsWith("https://")) {
      return { success: true, data: importPath };
    }
    if (importPath.startsWith("std/")) {
      return this.resolveStdPath(importPath);
    }
    if (importPath.startsWith("@")) {
      return this.resolveScopedPath(importPath);
    }
    if (importPath.startsWith("./") || importPath.startsWith("../")) {
      return this.resolveRelativePath(importPath, fromPath);
    }
    return this.resolveRelativePath(`./${importPath}`, fromPath);
  }
  /**
   * Resolve a standard library path.
   */
  resolveStdPath(importPath) {
    if (!this.options.stdLibPath) {
      return {
        success: false,
        error: `Standard library URL not configured. Cannot load: ${importPath}`
      };
    }
    const relativePath = importPath.slice(4);
    let absoluteUrl = this.joinUrl(this.options.stdLibPath, relativePath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Resolve a scoped package path.
   */
  resolveScopedPath(importPath) {
    const match = importPath.match(/^(@[^/]+)/);
    if (!match) {
      return {
        success: false,
        error: `Invalid scoped package path: ${importPath}`
      };
    }
    const scope = match[1];
    const scopeRoot = this.options.scopedPaths[scope];
    if (!scopeRoot) {
      return {
        success: false,
        error: `Scoped package "${scope}" not configured. Available: ${Object.keys(this.options.scopedPaths).join(", ") || "none"}`
      };
    }
    const relativePath = importPath.slice(scope.length + 1);
    let absoluteUrl = this.joinUrl(scopeRoot, relativePath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Resolve a relative path.
   */
  resolveRelativePath(importPath, fromPath) {
    const baseUrl = fromPath ? this.getParentUrl(fromPath) : this.options.basePath;
    let absoluteUrl = this.joinUrl(baseUrl, importPath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Fetch and parse an OrbitalSchema from a URL.
   */
  async fetchSchema(url) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.options.timeout
      );
      try {
        const response = await fetch(url, {
          ...this.options.fetchOptions,
          credentials: this.options.credentials,
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            ...this.options.fetchOptions?.headers
          }
        });
        if (!response.ok) {
          return {
            success: false,
            error: `HTTP ${response.status}: ${response.statusText} for ${url}`
          };
        }
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          return {
            success: false,
            error: `Invalid JSON in ${url}: ${e instanceof Error ? e.message : String(e)}`
          };
        }
        const parseResult = OrbitalSchemaSchema.safeParse(data);
        if (!parseResult.success) {
          const errors = parseResult.error.errors.map((e) => `  - ${e.path.join(".")}: ${e.message}`).join("\n");
          return {
            success: false,
            error: `Invalid schema in ${url}:
${errors}`
          };
        }
        return { success: true, data: parseResult.data };
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        return {
          success: false,
          error: `Request timeout for ${url} (${this.options.timeout}ms)`
        };
      }
      return {
        success: false,
        error: `Failed to fetch ${url}: ${e instanceof Error ? e.message : String(e)}`
      };
    }
  }
  /**
   * Join URL parts, handling relative paths and trailing slashes.
   */
  joinUrl(base, path) {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    if (typeof URL !== "undefined") {
      try {
        const baseUrl = base.endsWith("/") ? base : base + "/";
        return new URL(path, baseUrl).href;
      } catch {
      }
    }
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith("/") ? path : "/" + path;
    if (normalizedPath.startsWith("/./")) {
      return normalizedBase + normalizedPath.slice(2);
    }
    if (normalizedPath.startsWith("/../")) {
      const baseParts = normalizedBase.split("/");
      baseParts.pop();
      return baseParts.join("/") + normalizedPath.slice(3);
    }
    return normalizedBase + normalizedPath;
  }
  /**
   * Get parent URL (directory) from a URL.
   */
  getParentUrl(url) {
    if (typeof URL !== "undefined") {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split("/");
        pathParts.pop();
        urlObj.pathname = pathParts.join("/");
        return urlObj.href;
      } catch {
      }
    }
    const lastSlash = url.lastIndexOf("/");
    if (lastSlash !== -1) {
      return url.slice(0, lastSlash);
    }
    return url;
  }
  /**
   * Clear the cache.
   */
  clearCache() {
    this.cache.clear();
  }
  /**
   * Get cache statistics.
   */
  getCacheStats() {
    return { size: this.cache.size };
  }
};
function createHttpLoader(basePath, options) {
  return new HttpLoader({
    basePath,
    ...options
  });
}

// src/entities/loader/unified-loader.ts
var externalLoaderModule = null;
async function getExternalLoaderModule() {
  if (externalLoaderModule) {
    return externalLoaderModule;
  }
  if (isBrowser()) {
    return null;
  }
  try {
    externalLoaderModule = await import('./external-loader-2MKPT5PS.js');
    return externalLoaderModule;
  } catch {
    return null;
  }
}
var UnifiedImportChain = class _UnifiedImportChain {
  chain = [];
  push(path) {
    const normalized = this.normalizePath(path);
    if (this.chain.includes(normalized)) {
      const cycle = [
        ...this.chain.slice(this.chain.indexOf(normalized)),
        normalized
      ];
      return `Circular import detected: ${cycle.join(" -> ")}`;
    }
    this.chain.push(normalized);
    return null;
  }
  pop() {
    this.chain.pop();
  }
  clone() {
    const newChain = new _UnifiedImportChain();
    newChain.chain = [...this.chain];
    return newChain;
  }
  normalizePath(path) {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return path.replace(/\\/g, "/");
  }
};
var UnifiedLoader = class {
  options;
  httpLoader = null;
  fsLoader = null;
  fsLoaderInitialized = false;
  cache = /* @__PURE__ */ new Map();
  constructor(options) {
    this.options = options;
    this.httpLoader = new HttpLoader({
      basePath: options.basePath,
      stdLibPath: options.stdLibPath,
      scopedPaths: options.scopedPaths,
      ...options.http
    });
  }
  /**
   * Initialize the filesystem loader if available.
   */
  async initFsLoader() {
    if (this.fsLoaderInitialized) {
      return;
    }
    this.fsLoaderInitialized = true;
    if (this.options.forceLoader === "http") {
      return;
    }
    const module = await getExternalLoaderModule();
    if (module) {
      this.fsLoader = new module.ExternalOrbitalLoader({
        basePath: this.options.basePath,
        stdLibPath: this.options.stdLibPath,
        scopedPaths: this.options.scopedPaths,
        ...this.options.fileSystem
      });
    }
  }
  /**
   * Determine which loader to use for an import path.
   */
  getLoaderForPath(importPath) {
    if (this.options.forceLoader) {
      return this.options.forceLoader;
    }
    if (importPath.startsWith("http://") || importPath.startsWith("https://")) {
      return "http";
    }
    if (importPath.startsWith("std/") && this.options.stdLibPath) {
      if (this.options.stdLibPath.startsWith("http://") || this.options.stdLibPath.startsWith("https://")) {
        return "http";
      }
    }
    if (importPath.startsWith("@") && this.options.scopedPaths) {
      const match = importPath.match(/^(@[^/]+)/);
      if (match) {
        const scopePath = this.options.scopedPaths[match[1]];
        if (scopePath && (scopePath.startsWith("http://") || scopePath.startsWith("https://"))) {
          return "http";
        }
      }
    }
    if (isBrowser()) {
      return "http";
    }
    return "filesystem";
  }
  /**
   * Load a schema from an import path.
   *
   * Note: We delegate chain management to the inner loader (HttpLoader or FsLoader).
   * The inner loader handles circular import detection, so we don't push/pop here.
   * We only use the unified cache to avoid duplicate loads across loaders.
   */
  async load(importPath, fromPath, chain) {
    await this.initFsLoader();
    const importChain = chain ?? new UnifiedImportChain();
    const loaderType = this.getLoaderForPath(importPath);
    const resolveResult = this.resolvePath(importPath, fromPath);
    if (!resolveResult.success) {
      return resolveResult;
    }
    const absolutePath = resolveResult.data;
    const cached = this.cache.get(absolutePath);
    if (cached) {
      return { success: true, data: cached };
    }
    let result;
    if (loaderType === "http") {
      if (!this.httpLoader) {
        return {
          success: false,
          error: "HTTP loader not available"
        };
      }
      result = await this.httpLoader.load(importPath, fromPath, importChain);
    } else {
      if (!this.fsLoader) {
        if (this.httpLoader) {
          result = await this.httpLoader.load(
            importPath,
            fromPath,
            importChain
          );
        } else {
          return {
            success: false,
            error: `Filesystem loader not available and import "${importPath}" cannot be loaded via HTTP. This typically happens when loading local files in a browser environment.`
          };
        }
      } else {
        result = await this.fsLoader.load(importPath, fromPath, importChain);
      }
    }
    if (result.success) {
      this.cache.set(absolutePath, result.data);
    }
    return result;
  }
  /**
   * Load a specific orbital from a schema by name.
   */
  async loadOrbital(importPath, orbitalName, fromPath, chain) {
    const schemaResult = await this.load(importPath, fromPath, chain);
    if (!schemaResult.success) {
      return schemaResult;
    }
    const schema = schemaResult.data.schema;
    if (orbitalName) {
      const found = schema.orbitals.find((o) => o.name === orbitalName);
      if (!found) {
        return {
          success: false,
          error: `Orbital "${orbitalName}" not found in ${importPath}. Available: ${schema.orbitals.map((o) => o.name).join(", ")}`
        };
      }
      return {
        success: true,
        data: {
          orbital: found,
          orbitals: schema.orbitals,
          ...schema.config ? { schemaConfig: schema.config } : {},
          sourcePath: schemaResult.data.sourcePath,
          importPath
        }
      };
    }
    if (schema.orbitals.length === 0) {
      return {
        success: false,
        error: `No orbitals found in ${importPath}`
      };
    }
    return {
      success: true,
      data: {
        orbital: schema.orbitals[0],
        orbitals: schema.orbitals,
        ...schema.config ? { schemaConfig: schema.config } : {},
        sourcePath: schemaResult.data.sourcePath,
        importPath
      }
    };
  }
  /**
   * Resolve an import path to an absolute path/URL.
   */
  resolvePath(importPath, fromPath) {
    const loaderType = this.getLoaderForPath(importPath);
    if (loaderType === "http" && this.httpLoader) {
      return this.httpLoader.resolvePath(importPath, fromPath);
    }
    if (this.fsLoader) {
      return this.fsLoader.resolvePath(importPath, fromPath);
    }
    if (this.httpLoader) {
      return this.httpLoader.resolvePath(importPath, fromPath);
    }
    return {
      success: false,
      error: "No loader available for path resolution"
    };
  }
  /**
   * Clear all caches.
   */
  clearCache() {
    this.cache.clear();
    this.httpLoader?.clearCache();
    this.fsLoader?.clearCache();
  }
  /**
   * Get combined cache statistics.
   */
  getCacheStats() {
    let size = this.cache.size;
    if (this.httpLoader) {
      size += this.httpLoader.getCacheStats().size;
    }
    if (this.fsLoader) {
      size += this.fsLoader.getCacheStats().size;
    }
    return { size };
  }
  /**
   * Check if filesystem loading is available.
   */
  async hasFilesystemAccess() {
    await this.initFsLoader();
    return this.fsLoader !== null;
  }
  /**
   * Get current environment info.
   */
  getEnvironmentInfo() {
    return {
      isElectron: isElectron(),
      isBrowser: isBrowser(),
      isNode: isNode(),
      hasFilesystem: this.fsLoader !== null
    };
  }
};
function createUnifiedLoader(options) {
  return new UnifiedLoader(options);
}
createLogger("almadar:runtime:studio-config");

// src/traits/UsesIntegration.ts
async function preprocessSchema(schema, options) {
  const namespaceEvents = options.namespaceEvents ?? true;
  const resolveResult = await resolveSchema(schema, options);
  if (!resolveResult.success) {
    return { success: false, errors: resolveResult.errors };
  }
  const resolved = resolveResult.data;
  const warnings = resolveResult.warnings;
  const preprocessedOrbitals = [];
  const entitySharing = {};
  const eventNamespaces = {};
  for (const resolvedOrbital of resolved) {
    const orbitalName = resolvedOrbital.name;
    const persistence = resolvedOrbital.entitySource?.persistence ?? resolvedOrbital.entity.persistence ?? "persistent";
    entitySharing[orbitalName] = {
      entityName: resolvedOrbital.entity.name,
      persistence,
      isShared: persistence !== "runtime",
      sourceAlias: resolvedOrbital.entitySource?.alias,
      collectionName: resolvedOrbital.entity.collection
    };
    eventNamespaces[orbitalName] = {};
    for (const resolvedTrait of resolvedOrbital.traits) {
      const traitName = resolvedTrait.trait.name;
      const namespace = {
        emits: {},
        listens: {}
      };
      if (namespaceEvents && resolvedTrait.source.type === "imported") {
        const emits = resolvedTrait.trait.emits ?? [];
        for (const emit of emits) {
          const eventName = typeof emit === "string" ? emit : emit.event;
          namespace.emits[eventName] = `${orbitalName}.${traitName}.${eventName}`;
        }
        const listens = resolvedTrait.trait.listens ?? [];
        for (const listen of listens) {
          namespace.listens[listen.event] = listen.event;
        }
      }
      eventNamespaces[orbitalName][traitName] = namespace;
    }
    const preprocessedOrbital = {
      name: orbitalName,
      description: resolvedOrbital.original.description,
      visual_prompt: resolvedOrbital.original.visual_prompt,
      // The orbital's skin (registry key or inline ThemeDefinition) — dropped
      // by this field-by-field rebuild until 2026-09-01, so `/api/schema`
      // served theme-less orbitals and OrbitalThemeProvider no-opped.
      theme: resolvedOrbital.original.theme,
      // Resolved entity (always inline now)
      entity: resolvedOrbital.entity,
      // Resolved traits (inline definitions)
      traits: (resolvedOrbital.traits || []).map((rt) => {
        if (rt.config || rt.linkedEntity) {
          return {
            ref: rt.trait.name,
            config: rt.config,
            linkedEntity: rt.linkedEntity,
            // Include the resolved trait definition for runtime
            _resolved: rt.trait
          };
        }
        return rt.trait;
      }),
      // Resolved pages (inline definitions with path overrides applied)
      pages: resolvedOrbital.pages.map((rp) => rp.page),
      // Preserve other fields
      exposes: resolvedOrbital.original.exposes,
      domainContext: resolvedOrbital.original.domainContext,
      design: resolvedOrbital.original.design,
      // Gap #22: pass through auxiliary entities so OrbitalServerRuntime's
      // mock-seed branch registers SearchResult / FilterTarget / PagedItem
      // alongside the molecule's primary entity — and DERIVE them for
      // no-rebind imported traits. L1-emitted registry .orbs carry no
      // auxiliaryEntities (only the Rust inline phase adds them, and the
      // runtime path never runs it), so a trait imported without `-> Entity`
      // stays bound to its atom's own entity while that entity was never
      // registered or seeded here — hard zero rows.
      auxiliaryEntities: deriveAuxiliaryEntities(resolvedOrbital)
    };
    preprocessedOrbitals.push(preprocessedOrbital);
  }
  const preprocessedSchema = {
    ...schema,
    orbitals: preprocessedOrbitals
  };
  return {
    success: true,
    data: {
      schema: preprocessedSchema,
      entitySharing,
      eventNamespaces,
      warnings
    }
  };
}
function deriveAuxiliaryEntities(resolvedOrbital) {
  const combined = [...resolvedOrbital.original.auxiliaryEntities ?? []];
  const seen = /* @__PURE__ */ new Set([resolvedOrbital.entity.name]);
  for (const aux of combined) {
    if (typeof aux !== "string" && "name" in aux && typeof aux.name === "string") {
      seen.add(aux.name);
    }
  }
  for (const rt of resolvedOrbital.traits) {
    if (rt.source.type !== "imported" || rt.linkedEntity) continue;
    const imported = resolvedOrbital.imports.orbitals.get(rt.source.alias);
    const entity = imported?.orbital?.entity;
    if (!entity || typeof entity === "string" || !("fields" in entity) || !entity.name) continue;
    if (seen.has(entity.name)) continue;
    seen.add(entity.name);
    combined.push(entity);
  }
  return combined.length > 0 ? combined : void 0;
}
function getIsolatedCollectionName(orbitalName, entitySharing) {
  const info = entitySharing[orbitalName];
  if (!info) {
    throw new Error(`Unknown orbital: ${orbitalName}`);
  }
  if (info.persistence === "runtime") {
    return `${orbitalName}_${info.entityName}`;
  }
  return info.collectionName || info.entityName.toLowerCase() + "s";
}
function getNamespacedEvent(orbitalName, traitName, eventName, eventNamespaces) {
  const orbitalNs = eventNamespaces[orbitalName];
  if (!orbitalNs) return eventName;
  const traitNs = orbitalNs[traitName];
  if (!traitNs) return eventName;
  return traitNs.emits[eventName] || eventName;
}
function isNamespacedEvent(eventName) {
  return eventName.includes(".");
}
function parseNamespacedEvent(eventName) {
  const parts = eventName.split(".");
  if (parts.length === 3) {
    return { orbital: parts[0], trait: parts[1], event: parts[2] };
  }
  if (parts.length === 2) {
    return { trait: parts[0], event: parts[1] };
  }
  return { event: eventName };
}

// src/effects/composition/index.ts
var composition_exports = {};
__export(composition_exports, {
  applyEventWiring: () => applyEventWiring,
  composeBehaviors: () => composeBehaviors,
  detectLayoutStrategy: () => detectLayoutStrategy,
  pipeBehaviors: () => pipeBehaviors
});
function findInlineTrait(orbitals, name) {
  for (const orbital of orbitals) {
    for (const traitRef of orbital.traits) {
      if (isInlineTrait(traitRef) && traitRef.name === name) {
        return traitRef;
      }
    }
  }
  return null;
}
function hasEmit(emits, event) {
  return emits.some((e) => e.event === event);
}
function hasListen(listens, event, triggers) {
  return listens.some((l) => l.event === event && l.triggers === triggers);
}
function applyEventWiring(orbitals, wiring) {
  const cloned = JSON.parse(
    JSON.stringify(orbitals)
  );
  for (const entry of wiring) {
    const sourceTrait = findInlineTrait(cloned, entry.from);
    if (sourceTrait) {
      if (!sourceTrait.emits) {
        sourceTrait.emits = [];
      }
      if (!hasEmit(sourceTrait.emits, entry.event)) {
        sourceTrait.emits.push({
          event: entry.event,
          scope: "external"
        });
      }
    }
    const targetTrait = findInlineTrait(cloned, entry.to);
    if (targetTrait) {
      if (!targetTrait.listens) {
        targetTrait.listens = [];
      }
      if (!hasListen(targetTrait.listens, entry.event, entry.triggers)) {
        targetTrait.listens.push({
          event: entry.event,
          triggers: entry.triggers,
          scope: "external"
        });
      }
    }
  }
  return cloned;
}

// src/effects/composition/layout-strategy.ts
function hasSequentialChain(wiring) {
  if (wiring.length < 2) {
    return false;
  }
  const adjacency = /* @__PURE__ */ new Map();
  const allTargets = /* @__PURE__ */ new Set();
  for (const entry of wiring) {
    let targets = adjacency.get(entry.from);
    if (!targets) {
      targets = /* @__PURE__ */ new Set();
      adjacency.set(entry.from, targets);
    }
    targets.add(entry.to);
    allTargets.add(entry.to);
  }
  const roots = [];
  for (const from of adjacency.keys()) {
    if (!allTargets.has(from)) {
      roots.push(from);
    }
  }
  const starts = roots.length > 0 ? roots : [...adjacency.keys()];
  for (const start of starts) {
    let current = start;
    let length = 1;
    const visited = /* @__PURE__ */ new Set([current]);
    while (true) {
      const nexts = adjacency.get(current);
      if (!nexts || nexts.size === 0) break;
      let advanced = false;
      for (const next of nexts) {
        if (!visited.has(next)) {
          visited.add(next);
          current = next;
          length++;
          advanced = true;
          break;
        }
      }
      if (!advanced) break;
    }
    if (length >= 3) {
      return true;
    }
  }
  return false;
}
function detectLayoutStrategy(orbitals, eventWiring) {
  if (eventWiring && eventWiring.length > 0 && hasSequentialChain(eventWiring)) {
    return "wizard-flow";
  }
  const count = orbitals.length;
  if (count <= 1) {
    return "single";
  }
  if (count <= 4) {
    return "tabs";
  }
  return "sidebar";
}

// src/effects/composition/compose-behaviors.ts
function isSchema(input) {
  return "orbitals" in input && Array.isArray(input.orbitals);
}
function asDefinitions(inputs) {
  return inputs.flatMap(
    (input) => isSchema(input) ? input.orbitals : [input]
  );
}
function mergeLedgers(inputs) {
  const merged = /* @__PURE__ */ new Map();
  let sawLedger = false;
  for (const input of inputs) {
    if (!isSchema(input) || input.ledger === void 0) continue;
    sawLedger = true;
    for (const [id, entry] of Object.entries(input.ledger.entries)) {
      if (!merged.has(id)) merged.set(id, entry);
    }
  }
  if (!sawLedger) return void 0;
  const entries = {};
  for (const [id, entry] of [...merged.entries()].sort(
    ([a], [b]) => a < b ? -1 : a > b ? 1 : 0
  )) {
    entries[id] = entry;
  }
  return { schemaVersion: 1, entries };
}
function mergeSchemaVersions(inputs) {
  let max;
  for (const input of inputs) {
    if (!isSchema(input) || input.schemaVersion === void 0) continue;
    max = max === void 0 ? input.schemaVersion : Math.max(max, input.schemaVersion);
  }
  return max;
}
function toKebabCase(name) {
  return name.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function generatePages(orbitals, strategy) {
  switch (strategy) {
    case "single": {
      const orbital = orbitals[0];
      const name = orbital?.name ?? "Main";
      return [
        {
          name: `${name}Page`,
          path: "/",
          isInitial: true,
          primaryEntity: getEntityName(orbital)
        }
      ];
    }
    case "dashboard": {
      return [
        {
          name: "DashboardPage",
          path: "/",
          viewType: "dashboard",
          isInitial: true
        }
      ];
    }
    case "sidebar":
    case "tabs":
    case "wizard-flow": {
      return orbitals.map((orbital, index) => ({
        name: `${orbital.name}Page`,
        path: index === 0 ? "/" : `/${toKebabCase(orbital.name)}`,
        isInitial: index === 0,
        primaryEntity: getEntityName(orbital)
      }));
    }
  }
}
function getEntityName(orbital) {
  if (!orbital) return void 0;
  const entity = orbital.entity;
  if (typeof entity === "string") {
    return entity.replace(".entity", "");
  }
  return entity.name;
}
function composeBehaviors(input) {
  const {
    appName,
    orbitals: rawInputs,
    layoutStrategy: strategyInput,
    eventWiring
  } = input;
  const rawOrbitals = asDefinitions(rawInputs);
  const wiredOrbitals = eventWiring && eventWiring.length > 0 ? applyEventWiring(rawOrbitals, eventWiring) : rawOrbitals;
  const strategy = !strategyInput || strategyInput === "auto" ? detectLayoutStrategy(wiredOrbitals, eventWiring) : strategyInput;
  const pages = generatePages(wiredOrbitals, strategy);
  const orbitalsWithPages = wiredOrbitals.map((orbital, index) => {
    if (orbital.pages && orbital.pages.length > 0) {
      return orbital;
    }
    const page = strategy === "dashboard" || strategy === "single" ? pages[0] : pages[index];
    return {
      ...orbital,
      pages: page ? [page] : []
    };
  });
  const ledger = mergeLedgers(rawInputs);
  const schemaVersion = mergeSchemaVersions(rawInputs);
  const schema = {
    name: appName,
    version: "1.0.0",
    orbitals: orbitalsWithPages,
    ...schemaVersion !== void 0 ? { schemaVersion } : {},
    ...ledger !== void 0 ? { ledger } : {}
  };
  return {
    schema,
    layout: {
      strategy,
      pageCount: pages.length
    },
    wiring: {
      connections: eventWiring?.length ?? 0
    }
  };
}

// src/effects/composition/pipe.ts
function pipeBehaviors(seed, ...steps) {
  let current = seed;
  for (const step of steps) {
    current = step(current);
  }
  return current;
}

export { AwaitingRegistry, EffectExecutor, EventBus, HANDLER_MANIFEST, InFlightCalls, MountLifecycle, ServerLegCollector, StateMachineManager, TickScheduler, TraitMountError, UNMOUNT_EVENT, alreadyDeliveredFrom, appNavItems, applyEventWiring, applyOrbitalEventResponse, browserStoreName, buildEmitsFromTraits, buildSourceMatcher, buildTraitIndex, buildTraitIndexForOrbital, clientResolvesRenderBindings, collectDeclaredConfigDefaults, collectDeclaredEntityDefaults, collectListenerTargets, composeBehaviors, composition_exports, createChannelTransport, createClientEffectHandlers, createClientKernel, createHttpLoader, createIndexStageRunner, createInitialTraitState, createLocalStoreTransport, createMemoryCircuitStore, createResidenceTransport, createServerEffectHandlers, createTestExecutor, createTickScheduler, createUnifiedLoader, deliveryOf, detectLayoutStrategy, dispatchDeclaredInput, dispatchWithServerLeg, evaluateOrbitalEvent, eventRouteKey, findEntityAmongOrbitals, findInitialState, findTransition, formatPayloadValidationError, freshDeliveryGuardBindings, getIsolatedCollectionName, getNamespacedEvent, holdUntilServed, inlineNavItems, isBrowser, isEffectTuple, isElectron, isNamespacedEvent, isNode, isValidDurationString, loadLazyPage, normalizeEventKey, openBrowserHost, openBrowserStore, openBrowserStoreTransport, openBundledBrowserStore, parseDurationString, parseListenSource, parseNamespacedEvent, parseOrbitalTraits, pipeBehaviors, postServerLeg, preprocessSchema, processEvent, readTraitFrame, runServerEffectStage, runTraitCascade, seedBrowserStore, selectDispatchCandidates, serveChannel, sigilThemeKey, stampEmitSource, validateEventPayload, validatePayloadShapes };
